import { CFO_WORKBOOK_WRITER } from './cfoWriterContract'
import {
  areFuseGridLocalPackageWorkflowsEnabled,
  isFuseGridElectronDesktop,
  isFuseGridNativeDesktopShell,
  isFuseGridWebFirstTableauCreatorEnabled,
} from './desktop/isDesktop'
import {
  CREATOR_COLLAB_PROTOCOL_VERSION,
  type CreatorCollabAck,
  type CreatorCollabCommittedEvent,
} from '../protocol/creatorCollabProtocol'
import {
  normalizeDocumentSessionServerMessage,
  type DocumentSessionBootstrap,
} from '../protocol/documentSessionProtocol'
import {
  normalizeWorkbookBootstrap,
  type WorkbookBootstrap,
} from '../protocol/workbookDocumentProtocol'
import { safeParseJson } from '../utils/safeStorageParse'
import { accessSessionIdentity } from './accessSessionIdentity'
import type {
  FgArtifactManifest,
  FgDashboardResponse,
  FgDashboardOperation,
  FgDashboardAuthorRequest,
  FgDashboardAuthorResponse,
  FgDashboardProposal,
  FgDashboardRevisionsResponse,
  FgMetricDefinition,
  FgMetricKind,
  FgMetricsResponse,
  FgOperationalValue,
  FgOperationalValuesResponse,
  FgPanelTemplatesResponse,
  FgRollbackResponse,
} from '../analysis/analysisTypes'
import { clearDemoSessionMarker } from './demoSession'
import type {
  CeoCockpitResponse,
  CeoCockpitPeriodRun,
  CeoCockpitSnapshotIndex,
  CeoCriticalInputs,
  CeoCockpitSettings,
  DemoCompaniesResponse,
} from '../components/parity-dev/cfo/ceo/ceoCockpitTypes'
import {
  accountingBootstrapToCfoBootstrap,
  accountingCashToMetricComparisons,
  accountingCashToPlanning,
  accountingForecastToStatementForecast,
  accountingLedgerToGlSummary,
  accountingReceivablesToArAnalysis,
  accountingStatementsToRatios,
  accountingStatementsToMetricComparisons,
  accountingStatementsToPresentation,
  accountingStatementsToValuation,
  accountingStatementsToRenderContract,
  type AccountingCashResponse,
  type AccountingCapabilityDetail,
  type AccountingForecastResponse,
  type AccountingLedgerResponse,
  type AccountingReceivablesResponse,
  type AccountingStatementResponse,
  type AccountingWorkbookBootstrap,
} from './accountingCfoAdapters'

export type ServerLoginResponse = {
  status: string
  access_token: string
  refresh_token: string
  access_expires_at: string
  refresh_expires_at: string
  user_id: string
  session_id: string
  refresh_generation?: number
  refresh_cookie?: boolean
  must_set_password: boolean
  mfa_challenge_id?: string | null
  mfa_allowed_methods?: string[] | null
  next_path?: string | null
}

export type ServerSignupResponse = ServerLoginResponse & {
  site_slug: string
  workbook_id: string
  plan_code?: string | null
  plan_trial_ends_at?: string | null
}

export type ServerLocalAuthBootstrapResponse = {
  login: ServerLoginResponse
  seeded_email: string
  seeded_password: string
  created_user: boolean
  created_site: boolean
}

export type ServerDevLoginBridgeResponse = {
  code: string
  bridge_url: string
  target_origin: string
  expires_at: string
}

export type ServerForgotPasswordResponse = {
  status: string
}

export type ServerMfaFactor = {
  id: string
  factor_type: string
  display_name: string
  created_at: string
  confirmed_at: string
  last_used_at?: string | null
}

export type ServerMfaFactorListResponse = {
  factors: ServerMfaFactor[]
  recovery_codes_remaining: number
}

export type ServerTotpEnrollmentResponse = {
  factor: ServerMfaFactor
  secret: string
  otpauth_uri: string
}

export type ServerRecoveryCodesResponse = {
  codes: string[]
  recovery_codes_remaining: number
}

export type ServerPasskeyRegistrationResponse = {
  challenge_id: string
  options: any
}

export type ServerPasskeyAuthenticationResponse = {
  challenge_id: string
  options: any
}

// ---------------- Desktop handoff (Edit in Desktop) ----------------

export type DesktopCreateOpenIntentRequest = {
  workbook_id: string
  rev?: string | null
  state?: any | null
}

export type DesktopCreateOpenIntentResponse = {
  code: string
  expires_at_unix_ms: number
}

export type DesktopRedeemOpenIntentRequest = {
  code: string
  device?: any | null
}

export type DesktopRedeemOpenIntentResponse = {
  access_token: string
  refresh_token: string
  access_expires_at: string
  refresh_expires_at: string
  user_id: string
  session_id: string
  must_set_password: boolean
  workbook_id: string
  rev?: string | null
  state?: any | null
}

// ---------------- AI (Bedrock gateway) ----------------

export type AiChatMessage = {
  role: 'user' | 'assistant'
  content: string
  metadata?: {
    kind?: string
    chart?: {
      kind: 'waterfall' | 'bar' | 'table'
      title: string
      subtitle?: string | null
      items?: Array<{
        label: string
        value: number
        direction?: 'up' | 'down' | 'neutral' | string | null
        note?: string | null
        prompt?: string | null
      }>
      columns?: Array<{
        key: string
        label: string
        align?: 'left' | 'right' | 'center' | string | null
      }>
      rows?: Array<Record<string, any>>
      summary?: string | null
    }
    [key: string]: any
  } | null
}

export type AiChatRequest = {
  // Optional provider override (bedrock | openai). Defaults to server configuration.
  provider?: string | null
  // Optional override (server falls back to BEDROCK_MODEL_ID / OPENAI_MODEL_ID)
  model_id?: string | null
  system?: string | null
  messages: AiChatMessage[]
  // Optional image attachment (data URL). OpenAI only.
  image_data_url?: string | null
  max_tokens?: number | null
  temperature?: number | null
  top_p?: number | null
  reasoning_effort?: string | null
  // OpenAI Responses API web search. When enabled, the backend uses the hosted web_search tool.
  web_search?: {
    enabled?: boolean | null
    search_context_size?: 'low' | 'medium' | 'high' | string | null
    external_web_access?: boolean | null
    domains?: string[] | null
    user_location?: any
  } | null
}

export type AiChatResponse = {
  model_id: string
  output_text: string
  sources?: Array<{
    url: string
    title?: string | null
  }> | null
  usage?: {
    provider: string
    input_tokens?: number | null
    output_tokens?: number | null
    total_tokens?: number | null
    cache_read_input_tokens?: number | null
    cache_write_input_tokens?: number | null
    estimated_cost_usd?: number | null
    pricing_source?: string | null
  } | null
}

export type CompanyIntelligenceRuntimeMode = 'off' | 'validate' | 'enforce'

export type CompanyIntelligenceStatusResponse = {
  schema_version: string
  request_id: string
  status: {
    mode: CompanyIntelligenceRuntimeMode
    eligible: boolean
    eligibility_reason?: string | null
    head_status: 'valid' | 'stale' | 'missing' | 'invalid' | string
    snapshot_id?: string | null
    source_manifest_hash?: string | null
    validation_id?: string | null
    freshness_deadline?: string | null
    promoted_at?: string | null
    refresh_job_id?: string | null
    refresh_status?: string | null
    refresh_error_code?: string | null
  }
}

export type CreateFinancialWorkRunRequest = {
  workbook_id: string
  surface: 'website' | 'excel' | 'mcp' | 'evaluation'
  goal: string
  task_kind?: string | null
  workbook_instance_id?: string | null
  workbook_revision?: string | null
  selection?: string | null
  compute_mode?: 'economy' | 'adaptive' | 'quality' | null
  review_policy?: 'minimal' | 'default' | 'client_facing' | 'evaluation_sol_single_pass' | null
  task_cost_cap_usd?: number | null
  mutation_scope?: Record<string, any>
  effective_period?: string | null
  known_at?: string | null
  scenario_id?: string | null
}

export type FinancialWorkInspectionRequest = {
  planning_overlay?: Record<string, any> | null
  surface_context_overlay?: Record<string, any> | null
}

export type FinancialWorkTurnRequest =
  | { kind: 'submit_claims'; proposals: Array<Record<string, any>> }
  | { kind: 'record_review'; passed: boolean; receipt: Record<string, any> }
  | { kind: 'run_semantic_review' }
  | { kind: 'record_proof'; obligation_key: string; receipt: Record<string, any> }
  | { kind: 'accept_artifact_plan'; plan: Record<string, any>; mutation_scope_hash: string }
  | { kind: 'accept_artifact_plan'; candidate_hash: string; mutation_scope_hash: string }
  | { kind: 'record_host_proof'; stage: 'checkpoint_created' | 'mutation_applied' | 'workbook_validated' | 'saved' | 'reopened' | 'render_validated'; receipt: Record<string, any> }
  | { kind: 'record_failure'; stage: string; reason: string; receipt?: Record<string, any> }
  | { kind: 'finalize_analysis'; outcome: 'analysis_completed' | 'no_change_needed' | 'completed_with_exceptions'; exceptions: string[] }

export type CompanyIntelligenceContextItemRequest = {
  scope: 'company' | 'workbook' | 'conversation'
  item_type: string
  value: any
  confirmation_state?: 'proposed' | 'user_confirmed'
  conversation_run_id?: string | null
  provenance?: Record<string, any> | null
  expires_at?: string | null
}

export type CompanyIntelligenceContextItem = {
  context_item_id: string
  scope: 'company' | 'workbook' | 'conversation'
  item_type: string
  value: any
  authority_class: string
  confirmation_state: 'inferred' | 'proposed' | 'user_confirmed' | 'rejected' | 'expired'
  provenance?: Record<string, any> | null
  conversation_run_id?: string | null
  effective_from?: string | null
  effective_to?: string | null
  known_at?: string | null
  observed_at?: string | null
  expires_at?: string | null
  source_version?: string | null
  content_hash: string
  created_at?: string | null
  updated_at?: string | null
}

export type CompanyIntelligenceContextItemsResponse = {
  schema_version: string
  request_id: string
  items: CompanyIntelligenceContextItem[]
}

export type CompanyIntelligenceContextItemMutationResponse = {
  schema_version: string
  request_id: string
  item: CompanyIntelligenceContextItem
  refresh_job_id?: string | null
  superseded_context_item_id?: string | null
}

export type AiTokenUsage = NonNullable<AiChatResponse['usage']>

export type AiAgentEvent = {
  id: string
  kind: string
  message: string
  data?: any
}

export type AiAgentToolCall = {
  id: string
  name: string
  arguments: Record<string, any>
}

export type AiAgentToolResult = {
  tool_call_id: string
  name: string
  ok: boolean
  result?: any
  error?: string | null
  elapsed_ms?: number | null
}

export type AiAgentOutput = {
  id: string
  kind: 'html_report_canvas' | 'spreadsheet_evidence' | string
  title: string
  content_html?: string | null
  markdown?: string | null
  content_text?: string | null
  mime_type?: string | null
  file_extension?: string | null
  rows?: any[] | null
  metadata?: any
}

export type AiAgentAnswer = {
  headline: string
  executive_summary: string
  detailed_narrative: string[]
  caveats: string[]
  evidence_labels: string[]
}

export type AiAgentVisual = {
  id: string
  kind: string
  title: string
  subtitle?: string | null
  spec: any
}

export type AiAgentFollowup = {
  label: string
  prompt: string
}

export type AiAgentGapAnalysis = {
  summary: string
  missing_context: string[]
  recommended_next_data: string[]
  recommended_next_tools: string[]
}

export type AiCompletionSummary = {
  version: string
  headline: string
  what_i_found: string[]
  what_i_changed: string[]
  what_matters: string[]
  verification_performed: string[]
  exceptions_and_unresolved_items: string[]
  sheets_and_ranges_changed: string[]
  financially_verified: boolean
  completion_hash: string
}

export type AiContextUsedReceipt = {
  version: string
  snapshot_id: string
  snapshot_hash: string
  company_name: string
  provider: string
  source_as_of: string
  known_at: string
  effective_through: string
  latest_available_period?: string | null
  latest_reconciled_period?: string | null
  financial_period_attested: boolean
  financial_period_disclosure?: string | null
  source_health: string
  business_context_updated_at: string
  account_understanding_version: string
  account_layout_version_id?: string | null
  plan_publication_id?: string | null
  relevant_metric_ids: string[]
  relevant_statement_ids: string[]
  evidence_ref_count: number
  context_conflicts: string[]
  receipt_hash: string
}

export type AiFinancialWorkReceipt = {
  claim_validation_receipt?: Record<string, any> | null
  review_receipt?: Record<string, any> | null
  context_used?: AiContextUsedReceipt | null
  context_used_product_id?: string | null
  completion?: {
    completion_summary?: AiCompletionSummary | null
    product_id?: string | null
  } | null
}

export type AiAgentTimings = {
  total_elapsed_ms: number
  model_elapsed_ms: number
  tool_elapsed_ms: number
  output_elapsed_ms: number
  telemetry_elapsed_ms: number
}

export type AiAgentTelemetryStatus = {
  status: 'queued' | 'enqueue_failed' | string
  queue_depth: number
  warnings: string[]
}

export type AiAgentStartRequest = {
  provider?: string | null
  model_id?: string | null
  workbook_id?: string | null
  connection_id?: string | null
  active_section?: string | null
  selected_metric_id?: string | null
  selected_scenario_id?: string | null
  question: string
  workspace_context?: any
  output_targets?: string[] | null
  max_tokens?: number | null
  reasoning_effort?: string | null
  web_search?: AiChatRequest['web_search']
}

export type AiAgentStartResponse = {
  agent_run_id: string
  financial_work_run_id?: string | null
  status: string
  events: AiAgentEvent[]
  tool_calls: AiAgentToolCall[]
}

export type AiAgentContinueRequest = AiAgentStartRequest & {
  agent_run_id: string
  financial_work_run_id?: string | null
  tool_results: AiAgentToolResult[]
}

export type AiAgentCancelRequest = {
  agent_run_id: string
}

export type AiAgentContinueResponse = {
  agent_run_id: string
  status: string
  events: AiAgentEvent[]
  tool_calls?: AiAgentToolCall[]
  model_id: string
  usage?: AiTokenUsage | null
  sources?: Array<{
    url: string
    title?: string | null
  }> | null
  answer: AiAgentAnswer
  visuals: AiAgentVisual[]
  followups: AiAgentFollowup[]
  gap_analysis: AiAgentGapAnalysis
  timings: AiAgentTimings
  telemetry: AiAgentTelemetryStatus
  telemetry_warnings: string[]
  outputs: AiAgentOutput[]
  proposed_claims?: Array<Record<string, any>>
  financial_work_receipt?: AiFinancialWorkReceipt | null
}

export type AiAgentCancelResponse = {
  agent_run_id: string
  status: string
  events: AiAgentEvent[]
}

export type AiAgentRunLogListResponse = {
  runs: any[]
  warnings: string[]
}

export type AiAgentRunLogDetailResponse = {
  run?: any | null
  events: any[]
  tool_calls: any[]
  model_io: any[]
  outputs: any[]
  warnings: string[]
}

export type AiAgentClientEventRequest = {
  agent_run_id: string
  workbook_id?: string | null
  connection_id?: string | null
  selected_metric_id?: string | null
  event: string
  payload?: any
  elapsed_ms?: number | null
}

export type AiAgentClientEventResponse = {
  telemetry: AiAgentTelemetryStatus
}

export type AiAgentTelemetryHealthResponse = {
  configured: boolean
  queue_depth: number
  enqueued_count: number
  enqueue_failed_count: number
  flush_success_count: number
  flush_failure_count: number
  last_flush_elapsed_ms: number
  last_error?: string | null
}

// First-party event / feedback / bug-report spine (telemetry-spine).
// These mirror the server `crate::events` payload shapes.
export type FgTrackEventPayload = {
  event: string
  ai_trace_id?: string | null
  surface?: string | null
  workbook_id?: string | null
  session_id?: string | null
  props?: Record<string, unknown>
  client_ts?: string | null
}

export type FgAiFeedbackPayload = {
  ai_trace_id: string
  verdict: 'up' | 'down'
  note?: string | null
  surface?: string | null
  workbook_id?: string | null
  session_id?: string | null
  props?: Record<string, unknown>
  client_ts?: string | null
}

export type FgBugReportPayload = {
  ai_trace_id?: string | null
  surface: string
  message: string
  context?: Record<string, unknown>
  session_id?: string | null
  client_ts?: string | null
}

export type EventsIngestRequest = {
  events?: FgTrackEventPayload[]
  ai_feedback?: FgAiFeedbackPayload[]
  bug_reports?: FgBugReportPayload[]
}

export type EventsIngestResponse = {
  accepted: { events: number; ai_feedback: number; bug_reports: number }
  telemetry: { status: string; queue_depth: number; warnings: string[] }
}

export type FounderActivitySummary = {
  window_days: number
  since: string
  active_users: number
  sessions: number
  logins: number
  page_views: number
  dwell_ms: number
  errors: number
}

export type FounderActivityUserRow = {
  user_id: string
  email: string
  tenant_names: string[]
  site_names: string[]
  first_seen_at?: string | null
  last_seen_at?: string | null
  login_count: number
  session_count: number
  page_view_count: number
  dwell_ms: number
  error_count: number
}

export type FounderActivitySessionRow = {
  session_id: string
  user_id: string
  email: string
  created_at: string
  last_seen_at?: string | null
  revoked_at?: string | null
  user_agent?: string | null
  ip_address?: string | null
  page_view_count: number
  dwell_ms: number
  error_count: number
  last_path?: string | null
}

export type FounderActivityPageRow = {
  path: string
  surface?: string | null
  workbook_id?: string | null
  site_slug?: string | null
  page_view_count: number
  unique_users: number
  unique_sessions: number
  dwell_ms: number
  error_count: number
  last_seen_at?: string | null
}

export type FounderActivityCfoAreaRow = {
  cfo_page: string
  page_title?: string | null
  workbook_id?: string | null
  site_slug?: string | null
  page_view_count: number
  unique_sessions: number
  dwell_ms: number
  error_count: number
  first_seen_at?: string | null
  last_seen_at?: string | null
}

export type FounderActivityErrorRow = {
  id: string
  source: string
  event: string
  user_id?: string | null
  email?: string | null
  session_id?: string | null
  surface?: string | null
  workbook_id?: string | null
  path?: string | null
  message: string
  status?: number | null
  context: Record<string, unknown>
  created_at: string
}

export type FounderActivityDashboardResponse = {
  summary: FounderActivitySummary
  users: FounderActivityUserRow[]
  sessions: FounderActivitySessionRow[]
  pages: FounderActivityPageRow[]
  cfo_areas: FounderActivityCfoAreaRow[]
  errors: FounderActivityErrorRow[]
}

/** What a person may do in the founder console. */
export type FgCapability = 'tour_author' | 'crm'

export type AccessGrantRow = {
  id: string
  email: string
  capability: FgCapability
  grantedAt?: string | null
  note?: string | null
  /** They can sign in today. */
  hasLogin: boolean
  /** Invited, but hasn't set a password yet — access does not work until they do. */
  pendingSetup: boolean
}

export type FounderActivityAccessResponse = {
  allowed: boolean
  email?: string | null
  /** Founder-only areas: system health, billing, AI spend, activity. */
  founder?: boolean
  capabilities?: FgCapability[]
}

// WS2: AI spend ledger (mirrors server/src/ai_telemetry.rs + spend_guard.rs serialized shapes).
export type AiSpendByClient = {
  workbookId: string
  connectionId: string
  calls: number
  costUsd: number
  tokens: number
}
export type AiSpendByUser = {
  userId: string
  calls: number
  costUsd: number
  tokens: number
}
export type AiSpendBySection = {
  section: string
  modelId: string
  calls: number
  costUsd: number
  tokens: number
}
export type AiSpendByDay = {
  day: string
  calls: number
  costUsd: number
  tokens: number
}
export type AiSpendConversation = {
  conversationId: string
  sampleCallId: string
  firstAt: string
  lastAt: string
  section: string
  activeSection?: string | null
  metricLabel?: string | null
  question: string
  workbookId?: string | null
  userId?: string | null
  modelId: string
  calls: number
  costUsd: number
  tokens: number
  latencyMs: number
  workspaceContextBytes?: number
  payloadByteSize?: number
  instantCardKey?: string | null
  instantCardSurface?: string | null
  instantCardSourceComponent?: string | null
  firstLayerToolCount?: number
  toolNames?: string | null
  enrichmentPacketHit?: boolean | null
  enrichmentPacketStatus?: string | null
  enrichmentPacketBuildLatencyMs?: number
  enrichmentFallbackAgentUsed?: boolean | null
  enrichmentPacketKey?: string | null
  contextPacketHit?: boolean | null
  contextPacketStatus?: string | null
  contextPacketLookupMs?: number
  contextPacketBuildMs?: number
  contextPacketBytes?: number
  contextPacketKey?: string | null
  contextRejectedByCap?: boolean | null
  contextCapBytes?: number
  contextRejectionReason?: string | null
  usedBroadTool?: boolean | null
  usedColdArtifact?: boolean | null
  usedFallbackAgent?: boolean | null
}
export type AiSpendRecentCall = {
  callId: string
  conversationId?: string | null
  createdAt: string
  section: string
  label?: string | null
  activeSection?: string | null
  metricLabel?: string | null
  question?: string | null
  provider: string
  modelId: string
  workbookId?: string | null
  userId?: string | null
  costUsd: number
  tokens: number
  latencyMs: number
  status: string
  stopReason?: string | null
  upstreamRequestId?: string | null
  anthropicRequestId?: string | null
  awsRequestId?: string | null
  workspaceContextBytes?: number
  payloadByteSize?: number
  instantCardKey?: string | null
  instantCardSurface?: string | null
  instantCardSourceComponent?: string | null
  firstLayerToolCount?: number
  toolNames?: string | null
  enrichmentPacketHit?: boolean | null
  enrichmentPacketStatus?: string | null
  enrichmentPacketBuildLatencyMs?: number
  enrichmentFallbackAgentUsed?: boolean | null
  enrichmentPacketKey?: string | null
  contextPacketHit?: boolean | null
  contextPacketStatus?: string | null
  contextPacketLookupMs?: number
  contextPacketBuildMs?: number
  contextPacketBytes?: number
  contextPacketKey?: string | null
  contextRejectedByCap?: boolean | null
  contextCapBytes?: number
  contextRejectionReason?: string | null
  usedBroadTool?: boolean | null
  usedColdArtifact?: boolean | null
  usedFallbackAgent?: boolean | null
}
export type AiSpendReport = {
  configured: boolean
  totalCostUsd: number
  totalTokens: number
  totalCalls: number
  perClient: AiSpendByClient[]
  perUser: AiSpendByUser[]
  perSection: AiSpendBySection[]
  perDay: AiSpendByDay[]
  conversations: AiSpendConversation[]
  recentCalls: AiSpendRecentCall[]
}
export type AiSpendCaps = {
  userDaily?: number | null
  userMonthly?: number | null
  workbookDaily?: number | null
  workbookMonthly?: number | null
}
export type AiSpendCounterRow = {
  scope_kind: string
  scope_id: string
  period_kind: string
  period_key: string
  spend_usd: number
  tokens: number
  cap_usd?: number | null
}
export type FounderClientLabel = {
  workbookId: string
  siteSlug?: string | null
  companyName?: string | null
}
export type FounderUserLabel = {
  userId: string
  email?: string | null
  fullName?: string | null
}
export type FounderAiSpendResponse = {
  report: AiSpendReport
  caps: AiSpendCaps
  counters: AiSpendCounterRow[]
  clientLabels: FounderClientLabel[]
  userLabels: FounderUserLabel[]
}
export type FounderAiSpendFullPrompt = {
  callId: string
  section: string
  modelId: string
  systemPrompt: string
  userPrompt: string
  outputText: string
  truncated: boolean
}
// Founder system-health snapshot (server/src/founder_health.rs)
export type FounderSystemHealthCacheStat = {
  name: string
  entries: number
  maxEntries: number
}
export type FounderSystemHealthAlarm = {
  name: string
  state: string
  reason: string
  updatedAt?: string | null
}
export type FounderSystemHealthPoint = { t: string; v: number }
export type FounderSystemHealthSeries = {
  id: string
  label: string
  unit: string
  points: FounderSystemHealthPoint[]
}
export type FounderSystemHealthQueue = {
  name: string
  visibleMessages?: number | null
  oldestMessageAgeSecs?: number | null
}
export type FounderBillingResponse = {
  generated_at: string
  stripe_configured: boolean
  total_tenants: number
  legacy_tenants: number
  active_subscriptions: number
  trialing_subscriptions: number
  app_trials: number
  past_due: number
  canceled: number
  mrr_cents: number
  trial_pipeline_cents: number
  partner_client_units: number
  partner_quantity_drift: number
  partner_sync_failures: number
  by_plan: Array<{
    plan_code: string
    plan_interval: string
    plan_status: string
    count: number
  }>
  trials_expiring_soon: Array<{
    tenant_name: string
    slug: string
    plan_code: string
    trial_ends_at: string
    days_left: number
  }>
  recent_events: Array<{
    event_type: string
    tenant_id?: string | null
    created_at: string
  }>
}

export type FounderSystemHealthResponse = {
  generatedAt: string
  process: {
    rssMb?: number | null
    cacheStats: FounderSystemHealthCacheStat[]
    parquetMaterializeInUse: number
    parquetMaterializeMax: number
  }
  alarms: FounderSystemHealthAlarm[]
  series: FounderSystemHealthSeries[]
  queues: FounderSystemHealthQueue[]
  awsErrors: string[]
}

// #region Guided-tour analytics (server/src/tour_analytics.rs)
// Funnel over `fg_events` for the founder console's Tours tab. Mirrors
// TourAnalyticsResponse; camelCase via serde rename_all.

export type FounderTourSummary = {
  tourSlug: string
  starts: number
  finishes: number
  leads: number
  ctaClicks: number
  engagedSessions: number
  /** Finishes / starts — Storylane's headline "Completion". */
  completionPct: number
  engagementPct: number
  leadPct: number
  medianIntent: number
  demoSessions: number
  embedSessions: number
  anchorMisses: number
  lastSeenAt?: string | null
}

export type FounderTourStep = {
  tourSlug: string
  stepId: string
  stepIndex: number
  chapterId?: string | null
  sessionsReached: number
  pctOfStarters: number
  /** Share of everyone who reached the previous step — where people leave. */
  stepRetentionPct: number
  medianDwellMs: number
  anchorMisses: number
}

/** An anchor that stopped resolving for real visitors — the drift alarm. */
export type FounderTourAnchorAlert = {
  tourSlug: string
  stepId: string
  anchorId: string
  anchorKind?: string | null
  misses: number
  sessions: number
  lastSeenAt?: string | null
}

export type FounderTourSession = {
  sessionId: string
  tourSlug: string
  startedAt?: string | null
  stepsViewed: number
  stepCount: number
  completionPct: number
  dwellMs: number
  intent: number
  finished: boolean
  identified: boolean
  clickedCta: boolean
  variant?: string | null
  isDemo: boolean
  embed: boolean
  lastStepId?: string | null
}

export type FounderTourAnalyticsResponse = {
  windowDays: number
  since: string
  tours: FounderTourSummary[]
  steps: FounderTourStep[]
  anchorAlerts: FounderTourAnchorAlert[]
  variants: Array<{
    tourSlug: string
    variant: string | null
    starts: number
    finishes: number
    leads: number
    completionPct: number
    leadPct: number
    medianIntent: number
  }>
  sessions: FounderTourSession[]
}
// #endregion

// #region CRM (server/src/crm.rs) — leads joined to tour behaviour

export type CrmStage = 'new' | 'working' | 'qualified' | 'demo_booked' | 'customer' | 'lost'

export type CrmContact = {
  id: string
  email: string
  fullName?: string | null
  firmName?: string | null
  phone?: string | null
  source: string
  stage: CrmStage
  nextActionAt?: string | null
  lastActivityAt?: string | null
  createdAt?: string | null
  activityCount: number
  sessionIds: string[]
  wonAmountCents?: number | null
  wonAt?: string | null
}

export type CrmActivity = {
  id: string
  kind: 'note' | 'stage_change' | 'email_out' | 'call' | 'tour' | 'system'
  body: string
  meta: unknown
  createdAt?: string | null
}

export type CrmContactDetail = {
  contact: CrmContact
  activities: CrmActivity[]
  tourSessions: FounderTourSession[]
}
// #endregion

// #region Guided-tour persistence + share links (server/src/tour_store.rs)

export type PublishedTourResponse = {
  slug: string
  version: number
  doc: unknown
  docSha256: string
}

/** Which gate, if any, is still unmet on a share link. */
export type TourShareStatus = 'ok' | 'passcode_required' | 'email_required' | 'expired' | 'revoked'

export type TourShareOpenResponse = {
  status: TourShareStatus
  tourSlug?: string | null
  label?: string | null
  doc?: unknown
  version?: number | null
  audience: Record<string, string>
}

export type TourListRow = {
  id: string
  slug: string
  name: string
  context: string
  publishedVersion?: number | null
  draftVersion?: number | null
  archived: boolean
  updatedAt?: string | null
}

export type TourVersionRow = {
  id: string
  version: number
  status: 'draft' | 'published' | 'archived'
  doc: unknown
  docSha256: string
  anchorCheck: unknown
  notes?: string | null
  createdAt?: string | null
}

export type TourShareLinkRow = {
  token: string
  tourSlug: string
  label?: string | null
  audience: Record<string, string>
  pinnedVersion?: number | null
  requireEmail: boolean
  hasPasscode: boolean
  domainAllowlist: string[]
  expiresAt?: string | null
  revoked: boolean
  openedCount: number
  lastOpenedAt?: string | null
  createdAt?: string | null
}
// #endregion

// #region CFO metric + comparison engine (server/src/integrations/quickbooks/fpa.rs)
// Canonical metric registry + period-comparison contract consumed by the CFO
// brief, ratio library, presentation, and the workbook-wide agent. Mirrors the
// Rust types in fpa.rs (MetricComparisonResponse and friends). Additive only.

/** Which rendered statement a metric is sourced from. */
export type MetricStatementKind =
  'profit_and_loss' | 'balance_sheet' | 'cash_flow'

/** Whether a metric value is a currency amount or a unit-less ratio (0-1). */
export type MetricValueKind = 'currency' | 'ratio'

/**
 * Canonical period comparisons. Slugs are stable wire identifiers shared with
 * the Rust `ComparisonKind`:
 *  - `mom`              this month vs last month
 *  - `qoq`              latest complete calendar quarter vs prior quarter
 *  - `yoy`              this month vs the same month one year ago
 *  - `ytd`              calendar year-to-date vs prior-year-to-date
 *  - `l3m_vs_prior3m`   trailing 3 months vs the 3 months before (rolling)
 *  - `l12m_vs_prior12m` trailing 12 months vs the 12 months before (rolling)
 */
export type MetricComparisonKind =
  'mom' | 'qoq' | 'yoy' | 'ytd' | 'l3m_vs_prior3m' | 'l12m_vs_prior12m'

export type MetricComparison = {
  kind: MetricComparisonKind
  label: string
  current_period_start: string | null
  current_period_end: string | null
  prior_period_start: string | null
  prior_period_end: string | null
  current_value: number | null
  prior_value: number | null
  /** Absolute change (current - prior), in the metric's native unit. */
  delta_abs: number | null
  /** Relative change as a growth rate ((current/prior) - 1). */
  delta_pct: number | null
  current_pct_of_sales: number | null
  prior_pct_of_sales: number | null
  /** Change in % of sales, in percentage points (current - prior). */
  delta_pct_of_sales_pts: number | null
  /** True when every month of both windows was observed. */
  complete: boolean
}

export type MetricComparisonRow = {
  id: string
  label: string
  statement: MetricStatementKind
  value_kind: MetricValueKind
  latest_value: number | null
  latest_pct_of_sales: number | null
  trend?: Array<{
    period: string
    label: string
    value: number | null
    complete: boolean
  }> | null
  comparisons: MetricComparison[]
}

export type MetricComparisonResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  /** Resolved as-of month (YYYY-MM-DD, month start). */
  as_of: string
  grain: 'month'
  registry_version: string
  /** Content-addressed cache signature (`fgmc_<base36>`). */
  signature: string
  comparisons: MetricComparisonKind[]
  metrics: MetricComparisonRow[]
  warnings: string[]
}

export type GetMetricComparisonsOptions = {
  /** As-of month (YYYY-MM-DD); defaults to the binding's as-of or latest month. */
  asOf?: string | null
  /** Only `month` is supported today. */
  grain?: 'month' | null
  /** Restrict to specific comparison kinds; defaults to all. */
  comparisons?: MetricComparisonKind[] | null
  /** Restrict to specific metric ids; defaults to the full registry. */
  metrics?: string[] | null
}

export type QuickBooksGlSummaryBucket = {
  label: string
  amount: number
  count: number
}

export type CurrentQuickBooksGlLine = {
  date: string
  transaction_type: string | null
  transaction_id: string | null
  account_id: string | null
  identity_occurrence: number
  debit: string | null
  credit: string | null
  amount: string | null
  fields: Record<string, unknown>
}
export type CurrentQuickBooksGlPage = {
  contract: 'fg.current_native_gl_page.v1'
  workbook_id: string
  connection_id: string
  realm_id: string
  layout_version_id: string
  configuration_identity: string
  source_generation_id: string
  source_job_id: string
  native_revision: number
  native_checkpoint: string
  accounting_basis: 'accrual'
  home_currency: string
  observed_at: string
  cdc_observed_through: string
  coverage_start: string
  coverage_end: string
  start: string
  end: string
  account_id: string | null
  limit: number
  total_lines: number
  rows: CurrentQuickBooksGlLine[]
  next_cursor: string | null
  has_undated_controls: boolean
  reconciliation_status: 'unavailable'
  cash_basis_supported: false
  foreign_currency_conservation_verified: false
}
/** Exact provenance from the original native statement response. */
export type CurrentQuickBooksGlOrigin = {
  workbook_id: string
  connection_id: string
  realm_id: string
  layout_version_id: string
  configuration_identity: string
  native_revision: number
  source_job_id: string
  source_identity: string
  periods: string[]
}
export type GetCurrentQuickBooksGlOptions = {
  expectedOrigin?: CurrentQuickBooksGlOrigin
  start?: string
  end?: string
  accountId?: string
  limit?: number
  cursor?: string
  signal?: AbortSignal
}

export type QuickBooksGlSummaryResponse = {
  workbook_id: string
  connection_id: string
  contract: CfoContractMeta
  as_of: string | null
  statement_type: string | null
  scan_row_count: number
  row_count: number
  returned_row_limit: number
  limited: boolean
  totals: Record<string, number>
  top_accounts: QuickBooksGlSummaryBucket[]
  top_vendors: QuickBooksGlSummaryBucket[]
  top_classes: QuickBooksGlSummaryBucket[]
  rows: Record<string, string>[]
  warnings: string[]
}

export type GetQuickBooksGlSummaryOptions = {
  asOf?: string | null
  statementType?: string | null
  layoutNodeId?: string | null
  accountId?: string | null
  search?: string | null
  limit?: number | null
  signal?: AbortSignal | null
}
// #endregion

// #region CFO ambient insight spine (server/src/integrations/quickbooks/insights.rs)
// Deterministic "what changed / what needs attention" cards built from the
// canonical comparison engine (above) plus A/R + forecast-confidence signals.
// Ranked and wire-stable so the cfo-home Brief can render them and the
// workbook-wide agent can expose them as a tool. Mirrors the Rust types in
// insights.rs. Additive only.

/**
 * Stop-light severity. Ranking for "needs attention" is
 * `bad` > `check` > `watch` > `good`.
 */
export type InsightSeverity = 'good' | 'check' | 'watch' | 'bad'

/** Unit of a card's headline impact value. */
export type InsightImpactUnit = 'usd' | 'pct' | 'pct_pts' | 'months' | 'ratio'

export type InsightDirection = 'up' | 'down' | 'flat'

export type InsightImpact = {
  /** Magnitude in `unit`; sign carries direction for deltas. */
  value: number
  unit: InsightImpactUnit
  /** Registry metric the impact is about (e.g. `net_income`), absent for non-metric signals. */
  metric_id: string | null
  /** Comparison window that produced it, when applicable. */
  comparison: MetricComparisonKind | null
  direction: InsightDirection
}

/** Where the Brief should deep-link on "Show detail". */
export type InsightDrillTarget = {
  /** CFO Core tab key (e.g. `financials`, `cash`, `ar`, `forecast`). */
  tab: string
  /** Optional sub-view within the tab (e.g. `profit_and_loss`). */
  view: string | null
  /** Opaque params the tab interprets (metric id, comparison, focus, ...). */
  params: Record<string, unknown>
}

/** One monthly point of a metric's trailing series (for charts). */
export type InsightTrendPoint = { period: string; value: number }

/** Plain-language, non-financial-reader-friendly explanation for a card. */
export type InsightExplain = {
  /** "What this is" in one plain sentence. */
  what_it_is?: string | null
  /** "Why it matters" in one plain sentence. */
  why_it_matters?: string | null
  /** "What good looks like" — the healthy band in plain words. */
  good_range?: string | null
  /** Human category, e.g. "Liquidity", "Bookkeeping". */
  category?: string | null
  /** True for a bookkeeping / data-quality signal (cleanup, not a crisis). */
  is_data_quality: boolean
  /** The better-fit metric the client's ledger prefers, when this was demoted. */
  replacement_label?: string | null
}

export type InsightCard = {
  /** Stable across runs for the same detector + subject. */
  id: string
  /** Detector that produced the card (e.g. `period_variance`). */
  detector: string
  severity: InsightSeverity
  headline: string
  body: string
  impact: InsightImpact
  drill_target: InsightDrillTarget
  /** Within-severity ranking weight (larger == more important). */
  score: number
  /** 1-based position after deterministic ranking. */
  rank: number
  tags: string[]
  /** Plain-language explanation (ratio/custom-metric health cards). */
  explain?: InsightExplain | null
  /** Trailing monthly series for the headline metric, for charts. */
  trend?: InsightTrendPoint[] | null
  /** Pre-formatted, unit-correct display of the headline value (e.g. "-$882.3K"). */
  value_display?: string | null
  /** Machine category key for grouping/coloring, e.g. "liquidity", "bookkeeping". */
  category?: string | null
  /** Detector-owned, provider-neutral evidence keyed by visual template id. */
  visual_evidence?: Record<string, {
    title?: string
    unit?: string
    periods?: string[]
    source_lineage?: string[]
    data_through?: string | null
    freshness?: 'current' | 'provisional' | 'stale'
    provenance_state?: 'governed' | 'estimated' | 'unreconciled'
    polarity?: 'higher_is_better' | 'lower_is_better' | 'contextual'
    points: Array<Record<string, unknown>>
  }> | null
}

export type CfoInsightsResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  /** Resolved as-of month (YYYY-MM-DD, month start). */
  as_of: string
  grain: 'month'
  insight_set_version: string
  /** Content-addressed cache signature (`fgin_<base36>`). */
  signature: string
  /** Number of non-`good` cards (bad + check + watch). */
  attention_count: number
  /** Ranked, descending by attention. */
  cards: InsightCard[]
  warnings: string[]
}

export type CfoHomeKpiTile = {
  id: string
  label: string
  value_kind: MetricValueKind
  latest_value: number | null
  latest_pct_of_sales: number | null
  comparison_kind: MetricComparisonKind | null
  delta_abs: number | null
  delta_pct: number | null
  severity: 'good' | 'watch' | 'bad' | 'neutral' | string
}

export type CfoHomeForecastStatus = {
  package_id: string | null
  status: string
  active: boolean
  updated_at: string | null
}

export type CfoHomeArtifactStatus = {
  ready: boolean
  status: string
  artifact_count: number
  query_engines: CfoArtifactQueryEngineDescriptor[]
}

export type {
  CeoCockpitPeriodRun,
  CeoCockpitResponse,
  CeoCockpitSnapshotIndex,
  CeoCockpitSettings,
  CeoCockpitSummary,
  DemoCompaniesResponse,
  DemoCompany,
} from '../components/parity-dev/cfo/ceo/ceoCockpitTypes'

export type CfoHomeResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  company_name?: string | null
  as_of: string
  contract: CfoContractMeta
  latest_sync_at?: string | null
  kpi_tiles: CfoHomeKpiTile[]
  alerts: InsightCard[]
  attention_count: number
  comparisons: MetricComparisonResponse
  ar_summary: ArAnalysisResponse
  forecast_status: CfoHomeForecastStatus
  artifact_status: CfoHomeArtifactStatus
  current_source?: CfoCurrentStatementMetadata | null
  /**
   * Composite ratio health. Rust has always serialized this (`fpa.rs:2558`);
   * the client type omitted it, so the score, its grade and its category drivers
   * were unavailable to the surface that should be drawing them.
   */
  health?: RatioHealth | null
}

export type GetCfoInsightsOptions = {
  /** As-of month (YYYY-MM-DD); defaults to the binding's as-of or latest month. */
  asOf?: string | null
  /** Only `month` is supported today. */
  grain?: 'month' | null
}

/** Native statement history keeps original metadata and all three renders. */
export type GetCfoStatementsRenderOptions = GetCfoInsightsOptions & {
  /** Explicit contiguous native history, 1-120 months; omitted keeps the comparison window. */
  months?: number | null
}

/** One pipeline stage of an in-progress on-demand AI refresh. */
export type AiRefreshStage = {
  key: string
  label: string
  step: number
  total_steps: number
  pct: number
}

export type AiRefreshInProgress = {
  job_id: string
  status: string
  stage: AiRefreshStage
}

/** Staleness + in-progress state for a workbook's on-demand AI analysis. */
export type AiRefreshStatusResponse = {
  stale: boolean
  /** `never_run` | `new_month_closed` | `fresh` | `in_progress` | `no_binding` */
  reason: string
  latest_data_period: string | null
  last_analysis_period: string | null
  last_forecast_period: string | null
  last_close_period: string | null
  in_progress: AiRefreshInProgress | null
}

export type AiRefreshTriggerResponse = {
  job_id: string | null
  /** `queued` | `already_running` */
  status: string
  message: string
}

export type CfoRefreshActivity = {
  job_id: string
  total_tasks: number
  succeeded_tasks: number
  skipped_tasks: number
  running_tasks: number
  queued_tasks: number
  retry_tasks: number
  failed_tasks: number
  cancelled_tasks: number
  current_tasks: Array<{
    task_type: string
    label: string
    started_at: string | null
    heartbeat_at: string | null
  }>
  current_tasks_overflow: boolean
  last_activity_at: string | null
  observed_at: string
}

export type CfoMaterializationRefreshInfo = {
  run_id: string | null
  status: string | null
  stage_key: string | null
  stage_label: string | null
  pct: number | null
  job_ids: Record<string, string>
  started_at?: string | null
  last_worker_contact_at?: string | null
  /** Actual tasks of the selected company report pipeline; absent before graph admission. */
  activity?: CfoRefreshActivity | null
}

export type CfoMaterializationStatusResponse = {
  /** Paid source refresh authority; viewing a workbook never implies this. */
  can_refresh_quickbooks?: boolean
  /** Eligibility is decided by the server's company freshness/authority policy. */
  refresh_on_open?: boolean
  publication?: {
    run_id: string
    source_generation: string
    requested_start: string
    requested_end: string
    published_at: string
    required_reports: string[]
    coverage_verified: boolean
    reports: Record<string, { actual_start: string | null; actual_end: string | null; reconciled: boolean }>
  } | null
  status:
    | 'ready'
    | 'refreshing'
    | 'stale_served'
    | 'first_run'
    | 'failed'
    | 'blocked'
    | 'no_binding'
    | string
  requested_period?: string | null
  resolved_period?: string | null
  source_version?: string | null
  overlay_revision?: number
  last_ready_at?: string | null
  coverage_status?: 'covered' | 'missing' | 'failed' | string
  progress?: number | null
  failure?: Record<string, unknown> | null
  serving: {
    mode: 'last_ready' | 'current' | 'first_run' | 'none' | string
    as_of_period: string | null
    materialized_at: string | null
    source_fingerprint: string | null
  }
  latest: {
    closed_period: string | null
    source_fingerprint: string | null
    last_qbo_sync_at: string | null
  }
  stale_reasons: string[]
  refresh: CfoMaterializationRefreshInfo
  surfaces: Record<string, { status: string; stale_reasons: string[] }>
  /** Health of the bound QuickBooks connection, so disconnected companies get
   * a clear reconnect notice instead of the opaque `connection_not_ready`
   * reason code. Absent when the workbook has no statement binding. */
  connection?: {
    status: string
    needs_reconnect: boolean
    company_name: string | null
    last_sync_at: string | null
    /** Bound realm, so the reconnect CTA can preselect the same company on
     * Intuit's consent screen. */
    realm_id?: string | null
    /** Legacy `/connect` hint — do not navigate a browser to it (it is
     * JWT-guarded and yields `missing_token`). Reconnect via the authorize
     * flow instead. */
    reconnect_url: string | null
  } | null
}
// #endregion

// #region CFO A/R analysis (server/src/integrations/quickbooks/fpa.rs)
// Lightweight A/R summary (total open receivable + DSO with the month-over-month
// prior + delta) derived from the canonical comparison engine — no extra A/R
// query / BigQuery. The full customer aging + collectability cohorts live in the
// embedded legacy A/R panels. Mirrors the Rust types in fpa.rs. Additive only.

export type ArAgingBucket = {
  label: string
  amount: number
}

export type ArAnalysisResponse = {
  workbook_id: string
  /** Resolved as-of month (YYYY-MM-DD, month start). */
  as_of: string
  contract: CfoContractMeta
  /** Dense render-ready A/R trend/aging/customer/invoice analysis when the
   * governed server artifact is ready. Older/degraded responses may omit it
   * while still returning canonical summary fields. */
  ar_analysis?: QuickBooksArTrendAnalysis | null
  coverage?: {
    requested_start: string | null
    requested_end: string | null
    actual_start: string | null
    actual_end: string | null
    status: 'complete' | 'partial' | 'missing'
    source_generation: string | null
  } | null
  receivable_total: number | null
  receivable_prior: number | null
  dso: number | null
  dso_prior: number | null
  /** Change in DSO vs the prior month, in days (current - prior). */
  dso_delta: number | null
  /** Aging buckets when a cheap source is available; otherwise null (the
   * embedded legacy panel carries the bucketed detail). */
  buckets: ArAgingBucket[] | null
  /** Content-addressed cache signature (`fgar_<base36>`). */
  signature: string
  warnings: string[]
}

export type GetArAnalysisOptions = {
  asOf?: string | null
  grain?: 'month' | null
  responseMode?: 'summary' | 'full'
  artifactId?: string | null
}
// #endregion

// #region CFO presentation slide-deck export (server/src/integrations/quickbooks/fpa.rs)
// Structured slide-deck outline derived from the same curated presentation
// `reportBlocks` the HTML/PDF export uses. Additive — the existing HTML/PDF
// export paths are unchanged. Mirrors the Rust types in fpa.rs.

export type SlideDeckSlide = {
  id: string
  title: string
  section: string
  /** The block kind this slide renders (text / kpi_grid / statement_rollup …). */
  kind: string
  notes: string
}

export type SlideDeckResponse = {
  workbook_id: string
  company_name: string | null
  as_of: string
  theme: string
  slide_count: number
  slides: SlideDeckSlide[]
}
// #endregion

// #region CFO ratio library (server/src/integrations/quickbooks/fpa.rs)
// The full ratio set (liquidity / leverage / efficiency / profitability /
// coverage) computed on the same shared statement layer as the metric engine.
// Each ratio carries a monthly trend, period comparisons, optional
// decomposition (DuPont ROE, cash-conversion-cycle legs), and a plain-language
// interpretation. Mirrors the Rust types in fpa.rs. Additive only.

export type RatioCategory =
  'liquidity' | 'leverage' | 'efficiency' | 'profitability' | 'coverage'

/** How a ratio value should be read / formatted. */
export type RatioUnit = 'times' | 'ratio' | 'days' | 'currency'

export type RatioTrendDirection =
  'improving' | 'declining' | 'stable' | 'unknown'

export type RatioAssessment = 'healthy' | 'watch' | 'concern' | 'neutral'

export type RatioTrendPoint = {
  period: string
  value: number
}

/** Portfolio calendar gaps are distinct from known zero values. */
export type PortfolioTrendPoint = {
  period: string
  value: number | null
}

export type RatioComparisonPoint = {
  kind: MetricComparisonKind
  label: string
  current: number | null
  prior: number | null
  delta_abs: number | null
  delta_pct: number | null
}

export type RatioComponent = {
  id: string
  label: string
  value: number | null
  unit: RatioUnit
  /** Optional per-period series for derived legs (e.g. the DuPont equity_multiplier). Omitted for
   *  legs that aren't themselves a ratio. */
  trend?: RatioTrendPoint[]
}

/** One account inside a ratio component (e.g. a single liability line), with its move + share. */
export type RatioComponentAccount = {
  label: string
  value: number
  delta_abs: number | null
  share: number | null
}

/** Monthly series of one of the two raw inputs behind a ratio (numerator + denominator). */
export type RatioComponentSeries = {
  id: string
  label: string
  unit: RatioUnit
  /** 'numerator' | 'denominator' | 'minuend' | 'subtrahend' | 'balance' | 'driver'. */
  role: string
  trend: RatioTrendPoint[]
  /** Top constituent accounts behind this component (balance-sheet stocks only). */
  breakdown?: RatioComponentAccount[]
}

export type RatioBenchmark = {
  healthy_min: number | null
  healthy_max: number | null
  value?: number | null
  basis?: 'healthy_band' | 'industry_average' | string
  source_id?: string | null
  source_label?: string | null
  source_url?: string | null
  source_year?: number | null
  industry_code?: string | null
  industry_label?: string | null
  note: string
}

export type IndustryBenchmarkProfileSelection = {
  naics_code: string
  industry_title: string
  level: string
  returns: number | null
  selection_source: string
  confidence: number | null
  evidence_json: any
}

export type IndustryBenchmarkOption = {
  naics_code: string
  level: string
  industry_title: string
  returns: number | null
}

export type IndustryBenchmarkCatalogMetadata = {
  source_id: string
  source_label: string
  source_url: string
  source_year: number
  basis: string
  note: string
}

export type IndustryBenchmarkProfileResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  company_name?: string | null
  selected: IndustryBenchmarkProfileSelection | null
  candidates: IndustryBenchmarkProfileSelection[]
  options: IndustryBenchmarkOption[]
  catalog: IndustryBenchmarkCatalogMetadata
}

export type CompanyContextProfile = {
  website_url?: string | null
  website_title?: string | null
  business_summary?: string | null
  inferred_industry?: string | null
  geography?: string | null
  customer_segments_json: any
  services_json: any
  public_signals_json: any
  ai_context_notes?: string | null
  sources_json: any
  user_overrides_json: any
  confidence?: number | null
  enrichment_source: string
  last_web_search_at?: string | null
  updated_at?: string | null
}

export type CompanyContextResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  company_name?: string | null
  context: CompanyContextProfile | null
}

export type CompanyContextUpdatePayload = {
  website_url?: string | null
  website_title?: string | null
  business_summary?: string | null
  inferred_industry?: string | null
  geography?: string | null
  customer_segments_json?: any
  services_json?: any
  public_signals_json?: any
  ai_context_notes?: string | null
  sources_json?: any
  user_overrides_json?: any
  confidence?: number | null
  enrichment_source?: string | null
}

export type RatioCard = {
  id: string
  label: string
  category: RatioCategory
  unit: RatioUnit
  description: string
  /** Plain-language formula, e.g. "Current Assets ÷ Current Liabilities". */
  formula: string
  higher_is_better: boolean | null
  benchmark: RatioBenchmark
  latest: number | null
  latest_period: string | null
  trend: RatioTrendPoint[]
  trend_direction: RatioTrendDirection
  assessment: RatioAssessment
  comparisons: RatioComparisonPoint[]
  /** Decomposition legs (DuPont factors for ROE, DSO/DIO/DPO or DSO/DPO for CCC). */
  components: RatioComponent[]
  /** Monthly series of the two raw inputs (numerator + denominator); empty when not decomposable. */
  component_trends?: RatioComponentSeries[]
  /** Deterministic plain-language reading for novice users. */
  interpretation: string
  /** Reserved slot the agent/copilot fills with a richer explanation. */
  ai_interpretation: string | null
  /** Provenance when Client Understanding changed this card (added/demoted/re-banded). Drives the
   *  inline "AI changed this" badge → inspect → revert/redirect. Absent for untouched ratios. */
  ai_change?: RatioAiChange | null
}

/** Why/how the AI changed a ratio card (matches the server `RatioAiChange`). */
export type RatioAiChange = {
  /** 'added' | 'demoted' | 'reband' */
  kind: string
  claim_id: string
  origin_id: string
  assertion: string
  /** When demoted/dropped: the better metric the AI recommends focusing on instead. */
  replacement_id?: string | null
  replacement_label?: string | null
}

export type RatioCategoryGroup = {
  category: RatioCategory
  label: string
  ratios: RatioCard[]
}

/**
 * The slice of a `RatioCard` the firm cockpit's global focus-ratio column needs — enough to draw a
 * sparkline, an end value, a month-over-month delta and a benchmark. Denormalized onto each
 * portfolio row (server: `FocusRatioSlice` in fpa.rs) so switching the focused ratio is a
 * client-side re-render rather than a fetch per client.
 *
 * `benchmark` arrives verbatim on purpose: anchor precedence lives in `cfoRatioBenchmarking.ts` and
 * stays the single source of truth for every surface.
 */
export type FocusRatioSlice = {
  id: string
  label: string
  category: RatioCategory
  unit: RatioUnit
  /** `null` when context-dependent (DPO, A/P turnover) — such deltas render without a good/bad tone. */
  higher_is_better: boolean | null
  latest: number | null
  latest_period: string | null
  /** Trailing months only (up to 12), oldest first. */
  trend: PortfolioTrendPoint[]
  benchmark: RatioBenchmark
  assessment: RatioAssessment
}

/** Per-company category weighting for the composite health score, authored by AI (or provisional/user). */
export type HealthWeighting = {
  /** Lowercase category key → effective weight (AI's, or the user's override), normalized to sum 1.0. */
  weights: Record<string, number>
  /** The AI's original weights (the "AI dot"), present when the user has overridden. */
  ai_weights?: Record<string, number> | null
  rationale?: string | null
  /** 'ai' | 'user' | 'provisional' */
  source: string
  claim_id?: string | null
  origin_id?: string | null
  /** True until the AI has authored per-company weights (equal weighting in the meantime). */
  provisional: boolean
}

export type HealthPoint = { period: string; score: number }

/** One ratio's contribution to its category — join by `id` with the RatioCard for value/benchmark. */
export type HealthRatio = {
  id: string
  label: string
  score: number
  grade: string
  delta_1mo?: number | null
  /** Share of the category score this ratio carries, 0..1. */
  weight: number
  /** Per-ratio sub-score over the aligned months, for the sub-metric trend line. */
  series: HealthPoint[]
}

export type HealthCategory = {
  category: RatioCategory
  key: string
  label: string
  score?: number | null
  grade?: string | null
  /** AI (or provisional) weight for this category, 0..1. */
  weight: number
  delta_1mo?: number | null
  series: HealthPoint[]
  scorable_count: number
  /** "what moved it" — the biggest month-over-month mover in the category. */
  top_mover?: string | null
  /** Per-ratio sub-scores (scorable ratios only), best-scoring first. */
  ratios: HealthRatio[]
  /** Effective per-metric relative weights for this category's scorable metrics (metric_id → weight;
   *  0 = not weighted into the score). Drives the per-category weight editor. */
  ratio_weights: Record<string, number>
  /** The AI's original per-metric weights (the editor's "AI dot"), present when the user has overridden. */
  ai_ratio_weights?: Record<string, number> | null
  /** Short AI note on why these sub-metrics carry the weight for this category. */
  rationale?: string | null
}

export type HealthDriver = {
  category: string
  ratio_id: string
  ratio_label: string
  score: number
  delta_1mo?: number | null
  /** 'drag' (low weighted contribution) | 'drop' (biggest negative move) */
  kind: string
}

export type HealthAttentionTarget = {
  category: string
  ratio_id: string
  ratio_label: string
}

/** Composite financial-health score rolled up from the ratio categories, weighted per company by AI. */
export type RatioHealth = {
  overall?: number | null
  grade?: string | null
  delta_1mo?: number | null
  covered_categories: number
  categories: HealthCategory[]
  overall_series: HealthPoint[]
  drivers: HealthDriver[]
  /** AI-generated "why this needs attention", framed by the per-company weighting. */
  attention_reason?: string | null
  /** Stable identity for the exact ratio named by `attention_reason`. */
  attention_target?: HealthAttentionTarget | null
  weighting: HealthWeighting
  /** Per-company display preference: show the A+…F letter grades, or just the 0-100 score. */
  show_health_grades?: boolean
}

export type RatioAnalysisResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  /** Resolved as-of month (YYYY-MM-DD, month start). */
  as_of: string
  grain: 'month'
  contract: CfoContractMeta
  ratio_set_version: string
  /** Content-addressed cache signature (`fgrl_<base36>`). */
  signature: string
  benchmark_profile?: IndustryBenchmarkProfileSelection | null
  categories: RatioCategoryGroup[]
  warnings: string[]
  /** Composite financial-health score rolled up from `categories`, weighted per company by AI. */
  health?: RatioHealth | null
}

/**
 * One raw Client Understanding claim as the server serializes it (snake_case). The UI maps these to
 * the camelCase `UnderstandingClaim` via `mapServerClaim` (cfoUnderstanding.ts). One row = one
 * directive; rows sharing an `origin_id` are one diagnosed "fact" (drives ratios and/or forecast).
 */
export type ServerUnderstandingClaim = {
  id: string
  workbook_id: string
  connection_id: string
  realm_id: string
  scenario_key: string
  claim_type: string
  /** 'forecast.*' | 'metric.*' */
  directive_kind: string
  business_key: string
  origin_id: string
  supersedes_id: string | null
  assertion: string
  directive_json: unknown
  evidence_json: unknown
  confidence: number | null
  source: string
  status: string
  /** Believability-gate score for a forecast belief: 1 = passed, 0 = auto-applied + flagged. Null for
   *  metric beliefs (not gated). */
  gate_score: number | null
  revision: number
}

/** Response of `GET /workbooks/:id/understanding` — the active belief ledger for one workbook. */
export type UnderstandingLedgerResponse = {
  workbook_id: string
  claims: ServerUnderstandingClaim[]
  /** Served forecast cash-plausibility verdict; `false` => the AI flagged it and is re-diagnosing
   * (the AI Tuning panel shows an "under review" banner). `null`/absent when no verdict yet. */
  forecast_cash_plausible?: boolean | null
}

export type GetRatiosOptions = {
  /** As-of month (YYYY-MM-DD); defaults to the binding's as-of or latest month. */
  asOf?: string | null
  /** Only `month` is supported today. */
  grain?: 'month' | null
}
// #endregion

// #region CFO valuation module (server/src/integrations/quickbooks/valuation.rs)
// Transparent, novice-first valuation built on the metric + ratio layer:
// a value-driver explainer, industry-aware framework primers (earnings / EBITDA
// / revenue multiples + a basic DCF), and a simulation endpoint. Every
// assumption (multiple, WACC, growth, terminal growth, horizon, annualization)
// is surfaced in the response. Mirrors the Rust types in valuation.rs.

export type AnnualizationBasis = 'trailing_twelve' | 'run_rate_times12'

export type ValuationBaseInputs = {
  revenue_ttm: number | null
  ebitda_ttm: number | null
  net_income_ttm: number | null
  total_equity: number | null
  /**
   * Debt used in the equity bridge: interest-bearing debt (loans, notes,
   * credit cards) when `debt_basis` is `"interest_bearing"`, else total
   * liabilities (legacy proxy).
   */
  total_debt: number | null
  /** `"interest_bearing"` or null/absent (total-liabilities proxy). */
  debt_basis?: string | null
  cash: number | null
  /** Annual revenue growth (fraction). */
  revenue_growth: number | null
  net_margin: number | null
  ebitda_margin: number | null
  return_on_equity: number | null
  asset_turnover: number | null
  annualization_basis: AnnualizationBasis
}

export type ValuationAssumptions = {
  industry: string
  earnings_multiple: number
  ebitda_multiple: number
  revenue_multiple: number
  /** Discount rate / WACC (fraction). */
  wacc: number
  forecast_years: number
  /** Annual cash-flow growth during the explicit forecast (fraction). */
  forecast_growth: number
  /** Perpetual growth after the forecast horizon (fraction). */
  terminal_growth: number
  annualization_basis: AnnualizationBasis
  /** Plain-language documentation of every assumption. */
  notes: string[]
}

export type ValuationFrameworkResult = {
  id:
    | 'earnings_multiple'
    | 'ebitda_multiple'
    | 'revenue_multiple'
    | 'dcf'
    | string
  label: string
  method_summary: string
  /** Method relevance weight used to reconcile the headline estimate. */
  reconciliation_weight?: number | null
  reconciliation_note?: string | null
  enterprise_value_low?: number | null
  enterprise_value_mid?: number | null
  enterprise_value_high?: number | null
  enterprise_value: number | null
  equity_value_low?: number | null
  equity_value_mid?: number | null
  equity_value_high?: number | null
  equity_value: number | null
  /** The specific numbers + assumptions used, for full transparency. */
  inputs: Record<string, unknown>
  assumptions_text: string[]
}

export type ValueDriverDirection = 'positive' | 'negative'

export type ValueDriver = {
  id: 'growth' | 'margin' | 'capital_efficiency' | 'risk' | string
  label: string
  current_state: string
  metric_value: number | null
  /** `pct` (fraction), `times`, or `usd`. */
  metric_unit: string
  /** Standardized nudge applied to estimate sensitivity. */
  lever: string
  /** Estimated equity-value impact of `lever`. */
  value_impact: number | null
  direction: ValueDriverDirection
  explanation: string
}

export type ValuationIndustryContext = {
  naics_code?: string | null
  industry_title?: string | null
  valuation_primer: string
  selection_source: string
  confidence?: number | null
}

export type ValuationMarketBenchmarkContext = {
  category_id: string
  category_label: string
  earnings_multiple: number
  earnings_multiple_low?: number | null
  earnings_multiple_high?: number | null
  revenue_multiple: number
  revenue_multiple_low?: number | null
  revenue_multiple_high?: number | null
  source_id: string
  source_label: string
  source_url: string
  source_year: number
  basis: string
  note: string
}

export type ValuationPersonalizationContext = {
  industry?: ValuationIndustryContext | null
  company?: CompanyContextProfile | null
  market_benchmark?: ValuationMarketBenchmarkContext | null
  assumption_industry: string
}

/** Equity value produced by a single valuation method, as a low/mid/high band. */
export type MethodEquity = {
  id: string
  equity_value_low: number | null
  equity_value_mid: number | null
  equity_value_high: number | null
}

/** One month-end on the modeled-value history line. */
export type ValuationHistoryPoint = {
  period: string
  revenue_ttm: number | null
  ebitda_ttm: number | null
  net_income_ttm: number | null
  revenue_growth: number | null
  ebitda_margin: number | null
  net_margin: number | null
  total_debt: number | null
  cash: number | null
  /** Per-method equity bands so the client can re-blend with custom weights. */
  methods: MethodEquity[]
  /** Default-weight reconciled blend (fallback for consumers that don't re-blend). */
  equity_value_low: number | null
  equity_value_mid: number | null
  equity_value_high: number | null
}

/** A hybrid input set inside a value bridge (per-method, for client re-blending). */
export type ValuationBridgeStage = {
  /** `start` | `after_revenue` | `after_margin` | `end`. */
  id: string
  label: string
  methods: MethodEquity[]
}

/** A single driver contribution in a value bridge (default-weight precompute). */
export type ValuationBridgeStep = {
  /** `revenue` | `margin` | `balance`. */
  id: string
  label: string
  delta: number | null
}

/** A value bridge decomposing the change in modeled value between two month-ends. */
export type ValuationBridge = {
  /** `mom` | `yoy`. */
  id: string
  label: string
  from_period: string
  to_period: string
  start_value: number | null
  end_value: number | null
  total_change: number | null
  /** Per-method ingredients per hybrid stage so the client can re-blend. */
  stages: ValuationBridgeStage[]
  /** Default-weight precomputed steps. */
  steps: ValuationBridgeStep[]
}

export type ValuationMethodWeight = {
  id: string
  weight: number
}

/** Modeled-value history embedded in the valuation overview (no extra fetch). */
export type ValuationHistory = {
  /** Trailing-12 month-ends (oldest first). */
  points: ValuationHistoryPoint[]
  bridge_mom?: ValuationBridge | null
  bridge_yoy?: ValuationBridge | null
  default_weights: ValuationMethodWeight[]
}

/** A single one-time item the AI excluded from normalized earnings. */
export type ValuationAiNormalizationItem = {
  node_id?: string | null
  /** Signed amount excluded (gains positive, losses negative). */
  amount: number
  reason?: string | null
  claim_id: string
  origin_id: string
}

/**
 * AI one-time / non-recurring normalization derived from active
 * `forecast.normalize_out` beliefs. Present only when those beliefs net to a
 * non-zero adjustment and a reported TTM EBITDA exists. Additive: when absent,
 * the headline valuation is unchanged. A one-time GAIN reduces normalized
 * earnings, so `adjusted_ebitda = reported_ebitda - excluded_total`.
 */
export type ValuationAiNormalization = {
  /** Signed sum of the excluded item amounts. */
  excluded_total: number
  reported_ebitda?: number | null
  adjusted_ebitda?: number | null
  adjusted_equity_low?: number | null
  adjusted_equity_mid?: number | null
  adjusted_equity_high?: number | null
  items: ValuationAiNormalizationItem[]
}

export type NativeValuationPricing = {
  contract: 'fg.native_dcf_pricing.v1'
  actual_end: string
  currency_code: string
  accounting_basis: 'accrual' | 'cash'
  cash_flow_basis: 'fcff' | 'fcfe'
  years: number
  discount: { Wacc: { rate: number } } | { CostOfEquity: { rate: number } }
  terminal: 'NoTerminal' | { GordonGrowth: { growth: number } }
  equity_bridge?: {
    cash: Array<{ own_input: string; physical: QuickBooksNativePhysicalAccountRole }>
    liabilities: Record<string, { physical: QuickBooksNativePhysicalAccountRole; role: 'InterestBearingDebt' | { NonDebt: { reason: string } } }>
  } | null
}
export type NativeValuationMetadata = {
  contract: 'fg.current_native_valuation.v1'
  policy_authoring?: NativePolicyAuthoring | null
  source_identity: Record<string, unknown>
  training_source_identity: Record<string, unknown> | null
  model_run_id: string | null
  model_identity: string | null
  layout_version_id: string
  configuration_identity: string
  scenario_identity: string
  accounting_basis: 'accrual' | 'cash'
  currency_code: string
  actual_end: string
  pricing_source: string
  pricing: NativeValuationPricing | null
  pricing_configuration_identity: string
  requested_pricing_identity: string | null
  economic_policy_identity: string | null
  method_availability: Record<string, { enterprise_available: boolean; equity_available: boolean; reason: string | null }>
  forecast_year_bounds: Array<[string, string]>
  excluded_trailing_forecast_months: number
  range_semantics: string
}

export type ValuationOverviewResponse = {
  current_source?: NativeValuationMetadata | null
  workbook_id: string
  connection_id: string
  as_of: string
  contract: CfoContractMeta
  valuation_set_version: string
  /** Content-addressed cache signature (`fgval_<base36>`). */
  signature: string
  base_inputs: ValuationBaseInputs
  assumptions: ValuationAssumptions
  frameworks: ValuationFrameworkResult[]
  drivers: ValueDriver[]
  personalization_context?: ValuationPersonalizationContext | null
  equity_value_low: number | null
  equity_value_mid: number | null
  equity_value_high: number | null
  /** Per-month modeled-value history + MoM/YoY value bridges (per-method). */
  history?: ValuationHistory | null
  /** AI one-time add-back normalization; absent when nothing was excluded. */
  ai_normalization?: ValuationAiNormalization | null
  warnings: string[]
}

/**
 * Adjustable drivers for the simulation endpoint. All deltas are additive to the
 * base value (e.g. `net_margin_delta_pts: 0.02` adds 2 points of margin;
 * `revenue_growth_delta_pct: 0.05` adds 5 points of annual growth).
 */
export type ValuationSimulationRequest = {
  monthlySelection?: string | null
  native_dcf_pricing?: NativeValuationPricing | null
  industry?: string | null
  asOf?: string | null
  grain?: 'month' | null
  revenue_growth_delta_pct?: number | null
  net_margin_delta_pts?: number | null
  ebitda_margin_delta_pts?: number | null
  wacc_delta_pct?: number | null
  ebitda_multiple_delta?: number | null
  earnings_multiple_delta?: number | null
  terminal_growth_delta_pct?: number | null
}

export type ValuationFrameworkDelta = {
  id: string
  label: string
  base_equity_value: number | null
  simulated_equity_value: number | null
  delta_abs: number | null
  delta_pct: number | null
}

export type ValuationSimulationResponse = {
  current_source?: NativeValuationMetadata | null
  current_frameworks?: ValuationFrameworkResult[] | null
  workbook_id: string
  as_of: string
  valuation_set_version: string
  signature: string
  base_assumptions: ValuationAssumptions
  simulated_assumptions: ValuationAssumptions
  base_inputs: ValuationBaseInputs
  simulated_inputs: ValuationBaseInputs
  frameworks: ValuationFrameworkDelta[]
  simulated_equity_value_low: number | null
  simulated_equity_value_mid: number | null
  simulated_equity_value_high: number | null
  base_equity_value_mid: number | null
  applied_changes: string[]
  warnings: string[]
}

export type GetValuationOptions = {
  monthlySelection?: string | null
  nativeDcfPricing?: NativeValuationPricing | null
  asOf?: string | null
  grain?: 'month' | null
  /** Industry preset for the framework primers (e.g. `services`, `saas`). */
  industry?: string | null
}
// #endregion

// #region CFO Core segment entitlements + per-audience report sharing

/** Account/segment dimension an entitlement rule filters on. */
export type CfoEntitlementDimension =
  | 'account_id'
  | 'account_group'
  | 'account_type'
  | 'account_sub_type'
  | 'class'
  | 'location'

export type CfoEntitlementRule = {
  id: string
  subject_kind: 'user' | 'group'
  subject_id: string
  effect: 'allow' | 'deny'
  dimension: CfoEntitlementDimension
  dimension_values: string[]
  description?: string | null
}

export type CfoEntitlementRuleInput = {
  subject_kind: 'user' | 'group'
  subject_id: string
  effect: 'allow' | 'deny'
  dimension: CfoEntitlementDimension
  dimension_values: string[]
  description?: string | null
}

export type CfoReportShare = {
  id: string
  subject_kind: 'user' | 'group'
  subject_id: string
  audience_label?: string | null
}

export type CfoReportShareInput = {
  subject_kind: 'user' | 'group'
  subject_id: string
  audience_label?: string | null
}

export type CfoAudienceOptions = {
  users: Array<{ user_id: string; email: string }>
  groups: Array<{ group_id: string; name: string }>
}

export type CfoSharedReport = {
  workbook_id: string
  workbook_name: string
  site_id: string
  audience_label?: string | null
}
// #endregion

export type ServerConnection = {
  id: string
  name: string
  type: string
  config?: Record<string, any> | null
}

export type ServerCreateConnectionRequest = {
  name: string
  type: 'mysql' | 'postgres' | 'postgresql' | string
  config: any
}

export type ServerUpdateConnectionRequest = {
  name?: string | null
  config?: any | null
}

export type ServerTestConnectionResponse = {
  status: string
}

export type ServerPreviewResponse = {
  // Backend currently returns a 2D table-like array for preview output.
  data: any[][]
}

export type ServerConnectionSchema = {
  tables: Array<{
    catalog?: string | null
    schema?: string | null
    name: string
    qualified_name?: string | null
    kind?: string | null
    columns: Array<{ name: string; data_type: string }>
  }>
  diagnostics?: Array<{ level?: string | null; message: string }> | null
}

export type ServerQueryResponse = {
  id: string
  status: string
}

export type ServerExecuteQueryResponse = {
  result_id: string
  columns: string[]
  rows: any[][]
  row_count: number
}

export type ServerDatasource = {
  id: string
  site_id?: string
  project_id?: string
  name: string
  kind: string
  md_database?: string | null
  md_reference?: string | null
  md_sql?: string | null
  connection_id?: string | null
  external_table?: string | null
  external_sql?: string | null
  schedule_config?: any | null
  retention_config?: any | null
  encryption_policy?: any | null
  definition_json?: any | null
}

export type TableauPromotedDatasource = {
  datasource_id: string
  datasource_name: string
  datasource_key: string
  managed_name: string
  connection_id: string
  source_mode: 'primary' | 'tableau' | string
  source_note?: string | null
}

export type SignedArtifactRef = {
  uri: string
  signed_url?: string | null
  storage_type: string
  row_count?: number | null
  schema_sha256?: string | null
  sql_sha256?: string | null
  manifest_sha256?: string | null
  extract_identity?: string | null
}

export type WorksheetArtifactSummary = {
  worksheet_key: string
  worksheet_name: string
  datasource_key: string
  viz_result?: SignedArtifactRef | null
  reduced_extract?: SignedArtifactRef | null
  full_extract?: SignedArtifactRef | null
  can_auto_cache_full_extract?: boolean
}

export type TableauWorksheetArtifactsResponse = {
  workbook_id?: string | null
  resolved_import_id?: string | null
  workbook_revision?: string | null
  worksheets: WorksheetArtifactSummary[]
}

export type ServerExtractVersion = {
  id: string
  datasource_id: string
  version: number
  status: string
  artifact_uri?: string | null
  etag?: string | null
  bytes?: number | null
  parquet_version?: string | null
  compression?: string | null
  created_at?: string
  updated_at?: string
  error_message?: string | null
}

export type QuickBooksConnectionStatus = {
  connection_id: string
  datasource_id?: string | null
  realm_id: string
  company_name?: string | null
  environment: string
  status: string
  needs_reconnect: boolean
  scopes: string[]
  access_expires_at: string
  refresh_expires_at: string
  last_refresh_at?: string | null
  last_sync_at?: string | null
  last_error?: string | null
  last_intuit_tid?: string | null
  reconnect_url?: string | null
}

export type QuickBooksStatusResponse = {
  environment: string
  discovery?: {
    authorization_endpoint: string
    token_endpoint: string
    revocation_endpoint?: string | null
  } | null
  gcp_configured: boolean
  supported_entities: string[]
  connections: QuickBooksConnectionStatus[]
}

export type QuickBooksSyncScheduleJobSummary = {
  job_id: string
  kind: string
  status: string
  triggered_by: string
  started_at?: string | null
  completed_at?: string | null
  created_at: string
  updated_at: string
  last_error?: string | null
}

export type QuickBooksSyncScheduleConfig = {
  id: string
  enabled: boolean
  job_kind: string
  schedule_cadence: string
  interval_seconds?: number | null
  timezone: string
  next_run_at?: string | null
  last_scheduled_at?: string | null
  last_job_id?: string | null
  skip_cdc: boolean
  skip_exports: boolean
  force_refresh: boolean
  updated_at: string
}

export type QuickBooksDataProductFreshness = {
  product_key: string
  product_kind: string
  status: string
  period_start?: string | null
  period_end?: string | null
  fresh_at?: string | null
  validated_at?: string | null
  artifact_exported_at?: string | null
  row_count?: number | null
  byte_count?: number | null
  last_error?: string | null
  updated_at: string
}

export type QuickBooksSyncScheduleEntry = {
  connection_id: string
  datasource_id?: string | null
  realm_id: string
  company_name?: string | null
  environment: string
  status: string
  last_sync_at?: string | null
  gl_exported_at?: string | null
  gl_export_rows?: number | null
  gl_export_error?: string | null
  data_products?: QuickBooksDataProductFreshness[]
  active_job?: QuickBooksSyncScheduleJobSummary | null
  last_scheduled_job?: QuickBooksSyncScheduleJobSummary | null
  schedule?: QuickBooksSyncScheduleConfig | null
  can_manage: boolean
}

export type QuickBooksSyncScheduleListResponse = {
  site_id: string
  site_slug: string
  connections: QuickBooksSyncScheduleEntry[]
  refresh?: QuickBooksRefreshReceipt
}

export type QuickBooksRefreshReceipt = {
  source_job_id: string | null
  company_request_id?: string | null
  connection_id: string
  kind: string
  status: string
  joined_existing: boolean
  tasks_queued: number
  workbook_id?: string
  requested_period?: string | null
  reporting_handoff: 'waiting_for_source' | 'waiting_for_source_admission' | 'not_requested_by_source_job'
}

export type CfoQuickBooksRefreshResponse = { refresh: QuickBooksRefreshReceipt }

export type QuickBooksSyncScheduleUpsertRequest = {
  connection_id: string
  enabled?: boolean | null
  job_kind?: string | null
  schedule_cadence?: string | null
  interval_seconds?: number | null
  timezone?: string | null
  next_run_at?: string | null
  skip_cdc?: boolean | null
  skip_exports?: boolean | null
  force_refresh?: boolean | null
}

export type QuickBooksUsageSummary = {
  connection_id: string
  realm_id: string
  company_name?: string | null
  environment: string
  calls: number
  coreplusapi_units: number
  error_calls: number
  report_calls: number
  modern_report_calls: number
  avg_duration_ms: number
  response_bytes: number
  first_call_at?: string | null
  last_call_at?: string | null
}

export type QuickBooksUsagePeriod = {
  connection_id: string
  realm_id: string
  company_name?: string | null
  period_start: string
  calls: number
  coreplusapi_units: number
  error_calls: number
  report_calls: number
  modern_report_calls: number
  avg_duration_ms: number
}

export type QuickBooksUsageRefresh = {
  job_id: string
  connection_id: string
  realm_id: string
  company_name?: string | null
  kind: string
  status: string
  triggered_by: string
  created_at: string
  started_at?: string | null
  completed_at?: string | null
  last_error?: string | null
  calls: number
  coreplusapi_units: number
  error_calls: number
  report_calls: number
  modern_report_calls: number
}

export type QuickBooksUsageResponse = {
  from: string
  to: string
  grain: string
  summary: QuickBooksUsageSummary[]
  by_period: QuickBooksUsagePeriod[]
  refreshes: QuickBooksUsageRefresh[]
}

export type QuickBooksAuthorizeResponse = {
  authorize_url: string
}

/** Someone a QuickBooks connect link can say the request comes from. */
export type QuickBooksConnectLinkPerson = {
  user_id: string
  email: string
  full_name?: string | null
}

/** A standing link a CFO Core's editors hand to the company's QuickBooks
 * admin, who connects QuickBooks without a FuseGrid login. It never expires.
 * It acts as `requested_by`, the person the admin sees asking, and stops
 * working when revoked or when that person loses edit access. */
export type QuickBooksConnectLink = {
  id: string
  workbook_id: string
  url: string
  requested_by: QuickBooksConnectLinkPerson
  created_at: string
  created_by_email?: string | null
  last_opened_at?: string | null
  last_connected_at?: string | null
  connected_count: number
}

export type XeroAvailability = {
  connector_enabled: boolean
  self_serve_enabled: boolean
  gl_enabled: boolean
}

export type AccountingProviderDescriptor = {
  provider: 'quickbooks' | 'xero' | 'sage_intacct' | 'sage_accounting' | 'netsuite' | string
  display_name: string
  product_family: string
  logo_key: string
  status: 'active' | 'private_beta' | 'planned' | 'disabled' | string
  connector_enabled: boolean
  self_serve_enabled: boolean
  selection_kind: string
  auth_schemes: string[]
  environments: string[]
  supported_datasets: string[]
  webhook_supported: boolean
  data_use_policy: string
  /**
   * Per-tenant rollout, when the catalog was fetched for a site. Absent means
   * no tenant record exists and the global status applies. `connector_enabled`
   * and `self_serve_enabled` above already fold this in — read those rather
   * than re-deriving availability from these flags.
   */
  tenant_rollout?: {
    wave?: string | null
    connector_enabled: boolean
    self_serve_enabled: boolean
    sync_enabled: boolean
    gl_enabled: boolean
    ai_enabled: boolean
    ga_visible: boolean
  } | null
}

export type AccountingProviderCatalog = {
  providers: AccountingProviderDescriptor[]
}

/** One Sage business the authorizing credential can read. */
export type SageBusinessCandidate = {
  provider_account_id: string
  display_name: string
  environment: string
  regional_endpoint?: string | null
  country_code?: string | null
  metadata?: unknown
}

/** The legal entity behind a Sage business, with its fiscal calendar. */
export type SageEntityCandidate = {
  provider_entity_id: string
  parent_provider_entity_id?: string | null
  entity_type: string
  display_name: string
  country_code?: string | null
  base_currency_code?: string | null
  timezone?: string | null
  fiscal_year_end_month?: number | null
  fiscal_year_end_day?: number | null
  metadata?: unknown
}

export type SageBookCandidate = {
  provider_book_id: string
  display_name: string
  book_type: string
  accounting_basis: string
  currency_code?: string | null
  is_primary: boolean
  metadata?: unknown
}

/**
 * What the authorizing user's permissions actually allow.
 *
 * `dataset_permissions` is keyed by canonical dataset. A `false` means the
 * credential cannot read that dataset — it never means the dataset is empty.
 */
export type SageCapabilityProbe = {
  dataset_permissions: Record<string, boolean>
  provider_features: Record<string, boolean>
  reasons: Record<string, string>
}

export type SageProvisionedCompany = {
  company_id: string
  project_id: string
  workbook_id: string
  created: boolean
}

export type XeroOrganizationCandidate = {
  organization_id: string
  name: string
  type?: string | null
  country_code?: string | null
  state: string
}

export type XeroProvisionedCompany = {
  company_id: string
  workbook_id: string
  organization_id: string
  status: string
}

export type QuickBooksConnectionRequestResponse = {
  request_id: string
  invite_url: string
  mailto_url: string
  status: string
  expires_at: string
}

export type QuickBooksConnectionRequestEntry = {
  id: string
  workbook_id: string
  site_id: string
  project_id: string
  admin_email?: string | null
  note?: string | null
  status: string
  access_mode: string
  group_ids: string[]
  authorized_connection_id?: string | null
  datasource_id?: string | null
  error?: string | null
  expires_at: string
  created_at: string
  updated_at: string
  completed_at?: string | null
}

export type QuickBooksAttachableConnection = {
  connection_id: string
  datasource_id?: string | null
  realm_id: string
  company_name?: string | null
  environment: string
  status: string
  created_at: string
  last_sync_at?: string | null
  already_bound_to_workbook: boolean
  already_governed_for_site: boolean
  can_attach: boolean
  source: string
}

export type QuickBooksViewEntry = {
  id: string
  connection_id: string
  datasource_id?: string | null
  realm_id: string
  view_name: string
  s3_prefix: string
  enabled: boolean
  last_exported_at?: string | null
  last_rows?: number | null
  last_bytes?: number | null
  last_error?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type QuickBooksExtractEntry = {
  view_id: string
  connection_id: string
  datasource_id?: string | null
  extract_version_id?: string | null
  realm_id: string
  view_name: string
  s3_prefix: string
  last_exported_at?: string | null
  last_rows?: number | null
  last_bytes?: number | null
  last_error?: string | null
  objects: Array<{
    s3_uri: string
    signed_url: string
    expires_at: number
  }>
}

export type QuickBooksRunStatus =
  'queued' | 'running' | 'succeeded' | 'failed' | string

export type QuickBooksRunStage =
  | 'queued'
  | 'entities'
  | 'general_ledger'
  | 'views'
  | 'exports'
  | 'complete'
  | 'failed'
  | string

export type QuickBooksRunEntry = {
  id: string
  connection_id: string
  realm_id: string
  status: QuickBooksRunStatus
  current_stage?: QuickBooksRunStage | null
  started_at: string
  completed_at?: string | null
  stage_started_at?: string | null
  heartbeat_at?: string | null
  entities_attempted: number
  entities_succeeded: number
  rows_merged: number
  gl_report_rows: number
  views_exported: number
  bytes_exported: number
  triggered_by: string
  last_intuit_tid?: string | null
  last_error?: string | null
  details?: any
}

export type QuickBooksFirstRunResponse = {
  run_id: string
  realm_id: string
  status: QuickBooksRunStatus
}

export type QuickBooksDurableTaskEntry = {
  id: string
  task_type: string
  task_key: string
  status: string
  attempts: number
  max_attempts?: number
  priority: number
  payload?: any
  result?: any
  last_error?: string | null
  heartbeat_at?: string | null
  lease_owner?: string | null
  lease_expires_at?: string | null
  ready_at?: string | null
  depends_on_task_keys?: string[]
  started_at?: string | null
  completed_at?: string | null
  updated_at?: string | null
}

export type QuickBooksDurableJobEntry = {
  id: string
  connection_id: string
  realm_id: string
  kind: string
  status: string
  triggered_by: string
  payload?: any
  result?: any
  last_error?: string | null
  started_at?: string | null
  completed_at?: string | null
  created_at: string
  updated_at: string
  active_task?: QuickBooksDurableTaskEntry | null
  tasks: QuickBooksDurableTaskEntry[]
}

export type QuickBooksFpaLayoutNode = {
  id: string
  version_id: string
  parent_node_id?: string | null
  source_account_id?: string | null
  source_parent_account_id?: string | null
  node_type: 'account' | 'group' | 'heading' | 'spacer' | 'formula' | string
  statement_type:
    'profit_and_loss' | 'balance_sheet' | 'cash_flow' | 'excluded' | string
  cash_flow_section?: string | null
  display_name: string
  qbo_name?: string | null
  qbo_fully_qualified_name?: string | null
  account_number?: string | null
  qbo_account_type?: string | null
  qbo_account_sub_type?: string | null
  qbo_classification?: string | null
  normal_balance?: 'debit' | 'credit' | string | null
  include_in_reports: boolean
  sort_order: number
  formula_json?: any | null
  metadata?: any
}

export type QuickBooksFpaLayoutVersion = {
  id: string
  connection_id: string
  realm_id: string
  site_id?: string | null
  project_id?: string | null
  datasource_id?: string | null
  name: string
  status: 'draft' | 'published' | 'archived' | string
  cloned_from_version_id?: string | null
  node_count: number
  created_at: string
  updated_at: string
  published_at?: string | null
  nodes: QuickBooksFpaLayoutNode[]
}

export type QuickBooksFpaValidation = {
  ok: boolean
  errors: string[]
  warnings: string[]
}

// A service-line rollup pairs income layout nodes with their directly-attributable COGS nodes (per
// layout version) so the CFO surface can show direct contribution margin per line.
export type ServiceLineRollup = {
  id: string
  layout_version_id: string
  name: string
  income_node_ids: string[]
  cogs_node_ids: string[]
  auto_detected: boolean
  confidence?: number | null
  detection_signals: string[]
  status: 'active' | 'archived' | string
  sort_order: number
  created_at: string
  updated_at: string
}

export type SuggestedServiceLineRollup = {
  name: string
  income_node_ids: string[]
  cogs_node_ids: string[]
  confidence: number
  signals: string[]
}

export type ServiceLineRollupList = {
  rollups: ServiceLineRollup[]
  seeded: boolean
}

export type ServiceLineSeedResult = {
  rollups: ServiceLineRollup[]
  suggestions: SuggestedServiceLineRollup[]
}

export type QuickBooksFpaLayoutMutation = {
  layout: QuickBooksFpaLayoutVersion
  validation: QuickBooksFpaValidation
}

export type QuickBooksFpaJournalLine = {
  id?: string
  journal_entry_id?: string
  account_id: string
  layout_node_id?: string | null
  class_id?: string | null
  department_id?: string | null
  customer_id?: string | null
  vendor_id?: string | null
  employee_id?: string | null
  item_id?: string | null
  memo?: string | null
  debit?: number | null
  credit?: number | null
  sort_order?: number
}

export type QuickBooksFpaJournalEntry = {
  id: string
  connection_id: string
  realm_id: string
  layout_version_id?: string | null
  period_start: string
  entry_date: string
  memo?: string | null
  status: 'draft' | 'posted' | 'void' | string
  source: string
  created_at: string
  updated_at: string
  lines: QuickBooksFpaJournalLine[]
}

export type QuickBooksFpaStatementRow = {
  node_id: string
  parent_node_id?: string | null
  row_type: string
  statement_type: string
  label: string
  level: number
  account_id?: string | null
  account_number?: string | null
  cash_flow_section?: string | null
  qbo_account_type?: string | null
  qbo_account_sub_type?: string | null
  qbo_classification?: string | null
  values: Array<number | null>
  is_total: boolean
  is_formula: boolean
  normal_balance?: string | null
  semantic_key?: string | null
}

export type QuickBooksFpaStatementFormatting = {
  theme?: 'classic' | 'board' | 'minimal' | string
  scale?: 'ones' | 'thousands' | 'millions' | string
  decimals?: 0 | 1 | 2 | number
  negativeFormat?: 'parentheses' | 'minus' | string
  zeroDisplay?: 'blank' | 'dash' | 'zero' | string
  showAccountCodes?: boolean
  showDetailRows?: boolean
  showHeaders?: boolean
  showPercentOfSales?: boolean
  showTotalColumn?: boolean
}

export type QuickBooksFpaStatementRender = {
  connection_id: string
  layout_version_id: string
  statement_type: string
  balance_sheet_mode: string
  periods: string[]
  rows: QuickBooksFpaStatementRow[]
  warnings: string[]
}

export type CfoCurrentStatementMetadata = {
  /** Exact shared input token includes basis, layout and posted adjustments. */
  source_identity?: string
  configuration_identity?: string
  selected_period_end?: string | null
  source_revision: number
  source_job_id: string
  source_captured_at: string
  native_profit_and_loss_validated: boolean
  native_balance_sheet_validated: boolean
  native_cash_flow_validated: boolean
  general_ledger_verified: boolean
  cash_flow_unavailable_reason?: string | null
  exact_values?: Record<string, Array<string | null>>
}

export type QuickBooksCfoStatementsRenderContractResponse = {
  workbook_id: string
  connection_id: string
  realm_id: string
  /** Resolved as-of month (YYYY-MM-DD, month start). */
  as_of: string
  grain: 'month'
  contract: CfoContractMeta
  profit_and_loss: QuickBooksFpaStatementRender
  balance_sheet: QuickBooksFpaStatementRender
  cash_flow: QuickBooksFpaStatementRender
  warnings: string[]
  current_source?: CfoCurrentStatementMetadata | null
}

export type QuickBooksFpaWorkbookStatementBinding = {
  id: string
  workbook_id: string
  connection_id: string
  layout_version_id: string
  config_json: any
  created_at: string
  updated_at: string
}

/** Billing plan governing a workbook's tenant. Absent/null = unrestricted
 * (legacy tenant or older server) — default permissive. */
export type CfoWorkbookPlanInfo = {
  plan_code?: string | null
  trial_ends_at?: string | null
  trial_active?: boolean | null
  effective_tier?: string | null
  pro_features_available?: boolean | null
  /** Stripe subscription status ('active' | 'past_due' | …); null = never subscribed. */
  plan_status?: string | null
  has_subscription?: boolean | null
}

export type BillingHostedSessionResponse = { url: string }

export type BillingCheckoutConfirmationResponse = {
  status: string
  payment_status: string
  amount_total?: number | null
  currency?: string | null
  plan_code?: string | null
  plan_interval?: string | null
}

export type BillingStatusResponse = {
  configured: boolean
  can_manage_billing: boolean
  plan_code?: string | null
  plan_status?: string | null
  plan_interval?: string | null
  trial_ends_at?: string | null
  current_period_end?: string | null
  cancel_at_period_end: boolean
  has_subscription: boolean
  can_switch_to_partner: boolean
  billable_client_count: number
  subscription_quantity?: number | null
  partner_unit_amount_cents?: number | null
  estimated_monthly_cents?: number | null
  next_tier_at?: number | null
  quantity_sync_status: 'not_applicable' | 'not_subscribed' | 'pending' | 'synced' | 'retry' | string
}

export type QuickBooksCfoCoreBootstrapResponse = {
  current_source_mode?: 'qbo_native_current' | null
  accounting_provider?: string | null
  accounting_provider_display_name?: string | null
  accounting_company_id?: string | null
  accounting_connection_status?: string | null
  currency_code?: string | null
  country_code?: string | null
  bindings: QuickBooksFpaWorkbookStatementBinding[]
  connection_id?: string | null
  realm_id?: string | null
  company_name?: string | null
  layouts: QuickBooksFpaLayoutVersion[]
  adjustments: QuickBooksFpaJournalEntry[]
  selected_layout_id?: string | null
  statement_type?: string | null
  available_periods?: string[] | null
  default_period?: string | null
  cfo_analytics_ready?: boolean | null
  cfo_analytics_status?: string | null
  /** Explicit analytics state. Never infer progress from `cfo_analytics_status` prose. */
  cfo_analytics_state?: CfoAnalyticsState | null
  /** Why an unbound workbook has no CFO reports yet (present only without a statement binding). */
  binding_setup?: CfoBindingSetupStatus | null
  capabilities?: Record<string, boolean> | null
  capability_details?: Record<string, AccountingCapabilityDetail> | null
  plan?: CfoWorkbookPlanInfo | null
}

export type CfoAnalyticsState = 'ready' | 'stale_showing_previous' | 'needs_report_update' | 'binding_setup'

export type CfoBindingSetupState =
  | 'reporting_disabled'
  | 'no_quickbooks_connection'
  | 'multiple_connections'
  | 'connection_unavailable'
  | 'needs_reconnect'
  | 'syncing'
  | 'awaiting_first_complete_sync'
  | 'no_accounts'
  | 'setup_failed'
  /** The company's statement layout is an unpublished draft with errors; an editor must fix and publish it. Nothing is set up until then. */
  | 'layout_needs_review'
  | 'ready_to_set_up'

export type CfoBindingSetupStatus = {
  state: CfoBindingSetupState | string
  message: string
  needs_attention: boolean
  /** The viewer can resolve this state now with `setUpCfoReports` (edit access, committed accounts). */
  can_set_up?: boolean
}

export type CfoBindingSetupResponse = {
  workbook_id: string
  outcome: 'already_bound' | 'provisioned' | string
}

export type PlanVsActualPeriod = {
  period_start: string
  /** 'closed' | 'partial' | 'future'. A partial month is not comparable to a whole month of plan. */
  period_status: string
  actual: string | null
  plan: string | null
  variance: string | null
  variance_pct: string | null
  favorability: string | null
}

export type PlanVsActualRow = {
  statement_type: string
  node_id: string
  label: string
  level: number
  is_total: boolean
  periods: PlanVsActualPeriod[]
}

export type PlanVsActual = {
  schema_version: string
  actuals_integrity: string
  latest_closed: string | null
  rows: PlanVsActualRow[]
}

export type ServiceLinePairing = {
  id: string
  name: string
  /** Statement node ids whose revenue belongs to this line. */
  income_node_ids: string[]
  /** Statement node ids whose direct cost belongs to this line. */
  cogs_node_ids: string[]
  auto_detected: boolean
  confidence?: number | null
}

export type ForecastScenarioSeries = {
  node_id: string
  period_grain: string
  scenarios: Array<{ scenario_key: string; points: Array<{ period: string; value: number }> }>
}

export type ForecastBandPoint = {
  period: string
  low: number
  center: number
  high: number
  confidence: number | null
}

export type ForecastBandSeries = {
  node_id: string
  scenario_key: string
  period_grain: string
  points: ForecastBandPoint[]
}

export type RevenueMovementCustomer = {
  customer_id: string
  customer: string
  prior_revenue: number
  current_revenue: number
  contribution: number
}

export type RevenueMovementBucket = {
  /** new | reactivation | expansion | contraction | churn */
  category: string
  amount: number
  customer_count: number
  top_customers: RevenueMovementCustomer[]
}

export type RevenueMovement = {
  prior_period: string
  current_period: string
  prior_revenue: number
  current_revenue: number
  delta: number
  buckets: RevenueMovementBucket[]
  residual: number
  reconciles: boolean
  /** Always 'invoiced_revenue' — this is NOT the P&L revenue line. */
  basis: string
}

export type ArCohortCell = {
  age_days: number
  /** Share of the cohort still open at this age, 0..1. Null when unripe. */
  open_share: number | null
  open_amount: number | null
  /** Conditional probability of remaining open from the previous age band. */
  roll_rate: number | null
  /** False when the cohort has not aged this far — the cell is unknown, not zero. */
  observable: boolean
  days_matured: number
}

export type ArCohortRow = {
  cohort_month: string
  invoice_count: number
  billed_amount: number
  cells: ArCohortCell[]
}

export type ArCohortMatrix = {
  rows: ArCohortRow[]
  /** Always 'invoice_date'. The aging buckets age from the DUE date; the two are not reconcilable. */
  age_basis: string
  as_of: string
  readable: boolean
}

export type CashTroughProvenanceSlice = {
  provenance: string
  amount: number
}

export type CashTroughEvent = {
  label: string
  counterparty_name: string | null
  week_start: string
  amount: number
  provenance: string
}

export type CashTroughDriver = {
  row_key: string
  label: string
  amount: number
  share_of_dip: number
  by_provenance: CashTroughProvenanceSlice[]
  top_events: CashTroughEvent[]
}

export type CashTroughAttribution = {
  trough_week_start: string
  trough_ending_cash: number
  peak_week_start: string
  peak_ending_cash: number
  dip_amount: number
  weeks_to_trough: number
  trough_ending_cash_low: number | null
  /** The floor AT the trough week, not one number for the horizon — with a
   * rule-based floor it slopes. */
  min_cash_floor: number | null
  breaches_floor: boolean
  /** Every projected week below ITS OWN floor, earliest first. With a sloping
   * floor the trough and the first breach can differ: a later week can hold more
   * cash yet sit under a higher floor, so reporting only the trough would point
   * a treasurer at the wrong week. */
  breaches_floor_weeks?: string[]
  headroom: number | null
  trough_is_partial_bucket: boolean
  drivers: CashTroughDriver[]
  /**
   * Share of the dip that is actual, document-backed or recurring — how much of
   * the problem is already certain rather than modeled.
   */
  certain_share_of_dip: number | null
}

export type QuickBooksCfoPresentationResponse = {
  /** Allowlisted public report DTO; never a source or reviewer identity. */
  public_projection?: 'fg.cfo_public_presentation.v1'
  workbook_id: string
  binding_id: string
  connection_id: string
  layout_version_id: string
  company_name?: string | null
  layout_name: string
  can_edit: boolean
  config: any
  metrics: any
  ar_analysis?: QuickBooksArTrendAnalysis | null
  /** Real named scenario trajectories; null when only one scenario is modelled. */
  forecast_scenarios?: ForecastScenarioSeries | null
  /** Per-period forecast interval series; null when no bounded lines exist. */
  forecast_band?: ForecastBandSeries | null
  /** Prebuilt valuation overview; null when the artifact is not materialized. */
  valuation?: { frameworks?: ValuationFrameworkResult[] | null } | null
  /** Plan vs actual, when the workbook has a prepared primary plan. */
  plan_vs_actual?: PlanVsActual | null
  /** Active service-line income/COGS pairings for the layout. */
  service_lines?: ServiceLinePairing[] | null
  /** 13-week cash trough attribution; null when the horizon is not projectable. */
  cash_trough?: CashTroughAttribution | null
  statements: {
    profit_and_loss: QuickBooksFpaStatementRender
    balance_sheet: QuickBooksFpaStatementRender
    cash_flow: QuickBooksFpaStatementRender
  }
  warnings: string[]
  instant_cards?: any[] | null
  /** Served ratio cards for pinned `ratio_card` report blocks, keyed by ratio id. */
  ratio_cards?: Record<string, RatioCard> | null
  /** Governed identity and freshness for the exact live report response. */
  source_state?: {
    signature: string
    native_source_revision?: number | null
    native_source_job_id?: string | null
    native_statement_source_identities?: string[] | null
    native_configuration_identity?: string | null
    native_statement_period_ends?: string[] | null
    reporting_period?: string | null
    data_through?: string | null
    last_successful_refresh?: string | null
    reconciliation_status?: string | null
    close_readiness_status?: string | null
    forecast_included?: boolean
    new_data_available?: boolean
    review_state?: string | null
    last_reviewed_at?: string | null
    last_reviewed_by?: string | null
    last_reviewer_name?: string | null
    latest_revision_id?: string | null
    latest_revision_label?: string | null
    latest_issued_revision_id?: string | null
    latest_issued_revision_label?: string | null
  } | null
  review_baseline?: import('../components/parity-dev/cfo/visuals/types').HighlightReviewBaseline | null
  insights?: CfoInsightsResponse | null
  metric_comparisons?: MetricComparisonResponse | null
  visual_evidence?: import('../components/parity-dev/cfo/visuals/types').HighlightVisualEvidence[] | null
  key_highlights?: import('../components/parity-dev/cfo/visuals/types').KeyHighlight[] | null
  visual_selection_diagnostics?: { rejections: import('../components/parity-dev/cfo/visuals/types').HighlightSelectionRejection[]; persistence_error?: string | null } | null
  visual_selection_version?: string | null
  visual_evidence_schema_version?: string | null
  generated_at: string
}

/** Commentary on one report section (report-revision.js). */
export type ReportSectionCommentary = {
  body: string
  state: 'empty' | 'draft' | 'accepted'
  author: 'user' | 'ai' | null
}

/** One section of a `fg.report_composition.v2` revision document. */
export type ReportCompositionSection = {
  section_id: string
  section_type: string
  title: string
  config: Record<string, unknown>
  commentary: ReportSectionCommentary | null
}

/** The report's only model: public/excel-addin/report-revision.js. */
export type ReportCompositionRevision = {
  schema_version: 'fg.report_composition.v2'
  document_kind: 'revision'
  period: string
  sections: ReportCompositionSection[]
}

export type ReportRevisionAuthor = 'sheet' | 'studio' | 'ai' | 'undo' | 'roll_forward'

export type ReportRevisionRecord = {
  id: string
  revision: number
  parent_revision: number | null
  composition: ReportCompositionRevision
  composition_hash: string
  authored_via: ReportRevisionAuthor
  change_summary: string
  workbook_id: string
  host_workbook_instance_id: string
  created_at: string
  applied_at: string | null
}

/** Studio edit vocabulary: ReportOps plus `set_title` / `set_commentary`. */
export type ReportStudioEdit = { op: string; [key: string]: unknown }

export type ReportStudioAiEditProposal = {
  expected_revision: number
  change_summary: string
  edits: ReportStudioEdit[]
  model_id?: string
}

export type QuickBooksCfoPresentationArAnalysisResponse = {
  workbook_id: string
  ar_analysis?: QuickBooksArTrendAnalysis | null
  warnings: string[]
}

export type QuickBooksFpaPlanVersion = {
  id: string
  workbook_id: string
  connection_id: string
  layout_version_id: string
  binding_id?: string | null
  realm_id: string
  name: string
  kind: 'budget' | 'forecast' | string
  status: 'draft' | 'published' | 'archived' | 'failed' | string
  horizon_months: number
  start_period: string
  end_period: string
  actuals_basis: string
  source: string
  source_plan_version_id?: string | null
  metadata?: any
  validation_json?: any
  published_at?: string | null
  created_at: string
  updated_at: string
}

export type QuickBooksFpaPlanLine = {
  id: string
  plan_version_id: string
  node_id: string
  parent_node_id?: string | null
  statement_type: 'profit_and_loss' | 'balance_sheet' | 'cash_flow' | string
  row_type: string
  label: string
  level: number
  account_id?: string | null
  period_start: string
  value?: number | null
  generated_value?: number | null
  override_value?: number | null
  override_note?: string | null
  value_basis: 'activity' | 'ending_balance' | string
  source: string
  sort_order?: number | null
  is_total: boolean
  is_formula: boolean
  metadata?: any
  updated_at?: string | null
}

export type QuickBooksFpaForecastLineMetadata = {
  id: string
  package_id: string
  plan_line_id?: string | null
  model_run_id?: string | null
  statement_type: 'profit_and_loss' | 'balance_sheet' | 'cash_flow' | string
  node_id: string
  period_start: string
  period_grain?: string
  scenario_key?: string
  forecast_basis:
    | 'scheduled'
    | 'historical_trend'
    | 'detail_ml'
    | 'driver_assumption'
    | 'user_override'
    | 'formula'
    | string
  model_family?: string | null
  confidence?: number | null
  low_value?: number | null
  high_value?: number | null
  generated_value?: number | null
  final_value?: number | null
  backtest_json?: any
  evidence_json?: any
  driver_json?: any
  warning_json?: any
  feedback_state?: string
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  created_at: string
}

export type QuickBooksNativePhysicalAccountRole = {
  account_id: string
  parent_account_id: string | null
  account_type: string
  account_sub_type: string | null
  classification: string
  active: boolean
}
export type QuickBooksNativeEconomicCashFlowPolicy = {
  contract: 'fg.economic_cash_flow_policy.v1'
  basis: 'fcff' | 'fcfe'
  detail_roles: Record<string, {
    own_input: string
    physical: QuickBooksNativePhysicalAccountRole
    role: 'OperatingWorkingCapital' | 'OperatingNonCashAdjustment' | 'ProductiveInvestmentCash' | 'DebtPrincipalCash' | 'OwnerCash' | { ExcludedNonoperatingCash: { reason: string } }
  }>
  profit_roles: Record<string, {
    physical: QuickBooksNativePhysicalAccountRole
    role: 'Operating' | 'InterestExpense' | 'IncomeTaxExpense' | { ExcludedNonoperating: { reason: string } }
  }>
  interest_tax: {
    expense_own_inputs: string[]
    payable_own_inputs: string[]
    tax_expense_own_inputs: string[]
    shield: 'NoShield' | { CurrentPeriodPositiveTaxableProfit: { rate: string } }
  } | null
}
export type QuickBooksNativeEconomicPolicyReview = {
  framework: string
  policy: QuickBooksNativeEconomicCashFlowPolicy
  policy_identity: string
  model_identity: string
  source_identity: Record<string, unknown>
  training_scope: { workbook_id: string; connection_id: string; realm_id: string; layout_version_id: string; accounting_basis: string; currency_code: string; configuration_identity: string }
  accounting_basis: string
  currency_code: string
  training_end: string
  assumptions: Array<{ account: string; treatment: string }>
  manual_treatment: string
  interest_timing: string | null
  tax_shield: string | null
  qualification: string
}

export type QuickBooksFpaMlModelRun = {
  id: string
  connection_id: string
  workbook_id?: string | null
  plan_version_id?: string | null
  model_registry_id?: string | null
  realm_id: string
  run_kind: string
  status:
    | 'queued'
    | 'running'
    | 'completed'
    | 'failed'
    | 'insufficient_training_data'
    | string
  data_coverage_json?: any
  backtest_json?: any
  cost_json?: any
  warning_json?: any
  error_json?: any
  review_status?: string
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  economic_policy_review?: QuickBooksNativeEconomicPolicyReview | null
  started_at?: string | null
  finished_at?: string | null
  created_at: string
}

export type QuickBooksFpaForecastPackage = {
  id: string
  workbook_id: string
  connection_id: string
  plan_version_id?: string | null
  model_run_id?: string | null
  realm_id: string
  package_kind: string
  status: string
  horizon_months: number
  horizon_weeks: number
  start_period?: string | null
  end_period?: string | null
  summary_json?: any
  artifact_json?: any
  validation_json?: any
  scenario_key?: string
  active_scenario_key?: string
  is_active?: boolean
  selected_at?: string | null
  data_coverage_json?: any
  source_window_start?: string | null
  source_window_end?: string | null
  run_trigger?: string | null
  created_at: string
  updated_at: string
}

export type QuickBooksFpaMlFeedbackSummary = {
  total_events: number
  useful_signals: number
  false_positive_signals: number
  edited_forecasts: number
  accepted_assumptions: number
}

export type QuickBooksFpaMlFeedbackEvent = {
  id: string
  workbook_id: string
  connection_id: string
  plan_version_id?: string | null
  forecast_package_id?: string | null
  forecast_line_metadata_id?: string | null
  signal_id?: string | null
  model_run_id?: string | null
  scenario_key?: string | null
  realm_id: string
  event_type: string
  target_type: string
  target_id?: string | null
  original_value_json?: any
  final_value_json?: any
  reason_code?: string | null
  note?: string | null
  ui_surface?: string | null
  data_coverage_json?: any
  created_by?: string | null
  created_at: string
}

export type QuickBooksFpaMlFeedbackRequest = {
  connection_id: string
  plan_version_id?: string | null
  forecast_package_id?: string | null
  forecast_line_metadata_id?: string | null
  signal_id?: string | null
  model_run_id?: string | null
  scenario_key?: string | null
  event_type: string
  target_type: string
  target_id?: string | null
  original_value_json?: any
  final_value_json?: any
  reason_code?: string | null
  note?: string | null
  ui_surface?: string | null
  data_coverage_json?: any
}

export type QuickBooksFpaMlPackageResponse = {
  contract: CfoContractMeta
  package?: QuickBooksFpaForecastPackage | null
  line_metadata?: QuickBooksFpaForecastLineMetadata[]
  model_runs: QuickBooksFpaMlModelRun[]
  feedback_summary: QuickBooksFpaMlFeedbackSummary
}

export type QuickBooksFpaMlPackageListResponse = {
  packages: QuickBooksFpaForecastPackage[]
  active_package_id?: string | null
  active_scenario_key: string
}

export type QuickBooksFpaMlSelectionResponse = {
  package?: QuickBooksFpaForecastPackage | null
  active_scenario_key: string
}

export type QuickBooksFpaMlRunResponse = {
  job_id: string
  connection_id: string
  realm_id: string
  status: string
  message: string
  expected_output_groups: string[]
}

export type QuickBooksFpaMlJobStatusResponse = {
  job: any
}

export type QuickBooksFpaMlStatusResponse = {
  status: any
}

export type QuickBooksFpaMlDebugResponse = {
  debug: any
}

export type QuickBooksFpaMlOrchestrationSettingsResponse = {
  settings: any
}

export type QuickBooksFpaMlEvidenceResponse = {
  target_type: string
  target_id: string
  package_id?: string | null
  scenario_key?: string | null
  evidence_json?: any
  warning_json?: any
  driver_json?: any
  source_json?: any
}

export type QuickBooksFpaMlMonthlyForecastResponse = {
  current_source?: QuickBooksCurrentForecastRead | null
  scored_lines?: QuickBooksFpaMlScoredLine[] | null
  current_statements?: QuickBooksFpaMlStatementForecastResponse[] | null
  package?: QuickBooksFpaForecastPackage | null
  lines: QuickBooksFpaForecastLineMetadata[]
  feedback_summary: QuickBooksFpaMlFeedbackSummary | null
}

export type QuickBooksFpaMlScoredLine = {
  node_id: string
  statement_type: string
  period_start: string
  scenario_key: string
  forecast_basis: string
  model_family: string
  confidence: number
  low_value: number
  high_value: number
  generated_value: number
  final_value: number
  backtest_json: unknown
  evidence_json: unknown
  driver_json: unknown
  warning_json: unknown
}

export type NativePolicyWriteFence = {
  binding_id: string
  source_identity: Record<string, unknown>
  configuration_identity: string
  binding_configuration_identity: string
  layout_identity: string
}
export type NativePolicyAuthoring = {
  contract: 'fg.native_policy_authoring.v1'
  workbook_id: string
  connection_id: string
  layout_version_id: string
  accounting_basis: 'accrual' | 'cash'
  currency_code: string
  actual_end: string
  write_fence: NativePolicyWriteFence
  inventory: {
    physical_accounts: Record<string, QuickBooksNativePhysicalAccountRole>
    own_inputs: Record<string, { label: string | null; physical: QuickBooksNativePhysicalAccountRole | null; physical_unavailable_reason: string | null; input: {
      row_id: string; account_id: string; statement_type: string; category: string; normal_balance: string | null
      native_cash_account: boolean; retained_earnings_account: boolean
    } }>
    /** Original producer mapping. NonCash explicitly excludes its counterpart and retained detail. */
    cash_detail_candidates: Record<string, [string, string]> | null
    retained_account_ids: string[]
    movement_unavailable_reason: string | null
  } | null
  unavailable_reason: string | null
  modeled_cash_flow_policy: QuickBooksCurrentForecastRead['modeled_cash_flow_policy']
  economic_cash_flow_policy: QuickBooksNativeEconomicCashFlowPolicy | null
  saved_pricing: NativeValuationPricing | null
  saved_pricing_unavailable_reason: string | null
  training_on_read: false
  general_ledger_verified: false
  dated_timing_qualified: false
}

export type QuickBooksCurrentForecastRead = {
  contract: 'fg.current_native_forecast_read.v1'
  policy_authoring?: NativePolicyAuthoring | null
  source_identity: Record<string, unknown>
  model_run_id: string | null
  model_identity: string | null
  scenario_identity: string
  preview_baseline_scenario_identity?: string | null
  forecast_start_period?: string | null
  future_months?: number | null
  scenario_key: string
  authored_package_id: string | null
  authored_driver_assumptions: Record<string, unknown>
  connection_id: string
  realm_id: string
  layout_version_id: string
  accounting_basis: string
  currency_code: string
  actual_period_ends: string[]
  native_profit_and_loss_validated: boolean
  native_balance_sheet_validated: boolean
  native_cash_flow_validated: boolean
  native_cash_flow_unavailable_reason: string | null
  model_available: boolean
  unavailable_reason: string | null
  forecast_cash_flow_qualified: boolean
  native_cash_choice?: 'UniqueNativeCash' | 'CompleteNativeCashPool' | { LiquidityBasis: { node_ids: string[] } } | null
  modeled_cash_flow_policy?: {
    contract: 'fg.modeled_cash_movement_policy.v1'
    retained_account_id: string
    manual_counterpart: 'CashFinancing' | { NonCash: { own_input: string } }
  } | null
  modeled_cash_flow_provenance?: 'approved_explicit_movement_policy_not_native_observation'
  modeled_row_unavailable_reasons?: Array<{ period_start: string; rows: Record<string, string> }> | null
  current_statement_component_envelopes?: {
    statement_type: string
    periods: string[]
    rows: Record<string, { expected: Array<number | null>; low: Array<number | null>; high: Array<number | null> }>
  }
  general_ledger_verified: boolean
  dated_timing_qualified: boolean
  feedback_summary_available: boolean
  numeric_contract: string
  calibration: string
  range_semantics?: 'dependency_preserving_component_range_envelope' | null
  modeled_family_availability?: Record<string, boolean>
  modeled_family_unavailable_reasons?: Record<string, string | null>
  training_on_read: false
}

export type QuickBooksFpaMlCashWeek = {
  id: string
  package_id: string
  scenario_key?: string
  week_start: string
  cash_event_type: string
  source_type?: string | null
  source_id?: string | null
  source_key?: string
  counterparty_id?: string | null
  counterparty_name?: string | null
  expected_amount: string
  low_amount?: string | null
  high_amount?: string | null
  confidence?: number | null
  evidence_json?: any
  warning_json?: any
  feedback_state?: string
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  derived?: boolean
}

export type QuickBooksFpaMlCash13WeekResponse = {
  package?: QuickBooksFpaForecastPackage | null
  weeks: QuickBooksFpaMlCashWeek[]
}

export type QuickBooksCashPlanningPackage = {
  id: string
  status: string
  scenario_key: string
  active_scenario_key: string
  start_period?: string | null
  updated_at: string
}

export type QuickBooksCashAggregateDelta = {
  scenario_key: string
  week_start: string
  category_id: string
  amount_delta: string
}

export type QuickBooksCashPlanningRow = {
  id: string
  row_kind: 'ml_forecast' | 'expectation' | 'bridge' | string
  package_id?: string | null
  expectation_id?: string | null
  scenario_key: string
  week_start: string
  expected_date?: string | null
  metric_key?: string | null
  category_key?: string | null
  category_label?: string | null
  /** Treasurer's-table bucket (collections, other_inflows, payroll, rent,
   * insurance, vendors_ap, debt, taxes, other) — server-computed. */
  cash_table_row_key?: string | null
  cash_table_row_label?: string | null
  /** Sources-and-uses class from the server: operating | investing | financing.
   * Optional because an older backend does not send it — the cash table falls
   * back to its flat layout when no row carries one. */
  activity_class?: string | null
  activity_class_label?: string | null
  /** Where this dollar came from — server-computed. Only 'document',
   * 'recurring' and 'user' rows are editable. */
  provenance?: 'actual' | 'document' | 'recurring' | 'modeled' | 'user' | string | null
  /** The date-free identity this row can be claimed by: the QBO document id for
   * 'document' rows, the recurring stream_key for 'recurring' rows, null for
   * modeled rows (their source_key embeds the model's own date). */
  plan_identity?: string | null
  event_type: string
  label: string
  counterparty_name?: string | null
  expected_amount: string
  low_amount?: string | null
  high_amount?: string | null
  confidence?: number | null
  forecast_basis?: string | null
  assumption_summary?: string | null
  review_priority?: string | null
  editable?: boolean
  edit_target_type?: string | null
  edit_target_id?: string | null
  source_type?: string | null
  source_key?: string | null
  /** Present on expectations only. Carries the claim an expectation makes —
   * notably `claim.occurrence`, the ORIGINAL date of a re-timed recurring
   * occurrence. */
  source_payload_json?: { claim?: { occurrence?: string } } | null
  feedback_state?: string | null
  material: boolean
  evidence_json?: any
  warning_json?: any
  /** Returned by expectation mutations. The row fields remain at the top level
   * for backward compatibility while these receipts let the UI reconcile its
   * optimistic aggregate update without refetching the whole summary. */
  overlay_revision?: number
  aggregate_deltas?: QuickBooksCashAggregateDelta[]
}

export type QuickBooksCashPlanningWeekSummary = {
  week_start: string
  /** 'closed' (reviewed past week — actual overwrites forecast), 'current', or 'forecast'. */
  week_status?: 'closed' | 'current' | 'forecast' | string
  bucket_start?: string | null
  bucket_end?: string | null
  day_count?: number | null
  is_partial_bucket?: boolean
  opening_cash?: string | null
  receipts: string
  disbursements: string
  net_cash_change: string
  ending_cash: string
  /** Ending cash if THIS week alone lands at the unfavourable / favourable end
   * of the model's per-row range. Not a cumulative worst case — see
   * `ending_cash_low` on the server. Absent on closed weeks (actuals). */
  /** This week's resolved minimum-cash floor. Absent — never "0.00" — when no
   * floor is configured or the rule had nothing to measure; a zero floor is not
   * "no floor", it is a floor nothing can breach. */
  min_cash_floor?: string | null
  ending_cash_low?: string | null
  ending_cash_high?: string | null
  bridge_amount: string
  actual_net_cash_change?: string | null
  forecast_actual_variance?: string | null
  reviewed: boolean
  needs_review: boolean
  rollup_json?: any
  lineage_json?: any
}

export type QuickBooksCashPlanningReviewPrompt = {
  review_week_start: string
  status: string
  open_items: number
}

export type QuickBooksCashPlanningCashPosition = {
  status: 'ready' | 'blocking' | string
  message: string
  /** The anchor the response is actually built on. Resolved from the governed
   * balance-sheet series, so it can be EARLIER than the date requested when that
   * month is not closed yet. */
  as_of_date: string
  /** First day of the month the balance closes; null on a blocking position.
   * Differs from `as_of_date` only when the requested month has no close yet. */
  balance_period?: string | null
  ending_cash?: string | null
  source: string
}

export type QuickBooksCashMinFloorRule = {
  /** 'fixed' | 'pct_monthly_operating_inflow' | 'weeks_of_operating_outflow' */
  kind: string
  param?: number | null
  /** The measured input the rule was applied to, so the UI can answer "why is my
   * danger line $84,200?" without re-deriving it. */
  basis_amount?: number | null
  basis_label: string
  /** One entry per served week, aligned to `weeks`. `null` on any week the rule
   * could not be evaluated. */
  per_week: Array<number | null>
}

export type QuickBooksCashSubScheduleWeek = {
  week_start: string
  amount: string
  /** Row ids behind this child's cell, so it opens the same drill a category
   * cell does. Absent from an older server — the child then renders as a
   * read-only breakdown rather than a dead click. */
  row_ids?: string[]
}

export type QuickBooksCashSubScheduleEntry = {
  key: string
  label: string
  /** 'document' | 'recurring' | 'modeled'. Only the first two are movable, and
   * the modeled remainder renders last so an owner can see how much of a
   * category has a document behind it. */
  provenance: string
  row_count: number
  total: string
  by_week: QuickBooksCashSubScheduleWeek[]
}

export type QuickBooksCashBankObservation = {
  id: string
  balance_date: string
  bank_balance: string
  source: string
  account_scope?: string | null
  note?: string | null
  updated_at: string
}

export type QuickBooksCashTieOut = {
  observation: QuickBooksCashBankObservation
  /** GL cash rolled forward to the observation date. Null when the keyed date
   * is outside the reconciled window — there is nothing to compare against. */
  gl_cash?: string | null
  gl_anchor_date: string
  /** Bank minus GL. Null whenever `gl_cash` is. */
  float_amount?: string | null
  dates_align: boolean
  /** 'ties' | 'float' | 'unexplained' | 'out_of_window'. */
  status: string
  message: string
  /** The difference is larger than the min-cash floor. */
  exceeds_floor: boolean
  gl_detail_leg?: string | null
}

export type QuickBooksCashPlanningResponse = {
  serving?: {
    payload_version: 'summary_v2' | string
    summary_id?: string | null
    requested_period?: string | null
    resolved_period?: string | null
    source_version?: string | null
    overlay_revision: number
    last_ready_at?: string | null
    coverage_status: 'covered' | 'missing' | 'unavailable' | string
    readiness: 'ready' | 'materializing' | 'blocking' | string
    stale: boolean
    status_url?: string | null
    retry_after_seconds?: number | null
    evidence_row_count: number
    detail_handles: Array<{
      week_start: string
      category_id: string
      row_count: number
      summary_amount: string
    }>
  }
  contract: CfoContractMeta
  warnings: string[]
  current_source?: {
    write_fence?: NativeCashWriteFence
    source_revision: number
    source_job_id: string
    source_checkpoint: string
    cash_anchor_end: string
    accounting_basis: string
    currency: string
    native_balance_sheet_validated: boolean
    native_cash_flow_validated: boolean
    cash_anchor?: {
      membership: string
      native_source_balance: string
      advisor_adjustment: string
      adjusted_balance: string
    }
    general_ledger_verified: boolean
    model_version: string
    band_semantics: string
    assumption_fingerprint: string
    observed_through?: string
    due_date_assumption?: boolean
    unscheduled_document_count?: number
    cash_account_postings_conserved?: boolean
    posted_history: { status: string; reason?: string | null; requested_weeks: number; ledger_basis?: string; bank_reconciled?: boolean }
    ar_timing: { status: string; reason?: string | null; anchor_balance?: string | null }
    ap_timing: { status: string; reason?: string | null; anchor_balance?: string | null }
    model_forecast: { status: string }
    exact_settings: Record<string, string | null>
  } | null
  package?: QuickBooksCashPlanningPackage | null
  scenario_key: string
  as_of_date?: string | null
  cash_position?: QuickBooksCashPlanningCashPosition | null
  weeks: QuickBooksCashPlanningWeekSummary[]
  rows: QuickBooksCashPlanningRow[]
  expectations: QuickBooksCashPlanningRow[]
  bridge_rows: QuickBooksCashPlanningRow[]
  source_rows: QuickBooksCashPlanningSourceRow[]
  source_summary: QuickBooksCashPlanningSourceSummary | null
  scenarios: string[]
  scenario_summary?: QuickBooksCashPlanningScenarioSummary
  snapshot?: QuickBooksCashPlanningSnapshot | null
  /** Minimum-cash danger line for the 13-week table (per-workbook pref). */
  /** The materialised floor for whatever rule is configured. Always emitted, so
   * a client that reads only this stays correct without knowing rules exist. */
  min_cash_floor?: number | null
  min_cash_floor_rule?: QuickBooksCashMinFloorRule | null
  /** Behind "total available cash" = ending cash + reserves + undrawn credit. */
  cash_reserve_amount?: number | null
  credit_line_limit?: number | null
  /** What is drawn on the line, and when it was last confirmed. */
  credit_line_drawn?: number | null
  credit_line_drawn_at?: string | null
  /** Limit minus drawn, floored at zero. Null — NEVER the bare limit — when the
   * drawn balance is unknown, so a client cannot present room that may already
   * be spent as available. */
  credit_line_undrawn?: number | null
  /** Named children under the categories that have them, keyed by
   * `cash_table_row_key`. Only served for categories where naming the parties
   * tells an owner something they can act on. */
  sub_schedules?: Record<string, QuickBooksCashSubScheduleEntry[]>
  /** The weekly bank reconciliation. Absent until a balance has been keyed. */
  cash_tie_out?: QuickBooksCashTieOut | null
  /**
   * Trough week, the peak-to-trough dip, and its drivers cross-tabbed by
   * provenance. `null` when the horizon is not projectable — a blocking cash
   * position never projects from an invented $0, and this inherits that refusal.
   */
  cash_trough?: CashTroughAttribution | null
  permissions?: QuickBooksCashPlanningPermissions
  audit_summary?: QuickBooksCashPlanningAuditSummary
  assistant_state?: QuickBooksCashPlanningAssistantState
  review_prompt: QuickBooksCashPlanningReviewPrompt
  advanced_actions: string[]
}

export type QuickBooksCashPlanningDetailsResponse = {
  summary_id: string
  week: string
  category_id: string
  rows: Array<Record<string, unknown>>
  row_count: number
  expected_row_count: number
  detail_amount: string
  summary_amount: string
  next_cursor?: string | null
  source: 'partitioned_artifact' | 'summary_artifact' | 'bounded_bigquery' | string
}

export type QuickBooksCashPlanningScenario = {
  id: string
  scenario_key: string
  name: string
  status: string
  parent_scenario_id?: string | null
  is_base: boolean
  description?: string | null
  updated_at: string
}

export type QuickBooksCashPlanningScenarioSummary = {
  scenarios: QuickBooksCashPlanningScenario[]
  active_scenario_key: string
}

export type QuickBooksCashPlanningSnapshot = {
  id: string
  scenario_key: string
  status: string
  version_label?: string | null
  published_at?: string | null
  created_at: string
  summary_json?: any
}

export type QuickBooksCashPlanningPermissions = {
  can_edit: boolean
  can_publish_cash_forecast: boolean
}

export type QuickBooksCashPlanningAuditSummary = {
  latest_event_at?: string | null
  event_count: number
}

export type QuickBooksCashPlanningAssistantState = {
  latest_draft_id?: string | null
  latest_status?: string | null
  open_question_count: number
}

export type QuickBooksCashPlanningAuditEvent = {
  id: string
  event_type: string
  entity_type: string
  entity_id?: string | null
  scenario_key?: string | null
  summary?: string | null
  before_json?: any
  after_json?: any
  metadata_json?: any
  created_at: string
}

export type QuickBooksCashPlanningSourceRow = {
  id: string
  source_kind: 'ar_invoice' | 'ap_bill' | string
  source_id: string
  source_number?: string | null
  counterparty_id?: string | null
  counterparty_name?: string | null
  source_date?: string | null
  due_date?: string | null
  expected_cash_date?: string | null
  week_start?: string | null
  open_balance: string
  expected_amount: string
  days_past_due?: number | null
  existing_expectation_id?: string | null
  existing_expected_date?: string | null
  existing_expected_amount?: string | null
  source_payload_json?: any
}

export type QuickBooksCashPlanningSourceSummary = {
  ar_count: number
  ap_count: number
  ar_open_balance: string
  ap_open_balance: string
}

export type QuickBooksCashPlanningSourceResponse = {
  source_rows: QuickBooksCashPlanningSourceRow[]
  summary: QuickBooksCashPlanningSourceSummary
}

export type QuickBooksCashPlanningExportResponse = {
  file_name: string
  mime_type: string
  csv: string
}

/** Original native read authority; callers carry it unchanged into a human write. */
export type NativeCashWriteFence = {
  workbook_id: string
  connection_id: string
  realm_id: string
  company_id: string | null
  layout_version_id: string
  binding_id: string
  configuration: unknown
  accounting_basis: string
  source_revision: number
  source_job_id: string
  source_checkpoint: string
  statement_source_identity: string
  statement_periods: string[]
  human_input_versions: unknown
}

/** Decimal tokens are transmitted as text. Legacy numeric callers retain their body. */
function cashAuthoringBody<T extends object>(request: T, fields: string[]): T {
  const body = { ...request } as Record<string, unknown>
  for (const field of fields) {
    if (typeof body[field] === 'string') {
      body[`${field}_exact`] = body[field]
      delete body[field]
    }
  }
  return body as T
}

export type QuickBooksCashPlanningExpectationRequest = {
  native_write_fence?: NativeCashWriteFence
  scenario_key?: string | null
  /** Optional when `treasurer_row_key` is supplied — the server derives the
   * canonical event_type from the user's category + direction. */
  event_type?: string
  /** The treasurer row the user picked, plus an EXPLICIT direction. Preferred
   * over `event_type`: both the sign and the row are recovered downstream by
   * substring tests over the event_type, so the mapping lives on the server. */
  treasurer_row_key?: string | null
  direction?: 'in' | 'out' | null
  counterparty_name?: string | null
  description?: string | null
  expected_date: string
  expected_amount: number | string
  low_amount?: number | string | null
  high_amount?: number | string | null
  confidence?: number | null
  source_type?: string | null
  source_id?: string | null
  source_payload_json?: any
  note?: string | null
}

export type QuickBooksCashPlanningReviewItem = {
  id: string
  label: string
  event_type: string
  forecast_amount: string
  actual_amount?: string | null
  variance_amount?: string | null
  disposition: string
  resolution_action?: string | null
  rolled_to_expectation_id?: string | null
  actual_source_json?: any
  note?: string | null
}

export type QuickBooksCashPlanningReviewRun = {
  id: string
  review_week_start: string
  status: string
  completed_at?: string | null
  summary_json?: any
}

export type QuickBooksCashPlanningReviewResponse = {
  review: QuickBooksCashPlanningReviewRun
  items: QuickBooksCashPlanningReviewItem[]
}

export type QuickBooksCashPlanningAiOperation = {
  id: string
  action: string
  scenario_key: string
  event_type: string
  counterparty_name?: string | null
  description: string
  expected_date: string
  expected_amount: number
  confidence: number
  source_type: string
}

export type QuickBooksCashPlanningAiQuestion = {
  id: string
  severity: string
  question: string
}

export type QuickBooksCashPlanningAiDraftResponse = {
  draft_id: string
  status: string
  proposed_operations: QuickBooksCashPlanningAiOperation[]
  questions: QuickBooksCashPlanningAiQuestion[]
  warnings: string[]
  model_id?: string | null
}

export type QuickBooksFpaMlDriverImpact = {
  id: string
  package_id: string
  forecast_line_metadata_id?: string | null
  model_run_id?: string | null
  scenario_key?: string
  statement_type: string
  node_id: string
  period_start: string
  driver_key: string
  driver_label: string
  driver_type: string
  amount_impact: string
  confidence?: number | null
  explanation?: string | null
  evidence_json?: any
  feedback_state?: string
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  derived?: boolean
}

export type QuickBooksFpaMlDriverImpactResponse = {
  package?: QuickBooksFpaForecastPackage | null
  drivers: QuickBooksFpaMlDriverImpact[]
}

export type QuickBooksFpaForecastGrain = 'daily' | 'weekly' | 'month'
export type QuickBooksFpaForecastValueCase = 'expected' | 'high' | 'low'

export type QuickBooksFpaMlStatementForecastResponse = {
  current_actual_statement?: QuickBooksFpaStatementRender | null
  current_actual_source_identity?: Record<string, unknown> | null
  current_source?: QuickBooksCurrentForecastRead | null
  contract: CfoContractMeta
  warnings: string[]
  package?: QuickBooksFpaForecastPackage | null
  grain: QuickBooksFpaForecastGrain | string
  statement_type: 'profit_and_loss' | 'balance_sheet' | 'cash_flow' | string
  value_case: QuickBooksFpaForecastValueCase | string
  as_of_date?: string | null
  statement: QuickBooksFpaStatementRender
  validation?: any
  driver_assumptions?: any
}

export type QuickBooksFpaForecastLayoutNode = {
  layout_version_id: string
  node_id: string
  parent_node_id?: string | null
  row_type: string
  statement_type: string
  label: string
  level: number
  account_id?: string | null
  account_number?: string | null
  cash_flow_section?: string | null
  qbo_account_type?: string | null
  qbo_account_sub_type?: string | null
  qbo_classification?: string | null
  normal_balance?: string | null
  is_total: boolean
  is_formula: boolean
  sort_order: number
}

export type QuickBooksFpaMlCashDailyForecast = {
  forecast_date: string
  cash_event_type: string
  source_type?: string | null
  source_id?: string | null
  counterparty_name?: string | null
  expected_amount: number
  low_amount?: number | null
  high_amount?: number | null
  confidence?: number | null
  evidence_json?: any
}

export type QuickBooksFpaMlForecastPackageSnapshotResponse = {
  current_source?: QuickBooksCurrentForecastRead | null
  current_statements?: QuickBooksFpaMlStatementForecastResponse[] | null
  package?: QuickBooksFpaForecastPackage | null
  scenario_key: string
  as_of_date?: string | null
  start_date?: string | null
  end_date_exclusive?: string | null
  artifact_materialization?: any
  artifact_urls?: Record<string, any>
  layout_nodes: QuickBooksFpaForecastLayoutNode[]
  line_metadata: QuickBooksFpaForecastLineMetadata[]
  cash_daily: QuickBooksFpaMlCashDailyForecast[]
  driver_assumptions?: any
  validation?: any
}

export type QuickBooksFpaMlForecastArtifactsEnsureStatus =
  'ready' | 'queued' | 'running' | 'failed' | 'missing_source_data'

export type QuickBooksFpaMlForecastArtifactsEnsureResponse = {
  current_source?: QuickBooksCurrentForecastRead | null
  status: QuickBooksFpaMlForecastArtifactsEnsureStatus | string
  as_of_date: string
  forecast_start_period?: string | null
  scenario_key: string
  package_id?: string | null
  job_id?: string | null
  message: string
  validation?: any
}

export type QuickBooksFpaMlUserDelta = {
  id: string
  workbook_id: string
  scenario_key: string
  target_kind: 'line' | 'cash_week' | 'driver' | string
  business_key: string
  statement_type?: string | null
  node_id?: string | null
  period_grain?: string | null
  period_start?: string | null
  week_start?: string | null
  cash_event_type?: string | null
  source_key?: string | null
  counterparty_id?: string | null
  driver_key?: string | null
  value_override?: number | null
  low_override?: number | null
  high_override?: number | null
  date_override?: string | null
  baseline_value?: number | null
  baseline_low?: number | null
  baseline_high?: number | null
  baseline_date?: string | null
  feedback_state: string
  review_note?: string | null
  reason_code?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  last_package_id?: string | null
  revision: number
  created_at: string
  updated_at: string
}

export type QuickBooksFpaMlUserDeltaListResponse = {
  workbook_id: string
  scenario_key?: string | null
  generated_at: string
  deltas: QuickBooksFpaMlUserDelta[]
}

export type QuickBooksFpaMlDriverAssumptionResponse = {
  current_source?: QuickBooksCurrentForecastRead | null
  package?: QuickBooksFpaForecastPackage | null
  assumptions: any
}

export type QuickBooksFpaMlDriverPreviewLine = {
  statement_type: string
  node_id: string
  period_start: string
  scenario_key: string
  expected: number
  low: number
  high: number
}

export type QuickBooksFpaMlDriverPreviewResponse = {
  current_source?: QuickBooksCurrentForecastRead | null
  package_id: string | null
  engine_version: string
  computed_in_ms: number
  changed_line_count: number
  lines: QuickBooksFpaMlDriverPreviewLine[]
}

export type QuickBooksFpaMlScenarioAutopilotSuggestion = {
  id: string
  label: string
  summary: string
  expected_cash_impact: string
  confidence: number
  driver_changes?: any
  evidence_json?: any
}

export type QuickBooksFpaMlScenarioAutopilotResponse = {
  package?: QuickBooksFpaForecastPackage | null
  scenarios: QuickBooksFpaMlScenarioAutopilotSuggestion[]
}

export type QuickBooksFpaMlCashAction = {
  id: string
  action_type: string
  title: string
  description: string
  expected_cash_impact: string
  period_start?: string | null
  confidence: number
  evidence_json?: any
}

export type QuickBooksFpaMlCashActionResponse = {
  package?: QuickBooksFpaForecastPackage | null
  actions: QuickBooksFpaMlCashAction[]
  summary?: any
}

export type QuickBooksFpaMlSignal = {
  id: string
  workbook_id: string
  connection_id: string
  model_run_id?: string | null
  source_key?: string | null
  realm_id: string
  signal_type: string
  severity: string
  confidence?: number | null
  amount_impact?: string | null
  period_start?: string | null
  account_id?: string | null
  node_id?: string | null
  counterparty_id?: string | null
  counterparty_type?: string | null
  explanation: string
  recommended_action?: string | null
  evidence_json?: any
  status: string
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  created_at: string
}

export type QuickBooksFpaMlSignalListResponse = {
  package?: QuickBooksFpaForecastPackage | null
  signals: QuickBooksFpaMlSignal[]
}

export type QuickBooksFpaMlModelReviewResponse = {
  package?: QuickBooksFpaForecastPackage | null
  model_runs: QuickBooksFpaMlModelRun[]
  feedback_summary: QuickBooksFpaMlFeedbackSummary
  summary?: any
}

export type QuickBooksFpaMlForecastVsActualRow = {
  forecast_line_metadata_id: string
  package_id: string
  scenario_key?: string
  statement_type: string
  node_id: string
  period_start: string
  forecast_value?: string | null
  actual_value?: string | null
  variance?: string | null
  variance_pct?: number | null
  low_value?: string | null
  high_value?: string | null
  interval_hit?: boolean | null
  confidence?: number | null
  feedback_state: string
}

export type QuickBooksFpaMlForecastVsActualResponse = {
  package?: QuickBooksFpaForecastPackage | null
  rows: QuickBooksFpaMlForecastVsActualRow[]
  summary?: any
}

export type QuickBooksFpaPlanAssumption = {
  id: string
  plan_version_id: string
  key: string
  label: string
  value_json: any
  source: string
  confidence?: number | null
  citation_json?: any
  metadata?: any
  created_at: string
  updated_at?: string | null
}

export type QuickBooksFpaPlanDetail = {
  plan: QuickBooksFpaPlanVersion
  lines: QuickBooksFpaPlanLine[]
  assumptions: QuickBooksFpaPlanAssumption[]
}

export type QuickBooksFpaApplyMlForecastResponse = {
  detail: QuickBooksFpaPlanDetail
  applied_line_count: number
  skipped_formula_or_override_count: number
  package_id: string
  scenario_key: string
}

export type QuickBooksFpaGeneratePlanRequest = {
  kind?: 'budget' | 'forecast' | string
  name?: string | null
  horizon_months?: number | null
  start_period?: string | null
  autopublish?: boolean | null
  user_responses?: any
  workbook_inputs?: any
  prompt?: string | null
}

export type QuickBooksFpaPlanAssumptionDraft = {
  key: string
  label: string
  value_json: any
  source: string
  confidence?: number | null
  citation_json?: any
}

export type QuickBooksFpaPlanAgentDraftRequest = {
  kind?: 'budget' | 'forecast' | string
  horizon_months?: number | null
  current_layout_id?: string | null
  prompt?: string | null
  workbook_inputs?: any
  source_plan_version_id?: string | null
}

export type QuickBooksFpaPlanAgentDraftResponse = {
  status: string
  questions: any[]
  proposed_assumptions: QuickBooksFpaPlanAssumptionDraft[]
  evidence: any[]
  warnings: string[]
}

export type QuickBooksFpaGeneratePlanFromDraftRequest = {
  kind?: 'budget' | 'forecast' | string
  name?: string | null
  horizon_months?: number | null
  start_period?: string | null
  prompt?: string | null
  answered_questions?: any
  accepted_assumptions?: QuickBooksFpaPlanAssumptionDraft[]
  workbook_inputs?: any
  source_plan_version_id?: string | null
}

export type QuickBooksFpaUpdatePlanRequest = {
  name?: string | null
  status?: string | null
}

export type QuickBooksFpaUpdateAssumptionRequest = {
  label?: string | null
  value_json?: any
  source?: string | null
  confidence?: number | null
  citation_json?: any
  metadata?: any
}

export type QuickBooksFpaUpdateAssumptionsBulkRequest = {
  updates: Array<{
    assumption_id?: string | null
    key?: string | null
    label?: string | null
    value_json?: any
    source?: string | null
    confidence?: number | null
    citation_json?: any
    metadata?: any
  }>
}

export type QuickBooksFpaUpdateLinesBulkRequest = {
  updates: Array<{
    line_id: string
    override_value?: number | null
    override_note?: string | null
    override_formula?: string | null
  }>
}

export type QuickBooksFpaBudgetVsActualRow = {
  statement_type: string
  node_id: string
  label: string
  level: number
  is_total: boolean
  actuals: Array<number | null>
  plan: Array<number | null>
  variance: Array<number | null>
  variance_pct: Array<number | null>
  ytd_actual: Array<number | null>
  ytd_plan: Array<number | null>
  ytd_variance: Array<number | null>
}

export type QuickBooksFpaBudgetVsActualReport = {
  plan: QuickBooksFpaPlanVersion
  periods: string[]
  rows: QuickBooksFpaBudgetVsActualRow[]
  warnings: string[]
}

export type QuickBooksActivePlan = {
  schema_version: 'fg.active_plan.v1' | string
  availability: 'available' | 'unavailable' | string
  reason_code?: string | null
  message?: string | null
  plan_id?: string | null
  name?: string | null
  kind?: string | null
  primary_source?: 'fusegrid_default' | 'web' | 'excel' | string | null
  publication_token?: string | null
  publication_revision?: number | null
  published_at?: string | null
  layout_version_id?: string | null
  current_layout_version_id?: string | null
  layout_drift?: boolean | null
  accounting_basis?: string | null
  horizon?: { months?: number | null; start_period?: string | null; end_period?: string | null } | null
  validation?: Record<string, unknown> | null
  last_receipt?: Record<string, unknown> | null
  latest_publication_provenance?: {
    schema_version: 'fg.forecast_import_receipt.v1' | string
    source_filename?: string | null
    source_sha256: string
    sheets?: Array<{ id?: string | null; name: string }>
    external_workbooks?: string[]
    external_link_count: number
    external_links_acknowledged: boolean
    acknowledged_by?: string | null
    acknowledged_at?: string | null
    warning_status: 'stale_external_values_possible' | 'clear' | string
    [key: string]: unknown
  } | null
  newer_web_model_available?: boolean | null
  web_candidates?: Array<{
    plan_id: string
    name: string
    primary_source: 'fusegrid_default' | 'web' | 'legacy' | string
    source: string
    publication_token: string
    publication_revision: number
    published_at?: string | null
  }>
}

export type QuickBooksActivePlanVariancePeriod = {
  period_end?: string | null
  period_start: string
  period_status: 'closed' | 'partial' | 'future' | string
  actual: string | null
  plan: string | null
  variance: string | null
  variance_pct: string | null
  favorability: 'favorable' | 'unfavorable' | 'on_plan' | string | null
  ytd_actual: string | null
  ytd_plan: string | null
  ytd_variance: string | null
}

export type QuickBooksCurrentSavedPlanComparison = {
  contract: 'fg.current_saved_plan_comparison.v1' | string
  accounting_basis: 'accrual' | 'cash'
  currency_code: string
  source_revision: number
  source_job_id: string
  source_checkpoint: string
  source_token: string
  fiscal_year_start_month: number
  ytd_policy: 'fiscal_ytd_requires_complete_prefix'
  plan_money_provenance: 'legacy_binary64_shortest_decimal'
  native_profit_and_loss_validated: boolean
  native_balance_sheet_validated: boolean
  native_cash_flow_validated: boolean
  cash_flow_unavailable_reason?: string | null
  general_ledger_verified: boolean
}

export type QuickBooksActivePlanVarianceResponse = {
  current_source?: QuickBooksCurrentSavedPlanComparison | null
  schema_version: 'fg.plan_vs_actual.v1' | string
  comparison_basis: string
  currency_code: string
  plan: QuickBooksActivePlan
  coverage: {
    latest_closed?: string | null
    latest_available?: string | null
    includes_partial_current_month?: boolean | null
    actuals_integrity?: 'passed' | 'blocked' | 'unknown' | string | null
  }
  rows: Array<{
    statement_type: string
    node_id: string
    label: string
    level: number
    is_total: boolean
    periods: QuickBooksActivePlanVariancePeriod[]
  }>
  warnings?: unknown[]
}

export type QuickBooksFpaBudgetVsActualCommentaryItem = {
  statement_type: string
  node_id: string
  label: string
  period: string
  severity: string
  actual?: number | null
  plan?: number | null
  variance?: number | null
  variance_pct?: number | null
  ytd_actual?: number | null
  ytd_plan?: number | null
  ytd_variance?: number | null
  commentary: string
}

export type QuickBooksFpaBudgetVsActualCommentaryDraft = {
  status: string
  mode: string
  commentary: QuickBooksFpaBudgetVsActualCommentaryItem[]
  evidence: any[]
  warnings: string[]
}

export type QuickBooksGrowthScenarioResult = {
  revenue?: number | null
  cogs?: number | null
  operatingExpenses?: number | null
  profit?: number | null
  averageAssets?: number | null
  averageEquity?: number | null
  grossMarginPct?: number | null
  netMarginPct?: number | null
  dso?: number | null
  dio?: number | null
  dpo?: number | null
  operatingCycleDays?: number | null
  cashConversionCycleDays?: number | null
  cashRequiredPerSalesDollar?: number | null
  cashGeneratedPerSalesDollar?: number | null
  sfgPerCycle?: number | null
  cyclesPerYear?: number | null
  annualSfgRate?: number | null
  compoundAnnualSfgRate?: number | null
  classification?: string | null
  dupont?: {
    roe?: number | null
    netMargin?: number | null
    assetTurnover?: number | null
    equityMultiplier?: number | null
  }
}

export type QuickBooksGrowthScenarioAssumptions = {
  revenueGrowthPct?: number | null
  priceIncreasePct?: number | null
  volumeChangePct?: number | null
  cogsPctChange?: number | null
  operatingExpensePctChange?: number | null
  dsoDaysChange?: number | null
  dioDaysChange?: number | null
  dpoDaysChange?: number | null
  assetBasePctChange?: number | null
  equityPctChange?: number | null
}

export type QuickBooksGrowthStatementViewRef = {
  id?: string | null
  name?: string | null
  source?: 'default' | 'saved' | string
  statement_type?: string | null
  statementType?: string | null
  layout_version_id?: string | null
  layoutVersionId?: string | null
  period_count?: number | null
  periodCount?: number | null
  as_of_date?: string | null
  asOfDate?: string | null
}

export type QuickBooksGrowthAnalysisConfig = {
  version?: number
  enabled?: boolean
  executiveSummary?: string | null
  companyMemory?: string | null
  companyContext?: string | null
  source?: {
    statementViews?: Record<string, QuickBooksGrowthStatementViewRef>
    analysisWindowMonths?: number
    asOfDate?: string | null
    balanceBasis?: 'average_monthly' | 'ending_balance' | string
    profitMeasure?: 'net_income' | 'operating_income' | string
  }
  analysisWindowMonths?: number
  asOfDate?: string | null
  balanceBasis?: 'average_monthly' | 'ending_balance' | string
  profitMeasure?: 'net_income' | 'operating_income' | string
  targetGrowthPct?: number
  scenarios?: QuickBooksGrowthScenarioConfig[]
}

export type QuickBooksGrowthScenarioConfig = {
  id: string
  name: string
  enabled?: boolean
  assumptions?: QuickBooksGrowthScenarioAssumptions
  dsoDeltaDays?: number | null
  dioDeltaDays?: number | null
  dpoDeltaDays?: number | null
  cogsPctDelta?: number | null
  opexPctDelta?: number | null
  priceLiftPct?: number | null
}

export type QuickBooksGrowthScenario = {
  id: string
  name: string
  assumptions?:
    QuickBooksGrowthScenarioConfig | QuickBooksGrowthScenarioAssumptions | any
  result: QuickBooksGrowthScenarioResult
}

export type QuickBooksGrowthAnalysisResponse = {
  workbook_id?: string
  binding_id?: string | null
  connection_id?: string
  layout_version_id?: string
  company_name?: string | null
  layout_name?: string | null
  can_edit?: boolean
  selectedScenarioId?: string | null
  analysisMode?: 'growth' | 'roe' | string | null
  config?: QuickBooksGrowthAnalysisConfig
  inputs?: any
  baseline?: QuickBooksGrowthScenarioResult | null
  scenarios?: QuickBooksGrowthScenario[]
  trends?: {
    basis?: string | null
    availablePeriodCount?: number | null
    validPeriodCount?: number | null
    periods?: Array<{
      period?: string | null
      periodIndex?: number | null
      inputs?: any
      result?: QuickBooksGrowthScenarioResult | null
    }>
  }
  invoiceMetrics?: {
    source?: string | null
    current?: {
      period?: string | null
      periodStart?: string | null
      periodEnd?: string | null
      invoiceSales?: number | null
      invoiceCount?: number | null
      averageInvoiceAmount?: number | null
      dso?: number | null
    } | null
    history?: Array<{
      period?: string | null
      periodStart?: string | null
      periodEnd?: string | null
      invoiceSales?: number | null
      invoiceCount?: number | null
      averageInvoiceAmount?: number | null
      dso?: number | null
    }>
    methodology?: any
  }
  companyContext?: {
    companyName?: string | null
    realmId?: string | null
    country?: string | null
    currencyCode?: string | null
    fiscalYearStartMonth?: string | null
    source?: string | null
  } | null
  driverInsights?: Array<{
    id: string
    label: string
    annualSfgImpact?: number | null
    meetsTarget?: boolean | null
  }>
  warnings?: string[]
  methodology?: any
  performance?: any
  ar_analysis?: QuickBooksArTrendAnalysis | null
  generated_at?: string
}

export type QuickBooksArTrendPeriod = {
  period: string
  periodStart: string
  periodEnd: string
  averageAr?: number | null
  endingAr?: number | null
  activityEndingAr?: number | null
  invoiceEndingAr?: number | null
  unappliedCreditAr?: number | null
  authoritativeEndingAr?: number | null
  endingArSource?: string | null
  reconciliationAdjustment?: number | null
  controlAdjusted?: boolean | null
  currentAr?: number | null
  under90Ar?: number | null
  over90Ar?: number | null
  missingDueAr?: number | null
  sourceBucketTotalAr?: number | null
  agingBucketTotalAr?: number | null
  unclassifiedAr?: number | null
  sourceExcessAr?: number | null
  bucketReconciliationAr?: number | null
  knownReconcilingDifferenceAr?: number | null
  agingSource?: string | null
  invoiceSales?: number | null
  dsoSales?: number | null
  dsoSalesSource?: string | null
  averageDso?: number | null
  dso?: number | null
  weightedAverageAge?: number | null
  overdueAr?: number | null
  over60Ar?: number | null
  reconciliationDiagnostics?: Record<string, number | null | undefined>
}

export type QuickBooksArCustomerConcern = {
  /** Report-local opaque join key, not the connected QuickBooks customer ID. */
  publicCustomerKey?: string
  customerId?: string | null
  customer: string
  endingAr?: number | null
  overdueAr?: number | null
  over60Ar?: number | null
  over90Ar?: number | null
  dso?: number | null
  weightedAverageAge?: number | null
  riskScore?: number | null
  openInvoiceCount?: number | null
}

export type QuickBooksArInvoiceDrilldown = {
  invoiceKey: string
  periodStart?: string | null
  periodEnd?: string | null
  customerId?: string | null
  customer: string
  invoiceNumber?: string | null
  invoiceDate?: string | null
  dueDate?: string | null
  dueDateSource?: string | null
  originalAmount?: number | null
  openAmount?: number | null
  daysOpen?: number | null
  daysPastDue?: number | null
  allocationMethod?: string | null
}

export type QuickBooksArIncomeAccountAllocation = {
  periodStart?: string | null
  periodEnd?: string | null
  invoiceKey: string
  invoiceId?: string | null
  invoiceNumber?: string | null
  invoiceDate?: string | null
  dueDate?: string | null
  dueDateSource?: string | null
  customerId?: string | null
  customer: string
  invoiceLineId?: string | null
  lineIndex?: number | null
  lineAmount?: number | null
  allocationWeight?: number | null
  openAmount?: number | null
  currentAmount?: number | null
  oneToThirtyAmount?: number | null
  thirtyOneToSixtyAmount?: number | null
  sixtyOneToNinetyAmount?: number | null
  overNinetyAmount?: number | null
  daysOpen?: number | null
  daysPastDue?: number | null
  agingBucket?: string | null
  incomeAccountId?: string | null
  incomeAccountName?: string | null
  serviceItemId?: string | null
  serviceItemName?: string | null
  classId?: string | null
  className?: string | null
  description?: string | null
  allocationMethod?: string | null
  unclassifiedReason?: string | null
  leftCensored?: boolean | null
}

export type QuickBooksArCollectabilityThreshold = {
  thresholdDays: number
  cohortInvoiceCount: number
  fullyPaidInvoiceCount: number
  anyRecoveryInvoiceCount: number
  cohortOriginalAmount?: number | null
  openAtThresholdAmount?: number | null
  recoveredAfterThresholdAmount?: number | null
  remainingAsOfAmount?: number | null
  amountRecoveryRate?: number | null
  fullPaymentRate?: number | null
  anyRecoveryRate?: number | null
  medianDaysToFullPaymentAfterThreshold?: number | null
  p75DaysToFullPaymentAfterThreshold?: number | null
  confidence?: 'high' | 'medium' | 'low' | string | null
}

export type QuickBooksArCollectabilityCustomer = {
  /** Report-local opaque join key, not the connected QuickBooks customer ID. */
  publicCustomerKey?: string
  customerId?: string | null
  customer: string
  invoiceCount?: number | null
  openAmount?: number | null
  over90Amount?: number | null
  over180Amount?: number | null
  over270Amount?: number | null
  over360Amount?: number | null
  expectedCollectibleAmount?: number | null
  expectedWriteOffAmount?: number | null
  unknownBenchmarkAmount?: number | null
  worstPastDueDays?: number | null
}

export type QuickBooksArCollectabilityInvoice = {
  invoiceKey: string
  customerId?: string | null
  customer: string
  invoiceNumber?: string | null
  invoiceDate?: string | null
  dueDate?: string | null
  openAmount?: number | null
  daysPastDue?: number | null
  thresholdDays?: number | null
  benchmarkRecoveryRate?: number | null
  expectedCollectibleAmount?: number | null
  expectedWriteOffAmount?: number | null
  confidence?: 'high' | 'medium' | 'low' | string | null
  leftCensored?: boolean | null
}

export type QuickBooksArBadDebtReserveMethod = {
  id: string
  label: string
  reserveAmount?: number | null
  percentOfSales?: number | null
  currentMonthAccrual?: number | null
  explanation?: string | null
}

export type QuickBooksArBadDebtReserveTrendPoint = {
  periodStart?: string | null
  periodEnd?: string | null
  atRiskOpen?: number | null
  expectedCollectibleAmount?: number | null
  reserveAmount?: number | null
  trailingSales?: number | null
  percentOfSales?: number | null
}

export type QuickBooksArBadDebtReserveAnalysis = {
  asOfDate?: string | null
  recommendedMethod?: string | null
  currentMonthSales?: number | null
  trailingSalesStart?: string | null
  trailingTwelveMonthSales?: number | null
  recommendedReserveAmount?: number | null
  recommendedPercentOfSales?: number | null
  recommendedCurrentMonthAccrual?: number | null
  expectedCollectibleAmount?: number | null
  atRiskOpenAmount?: number | null
  methods?: QuickBooksArBadDebtReserveMethod[]
  trend?: QuickBooksArBadDebtReserveTrendPoint[]
  methodology?: any
}

export type QuickBooksArCollectabilityAnalysis = {
  summaryTotals?: {
    totalAtRiskOpen: number
    benchmarkedAtRiskOpen: number
    totalExpectedCollectible: number
    totalExpectedWriteOff: number
    over360Exposure: number
    hasBenchmarks: boolean
    weightedRecoveryRate: number | null
  }
  asOfDate?: string | null
  thresholds?: QuickBooksArCollectabilityThreshold[]
  customers?: QuickBooksArCollectabilityCustomer[]
  currentInvoices?: QuickBooksArCollectabilityInvoice[]
  badDebtReserve?: QuickBooksArBadDebtReserveAnalysis | null
  sampleInvoiceCount?: number | null
  leftCensoredInvoiceCount?: number | null
  missingDueInvoiceCount?: number | null
  warnings?: string[]
  methodology?: any
}

export type QuickBooksArTrendAnalysis = {
  /** False means this public report requested no customer detail; do not imply no concerns. */
  publicDetailAvailable?: boolean
  serving?: { mode: 'summary' | 'full'; artifactId: string; source?: never }
    | { mode: 'full'; source: 'native-current-postings'; artifactId?: never }
  asOfDate?: string | null
  periodGrain?: string | null
  topCustomerLimit?: number | null
  excludedCustomers?: Array<{
    customerId?: string | null
    customer: string
    endingAr?: number | null
    invoiceCount?: number | null
  }>
  periods: QuickBooksArTrendPeriod[]
  customerPeriodTrends?: Record<string, QuickBooksArTrendPeriod[]>
  customerTrends?: QuickBooksArCustomerConcern[]
  topCustomerConcerns?: QuickBooksArCustomerConcern[]
  invoiceDrilldowns?: QuickBooksArInvoiceDrilldown[]
  periodInvoiceDrilldowns?: QuickBooksArInvoiceDrilldown[]
  periodIncomeAccountAllocations?: QuickBooksArIncomeAccountAllocation[]
  collectability?: QuickBooksArCollectabilityAnalysis | null
  warnings?: string[]
  methodology?: any
}

export type QuickBooksCfoPresentationAiDraftEvidence = {
  id: string
  label: string
  statement?: 'profit_and_loss' | 'balance_sheet' | 'cash_flow' | string | null
  period?: string | null
  value?: number | null
  priorValue?: number | null
  delta?: number | null
  note?: string | null
}

export type QuickBooksCfoPresentationAiDraftResponse = {
  executiveSummary: string
  sectionSummaries?: Record<string, string>
  actions: string[]
  evidence: QuickBooksCfoPresentationAiDraftEvidence[]
  model_id: string
  generated_at: string
  warnings: string[]
}

export type QuickBooksCfoPresentationAgentEditResponse = {
  presentation: QuickBooksCfoPresentationResponse
  agent_run_id: string
  change_summary: string
  warnings: string[]
}

export type QuickBooksCfoPresentationAgentUndoResponse = {
  presentation: QuickBooksCfoPresentationResponse
  agent_run_id: string
  change_summary: string
  warnings: string[]
}

// Per-month frozen board-pack snapshots (freeze-on-publish history).
export type CfoPresentationSnapshotSummary = {
  period: string
  status: string
  report_title: string
  as_of_date?: string | null
  source_period?: string | null
  published_by?: string | null
  published_at?: string | null
  updated_at?: string | null
}

export type CfoPresentationSnapshotListResponse = {
  workbook_id: string
  snapshots: CfoPresentationSnapshotSummary[]
}

export type CfoPresentationSnapshotResponse = {
  period: string
  status: string
  report_title: string
  as_of_date?: string | null
  source_period?: string | null
  published_at?: string | null
  /** The frozen presentation, served with can_edit = false. */
  snapshot: QuickBooksCfoPresentationResponse
}

/** Lightweight publish acknowledgement — the frozen blob is never echoed back. */
export type CfoPresentationPublishResponse = {
  period: string
  status: string
  report_title: string
  as_of_date?: string | null
  published_at?: string | null
}

export type CfoReportRevisionSummary = {
  id: string
  reporting_period: string
  revision_number: number
  label: string
  status: 'captured' | 'issued' | 'archived' | string
  source_signature: string
  configuration_hash: string
  highlight_digest: string
  supersedes_revision_id?: string | null
  created_at?: string | null
}

export type CfoReportRevisionResponse = {
  revision: CfoReportRevisionSummary
  snapshot: QuickBooksCfoPresentationResponse
}

export type CfoReviewCheckpoint = {
  id: string
  reporting_period: string
  source_signature: string
  configuration_hash: string
  insight_digest: string
  highlight_digest: string
  data_state: any
  reviewed_by?: string | null
  reviewer_name?: string | null
  reviewed_at?: string | null
}

export type CfoReportMaterialization = {
  id: string
  revision_id: string
  format: 'pdf' | 'html' | 'presentation' | string
  status: string
  content_hash?: string | null
  mime_type?: string | null
  renderer_version?: string | null
  theme_version?: string | null
  storage_uri?: string | null
  byte_size?: number | null
  error_message?: string | null
  created_at?: string | null
}

// External, revocable view-only token links for a board pack.
export type CfoShareLink = {
  id: string
  workbook_id: string
  period?: string | null
  revision_id?: string | null
  revision_label?: string | null
  label?: string | null
  status: string // active | revoked | expired
  expires_at?: string | null
  revoked_at?: string | null
  last_viewed_at?: string | null
  view_count: number
  created_at?: string | null
}

export type CreateCfoShareLinkResponse = {
  /** Plaintext token, returned ONCE at mint. */
  token: string
  link: CfoShareLink
}

export type PublicCfoSnapshot = {
  company_name?: string | null
  report_title: string
  label?: string | null
  period?: string | null
  generated_at?: string | null
  /** Frozen presentation payload (internal ids stripped, can_edit=false). */
  presentation: any
}

export type QuickBooksFpaCacheTableInfo = {
  id: string
  name: string
  row_count: number
  parquet_url?: string | null
  parquet_urls?: string[] | null
  parquet_part_bytes?: number[] | null
  artifact_uri?: string | null
  artifact_uris?: string[] | null
  artifact_part_bytes?: number[] | null
  datasource_id?: string | null
  extract_version_id?: string | null
  storage_type?: string | null
  row_count_hint?: number | null
}

export type QuickBooksFpaAgentArtifact = {
  id: string
  name: string
  table_name: string
  artifact_kind: string
  date_grain?: string | null
  row_count: number
  freshness: string
  required: boolean
  ready: boolean
  semantic_tags: string[]
  join_keys: string[]
  known_metrics: string[]
  parquet_url?: string | null
  parquet_urls?: string[] | null
  parquet_part_bytes?: number[] | null
  artifact_uri?: string | null
  artifact_uris?: string[] | null
  artifact_part_bytes?: number[] | null
  storage_type?: string | null
}

export type CfoContractReadiness =
  'ready' | 'stale' | 'rebuilding' | 'missing_data' | 'degraded'

export type CfoArtifactExecutionEngine =
  | 'postgres'
  | 'bigquery'
  | 'artifact_json'
  | 'artifact_parquet'
  | 'direct_artifact'
  | 'embedded_duckdb'
  | 'quack_duckdb'

export type CfoContractSource = {
  source_id: string
  source_kind: string
  execution_engine: CfoArtifactExecutionEngine
  artifact_ids: string[]
  row_count?: number | null
  freshness_token?: string | null
}

export type CfoContractMeta = {
  contract_id: string
  contract_version: string
  generated_at: string
  freshness_token: string
  readiness: CfoContractReadiness
  rebuild_state: string
  execution_engine: CfoArtifactExecutionEngine
  sources: CfoContractSource[]
  warnings: string[]
  row_count?: number | null
}

export type CfoArtifactQueryEngineDescriptor = {
  id: string
  label: string
  execution_engine: CfoArtifactExecutionEngine
  status: string
  protocol: string
  internal_only: boolean
  public_sql_allowed: boolean
  intended_workloads: string[]
}

export type CfoPageContractDescriptor = {
  id: string
  route: string
  role: string
  ai_tool_contract: boolean
  backing_engines: CfoArtifactExecutionEngine[]
}

export type QuickBooksFpaCacheManifest = {
  connection_id: string
  realm_id: string
  company_name?: string | null
  cache_dataset_id: string
  source_views: string[]
  tables: QuickBooksFpaCacheTableInfo[]
  min_period?: string | null
  max_period?: string | null
  row_counts: Record<string, number>
  cache_level?: 'summary' | 'detail' | string
  gl_detail_ready?: boolean
  gl_detail_status?: 'not_started' | 'generating' | 'ready' | 'failed' | string
  ar_support_detail_ready?: boolean
  ar_support_detail_status?: 'ready' | 'missing' | string
  ar_collectability_detail_ready?: boolean
  ar_collectability_detail_status?: 'ready' | 'missing' | string
  ar_analysis_ready?: boolean
  ar_analysis_status?: 'ready' | 'missing' | string
  cfo_analytics_ready?: boolean
  cfo_analytics_status?: 'ready' | 'missing' | string
  ml_forecast_ready?: boolean
  ml_forecast_status?:
    | 'not_started'
    | 'generating'
    | 'ready'
    | 'failed'
    | 'insufficient_training_data'
    | string
  ml_signals_ready?: boolean
  ml_signals_status?:
    | 'not_started'
    | 'generating'
    | 'ready'
    | 'failed'
    | 'insufficient_training_data'
    | string
  ml_artifacts?: QuickBooksFpaAgentArtifact[]
  agent_artifacts?: QuickBooksFpaAgentArtifact[]
  generated_at: string
}

export type QuickBooksCfoArtifactCatalogResponse = {
  workbook_id: string
  connection_id?: string | null
  realm_id?: string | null
  company_name?: string | null
  contract: CfoContractMeta
  manifest?: QuickBooksFpaCacheManifest | null
  artifacts: QuickBooksFpaAgentArtifact[]
  query_engines: CfoArtifactQueryEngineDescriptor[]
  page_contracts: CfoPageContractDescriptor[]
}

export type QuickBooksFpaCacheTablePayload = {
  id: string
  name: string
  rows: any[]
  row_count?: number | null
  parquet_url?: string | null
  parquet_urls?: string[] | null
  parquet_part_bytes?: number[] | null
  artifact_uri?: string | null
  artifact_uris?: string[] | null
  artifact_part_bytes?: number[] | null
  datasource_id?: string | null
  extract_version_id?: string | null
  storage_type?: string | null
  row_count_hint?: number | null
}

export type QuickBooksFpaCachePrepareResponse = {
  manifest: QuickBooksFpaCacheManifest
  tables: QuickBooksFpaCacheTablePayload[]
}

export type QuickBooksFpaCacheStatusResponse = {
  ready: boolean
  manifest?: QuickBooksFpaCacheManifest | null
}

export type ServerExtractRun = {
  id: string
  datasource_id: string
  extract_version_id?: string | null
  requested_by: string
  reason?: string | null
  status: string // 'queued' | 'running' | 'completed' | 'failed'
  started_at?: string | null
  finished_at?: string | null
  bytes?: number | null
  row_count?: number | null
  error_message?: string | null
  created_at?: string
}

export type ServerRefreshSchedule = {
  id: string
  datasource_id: string
  schedule_type: 'cron' | 'interval' | string
  schedule_value: string
  timezone: string
  enabled: boolean
  requires_approval: boolean
  next_run_at?: string | null
  created_at: string
  updated_at: string
}

export type ServerTableauPublishedExtract = {
  import_id: string
  datasource_key: string
  datasource_name: string
  extract_uri: string
  storage_type: string
  engine: string
  row_count?: number | null
  manifest_sha256?: string | null
  schema_sha256?: string | null
  updated_at?: string | null
}

export type ServerUserFieldOverride = {
  dataset_id: string
  field_id: string
  display_name?: string | null
  hidden?: boolean | null
  type_override?: string | null
  updated_at?: string
}

export type ServerWorkbook = {
  id: string
  site_id: string
  site_slug?: string | null
  workbook_kind?: 'spreadsheet' | 'tableau' | string | null
  has_quickbooks_connection?: boolean
  accounting_provider?: 'quickbooks' | 'xero' | string | null
  name: string
  created_by: string
  created_at: string
  updated_at: string
  role?: string | null
  can_edit?: boolean
  last_viewed_at?: string | null
  last_opened_tab?: { kind: string; name?: string | null } | null
  thumbnail_url?: string | null
  is_favorite?: boolean | null
}

export type ServerWorkbookDeleteImpactDatasource = {
  datasource_id: string
  datasource_name: string
  datasource_kind: string
  total_workbook_count: number
  other_workbook_count: number
  extract_run_count: number
  published_artifact_count: number
  can_delete_with_workbook: boolean
  can_manage_datasource: boolean
}

export type ServerWorkbookDeleteImpact = {
  workbook_id: string
  workbook_name: string
  workbook_refresh_job_count: number
  datasource_count: number
  shared_datasource_count: number
  exclusive_datasource_count: number
  exclusive_deletable_datasource_count: number
  exclusive_retained_datasource_count: number
  extract_run_count: number
  published_artifact_count: number
  datasources: ServerWorkbookDeleteImpactDatasource[]
}

export type ServerWorkbookDeleteResult = {
  status: string
  deleted_unshared_datasource_count: number
  retained_datasource_count: number
}

export type ServerTenant = {
  id: string
  name: string
  slug: string
}

export type PlatformTenant = ServerTenant & {
  admin_emails: string[]
  plan_code: string | null
  plan_status: string | null
  trial_ends_at: string | null
  has_subscription: boolean
  can_extend_trial: boolean
}

export type PlatformTrialExtensionRequest = {
  trial_ends_at: string
  expected_trial_ends_at: string | null
  reason: string
}

export type PlatformTrialExtensionResponse = {
  tenant_id: string
  previous_trial_ends_at: string
  trial_ends_at: string
  audit_event_id: string
}

export type PlatformSite = {
  id: string
  tenant_id: string
  name: string
  slug: string
  user_count: number
  project_count: number
  current_site_role: string | null
  current_site_status: string | null
  platform_grant_id: string | null
  approval_by: string | null
  approval_received_on: string | null
}

export type PlatformTenantDirectory = {
  tenants: PlatformTenant[]
  sites: PlatformSite[]
}

export type PlatformStaff = { user_id: string; email: string; role: 'founder' | 'sales' | 'admin' }

export type ServerSite = {
  cfo_reporting_enabled?: boolean
  id: string
  tenant_id: string
  name: string
  slug: string
  is_personal: boolean
  project_count: number
  user_count: number
  workbook_count: number
  current_site_role?: string | null
}

export type ServerCfoExperience = {
  site: ServerSite
  workbook: ServerWorkbook
}

export type ServerSiteMembership = {
  setup_pending?: boolean
  last_sign_in_at?: string | null
  can_generate_setup_link?: boolean
  site_id: string
  site_slug: string
  user_id: string
  email: string
  /// Present once the account has a name. An admin-invited user has only an
  /// email until they finish setup, so this stays optional.
  full_name?: string | null
  site_role: string
  status: string
}

export type ServerTenantUser = {
  user_id: string
  email: string
  full_name?: string | null
  tenant_admin: boolean
  memberships: ServerSiteMembership[]
}

export type ServerGroup = {
  id: string
  site_id: string
  name: string
  slug: string
  is_default_all_users: boolean
  grant_site_role?: string | null
  member_count: number
}

export type ServerProject = {
  id: string
  site_id: string
  parent_project_id?: string | null
  owner_user_id?: string | null
  name: string
  slug: string
  description?: string | null
  asset_permission_mode: string
  is_personal_space: boolean
  workbook_count: number
}

export type ServerProjectPermissionRule = {
  id: string
  project_id: string
  subject_kind: 'user' | 'group' | string
  subject_id: string
  capabilities: Record<string, string>
  is_project_leader: boolean
}

export type ServerSiteWorkbookSummary = {
  id: string
  project_id: string
  project_name: string
  name: string
  created_by: string
  created_at: string
  updated_at: string
  can_edit: boolean
  role: string
  /** Temporary compatibility alias: true when the workbook has any accounting
   * CFO binding. New callers should use `accounting_provider`. */
  has_quickbooks_connection?: boolean
  /** Status of the bound QuickBooks connection (`active`, `needs_reconnect`,
   * `revoked`, …). Lets the experiences list flag disconnected books. */
  qbo_connection_status?: string | null
  qbo_company_name?: string | null
  /** `active` while a standing QuickBooks connect link is live, `revoked`
   * when every link was revoked, absent when the CFO Core never had one. */
  qbo_connect_link_status?: 'active' | 'revoked' | string | null
  accounting_provider?: 'quickbooks' | 'xero' | string | null
  accounting_connection_status?: string | null
  accounting_company_name?: string | null
  currency_code?: string | null
  country_code?: string | null
}

/** Firm Cockpit — one row per client (Project + its connected CFO workbook). */
/** One ranked issue in a firm-cockpit client's collapsible attention drawer. */
export type ServerFirmAttentionItem = {
  severity?: string | null
  headline?: string | null
  /** Plain-language supporting line (falls back to a pre-formatted value). */
  detail?: string | null
  category?: string | null
  /** Deep-link target within the client's CFO Core. */
  drill?: {
    tab?: string
    view?: string | null
    params?: unknown
  } | null
}

export type ServerFirmPortfolioClient = {
  project_id: string
  client_name: string
  workbook_id: string
  site_slug: string
  company_name?: string | null
  accounting_provider: 'quickbooks' | 'xero' | string
  is_demo?: boolean
  accounting_connection_status?: string | null
  currency_code?: string | null
  country_code?: string | null
  capabilities?: Record<string, boolean | string | null>
  health: 'healthy' | 'watch' | 'at_risk' | 'blocked' | string
  close_stage:
    | 'needs_review'
    | 'reviewed'
    | 'issued'
    | 'shared'
    | 'viewed'
    | 'stale'
    | string
  close_status?: string | null
  readiness_overall?: string | null
  readiness_score?: number | null
  pnl_tone?: string | null
  attention_count: number
  revenue?: number | null
  revenue_prior?: number | null
  gross_margin_pct?: number | null
  net_income?: number | null
  net_margin_pct?: number | null
  dso?: number | null
  dso_prior?: number | null
  /** AI-weighted composite financial-health score (0-100) + its month-over-month change, grade, tier. */
  health_score?: number | null
  health_delta_1mo?: number | null
  health_grade?: string | null
  health_tier?: string | null
  covered_categories?: number | null
  /** AI-generated "why this client needs attention". */
  attention_reason?: string | null
  /** Drill for the exact ratio named by `attention_reason`. */
  health_attention_drill?: {
    tab?: string
    view?: string | null
    params?: unknown
  } | null
  /** Top ranked "needs attention" items for the cockpit's per-client drawer. Ordered
   * worst-first, trimmed to a scannable few; empty/absent when the client is all-clear. */
  attention_items?: ServerFirmAttentionItem[] | null
  /** Per-ratio pack behind the roster's global focus-ratio column. Absent until this client's CFO
   *  Home has rendered at least once, in which case the row shows the "not analyzed yet" state. */
  focus_ratios?: FocusRatioSlice[] | null
  /** 12-month revenue series for the roster sparkline, oldest first. Absent until backfilled. */
  revenue_trend?: PortfolioTrendPoint[] | null
  top_flag_severity?: string | null
  top_flag_headline?: string | null
  top_flag_drill?: {
    tab?: string
    view?: string | null
    params?: unknown
  } | null
  period?: string | null
  last_analyzed_at?: string | null
  last_sync_at?: string | null
  qbo_status?: string | null
  pack_published: boolean
  live_board_report: {
    authenticated_route: string
    reporting_period?: string | null
    data_through?: string | null
    refresh_timestamp?: string | null
    review_state: 'needs_review' | 'reviewed' | 'issued' | 'shared' | 'viewed' | 'stale' | string
    source_signature?: string | null
    last_reviewed_at?: string | null
    last_reviewer_name?: string | null
    latest_revision_id?: string | null
    latest_revision_label?: string | null
    available_materializations: string[]
    new_data_available: boolean
  }
  active_share_links: number
  share_view_count: number
  assigned_to_user_id?: string | null
  assigned_to_email?: string | null
  assigned_to_name?: string | null
}

export type ServerFirmPortfolio = {
  site_slug: string
  site_name: string
  can_manage_billing?: boolean
  summary: {
    client_count: number
    need_review: number
    closes_ready: number
    sent_and_viewed: number
  }
  clients: ServerFirmPortfolioClient[]
}

export type ServerSiteSummary = {
  tenant: ServerTenant
  site: ServerSite
  groups: ServerGroup[]
  projects: ServerProject[]
  workbooks: ServerSiteWorkbookSummary[]
}

export type ServerWorkbookUserStateRequest = {
  last_opened_tab?: { kind?: string | null; name?: string | null } | null
  last_authoring_mode?: 'cloud' | 'local' | string | null
  thumbnail_url?: string | null
  is_favorite?: boolean | null
}

export type ServerWorkbookUserState = {
  workbook_id: string
  user_id: string
  last_viewed_at: string
  last_opened_tab?: { kind: string; name?: string | null } | null
  last_authoring_mode?: string | null
  thumbnail_url?: string | null
  is_favorite: boolean
}

export type ServerWorkbookDocument = {
  workbook_id: string
  version: number
  document_json: any
  updated_at: string
}

export type ServerWorkbookFieldOverride = {
  dataset_id: string
  field_id: string
  override_json: any
  updated_at: string
}

export type ServerSemanticRegistry = {
  version?: number
  import_id?: string
  generated_at?: string
  datasources?: any[]
  fields?: any[]
  parameters?: any[]
  sets?: any[]
  advanced?: any
  parse_diagnostics?: TableauParseDiagnostics
}

export type ServerBindingMap = {
  version?: number
  import_id?: string
  generated_at?: string
  datasources?: any[]
}

export type ServerDocumentSessionLease = {
  session_id: string
  resume_token: string
  expires_at_epoch_ms: number
}

export type ServerDocumentSessionCheckpoint = {
  workbook_id: string
  session_id: string
  snapshot_seq: number
  last_op_seq: number
}

export type ServerDocumentProjectionDescriptor = {
  kind: string
  version: number
  uri?: string | null
  applied_op_seq: number
  metadata?: unknown
}

export type ServerSheetChunkDescriptor = {
  key: {
    sheet_id: string
    row_block: number
    col_block: number
  }
  snapshot_seq: number
  op_seq: number
  bytes_hint?: number | null
}

export type ServerDocumentSessionCommittedUpdate = {
  op_seq: number
  doc_scope: 'workbook_root' | 'sheet_chunk' | 'presence' | string
  chunk_id?: string | null
  update_payload_b64: string
  actor_user_id: string
  client_id?: string | null
  client_update_id?: string | null
}

export type ServerDocumentSessionBootstrap = DocumentSessionBootstrap
export type ServerWorkbookBootstrap = WorkbookBootstrap

export type ServerTableauWorkbookSnapshot = {
  seq: number
  snapshot_format: string
  snapshot_json: any
  created_at: string
}

export type ServerTableauWorkbookEvent = CreatorCollabCommittedEvent

export type ServerTableauWorkbookBootstrap = {
  workbook_id: string
  head_seq: number
  applied_seq: number
  workbook_ir_shell?: any | null
  snapshot?: ServerTableauWorkbookSnapshot | null
  events: ServerTableauWorkbookEvent[]
}

export type ServerTableauCurrentWorkbookIr = {
  workbook_id: string
  head_seq: number
  workbook_kind?: 'spreadsheet' | 'tableau' | string | null
  import_id?: string | null
  workbook_ir: any
  execution: {
    baseline_import_id?: string | null
    active_import_id?: string | null
    last_ready_import_id?: string | null
    resolved_import_id?: string | null
    restore_artifact_import_id?: string | null
    resolution_reason?: string | null
    canonical_base_descriptor?: any
  }
}

export type ServerAppendTableauWorkbookEventsAck = {
  workbook_id: string
  head_seq: number
  acks: CreatorCollabAck[]
}

function joinUrl(base: string, path: string): string {
  const b = String(base || '').replace(/\/+$/, '')
  const p = String(path || '').replace(/^\/+/, '')
  return `${b}/${p}`
}

export type ServerWorkbookExport = {
  format: string
  exported_at: string
  workbook: ServerWorkbook
  document: ServerWorkbookDocument
}

export type ServerWorkbookImportRequest = {
  name?: string | null
  document_json: any
  workbook_kind?: 'spreadsheet' | 'tableau' | string | null
}

export type ServerWorkbookImportResponse = {
  workbook: ServerWorkbook
}

// ---------------- Tableau Import (workbook-scoped) ----------------

export type TableauCreateSessionRequest = {
  server_url: string
  site_content_url: string
  pat_name: string
  pat_secret: string
}

export type TableauCreateSessionResponse = {
  session_key: string
  api_version: string
  base_url: string
  site_id: string
  tableau_user_id: string
}

export type TableauKeychainCredentials = {
  server_url: string
  site_url: string
  pat_name: string
  pat_secret: string
}

export type TableauKeychainDebug = {
  service?: string
  account?: string
  found?: boolean
  raw_len?: number
  parse_ok?: boolean
  parse_error?: string | null
  verify_ok?: boolean
  verify_len?: number
  verify_matches?: boolean
  verify_error?: string | null
  error?: string | null
  cleared?: boolean
}

export type TableauKeychainGetResponse = {
  value: TableauKeychainCredentials | null
  debug?: TableauKeychainDebug
}

export type TableauKeychainSetResponse = {
  ok: boolean
  cleared?: boolean
  debug?: TableauKeychainDebug
}

export type TableauWorkbookRecord = {
  id: string
  name: string
  luid?: string | null
  content_url?: string | null
  webpage_url?: string | null
}

export type TableauListWorkbooksResponse = {
  workbooks: TableauWorkbookRecord[]
  total_available?: number | null
}

export type TableauParseDiagnostic = {
  level: 'error' | 'warning' | 'info' | string
  code: string
  message: string
  xml_path?: string | null
  worksheet?: string | null
  datasource?: string | null
}

export type TableauParseDiagnostics = {
  diagnostics: TableauParseDiagnostic[]
}

export type TableauServerImportSummary = {
  import_id: string
  fusegrid_workbook_id?: string | null
  tableau_workbook_id?: string | null
  created_at_ts: string
  container_kind: string
  artifacts: Record<string, unknown>
  notes?: string[]
}

export type TableauLocalImportSummary = {
  import_id: string
  file_kind: 'twb' | 'twbx'
  source: 'electron_local'
}

export type TableauWorkbookSummaryResponse = {
  summary: any
  views: Array<{ id: string; name: string }>
  parse_diagnostics?: TableauParseDiagnostics | null
  unsupported_constructs?: TableauParseDiagnostic[] | null
  import?: TableauServerImportSummary | TableauLocalImportSummary | null
  debug?: any
}

export type TableauViewDataTableResponse = {
  columns: string[]
  rows: any[][]
  row_count: number
}

export type TableauSpecExportDatasource = {
  datasource_name?: string | null
  server_name?: string | null
  connection_type?: string | null
  extract_uri?: string | null
  schema_json?: any | null
  field_manifest?: any | null
}

export type TableauSpecExportBody = {
  workbook_name?: string | null
  worksheet_spec: any
  datasource?: TableauSpecExportDatasource | null
}

export type TableauSpecExportStoreResponse = {
  ok: boolean
  filename: string
  path?: string | null
  bytes: number
  notes?: string[]
  published_workbook_id?: string | null
  published_workbook_name?: string | null
  published_view_id?: string | null
  published_view_name?: string | null
  published_project_id?: string | null
}

// ---------------- Parity Harness ----------------

export type ParityStateVectorV1 = {
  version: 1
  viewport: { width: number; height: number }
  active_asset: {
    kind: 'worksheet' | 'dashboard' | string
    view_id: string
    name?: string | null
  }
  parameters: any
  filters: any
  selection?: any | null
}

export type ParityCreateRunRequest = {
  session_key: string
  tableau_workbook_id: string
  view_id: string
  view_name?: string | null
  asset_kind: 'worksheet' | 'dashboard' | string
  state_vector: ParityStateVectorV1
  skip_oracle_csv?: boolean | null
  oracle_csv_row_limit?: number | null
}

export type ParityCreateRunResponse = {
  run_id: string
  artifacts_base_url: string
}

export type ParityUploadFusegridRequest = {
  fusegrid_png_b64?: string | null
  fusegrid_csv?: string | null
  fusegrid_debug_json?: any | null
}

export type ParityUploadFusegridBlobResponse = {
  ok: boolean
  name: string
  offset: number
  wrote: number
  done: boolean
}

export type ParityComputeDiffRequest = {
  png_channel_threshold?: number | null
  csv_keys?: string[] | null
  csv_tolerance?: number | null
  max_csv_examples?: number | null
  // Optional gate thresholds (server will report pass/fail when set)
  max_png_changed_pct?: number | null
  max_csv_total_mismatches?: number | null
  max_csv_missing_on_right?: number | null
  max_csv_extra_on_right?: number | null
  skip_png?: boolean | null
  skip_csv?: boolean | null
  fallback_fusegrid_to_oracle?: boolean | null
}

export type ParityDiffReport = {
  run_id: string
  png?: {
    width: number
    height: number
    total_pixels: number
    changed_pixels: number
    changed_pct: number
    max_channel_delta: number
    mean_abs_channel_delta: number
  } | null
  csv?: {
    keys: string[]
    tolerance: number
    left_rows: number
    right_rows: number
    missing_on_right: number
    extra_on_right: number
    total_mismatches: number
    numeric_mismatches: number
    string_mismatches: number
  } | null
  csv_examples: string[]
  errors: string[]
  gate?: {
    enabled: boolean
    passed: boolean
    failures: string[]
    thresholds: any
  } | null
  artifacts: any
}

export type ParityRunCloudSanityRequest = {
  session_key: string
  tableau_workbook_id: string
  view_id: string
  view_name?: string | null
  asset_kind: 'worksheet' | 'dashboard' | string
  state_vector: ParityStateVectorV1
  png_channel_threshold?: number | null
  csv_keys?: string[] | null
  csv_tolerance?: number | null
  max_csv_examples?: number | null
  oracle_csv_row_limit?: number | null
}

export type ParityRunCloudSanityResponse = {
  run_id: string
  report: ParityDiffReport
  artifacts_base_url: string
}

export type ParityCreateSuiteRequest = {
  session_key: string
  tableau_workbook_ids: string[]
  state_vectors?: ParityStateVectorV1[] | null
  max_views_per_workbook?: number | null
  oracle_csv_row_limit?: number | null
  include_worksheets?: boolean | null
  include_dashboards?: boolean | null
  viewport?: { width: number; height: number } | null
}

export type ParityCreateSuiteResponse = {
  suite_id: string
  status_url: string
}

export type ParityComputeSuiteDiffRequest = {
  run_ids?: string[] | null
  max_runs?: number | null
  diff: ParityComputeDiffRequest
}

export type ParitySuiteDiffReport = {
  suite_id: string
  computed_at: string
  runs_total: number
  runs_processed: number
  gate_enabled: boolean
  gate_passed: number
  gate_failed: number
  failures_sample: any[]
  errors: string[]
  artifacts: any
}

type ExtractionStatusDebugInfo = {
  backendInstance?: string | null
  artifactsDir?: string | null
  dumpPath?: string | null
}

type WorksheetArtifactProgressSummary = {
  total: number
  viz_ready: number
  viz_failed: number
  viz_pending: number
  reduced_ready: number
  done: boolean
  all_ready: boolean
  failed_keys: string[]
  pending_keys: string[]
  updated_at?: string | null
}

type ExtractionStatusPayload = {
  results: any[]
  debug: ExtractionStatusDebugInfo
  worksheetArtifactSummary: WorksheetArtifactProgressSummary | null
  run_id?: string | null
  run_status?: string | null
  stable_error_code?: string | null
}

type LocalExtractionStatusCacheEntry = {
  results: any[]
  debug: ExtractionStatusDebugInfo
  ts: number
}

type RemoteExtractionStatusCacheEntry = {
  payload: ExtractionStatusPayload
  ts: number
}

type LocalBindingMapCacheEntry = {
  bindingMap: ServerBindingMap
  ts: number
}

const LOCAL_EXTRACTION_STATUS_CACHE_TTL_MS = 30 * 60 * 1000
const REMOTE_EXTRACTION_STATUS_FINAL_CACHE_TTL_MS = 30 * 60 * 1000
const REMOTE_EXTRACTION_STATUS_TRANSIENT_CACHE_TTL_MS = 1_000
const LOCAL_EXTRACTION_STATUS_STORAGE_KEY =
  '__fg_local_extraction_status_cache_v1'
const EXTRACTION_STATUS_EPOCH_KEY_PREFIX = '__fg_extraction_status_epoch__'
const _localExtractionStatusCache = new Map<
  string,
  LocalExtractionStatusCacheEntry
>()
const _localExtractionStatusInflight = new Map<
  string,
  Promise<{ results: any[]; debug: ExtractionStatusDebugInfo }>
>()
const _remoteExtractionStatusCache = new Map<
  string,
  RemoteExtractionStatusCacheEntry
>()
const _remoteExtractionStatusInflight = new Map<
  string,
  Promise<ExtractionStatusPayload>
>()
const _remoteExtractionStatusEpoch = new Map<string, number>()

const invalidateRemoteExtractionStatus = (importId: string): void => {
  const id = String(importId || '').trim()
  if (!id) return
  _remoteExtractionStatusEpoch.set(
    id,
    (_remoteExtractionStatusEpoch.get(id) ?? 0) + 1,
  )
  const cacheKeyPrefix = `${id}?`
  for (const key of _remoteExtractionStatusCache.keys()) {
    if (key.startsWith(cacheKeyPrefix)) _remoteExtractionStatusCache.delete(key)
  }
  // Removing the shared promise makes the next poll start a request for the
  // new run. The epoch check below prevents the detached old request from
  // repopulating the cache after it eventually resolves.
  for (const key of _remoteExtractionStatusInflight.keys()) {
    if (key.startsWith(cacheKeyPrefix)) _remoteExtractionStatusInflight.delete(key)
  }
}

const EXTRACTION_TERMINAL_STATUSES = new Set([
  'published',
  'extracted',
  'failed',
  'live',
  'skipped',
])

const isFinalLocalExtractionResults = (results?: any[] | null): boolean => {
  const list = Array.isArray(results) ? results : []
  if (list.length === 0) return false
  for (const r of list) {
    const status = String((r as any)?.status ?? '')
      .trim()
      .toLowerCase()
    if (!EXTRACTION_TERMINAL_STATUSES.has(status)) return false
  }
  return true
}

const isWorksheetArtifactProgressComplete = (
  summary: WorksheetArtifactProgressSummary | null | undefined,
): boolean => {
  if (!summary || typeof summary !== 'object') return false
  if (summary.done === true || summary.all_ready === true) return true
  const total = Number(summary.total ?? 0)
  const pending = Number(summary.viz_pending ?? Number.NaN)
  return total > 0 && Number.isFinite(pending) && pending <= 0
}

const isExtractionStatusTerminalForWait = (
  payload:
    | {
        results?: any[]
        worksheetArtifactSummary?: WorksheetArtifactProgressSummary | null
        run_status?: string | null
      }
    | null
    | undefined,
): boolean => {
  if (!payload || typeof payload !== 'object') return false
  const runStatus = String(payload.run_status ?? '').trim().toLowerCase()
  if (runStatus === 'ready' || runStatus === 'failed' || runStatus === 'expired') {
    return true
  }
  if (runStatus === 'pending' || runStatus === 'running') return false
  const results = Array.isArray(payload.results) ? payload.results : []
  if (results.length > 0) {
    return isFinalLocalExtractionResults(results)
  }
  return isWorksheetArtifactProgressComplete(payload.worksheetArtifactSummary)
}

const getRemoteExtractionStatusCacheTtlMs = (
  payload:
    | {
        results?: any[]
        worksheetArtifactSummary?: WorksheetArtifactProgressSummary | null
        run_status?: string | null
      }
    | null
    | undefined,
): number =>
  isExtractionStatusTerminalForWait(payload)
    ? REMOTE_EXTRACTION_STATUS_FINAL_CACHE_TTL_MS
    : REMOTE_EXTRACTION_STATUS_TRANSIENT_CACHE_TTL_MS

const extractionStatusEpochKey = (importId: string): string =>
  `${EXTRACTION_STATUS_EPOCH_KEY_PREFIX}${String(importId || '').trim()}`

const markExtractionStatusEpoch = (
  importId: string,
  epochMs?: number,
): number => {
  const id = String(importId || '').trim()
  if (!id || typeof window === 'undefined') return 0
  const ts =
    typeof epochMs === 'number' && Number.isFinite(epochMs)
      ? Math.max(0, Math.floor(epochMs))
      : Date.now()
  const key = extractionStatusEpochKey(id)
  try {
    window.localStorage.setItem(key, String(ts))
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.setItem(key, String(ts))
  } catch {
    // ignore
  }
  return ts
}

const LOCAL_BINDING_MAP_CACHE_TTL_MS = 30 * 60 * 1000
const LOCAL_BINDING_MAP_STORAGE_KEY = '__fg_local_binding_map_cache_v1'
const _localBindingMapCache = new Map<string, LocalBindingMapCacheEntry>()
const _localBindingMapInflight = new Map<string, Promise<ServerBindingMap>>()

const hasUsableBindingMap = (value: any): value is ServerBindingMap => {
  if (!value || typeof value !== 'object') return false
  const datasources = Array.isArray((value as any)?.datasources)
    ? (value as any).datasources
    : []
  return datasources.length > 0
}

// Restore-path safety for local Electron invokes.
// Prevent indefinite hangs when native sidecar startup is delayed/unhealthy.
const LOCAL_RESTORE_INVOKE_TIMEOUT_MS = 25_000
const LOCAL_RESTORE_INVOKE_MAX_ATTEMPTS = 2
const LOCAL_RESTORE_INVOKE_RETRY_DELAY_MS = 1_000
const LOCAL_EXTRACTION_STATUS_LOOKUP_TIMEOUT_MS = 180_000
const LOCAL_EXTRACTION_STATUS_LOOKUP_MAX_ATTEMPTS = 1
const LOCAL_ARTIFACT_READ_TIMEOUT_MS_DEFAULT = 25_000
const LOCAL_ARTIFACT_READ_TIMEOUT_MS_FAST = 2_500
const LOCAL_OPTIONAL_ARTIFACT_MISS_TTL_MS = 60_000
const LOCAL_OPTIONAL_ARTIFACTS = new Set<string>([
  'data_registry.json',
  'extraction_plan.json',
  'spreadsheet_sheets.json',
  'dashboard_drafts.json',
  'collab_link.json',
  'worksheet_build_cache.json',
  'canonical_manifest.json',
  'workbook_param_defaults.json',
  'duckdb_cache_manifest.json',
  'snapshot_manifest_v2.json',
  'tableau_import_model.json',
  'grid_dashboard_manifest.json',
  'datasource_connection_overrides.json',
])
const _localOptionalArtifactMissingUntil = new Map<string, number>()
const _remoteOptionalArtifactMissingUntil = new Map<string, number>()
const _remoteOptionalArtifactInflight = new Map<string, Promise<Blob>>()
const TABLEAU_SESSION_KEY_STORAGE_KEY = 'fusegrid_tableau_session_key'

const hydrateLocalExtractionStatusCache = () => {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(LOCAL_EXTRACTION_STATUS_STORAGE_KEY)
    if (!raw) return
    const parsed =
      safeParseJson<Record<string, LocalExtractionStatusCacheEntry>>(raw)
    if (!parsed || typeof parsed !== 'object') return
    const now = Date.now()
    for (const [importId, entry] of Object.entries(parsed || {})) {
      if (!entry || typeof entry !== 'object') continue
      const ts = Number((entry as any).ts || 0)
      if (!Number.isFinite(ts) || ts <= 0) continue
      const age = now - ts
      if (age < LOCAL_EXTRACTION_STATUS_CACHE_TTL_MS) {
        _localExtractionStatusCache.set(importId, {
          results: Array.isArray((entry as any).results)
            ? (entry as any).results
            : [],
          debug:
            (entry as any).debug && typeof (entry as any).debug === 'object'
              ? (entry as any).debug
              : {
                  backendInstance: 'electron-sidecar',
                  artifactsDir: null,
                  dumpPath: null,
                },
          ts,
        })
      }
    }
  } catch {
    // ignore
  }
}

const persistLocalExtractionStatusCache = () => {
  if (typeof window === 'undefined') return
  try {
    const obj: Record<string, LocalExtractionStatusCacheEntry> = {}
    for (const [importId, entry] of _localExtractionStatusCache.entries()) {
      obj[importId] = entry
    }
    window.localStorage.setItem(
      LOCAL_EXTRACTION_STATUS_STORAGE_KEY,
      JSON.stringify(obj),
    )
  } catch {
    // ignore
  }
}

hydrateLocalExtractionStatusCache()

const hydrateLocalBindingMapCache = () => {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(LOCAL_BINDING_MAP_STORAGE_KEY)
    if (!raw) return
    const parsed = safeParseJson<Record<string, LocalBindingMapCacheEntry>>(raw)
    if (!parsed || typeof parsed !== 'object') return
    const now = Date.now()
    for (const [importId, entry] of Object.entries(parsed || {})) {
      if (!entry || typeof entry !== 'object') continue
      const ts = Number((entry as any).ts || 0)
      if (!Number.isFinite(ts) || ts <= 0) continue
      const age = now - ts
      if (age < LOCAL_BINDING_MAP_CACHE_TTL_MS) {
        const bindingMap =
          (entry as any).bindingMap &&
          typeof (entry as any).bindingMap === 'object'
            ? ((entry as any).bindingMap as ServerBindingMap)
            : null
        if (!hasUsableBindingMap(bindingMap)) continue
        _localBindingMapCache.set(importId, { bindingMap, ts })
      }
    }
  } catch {
    // ignore
  }
}

const persistLocalBindingMapCache = () => {
  if (typeof window === 'undefined') return
  try {
    const obj: Record<string, LocalBindingMapCacheEntry> = {}
    for (const [importId, entry] of _localBindingMapCache.entries()) {
      obj[importId] = entry
    }
    window.localStorage.setItem(
      LOCAL_BINDING_MAP_STORAGE_KEY,
      JSON.stringify(obj),
    )
  } catch {
    // ignore
  }
}

hydrateLocalBindingMapCache()

const _jsonRequestInflight = new Map<string, Promise<any>>()
type AuthRefreshOutcome =
  | 'refreshed'
  | 'terminal'
  | 'transient'
  | 'unavailable'
const _authRefreshInflight = new Map<string, Promise<AuthRefreshOutcome>>()
const DEFAULT_TABLEAU_CURRENT_IR_REQUEST_TIMEOUT_MS = 30_000
let _traceRouteKey = ''
let _traceCorrelationId = ''
let _traceRequestSequence = 0

const nextBrowserTraceHeaders = (): {
  correlationId: string
  requestId: string
} | null => {
  if (typeof window === 'undefined') return null
  const routeKey = `${window.location.pathname}${window.location.search}`
  if (!_traceCorrelationId || _traceRouteKey !== routeKey) {
    _traceRouteKey = routeKey
    _traceRequestSequence = 0
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
    _traceCorrelationId = `fg-${uuid}`
  }
  _traceRequestSequence += 1
  return {
    correlationId: _traceCorrelationId,
    requestId: `${_traceCorrelationId}:${_traceRequestSequence}`,
  }
}

const attachInflightCleanup = <T>(
  promise: Promise<T>,
  cleanup: () => void,
): void => {
  promise.then(
    () => {
      cleanup()
    },
    () => {
      cleanup()
    },
  )
}


export type CfoCloseEvidenceDisposition = 'unknown' | 'blocked' | 'ready_with_exceptions' | 'ready'
export type CfoCloseWorkflowStatus = 'not_started' | 'in_progress' | 'ready_for_review' | 'reviewed' | 'attested'
export type CfoCloseProviderLockStatus = 'open' | 'locked' | 'changed_after_lock' | 'unknown'
export type CfoCloseReasonableness = 'insufficient_data' | 'plausible' | 'needs_attention' | 'anomalous'
export type CfoCloseEvidenceResult = 'passed' | 'failed' | 'unavailable' | 'not_applicable'
export type CfoCloseSeverity = 'blocking' | 'high' | 'medium' | 'informational'

export type CfoClosePeriodSummary = {
  period: string
  period_end: string
  evidence_disposition: CfoCloseEvidenceDisposition
  workflow_status: CfoCloseWorkflowStatus
  provider_lock_status: CfoCloseProviderLockStatus
  reasonableness: CfoCloseReasonableness
  updated_at?: string | null
}

export type CfoClosePeriodsResponse = {
  schema_version: 'fg.close_review.v2' | string
  workbook_id: string
  company_id: string
  company_name: string
  provider: string
  periods: CfoClosePeriodSummary[]
  can_edit: boolean
}

export type CfoCloseEvidenceItem = {
  evidence_id: string
  stable_key: string
  facet: string
  assertion: string
  result: CfoCloseEvidenceResult
  severity: CfoCloseSeverity
  title: string
  explanation: string
  observed_value?: unknown
  expected_value?: unknown
  tolerance?: unknown
  amount?: number | null
  currency_code?: string | null
  source_refs?: unknown
  source_lineage?: unknown
  detector_version: string
  confidence?: number | null
}

export type CfoCloseFinding = {
  finding_id: string
  stable_key: string
  evidence_id: string
  facet: string
  assertion: string
  severity: CfoCloseSeverity
  lifecycle: 'open' | 'resolved' | 'accepted_exception' | 'reopened' | string
  title: string
  explanation: string
  recommended_action: string
  amount?: number | null
  currency_code?: string | null
  confidence?: number | null
  provenance: 'deterministic' | 'statistical' | 'ml' | 'ai' | 'manual' | 'legacy' | string
  disposition_reason?: string | null
}

export type CfoCloseTask = {
  id: string
  task_key: string
  facet: string
  title: string
  description?: string | null
  task_type: 'human' | 'machine' | 'provider' | string
  status: 'not_started' | 'in_progress' | 'blocked' | 'complete' | 'waived' | string
  owner_user_id?: string | null
  reviewer_user_id?: string | null
  due_date?: string | null
  due_business_day?: number | null
  dependency_keys?: string[]
  evidence_required: boolean
  evidence_refs?: unknown[]
  comments?: unknown[]
  waiver_reason?: string | null
  completed_at?: string | null
  custom?: boolean
  custom_template_id?: string | null
  recurrence?: 'one_time' | 'monthly' | null
  persisted?: boolean
}

export type CfoCloseReview = {
  current_source?: { contract: 'fg.close.current_native.v1'; available: boolean; period_start: string; period_end: string | null; accounting_basis: 'accrual' | 'cash'; source_revision?: number; source_checkpoint?: string; source_job_id?: string; general_ledger_verified: false; native_cash_flow_validated?: boolean; cash_flow_unavailable_reason?: string | null }
  schema_version: 'fg.close_review.v2' | string
  review_id: string
  company: { id: string; name: string; provider: string; currency_code: string; entity_id?: string | null; book_id?: string | null }
  period: { start: string; end: string; fiscal_calendar: string; scope: string }
  states: {
    evidence_disposition: CfoCloseEvidenceDisposition
    workflow_status: CfoCloseWorkflowStatus
    provider_lock_status: CfoCloseProviderLockStatus
    reasonableness: CfoCloseReasonableness
    attested_with_open_blockers: boolean
    stale: boolean
    synthetic_demo?: boolean
  }
  headline: { title: string; blocking_count: number; open_finding_count: number; unavailable_count: number; overdue_task_count: number; advisory_policy: boolean }
  legacy_readiness_score?: number | null
  materiality: Record<string, unknown>
  facets: Array<{ facet: string; label: string; result: CfoCloseEvidenceResult; coverage?: number | null; counts: Record<string, number> }>
  evidence: CfoCloseEvidenceItem[]
  findings: CfoCloseFinding[]
  tasks: { items: CfoCloseTask[]; completed: number; total: number; overdue: number }
  signoffs: Array<Record<string, any>>
  documents: Array<Record<string, any>>
  provider_lock: { status: CfoCloseProviderLockStatus; close_date?: string | null; read_only: boolean }
  reasonableness: Record<string, unknown>
  next_actions: Array<{ finding_id: string; severity: CfoCloseSeverity; title: string; recommended_action: string }>
  history_context: Record<string, unknown>
  demo_disclosure?: {
    synthetic_demo: boolean
    human_attestation: boolean
    evidence_mode?: string
    company_slug?: string
    workflow_seeded?: boolean
    objective_blockers_overridden?: number
    objective_unavailable_overridden?: number
    scripted_exceptions?: number
    description: string
  } | null
  freshness: { source_set_token: string; persisted_source_set_token?: string | null; source_hashes: Record<string, unknown>; last_sync_at?: string | null; generated_at: string; stale: boolean }
  versions: Record<string, string>
  limitations: string[]
  legacy_ai_review?: any
  permissions: { can_edit: boolean }
  counts: Record<string, number>
}

export type CfoCloseTaskCompletion = {
  task_id: string
  task_key: string
  facet: string
  title: string
  status: 'not_started' | 'in_progress' | 'blocked' | 'complete' | 'waived' | string
  owner_user_id?: string | null
  reviewer_user_id?: string | null
  due_date?: string | null
  due_business_day?: number | null
  completed_at?: string | null
  completed_by?: string | null
  completion_business_day?: number | null
  on_time?: boolean | null
  completion_event_count: number
}

export type CfoCloseHistoryPeriod = {
  period: string
  evidence_disposition: CfoCloseEvidenceDisposition
  workflow_status: CfoCloseWorkflowStatus
  provider_lock_status: CfoCloseProviderLockStatus
  reasonableness: CfoCloseReasonableness
  open_findings: number
  recurring_findings: number
  open_tasks: number
  completed_tasks: number
  waived_tasks: number
  total_tasks: number
  completion_rate?: number | null
  on_time_tasks: number
  late_tasks: number
  on_time_rate?: number | null
  average_completion_business_day?: number | null
  close_completed_at?: string | null
  close_business_day?: number | null
  signoff_count: number
  reopened_at?: string | null
  updated_at?: string | null
  task_completions: CfoCloseTaskCompletion[]
}

export type CfoCloseEffectiveness = {
  trend: 'improving' | 'stable' | 'declining' | 'insufficient_history' | string
  comparable_periods: number
  rationale: string
  method?: string
  latest?: {
    period: string
    close_business_day?: number | null
    on_time_rate?: number | null
    late_tasks: number
    reopened: boolean
    recurring_findings: number
  }
  previous?: {
    period: string
    close_business_day?: number | null
    on_time_rate?: number | null
    late_tasks: number
    reopened: boolean
    recurring_findings: number
  }
  change?: {
    close_business_days?: number | null
    on_time_rate_points?: number | null
    late_tasks?: number | null
    recurring_findings?: number | null
  }
}

export type CfoCloseHistoryResponse = {
  schema_version: 'fg.close_history.v2' | string
  workbook_id: string
  company_id: string
  effectiveness?: CfoCloseEffectiveness
  periods: CfoCloseHistoryPeriod[]
  methodology?: Record<string, string>
}

export class ServerApiClient {
  readonly baseUrl: string
  private readonly cfoRefreshInFlight = new Map<string, Promise<CfoQuickBooksRefreshResponse>>()
  private readonly isDevTunnelBase: boolean
  private readonly syncAuthFromStorage: boolean
  private readonly emitAuthEvents: boolean
  private readonly authTokenStorageKey: string
  private readonly authRefreshTokenStorageKey: string
  private readonly authRefreshRetryAfterStorageKey: string
  private readonly authRefreshGenerationStorageKey: string
  private readonly authRefreshCookieStorageKey: string
  private readonly authRefreshCoordinatorKey: string
  private token: string | null
  private refreshToken: string | null
  private readonly localAuthBootstrapInflight = new Map<
    string,
    Promise<ServerLocalAuthBootstrapResponse>
  >()
  private readonly _tableauWorkbookSummaryInflight = new Map<
    string,
    Promise<TableauWorkbookSummaryResponse>
  >()

  // Electron-only: some Tableau "local_*" imports are fully sidecar-backed (no HTTP backend).
  private isElectronLocalTableauImportId(importId: string): boolean {
    const id = String(importId || '').trim()
    if (!id) return false
    const lower = id.toLowerCase()
    if (!(lower.startsWith('local-') || lower.startsWith('local_')))
      return false
    try {
      const w: any = globalThis as any
      return typeof w?.__FG_ELECTRON_INVOKE__ === 'function'
    } catch {
      return false
    }
  }

  private shouldUseElectronTableauBridge(): boolean {
    try {
      const w: any = globalThis as any
      return typeof w?.__FG_ELECTRON_INVOKE__ === 'function'
    } catch {
      return false
    }
  }

  usesElectronTableauBridge(): boolean {
    return this.shouldUseElectronTableauBridge()
  }

  private async electronInvoke<T = any>(cmd: string, args?: any): Promise<T> {
    const w: any = globalThis as any
    const fn = w?.__FG_ELECTRON_INVOKE__
    if (typeof fn !== 'function') {
      throw new Error('Electron invoke bridge unavailable')
    }
    return await fn(String(cmd || ''), args ?? {})
  }

  private isUnknownTableauSessionError(err: any): boolean {
    const msg =
      `${String(err?.message || '')} ${String(err?.cause?.message || '')}`.toLowerCase()
    return msg.includes('unknown tableau session_key')
  }

  private readLocalTableauSessionKey(): string {
    if (typeof window === 'undefined') return ''
    try {
      return String(
        window.localStorage.getItem(TABLEAU_SESSION_KEY_STORAGE_KEY) || '',
      ).trim()
    } catch {
      return ''
    }
  }

  private writeLocalTableauSessionKey(sessionKey: string): void {
    if (typeof window === 'undefined') return
    const key = String(sessionKey || '').trim()
    if (!key) return
    try {
      window.localStorage.setItem(TABLEAU_SESSION_KEY_STORAGE_KEY, key)
    } catch {
      // ignore
    }
  }

  private async ensureElectronLocalTableauSessionKey(
    sessionKey: string,
  ): Promise<string> {
    const initialKey =
      String(sessionKey || '').trim() || this.readLocalTableauSessionKey()
    if (!this.shouldUseElectronTableauBridge() || !initialKey) return initialKey

    let sessionExists: boolean | null = null
    try {
      const probe: any = await this.electronInvoke(
        'tableau_has_session_local',
        { session_key: initialKey },
      )
      sessionExists = probe?.exists === true
    } catch {
      sessionExists = null
    }
    if (sessionExists === true || sessionExists == null) {
      return initialKey
    }

    let creds: TableauKeychainCredentials | null = null
    try {
      creds = (await this.tableauKeychainGet())?.value ?? null
    } catch {
      creds = null
    }
    if (!creds) return initialKey

    const server_url = String(creds.server_url || '').trim()
    const site_content_url = String(creds.site_url || '').trim()
    const pat_name = String(creds.pat_name || '').trim()
    const pat_secret = String(creds.pat_secret || '')
    if (!server_url || !pat_name || !pat_secret) {
      return initialKey
    }

    try {
      const refreshed = await this.tableauCreateSession({
        server_url,
        site_content_url,
        pat_name,
        pat_secret,
      })
      const refreshedKey = String(refreshed?.session_key || '').trim()
      if (refreshedKey) {
        this.writeLocalTableauSessionKey(refreshedKey)
        return refreshedKey
      }
    } catch {
      // fall through to original key and let caller surface the underlying failure
    }
    return initialKey
  }

  private async electronInvokeTableauWithSessionRecovery<T = any>(
    cmd: string,
    req: Record<string, any>,
  ): Promise<T> {
    try {
      return await this.electronInvoke<T>(cmd, { req })
    } catch (err) {
      if (!this.isUnknownTableauSessionError(err)) throw err
      const refreshedSessionKey =
        await this.ensureElectronLocalTableauSessionKey('')
      if (!refreshedSessionKey) throw err
      const retryReq = { ...(req || {}), session_key: refreshedSessionKey }
      return await this.electronInvoke<T>(cmd, { req: retryReq })
    }
  }

  private isRetryableLocalInvokeError(err: any): boolean {
    const msg = String(err?.message || err || '')
      .trim()
      .toLowerCase()
    if (!msg) return true
    // Input/state errors are deterministic; retries won't help.
    if (
      msg.includes('unknown import_id') ||
      msg.includes('missing import_id') ||
      msg.includes('invalid args') ||
      msg.includes('missing artifact name') ||
      msg.includes('invalid artifact name') ||
      msg.includes('bad request') ||
      msg.includes('no such file') ||
      msg.includes('not found')
    ) {
      return false
    }
    return (
      msg.includes('timed out') ||
      msg.includes('timeout') ||
      msg.includes('network') ||
      msg.includes('fetch') ||
      msg.includes('econn') ||
      msg.includes('socket') ||
      msg.includes('aborted') ||
      msg.includes('invoke failed') ||
      msg.includes('native engine exited before ready') ||
      msg.includes('bridge unavailable')
    )
  }

  private isMissingLocalArtifactError(err: any): boolean {
    const msg = String(err?.message || err || '')
      .trim()
      .toLowerCase()
    if (!msg) return false
    return (
      msg.includes('unknown import_id') ||
      msg.includes('missing import_id') ||
      msg.includes('no such file') ||
      msg.includes('os error 2') ||
      msg.includes('not found') ||
      msg.includes('__fg_missing_local_import')
    )
  }

  private async electronInvokeWithTimeoutRetry<T = any>(
    cmd: string,
    args: any,
    opts?: {
      timeoutMs?: number
      maxAttempts?: number
      retryDelayMs?: number
      opName?: string
      importId?: string
    },
  ): Promise<T> {
    const timeoutMs = Math.max(
      1_000,
      Number(opts?.timeoutMs ?? LOCAL_RESTORE_INVOKE_TIMEOUT_MS) ||
        LOCAL_RESTORE_INVOKE_TIMEOUT_MS,
    )
    const maxAttempts = Math.max(
      1,
      Number(opts?.maxAttempts ?? LOCAL_RESTORE_INVOKE_MAX_ATTEMPTS) ||
        LOCAL_RESTORE_INVOKE_MAX_ATTEMPTS,
    )
    const retryDelayMs = Math.max(
      0,
      Number(opts?.retryDelayMs ?? LOCAL_RESTORE_INVOKE_RETRY_DELAY_MS) ||
        LOCAL_RESTORE_INVOKE_RETRY_DELAY_MS,
    )
    const opName = String(opts?.opName || cmd || 'local invoke')
    const iid = String(opts?.importId || '').trim() || null

    let lastErr: any = null
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      let timer: ReturnType<typeof setTimeout> | null = null
      try {
        const timeout = new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            reject(new Error(`Local ${opName} timed out after ${timeoutMs}ms`))
          }, timeoutMs)
        })
        const invoke = this.electronInvoke<T>(cmd, args)
        return (await Promise.race([invoke, timeout])) as T
      } catch (err: any) {
        lastErr = err
        const retryable = this.isRetryableLocalInvokeError(err)
        if (!retryable || attempt >= maxAttempts) {
          const wrapped: any = new Error(
            `Local ${opName} failed after ${attempt}/${maxAttempts} attempts: ${String(err?.message || err || '')}`,
          )
          wrapped.importId = iid
          wrapped.command = cmd
          wrapped.attempt = attempt
          wrapped.maxAttempts = maxAttempts
          wrapped.timeoutMs = timeoutMs
          wrapped.cause = err
          throw wrapped
        }
        const delayMs = Math.max(
          0,
          Math.round(retryDelayMs * Math.pow(1.5, Math.max(0, attempt - 1))),
        )
        console.warn('[ServerApiClient] local invoke retrying', {
          command: cmd,
          opName,
          importId: iid,
          attempt,
          maxAttempts,
          timeoutMs,
          retryDelayMs: delayMs,
          error: String(err?.message || err || ''),
        })
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs))
        }
      } finally {
        if (timer != null) clearTimeout(timer)
      }
    }
    throw lastErr
  }

  constructor(opts?: {
    baseUrl?: string
    token?: string | null
    authStoragePrefix?: string | null
    emitAuthEvents?: boolean
  }) {
    const explicit = String(opts?.baseUrl ?? '').trim()
    const envBase = String(
      (import.meta as any).env?.VITE_API_BASE_URL ?? '',
    ).trim()
    const isBrowser = typeof window !== 'undefined'
    const authStoragePrefix = (() => {
      const raw = String(opts?.authStoragePrefix ?? 'fusegrid_api').trim()
      const safe = raw.replace(/[^A-Za-z0-9_:-]/g, '_').replace(/^_+|_+$/g, '')
      return safe || 'fusegrid_api'
    })()
    this.authTokenStorageKey = `${authStoragePrefix}_token`
    this.authRefreshTokenStorageKey = `${authStoragePrefix}_refresh_token`
    this.authRefreshRetryAfterStorageKey = `${authStoragePrefix}_refresh_retry_after_ms`
    this.authRefreshGenerationStorageKey = `${authStoragePrefix}_refresh_generation`
    this.authRefreshCookieStorageKey = `${authStoragePrefix}_refresh_cookie`
    this.emitAuthEvents = opts?.emitAuthEvents ?? true

    const normalizeBaseUrl = (u: string) =>
      String(u || '')
        .trim()
        .replace(/\/+$/, '')
    const isHttpLike = (u: string) =>
      /^https?:\/\//i.test(String(u || '').trim())
    const LS_KEY = 'fusegrid_api_base_override'

    const readQueryParam = (key: string) => {
      if (!isBrowser) return ''
      try {
        const sp = new URLSearchParams(window.location.search)
        return String(sp.get(key) || '').trim()
      } catch {
        return ''
      }
    }

    // Runtime API base override for fast backend iteration (CloudFront -> tunnel -> localhost):
    // - set:   ?apiBase=https://your-tunnel.example.com
    // - reset: ?apiBaseReset=1
    let queryApiBase = ''
    if (isBrowser) {
      try {
        const qpReset = readQueryParam('apiBaseReset')
        const qpOverride = readQueryParam('apiBase')
        queryApiBase = qpOverride
        if (qpReset === '1' || qpReset.toLowerCase() === 'true') {
          window.localStorage.removeItem(LS_KEY)
        } else if (qpOverride) {
          const candidate = normalizeBaseUrl(qpOverride)
          if (isHttpLike(candidate)) {
            window.localStorage.setItem(LS_KEY, candidate)
          }
        }
      } catch {
        // ignore
      }
    }

    const runtimeOverride = isBrowser
      ? String(window.localStorage.getItem(LS_KEY) || '').trim()
      : ''
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1')
    const browserOrigin = isBrowser ? window.location.origin : ''
    const isProductionBuild = Boolean((import.meta as any).env?.PROD)
    const isHttpsHostedPage =
      isBrowser && window.location.protocol === 'https:' && !isLocalhost

    const looksLocalBase = (u: string) => {
      const s = String(u || '')
        .trim()
        .toLowerCase()
      return (
        s.startsWith('http://localhost') ||
        s.startsWith('http://127.0.0.1') ||
        s.startsWith('https://localhost') ||
        s.startsWith('https://127.0.0.1')
      )
    }

    // In localhost dev, prefer same-origin `/api` via Vite proxy to avoid browser CORS preflights.
    // Keep remote tunnels as explicit opt-in with `?apiBase=...` in the current URL.
    // Do not keep persisted localhost overrides: stale `127.0.0.1:3000` values frequently outlive
    // the backend process and leave the UI talking to a dead port.
    const queryOverride = normalizeBaseUrl(queryApiBase)
    const queryOverrideIsValid = Boolean(
      queryOverride && isHttpLike(queryOverride),
    )
    const runtimeOverrideIsValid = Boolean(
      runtimeOverride && isHttpLike(runtimeOverride),
    )
    const trustedPersistedRuntimeOverride = (() => {
      if (!runtimeOverrideIsValid) return ''
      if (isLocalhost) return normalizeBaseUrl(runtimeOverride)
      if (!isProductionBuild && !isHttpsHostedPage)
        return normalizeBaseUrl(runtimeOverride)
      return ''
    })()
    if (
      isBrowser &&
      runtimeOverride &&
      runtimeOverrideIsValid &&
      !trustedPersistedRuntimeOverride &&
      !queryOverrideIsValid
    ) {
      try {
        window.localStorage.removeItem(LS_KEY)
      } catch {}
    }

    const localhostBaseUrl = (() => {
      if (!isLocalhost) return ''
      if (explicit) return explicit
      if (queryOverrideIsValid) return queryOverride
      if (
        trustedPersistedRuntimeOverride &&
        looksLocalBase(trustedPersistedRuntimeOverride)
      ) {
        return trustedPersistedRuntimeOverride
      }
      // Stale persisted remote overrides (ngrok/cloudflare/etc) commonly cause localhost CORS issues.
      if (
        runtimeOverrideIsValid &&
        !looksLocalBase(runtimeOverride) &&
        isBrowser
      ) {
        try {
          window.localStorage.removeItem(LS_KEY)
        } catch {}
      }
      return window.location.origin
    })()

    const baseUrl =
      localhostBaseUrl ||
      explicit ||
      (queryOverrideIsValid ? queryOverride : '') ||
      trustedPersistedRuntimeOverride ||
      envBase ||
      browserOrigin

    this.baseUrl = baseUrl.replace(/\/+$/, '')
    this.authRefreshCoordinatorKey = `${this.baseUrl}|${this.authRefreshTokenStorageKey}`
    const stored =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(this.authTokenStorageKey) || ''
        : ''
    const storedRefresh =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(this.authRefreshTokenStorageKey) || ''
        : ''
    const initialToken = (opts?.token ?? stored ?? '').trim()
    this.syncAuthFromStorage = opts?.token == null

    this.isDevTunnelBase = (() => {
      try {
        const u = new URL(this.baseUrl)
        const h = (u.hostname || '').toLowerCase()
        return (
          h === 'localhost' ||
          h === '127.0.0.1' ||
          h.endsWith('.trycloudflare.com') ||
          h.includes('ngrok')
        )
      } catch {
        const s = String(this.baseUrl || '').toLowerCase()
        return (
          s.includes('localhost') ||
          s.includes('127.0.0.1') ||
          s.includes('trycloudflare.com') ||
          s.includes('ngrok')
        )
      }
    })()

    this.token = initialToken || null
    this.refreshToken = storedRefresh.trim() || null
    this.syncSessionTokensFromStorage()
  }

  private readStoredSessionTokens(): {
    token: string | null
    refreshToken: string | null
  } {
    if (typeof window === 'undefined') {
      return {
        token: this.token,
        refreshToken: this.refreshToken,
      }
    }
    try {
      const token =
        String(
          window.localStorage.getItem(this.authTokenStorageKey) || '',
        ).trim() || null
      const refreshToken =
        String(
          window.localStorage.getItem(this.authRefreshTokenStorageKey) || '',
        ).trim() || null
      return { token, refreshToken }
    } catch {
      return {
        token: this.token,
        refreshToken: this.refreshToken,
      }
    }
  }

  private syncSessionTokensFromStorage(): void {
    if (!this.syncAuthFromStorage) return
    const stored = this.readStoredSessionTokens()
    this.token = stored.token
    this.refreshToken = stored.refreshToken
  }

  private emitAuthStateChanged(): void {
    if (!this.emitAuthEvents) return
    if (typeof window === 'undefined') return
    try {
      window.dispatchEvent(
        new CustomEvent('fusegrid_api_auth_state_changed', {
          detail: {
            token: this.token,
            refreshToken: this.refreshToken,
          },
        }),
      )
    } catch {
      // ignore
    }
  }

  private readRefreshGeneration(): number {
    if (typeof window === 'undefined') return 0
    try {
      const raw = Number(
        window.localStorage.getItem(this.authRefreshGenerationStorageKey) ||
          '0',
      )
      return Number.isSafeInteger(raw) && raw >= 0 ? raw : 0
    } catch {
      return 0
    }
  }

  private hasRefreshCookie(): boolean {
    if (typeof window === 'undefined') return false
    try {
      return (
        window.localStorage.getItem(this.authRefreshCookieStorageKey) === '1'
      )
    } catch {
      return false
    }
  }

  private setSessionTokens(
    token: string | null,
    refreshToken: string | null,
    refreshGeneration?: number,
    refreshCookie?: boolean,
  ) {
    const normalizedGeneration = Number(refreshGeneration)
    if (
      Number.isSafeInteger(normalizedGeneration) &&
      normalizedGeneration >= 0 &&
      normalizedGeneration < this.readRefreshGeneration()
    ) {
      this.syncSessionTokensFromStorage()
      return
    }
    const priorRefreshToken = this.refreshToken
    this.token = token && token.trim() ? token.trim() : null
    this.refreshToken =
      refreshToken && refreshToken.trim() ? refreshToken.trim() : null
    if (typeof window !== 'undefined') {
      if (this.token)
        window.localStorage.setItem(this.authTokenStorageKey, this.token)
      else window.localStorage.removeItem(this.authTokenStorageKey)
      if (this.refreshToken)
        window.localStorage.setItem(
          this.authRefreshTokenStorageKey,
          this.refreshToken,
        )
      else window.localStorage.removeItem(this.authRefreshTokenStorageKey)
      if (!this.token && !this.refreshToken) {
        window.localStorage.removeItem(this.authRefreshGenerationStorageKey)
        window.localStorage.removeItem(this.authRefreshCookieStorageKey)
      } else if (
        Number.isSafeInteger(normalizedGeneration) &&
        normalizedGeneration >= 0
      ) {
        window.localStorage.setItem(
          this.authRefreshGenerationStorageKey,
          String(normalizedGeneration),
        )
      } else if (this.refreshToken !== priorRefreshToken) {
        window.localStorage.setItem(this.authRefreshGenerationStorageKey, '0')
      }
      if (refreshCookie === true) {
        window.localStorage.setItem(this.authRefreshCookieStorageKey, '1')
      } else if (this.refreshToken && this.refreshToken !== priorRefreshToken) {
        window.localStorage.removeItem(this.authRefreshCookieStorageKey)
      }
    }
    if (
      !this.token &&
      !this.refreshToken &&
      this.authTokenStorageKey === 'fusegrid_api_token' &&
      this.authRefreshTokenStorageKey === 'fusegrid_api_refresh_token'
    ) {
      clearDemoSessionMarker()
    }
    this.emitAuthStateChanged()
  }

  private readRefreshRetryAfterMs(): number {
    if (typeof window === 'undefined') return 0
    try {
      const raw = Number(
        window.localStorage.getItem(this.authRefreshRetryAfterStorageKey) ||
          '0',
      )
      return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0
    } catch {
      return 0
    }
  }

  private writeRefreshRetryAfterMs(nextMs: number): void {
    if (typeof window === 'undefined') return
    try {
      if (nextMs > Date.now()) {
        window.localStorage.setItem(
          this.authRefreshRetryAfterStorageKey,
          String(Math.floor(nextMs)),
        )
      } else {
        window.localStorage.removeItem(this.authRefreshRetryAfterStorageKey)
      }
    } catch {
      // ignore
    }
  }

  setToken(token: string | null) {
    this.setSessionTokens(token, this.refreshToken)
  }

  localAuthBootstrapCandidate(): boolean {
    return this.isDevTunnelBase
  }

  getDuckDbHttpBaseUrl(): string {
    const base =
      String(this.baseUrl || '').trim() ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    const cleaned = base.replace(/\/+$/, '')
    if (!cleaned) return cleaned
    try {
      return new URL(cleaned).toString().replace(/\/+$/, '')
    } catch {
      return cleaned
    }
  }

  coerceDuckDbHttpUrl(rawUrl: string): string {
    const raw = String(rawUrl || '').trim()
    if (!raw) return raw
    try {
      if (raw.startsWith('/api/')) {
        return new URL(
          raw,
          this.getDuckDbHttpBaseUrl().replace(/\/+$/, '') + '/',
        ).toString()
      }
      const u = new URL(raw)
      if (!u.pathname.startsWith('/api/')) return raw
      const host = String(u.hostname || '').toLowerCase()
      if ((host === 'localhost' || host === '127.0.0.1') && u.port === '5173') {
        const base = new URL(this.getDuckDbHttpBaseUrl())
        u.protocol = base.protocol
        u.hostname = base.hostname
        u.port = base.port
        return u.toString()
      }
      return raw
    } catch {
      return raw
    }
  }

  getToken(): string | null {
    this.syncSessionTokensFromStorage()
    return this.token
  }

  private shouldMigrateRefreshTokenToCookie(): boolean {
    if (typeof window === 'undefined' || window.location.protocol !== 'https:')
      return false
    try {
      const api = new URL(this.baseUrl)
      if (api.protocol !== 'https:') return false
      if (api.origin === window.location.origin) return true
      const pageHost = window.location.hostname.toLowerCase()
      const apiHost = api.hostname.toLowerCase()
      return (
        (pageHost === 'fusegrid.io' || pageHost.endsWith('.fusegrid.io')) &&
        (apiHost === 'fusegrid.io' || apiHost.endsWith('.fusegrid.io'))
      )
    } catch {
      return false
    }
  }

  private async migrateLoginResponseToRefreshCookie(
    login: ServerLoginResponse,
  ): Promise<void> {
    if (!this.shouldMigrateRefreshTokenToCookie() || !login.refresh_token)
      return
    const outcome = await this.refreshSessionTokensInternal().catch(
      () => 'transient' as AuthRefreshOutcome,
    )
    if (outcome !== 'refreshed') return
    this.syncSessionTokensFromStorage()
    login.access_token = String(this.token || '')
    login.refresh_token = String(this.refreshToken || '')
    login.refresh_generation = this.readRefreshGeneration()
    login.refresh_cookie = this.hasRefreshCookie()
  }

  private decodeJwtExpiryUnixMs(
    tokenRaw: string | null | undefined,
  ): number | null {
    const token = String(tokenRaw || '').trim()
    if (!token) return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = parts[1]
    if (!payload) return null
    try {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
      const padded = normalized.padEnd(
        Math.ceil(normalized.length / 4) * 4,
        '=',
      )
      const decoded =
        typeof atob === 'function'
          ? atob(padded)
          : Buffer.from(padded, 'base64').toString('utf8')
      const parsed = JSON.parse(decoded)
      const exp = Number(parsed?.exp)
      return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
    } catch {
      return null
    }
  }

  private async ensureFreshRealtimeAuthToken(): Promise<void> {
    this.syncSessionTokensFromStorage()
    const nowMs = Date.now()
    const token = String(this.token || '').trim()
    const refreshToken = String(this.refreshToken || '').trim()
    const hasRefreshTransport = Boolean(refreshToken) || this.hasRefreshCookie()
    if (!token) {
      if (!hasRefreshTransport) {
        if (this.localAuthBootstrapCandidate()) {
          const bootstrapped = await this.bootstrapLocalAuthSession().catch(
            () => null,
          )
          if (bootstrapped?.login?.access_token) return
        }
        throw new Error('Realtime auth token unavailable')
      }
      const refreshOutcome = await this.refreshSessionTokensInternal().catch(
        () => 'transient' as AuthRefreshOutcome,
      )
      if (
        refreshOutcome !== 'refreshed' ||
        !String(this.token || '').trim()
      ) {
        if (this.localAuthBootstrapCandidate()) {
          const bootstrapped = await this.bootstrapLocalAuthSession().catch(
            () => null,
          )
          if (bootstrapped?.login?.access_token) return
        }
        throw new Error('Realtime auth token refresh failed')
      }
      return
    }
    const expiresAtMs = this.decodeJwtExpiryUnixMs(token)
    if (expiresAtMs !== null && expiresAtMs <= nowMs + 90_000) {
      const refreshOutcome = await this.refreshSessionTokensInternal().catch(
        () => 'transient' as AuthRefreshOutcome,
      )
      const nextToken = String(this.token || '').trim()
      const nextExpiresAtMs = this.decodeJwtExpiryUnixMs(nextToken)
      if (
        refreshOutcome !== 'refreshed' ||
        !nextToken ||
        (nextExpiresAtMs !== null && nextExpiresAtMs <= Date.now() + 5_000)
      ) {
        if (this.localAuthBootstrapCandidate()) {
          const bootstrapped = await this.bootstrapLocalAuthSession().catch(
            () => null,
          )
          if (bootstrapped?.login?.access_token) return
        }
        throw new Error('Realtime auth token refresh failed')
      }
    }
  }

  private isAuthFailure(status: number, message: string): boolean {
    const m = String(message || '')
    // 401 = the session is dead. A bare 403 is an AUTHORIZATION answer for a
    // live session (e.g. a viewer-role member hitting an admin-only endpoint
    // like /sites/:slug/users) — treating it as session death bounced
    // read-only users to the login page whenever a wide-layout component made
    // one restricted call. Only token-language 403s indicate a bad session,
    // and the message patterns below already catch those at any status.
    return (
      status === 401 ||
      (status === 400 &&
        /authorization.*missing|missing authorization|header of type `?authorization`? was missing/i.test(
          m,
        )) ||
      /invalid token/i.test(m) ||
      /expiredsignature/i.test(m) ||
      /token verification failed/i.test(m)
    )
  }

  private normalizeRequestPath(pathOrUrl: string): string {
    const raw = String(pathOrUrl || '').trim()
    if (!raw) return raw
    try {
      if (/^https?:\/\//i.test(raw)) return new URL(raw).pathname
      if (raw.startsWith('/'))
        return new URL(raw, 'http://fusegrid.local').pathname
      return new URL(raw, `${this.baseUrl.replace(/\/+$/, '')}/`).pathname
    } catch {
      return raw.split('?')[0] || raw
    }
  }

  private authFailureStrategy(
    pathOrUrl: string,
  ): 'ignore' | 'invalidate' | 'refresh' {
    const path = this.normalizeRequestPath(pathOrUrl)
    if (
      /^\/api\/auth\/(?:login|forgot-password|reset-password)(?:\/|$)/.test(
        path,
      )
    )
      return 'ignore'
    if (
      /^\/api\/auth\/mfa\/challenges\/[^/]+\/(?:totp\/verify|recovery-code\/verify|passkey\/start|passkey\/finish)(?:\/|$)/.test(
        path,
      )
    ) {
      return 'ignore'
    }
    if (/^\/api\/auth\/mfa\//.test(path)) return 'refresh'
    if (/^\/api\/auth\//.test(path)) return 'invalidate'
    return 'refresh'
  }

  private shouldInvalidateUnrecoveredAuthFailure(
    pathOrUrl: string,
    status: number,
    message: string,
  ): boolean {
    if (this.authFailureStrategy(pathOrUrl) === 'ignore') return false
    if (
      (this.refreshToken || this.hasRefreshCookie()) &&
      this.readRefreshRetryAfterMs() > Date.now()
    ) {
      return false
    }
    if (status === 401) return true
    return (
      status === 400 &&
      /authorization.*missing|missing authorization|header of type `?authorization`? was missing/i.test(
        String(message || ''),
      )
    )
  }

  private invalidateAuth(status: number, message: string) {
    const m = String(message || '')
    this.setSessionTokens(null, null)
    if (!this.emitAuthEvents) return
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent('fusegrid_api_auth_invalid', {
            detail: { status, message: m },
          }),
        )
      } catch {
        // ignore
      }
    }
  }

  private emitApiErrorTelemetry(input: {
    status: number
    message: string
    url: string
    path?: string | null
    kind?: string | null
  }): void {
    if (typeof window === 'undefined') return
    const path = String(
      input.path || this.normalizeRequestPath(input.url) || '',
    ).trim()
    if (/^\/api\/events(?:\/|$)/.test(path)) return
    try {
      window.dispatchEvent(
        new CustomEvent('fusegrid_api_error', {
          detail: {
            status: input.status,
            message: input.message,
            url: input.url,
            path,
            kind: input.kind || 'api',
          },
        }),
      )
    } catch {
      // Telemetry must not affect product behavior.
    }
  }

  private buildApiError(
    status: number,
    message: string,
    url: string,
    path?: string,
  ) {
    this.emitApiErrorTelemetry({ status, message, url, path, kind: 'api' })
    const err: any = new Error(`API ${status}: ${message}`)
    err.status = status
    err.body = message
    err.url = url
    return err
  }

  private logDocumentSessionBootstrapFailureBody(
    path: string,
    status: number,
    message: string,
    url: string,
  ): void {
    if (status < 500 || !String(path || '').includes('/session/bootstrap'))
      return
    const body = String(message || '')
    const cappedBody =
      body.length > 4000 ? `${body.slice(0, 4000)}...<truncated>` : body
    const traceIdMatch = body.match(/trace_id=([0-9a-fA-F-]{8,})/)
    console.warn('[ServerApiClient] document session bootstrap failed', {
      status,
      path,
      url,
      trace_id: traceIdMatch?.[1] || null,
      body: cappedBody,
    })
  }

  private createTimedRequestInit(
    init?: RequestInit,
    timeoutMsRaw?: number,
  ): {
    init: RequestInit
    cleanup: () => void
    isTimedOut: () => boolean
  } {
    const timeoutMs = Number(timeoutMsRaw)
    const parentSignal = init?.signal ?? null
    if ((!Number.isFinite(timeoutMs) || timeoutMs <= 0) && !parentSignal) {
      return {
        init: { ...(init || {}) },
        cleanup: () => {},
        isTimedOut: () => false,
      }
    }
    const controller = new AbortController()
    let timedOut = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const abortFromParent = () => {
      try {
        controller.abort((parentSignal as any)?.reason)
      } catch {
        controller.abort()
      }
    }
    if (parentSignal) {
      if (parentSignal.aborted) abortFromParent()
      else
        parentSignal.addEventListener('abort', abortFromParent, { once: true })
    }
    if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
      timer = setTimeout(
        () => {
          timedOut = true
          try {
            controller.abort(
              new Error(`Request timed out after ${Math.floor(timeoutMs)}ms`),
            )
          } catch {
            controller.abort()
          }
        },
        Math.max(1, Math.floor(timeoutMs)),
      )
    }
    return {
      init: {
        ...(init || {}),
        signal: controller.signal,
      },
      cleanup: () => {
        if (timer) {
          clearTimeout(timer)
          timer = null
        }
        if (parentSignal)
          parentSignal.removeEventListener('abort', abortFromParent)
      },
      isTimedOut: () => timedOut,
    }
  }

  private async waitForRefreshStorageChange(
    attemptedRefreshToken: string,
    attemptedAccessToken: string,
    attemptedGeneration: number,
    timeoutMs = 1_000,
  ): Promise<boolean> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 25))
      this.syncSessionTokensFromStorage()
      const storedRefresh = String(this.refreshToken || '').trim()
      const storedAccess = String(this.token || '').trim()
      if (
        (storedRefresh && storedRefresh !== attemptedRefreshToken) ||
        (storedAccess && storedAccess !== attemptedAccessToken) ||
        this.readRefreshGeneration() > attemptedGeneration
      )
        return true
    }
    return false
  }

  private async performRefreshSessionTokens(): Promise<AuthRefreshOutcome> {
    this.syncSessionTokensFromStorage()
    const refreshToken = String(this.refreshToken || '').trim()
    const accessToken = String(this.token || '').trim()
    const refreshGeneration = this.readRefreshGeneration()
    const refreshCookie = this.hasRefreshCookie()
    if (!refreshToken && !refreshCookie) return 'unavailable'
    if (this.readRefreshRetryAfterMs() > Date.now()) return 'transient'

    const requestBody = refreshToken
      ? JSON.stringify({ refresh_token: refreshToken })
      : JSON.stringify({})
    const refreshUrl = joinUrl(this.baseUrl, '/api/auth/refresh')
    const headers = { 'Content-Type': 'application/json' }
    const sendRefresh = async (
      targetUrl: string,
    ): Promise<AuthRefreshOutcome> => {
      const res = await fetch(targetUrl, {
        method: 'POST',
        headers,
        body: requestBody,
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        if (res.status === 409) {
          const changed = await this.waitForRefreshStorageChange(
            refreshToken,
            accessToken,
            refreshGeneration,
          )
          if (!changed) this.writeRefreshRetryAfterMs(Date.now() + 1_000)
          return changed ? 'refreshed' : 'transient'
        }
        if (this.isAuthFailure(res.status, text || res.statusText)) {
          this.invalidateAuth(res.status, text || res.statusText)
          this.writeRefreshRetryAfterMs(0)
          return 'terminal'
        }
        if (res.status === 400 || res.status === 403) {
          this.invalidateAuth(res.status, text || res.statusText)
          this.writeRefreshRetryAfterMs(0)
          return 'terminal'
        }
        this.writeRefreshRetryAfterMs(Date.now() + 30_000)
        return 'transient'
      }
      const out = (await res.json()) as ServerLoginResponse
      this.setSessionTokens(
        out.access_token,
        out.refresh_token,
        out.refresh_generation,
        out.refresh_cookie,
      )
      this.writeRefreshRetryAfterMs(0)
      return 'refreshed'
    }

    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      refreshUrl.startsWith('http://')
    ) {
      const httpsUrl = refreshUrl.replace('http://', 'https://')
      try {
        const httpsAttempt = await sendRefresh(httpsUrl)
        if (httpsAttempt === 'refreshed' || httpsAttempt === 'terminal')
          return httpsAttempt
      } catch (e) {
        console.warn(
          '[ServerApiClient] HTTPS refresh request failed, trying HTTP fallback:',
          e,
        )
      }
    }
    try {
      return await sendRefresh(refreshUrl)
    } catch {
      this.writeRefreshRetryAfterMs(Date.now() + 30_000)
      return 'transient'
    }
  }

  private async refreshSessionTokensInternal(): Promise<AuthRefreshOutcome> {
    this.syncSessionTokensFromStorage()
    const requestedRefreshToken = String(this.refreshToken || '').trim()
    const requestedGeneration = this.readRefreshGeneration()
    const existing = _authRefreshInflight.get(this.authRefreshCoordinatorKey)
    if (existing) {
      const outcome = await existing
      this.syncSessionTokensFromStorage()
      return outcome
    }

    const coordinated = (async (): Promise<AuthRefreshOutcome> => {
      const run = async () => {
        this.syncSessionTokensFromStorage()
        const currentRefreshToken = String(this.refreshToken || '').trim()
        if (
          currentRefreshToken &&
          (currentRefreshToken !== requestedRefreshToken ||
            this.readRefreshGeneration() > requestedGeneration)
        ) {
          return 'refreshed' as AuthRefreshOutcome
        }
        return await this.performRefreshSessionTokens()
      }
      const locks =
        typeof navigator !== 'undefined' ? (navigator as any).locks : null
      if (locks?.request) {
        return await locks.request(
          `fusegrid-auth-refresh:${this.authRefreshCoordinatorKey}`,
          run,
        )
      }
      return await run()
    })()
    _authRefreshInflight.set(this.authRefreshCoordinatorKey, coordinated)
    try {
      return await coordinated
    } finally {
      if (
        _authRefreshInflight.get(this.authRefreshCoordinatorKey) === coordinated
      ) {
        _authRefreshInflight.delete(this.authRefreshCoordinatorKey)
      }
      this.syncSessionTokensFromStorage()
    }
  }

  private async maybeRecoverAuthFailure(
    pathOrUrl: string,
    status: number,
    message: string,
  ): Promise<boolean> {
    if (!this.isAuthFailure(status, message)) return false
    const strategy = this.authFailureStrategy(pathOrUrl)
    if (strategy === 'ignore') return false
    if (strategy === 'invalidate') {
      this.invalidateAuth(status, message)
      return false
    }
    const refreshOutcome = await this.refreshSessionTokensInternal()
    if (refreshOutcome === 'refreshed') return true
    if (refreshOutcome === 'transient') return false
    if (this.localAuthBootstrapCandidate()) {
      const bootstrapped = await this.bootstrapLocalAuthSession().catch(
        () => null,
      )
      if (bootstrapped?.login?.access_token) return true
    }
    if (refreshOutcome !== 'terminal') this.invalidateAuth(status, message)
    return false
  }

  /** Job-finance uses the same authenticated transport and session recovery as
   * the rest of the application, including contributor-only endpoints. */
  async jobCostingRequest<T>(workbookId: string, resource: string, init?: RequestInit): Promise<T> {
    if (!/^[a-z0-9/_?=&.%:-]*$/i.test(resource) || resource.includes('..')) {
      throw new Error('Invalid job-costing resource')
    }
    return this.request<T>(`/api/job-costing/${encodeURIComponent(workbookId)}/${resource}`, init)
  }

  qoeDownload(path: string): Promise<Blob> {
    if (!path.startsWith('/api/accounting/qoe/')) throw new Error('Invalid QoE API path')
    return this.requestBlob(path)
  }

  /** Authenticated QoE requests share token refresh and error handling with CFO Core. */
  qoeRequest<T>(path: string, init?: RequestInit): Promise<T> {
    if (!path.startsWith('/api/accounting/qoe/')) throw new Error('Invalid QoE API path')
    return this.request<T>(path, init)
  }

  private async request<T>(
    path: string,
    init?: RequestInit,
    options?: { allowAuthRetry?: boolean; timeoutMs?: number },
  ): Promise<T> {
    const allowAuthRetry = options?.allowAuthRetry !== false
    this.syncSessionTokensFromStorage()
    const requestPath = this.normalizeRequestPath(path)
    const accessExpiresAtMs = this.decodeJwtExpiryUnixMs(this.token)
    if (
      allowAuthRetry &&
      !/^\/api\/auth\//.test(requestPath) &&
      (this.refreshToken || this.hasRefreshCookie()) &&
      accessExpiresAtMs !== null &&
      accessExpiresAtMs <= Date.now() + 90_000
    ) {
      await this.refreshSessionTokensInternal().catch(
        () => 'transient' as AuthRefreshOutcome,
      )
      this.syncSessionTokensFromStorage()
    }
    const isBrowser = typeof window !== 'undefined'
    const isCrossOrigin =
      isBrowser &&
      (() => {
        try {
          const base = new URL(String(this.baseUrl || '').replace(/\/+$/, ''))
          return base.origin !== window.location.origin
        } catch {
          return false
        }
      })()

    // Avoid forcing a preflight on GET/HEAD by not setting Content-Type unless we actually have a body.
    const method = String(init?.method || 'GET').toUpperCase()

    const headers: Record<string, string> = {
      ...(init?.headers
        ? Object.fromEntries(new Headers(init.headers as any).entries())
        : {}),
    }
    const hasBody = init?.body != null
    const hasContentType = Object.keys(headers).some(
      (k) => k.toLowerCase() === 'content-type',
    )
    const isFormData =
      typeof FormData !== 'undefined' && init?.body instanceof FormData
    if (hasBody && !hasContentType && !isFormData) {
      headers['Content-Type'] = 'application/json'
    }
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    const traceHeaders = nextBrowserTraceHeaders()
    if (traceHeaders) {
      if (!Object.keys(headers).some((key) => key.toLowerCase() === 'x-correlation-id')) {
        headers['X-Correlation-ID'] = traceHeaders.correlationId
      }
      if (!Object.keys(headers).some((key) => key.toLowerCase() === 'x-request-id')) {
        headers['X-Request-ID'] = traceHeaders.requestId
      }
    }

    const url = joinUrl(this.baseUrl, path)
    const requestKey =
      method === 'GET' && !hasBody && !init?.signal
        ? JSON.stringify({
            method,
            url,
            allowAuthRetry,
            timeoutMs: Number.isFinite(Number(options?.timeoutMs))
              ? Math.max(0, Math.floor(Number(options?.timeoutMs)))
              : null,
            auth: String(headers['Authorization'] || '').trim() || null,
          })
        : null

    const execute = async (): Promise<T> => {
      const timedInit = this.createTimedRequestInit(init, options?.timeoutMs)
      const requestInit = timedInit.init
      const throwTimeoutError = () => {
        throw new Error(
          `API request timed out after ${Math.max(1, Math.floor(Number(options?.timeoutMs) || 0))}ms: ${path}`,
        )
      }
      // If frontend is HTTPS but API URL is HTTP, try HTTPS first (to avoid mixed content errors)
      // If HTTPS fails, fall back to HTTP (for development)
      try {
        if (
          typeof window !== 'undefined' &&
          window.location.protocol === 'https:' &&
          url.startsWith('http://')
        ) {
          const httpsUrl = url.replace('http://', 'https://')
          try {
            const res = await fetch(httpsUrl, {
              ...requestInit,
              headers,
              credentials: requestInit.credentials || 'include',
            })
            if (res.ok) {
              return (await res.json()) as T
            }
            const text = await res.text().catch(() => '')
            const message = text || res.statusText
            this.logDocumentSessionBootstrapFailureBody(
              path,
              res.status,
              message,
              httpsUrl,
            )
            if (this.isAuthFailure(res.status, message)) {
              if (
                allowAuthRetry &&
                (await this.maybeRecoverAuthFailure(path, res.status, message))
              ) {
                return await this.request<T>(path, init, {
                  ...options,
                  allowAuthRetry: false,
                })
              }
              if (
                this.shouldInvalidateUnrecoveredAuthFailure(
                  path,
                  res.status,
                  message,
                )
              ) {
                this.invalidateAuth(res.status, message)
              }
              throw this.buildApiError(res.status, message, httpsUrl)
            }
          } catch (e) {
            if (timedInit.isTimedOut()) throwTimeoutError()
            if (typeof (e as any)?.status === 'number') throw e
            // HTTPS failed, try HTTP as fallback
            console.warn(
              '[ServerApiClient] HTTPS request failed, trying HTTP fallback:',
              e,
            )
          }
        }

        let res: Response
        try {
          res = await fetch(url, {
            ...requestInit,
            headers,
            credentials: requestInit.credentials || 'include',
          })
        } catch (e) {
          if (timedInit.isTimedOut()) throwTimeoutError()
          if (isCrossOrigin) {
            console.warn(
              '[ServerApiClient] request fetch threw (likely CORS/network)',
              {
                method,
                path,
                hasAuthHeader: !!headers['Authorization'],
              },
            )
          }
          this.emitApiErrorTelemetry({
            status: 0,
            message: String(
              (e as any)?.message || e || 'Network request failed',
            ),
            url,
            path,
            kind: 'network',
          })
          throw e
        }

        if (!res.ok) {
          const text = await res.text().catch(() => '')
          const message = text || res.statusText
          this.logDocumentSessionBootstrapFailureBody(
            path,
            res.status,
            message,
            url,
          )
          if (
            allowAuthRetry &&
            (await this.maybeRecoverAuthFailure(path, res.status, message))
          ) {
            return await this.request<T>(path, init, {
              ...options,
              allowAuthRetry: false,
            })
          }
          if (
            this.shouldInvalidateUnrecoveredAuthFailure(
              path,
              res.status,
              message,
            )
          ) {
            this.invalidateAuth(res.status, message)
          }
          throw this.buildApiError(res.status, message, url)
        }

        return (await res.json()) as T
      } finally {
        timedInit.cleanup()
      }
    }

    if (!requestKey) {
      return await execute()
    }

    let inflight = _jsonRequestInflight.get(requestKey) as
      Promise<T> | undefined
    if (!inflight) {
      inflight = execute()
      _jsonRequestInflight.set(requestKey, inflight)
      attachInflightCleanup(inflight, () => {
        const current = _jsonRequestInflight.get(requestKey)
        if (current === inflight) {
          _jsonRequestInflight.delete(requestKey)
        }
      })
    }
    return await inflight
  }

  private async requestBlob(
    path: string,
    init?: RequestInit,
    options?: { allowAuthRetry?: boolean; onResponse?: (response: Response) => void },
  ): Promise<Blob> {
    const allowAuthRetry = options?.allowAuthRetry !== false
    const t0 =
      typeof performance !== 'undefined' &&
      typeof performance.now === 'function'
        ? performance.now()
        : Date.now()

    // Blob endpoints are frequently fetched cross-origin (CloudFront -> API).
    // IMPORTANT: We prefer Authorization header over `?token=` for artifacts because long token
    // querystrings have been observed to hit upstream timeouts without reaching the API handler.
    const isBrowser = typeof window !== 'undefined'
    const isCrossOrigin =
      isBrowser &&
      (() => {
        try {
          const base = new URL(String(this.baseUrl || '').replace(/\/+$/, ''))
          return base.origin !== window.location.origin
        } catch {
          return false
        }
      })()

    const tokenViaQuery = false

    const headers: Record<string, string> = {
      ...(init?.headers
        ? Object.fromEntries(new Headers(init.headers as any).entries())
        : {}),
    }
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    const url = joinUrl(this.baseUrl, path)

    const sanitizeUrl = (u: string) => {
      try {
        // Redact token query if present
        return u.replace(/([?&]token=)[^&]+/i, '$1<redacted>')
      } catch {
        return u
      }
    }

    // High-signal debug logging for Tableau artifact + export fetches.
    const isTableauArtifact =
      /\/api\/tableau\/imports\/[^/]+\/artifacts\//.test(String(path || ''))
    const isTableauExport = /\/api\/tableau\/spec\/export/.test(
      String(path || ''),
    )
    const shouldLog = isTableauArtifact || isTableauExport
    const logKind = isTableauArtifact
      ? 'tableau-artifact'
      : isTableauExport
        ? 'tableau-export'
        : 'blob'
    if (shouldLog) {
      console.log('[ServerApiClient] requestBlob start', {
        kind: logKind,
        baseUrl: this.baseUrl,
        isCrossOrigin,
        tokenViaQuery,
        url: sanitizeUrl(url),
      })
    }
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      url.startsWith('http://')
    ) {
      const httpsUrl = url.replace('http://', 'https://')
      try {
        const res = await fetch(httpsUrl, { ...init, headers })
        if (res.ok) { options?.onResponse?.(res); return await res.blob() }
        const text = await res.text().catch(() => '')
        const message = text || res.statusText
        if (this.isAuthFailure(res.status, message)) {
          if (
            allowAuthRetry &&
            (await this.maybeRecoverAuthFailure(path, res.status, message))
          ) {
            return await this.requestBlob(path, init, {
              allowAuthRetry: false, onResponse: options?.onResponse,
            })
          }
          throw this.buildApiError(res.status, message, httpsUrl)
        }
      } catch (e) {
        if (typeof (e as any)?.status === 'number') throw e
        console.warn(
          '[ServerApiClient] HTTPS blob request failed, trying HTTP fallback:',
          e,
        )
      }
    }

    let res: Response
    try {
      res = await fetch(url, { ...init, headers })
    } catch (e: any) {
      if (shouldLog) {
        const t1 =
          typeof performance !== 'undefined' &&
          typeof performance.now === 'function'
            ? performance.now()
            : Date.now()
        console.warn('[ServerApiClient] requestBlob fetch threw', {
          kind: logKind,
          url: sanitizeUrl(url),
          elapsed_ms: Math.round((t1 as any) - (t0 as any)),
          name: String(e?.name || ''),
          message: String(e?.message || e || '').slice(0, 500),
        })
      }
      this.emitApiErrorTelemetry({
        status: 0,
        message: String(e?.message || e || 'Blob request failed'),
        url,
        path,
        kind: 'network',
      })
      throw e
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      if (shouldLog) {
        const t1 =
          typeof performance !== 'undefined' &&
          typeof performance.now === 'function'
            ? performance.now()
            : Date.now()
        console.warn('[ServerApiClient] requestBlob failed', {
          kind: logKind,
          status: res.status,
          url: sanitizeUrl(url),
          elapsed_ms: Math.round((t1 as any) - (t0 as any)),
          body: String(text || '').slice(0, 500),
        })
      }
      const message = text || res.statusText
      if (
        allowAuthRetry &&
        (await this.maybeRecoverAuthFailure(path, res.status, message))
      ) {
        return await this.requestBlob(path, init, { allowAuthRetry: false, onResponse: options?.onResponse })
      }
      throw this.buildApiError(res.status, message, url)
    }
    if (shouldLog) {
      const t1 =
        typeof performance !== 'undefined' &&
        typeof performance.now === 'function'
          ? performance.now()
          : Date.now()
      console.log('[ServerApiClient] requestBlob ok', {
        kind: logKind,
        status: res.status,
        url: sanitizeUrl(url),
        elapsed_ms: Math.round((t1 as any) - (t0 as any)),
        bytes: Number(res.headers.get('content-length') || 0) || null,
      })
    }
    options?.onResponse?.(res)
    return await res.blob()
  }

  private async requestNoContent(
    path: string,
    init?: RequestInit,
    options?: { allowAuthRetry?: boolean; timeoutMs?: number },
  ): Promise<void> {
    const allowAuthRetry = options?.allowAuthRetry !== false
    const isBrowser = typeof window !== 'undefined'
    const isCrossOrigin =
      isBrowser &&
      (() => {
        try {
          const base = new URL(String(this.baseUrl || '').replace(/\/+$/, ''))
          return base.origin !== window.location.origin
        } catch {
          return false
        }
      })()

    const method = String(init?.method || 'GET').toUpperCase()

    const headers: Record<string, string> = {
      ...(init?.headers
        ? Object.fromEntries(new Headers(init.headers as any).entries())
        : {}),
    }
    const hasBody = init?.body != null
    const hasContentType = Object.keys(headers).some(
      (k) => k.toLowerCase() === 'content-type',
    )
    if (hasBody && !hasContentType) {
      headers['Content-Type'] = 'application/json'
    }
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`

    const url = joinUrl(this.baseUrl, path)
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      url.startsWith('http://')
    ) {
      const httpsUrl = url.replace('http://', 'https://')
      const timedInit = this.createTimedRequestInit(init, options?.timeoutMs)
      try {
        const res = await fetch(httpsUrl, { ...timedInit.init, headers })
        if (res.ok) return
        const text = await res.text().catch(() => '')
        const message = text || res.statusText
        if (this.isAuthFailure(res.status, message)) {
          if (
            allowAuthRetry &&
            (await this.maybeRecoverAuthFailure(path, res.status, message))
          ) {
            return await this.requestNoContent(path, init, {
              ...options,
              allowAuthRetry: false,
            })
          }
          throw this.buildApiError(res.status, message, httpsUrl)
        }
      } catch (e) {
        if (timedInit.isTimedOut())
          throw new Error(
            `API request timed out after ${Math.max(1, Math.floor(Number(options?.timeoutMs) || 0))}ms: ${path}`,
          )
        if (typeof (e as any)?.status === 'number') throw e
        console.warn(
          '[ServerApiClient] HTTPS request failed, trying HTTP fallback:',
          e,
        )
      } finally {
        timedInit.cleanup()
      }
    }

    let res: Response
    const timedInit = this.createTimedRequestInit(init, options?.timeoutMs)
    try {
      res = await fetch(url, { ...timedInit.init, headers })
    } catch (e) {
      if (timedInit.isTimedOut())
        throw new Error(
          `API request timed out after ${Math.max(1, Math.floor(Number(options?.timeoutMs) || 0))}ms: ${path}`,
        )
      if (isCrossOrigin) {
        console.warn(
          '[ServerApiClient] requestNoContent fetch threw (likely CORS/network)',
          {
            method,
            path,
            hasAuthHeader: !!headers['Authorization'],
          },
        )
      }
      this.emitApiErrorTelemetry({
        status: 0,
        message: String(
          (e as any)?.message || e || 'No-content request failed',
        ),
        url,
        path,
        kind: 'network',
      })
      throw e
    } finally {
      timedInit.cleanup()
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      const message = text || res.statusText
      if (
        allowAuthRetry &&
        (await this.maybeRecoverAuthFailure(path, res.status, message))
      ) {
        return await this.requestNoContent(path, init, {
          ...options,
          allowAuthRetry: false,
        })
      }
      throw this.buildApiError(res.status, message, url)
    }
  }

  async getScheduleOptional(
    datasourceId: string,
  ): Promise<ServerRefreshSchedule | null> {
    // Prefer server-side optional endpoint to avoid noisy 404s in the browser console.
    try {
      return await this.request(
        `/api/datasources/${encodeURIComponent(datasourceId)}/schedule_optional`,
        { method: 'GET' },
      )
    } catch (e: any) {
      // Backward compat: if the endpoint isn't deployed yet, fall back to the 404-based approach.
      const status = Number((e as any)?.status ?? NaN)
      const body = String((e as any)?.body || '')
      const msg = String(e?.message || e || '')
      const bodyTrim = body.trim()
      // Distinguish "route missing" (old server) from "datasource not found" (real 404).
      // - Axum default 404 body is usually "Not Found"
      // - Some proxies return HTML for missing routes
      // - Our app-level 404s include specific messages like "Datasource not found"
      const looksLikeMissingRoute =
        status === 404 &&
        !/datasource not found/i.test(bodyTrim) &&
        (bodyTrim === '' ||
          /^not\s*found$/i.test(bodyTrim) ||
          /cannot\s+(get|post|put|delete|patch|options)\b/i.test(bodyTrim) ||
          /no\s+route\b/i.test(bodyTrim) ||
          /<html[\s>]/i.test(bodyTrim) ||
          /\bnot\s*found\b/i.test(msg))

      if (looksLikeMissingRoute) {
        try {
          return await this.getSchedule(datasourceId)
        } catch (e2: any) {
          const st2 = Number((e2 as any)?.status ?? NaN)
          if (st2 === 404) return null
          const msg2 = String(e2?.message || e2 || '')
          if (/\bAPI\s+404\b/i.test(msg2)) return null
          throw e2
        }
      }
      throw e
    }
  }

  async login(email: string, password: string): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (
      (out.status === 'authenticated' ||
        out.status === 'mfa_enrollment_required') &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
      clearDemoSessionMarker()
    } else {
      this.setSessionTokens(null, null)
    }
    return out
  }

  /**
   * Public one-click demo access: mints a short-lived session for the shared
   * read-only demo user on the sample firm site. No credentials involved.
   */
  async demoSession(): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      '/api/auth/demo-session',
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  /**
   * Demo module gate: record the visitor's contact info against the current
   * shared demo session (unlocking the rest of the demo) and trigger the
   * follow-up email. Server requires the demo bearer session.
   */
  async submitDemoLead(payload: {
    email: string
    full_name?: string | null
    firm_name?: string | null
    phone?: string | null
    /** "1-5" | "6-20" | "21-50" | "50+" | "not-a-firm" — how many clients they'd bring. */
    scale?: string | null
    pages_viewed?: string[]
    /** 'tour' when captured by a tour lead step; defaults to the module gate. */
    source?: 'demo_gate' | 'tour'
  }): Promise<{ status: string }> {
    return this.request<{ status: string }>('/api/marketing/demo-lead', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  /**
   * Self-serve CFO Core signup. Creates the account + workspace + CFO workbook
   * and returns a logged-in session plus the customer's site slug and the
   * workbook id to connect QuickBooks against.
   */
  async signup(payload: {
    email: string
    password: string
    full_name?: string | null
    company_name?: string | null
    phone?: string | null
    plan?: string | null
    utm_source?: string | null
  }): Promise<ServerSignupResponse> {
    const out = await this.request<ServerSignupResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
      clearDemoSessionMarker()
    }
    return out
  }

  /** Start a Stripe-hosted Checkout session; redirect the browser to `.url`. */
  async billingCheckout(payload: {
    workbook_id: string
    plan: 'essentials' | 'pro' | 'partner'
    interval: 'monthly' | 'annual'
    success_url: string
    cancel_url: string
  }): Promise<BillingHostedSessionResponse> {
    return await this.request<BillingHostedSessionResponse>(
      '/api/billing/checkout',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
  }

  /** Stripe Billing Portal session (manage card / plan / cancel / invoices). */
  async billingPortal(payload: {
    workbook_id: string
    return_url: string
  }): Promise<BillingHostedSessionResponse> {
    return await this.request<BillingHostedSessionResponse>(
      '/api/billing/portal',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
  }

  async billingStatus(workbookId: string): Promise<BillingStatusResponse> {
    return await this.request<BillingStatusResponse>(
      `/api/billing/status?workbook_id=${encodeURIComponent(workbookId)}`,
      {
        method: 'GET',
      },
    )
  }

  /** Replace an existing single-item subscription with monthly Partner billing. */
  async switchToPartnerBilling(workbookId: string): Promise<BillingStatusResponse> {
    return await this.request<BillingStatusResponse>('/api/billing/partner/switch', {
      method: 'POST',
      body: JSON.stringify({ workbook_id: workbookId }),
    })
  }

  async bootstrapLocalAuthSession(payload?: {
    email?: string | null
    password?: string | null
    site_name?: string | null
  }): Promise<ServerLocalAuthBootstrapResponse> {
    const requestBody = {
      email: payload?.email ?? null,
      password: payload?.password ?? null,
      site_name: payload?.site_name ?? null,
    }
    const cacheKey = JSON.stringify(requestBody)
    const existing = this.localAuthBootstrapInflight.get(cacheKey)
    if (existing) return await existing
    const promise = (async () => {
      const out = await this.request<ServerLocalAuthBootstrapResponse>(
        '/api/auth/dev/local-session',
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
        },
        {
          allowAuthRetry: false,
        },
      )
      const login = out?.login
      if (
        login?.status === 'authenticated' &&
        login.access_token &&
        login.refresh_token
      ) {
        this.setSessionTokens(
          login.access_token,
          login.refresh_token,
          login.refresh_generation,
        )
      } else {
        this.setSessionTokens(null, null)
      }
      return out
    })()
    this.localAuthBootstrapInflight.set(cacheKey, promise)
    try {
      return await promise
    } finally {
      if (this.localAuthBootstrapInflight.get(cacheKey) === promise) {
        this.localAuthBootstrapInflight.delete(cacheKey)
      }
    }
  }

  async devImpersonateUser(targetUserId: string): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      '/api/auth/dev/impersonate',
      {
        method: 'POST',
        body: JSON.stringify({ target_user_id: targetUserId }),
      },
      {
        allowAuthRetry: false,
      },
    )
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  async createDevLoginBridge(payload?: {
    target_origin?: string | null
    redirect_path?: string | null
  }): Promise<ServerDevLoginBridgeResponse> {
    return await this.request<ServerDevLoginBridgeResponse>(
      '/api/auth/dev-login-bridge',
      {
        method: 'POST',
        body: JSON.stringify({
          target_origin: payload?.target_origin ?? null,
          redirect_path: payload?.redirect_path ?? null,
        }),
      },
    )
  }

  async redeemDevLoginBridge(
    code: string,
    targetOrigin?: string | null,
  ): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      '/api/auth/dev-login-bridge/redeem',
      {
        method: 'POST',
        body: JSON.stringify({
          code,
          target_origin:
            targetOrigin ??
            (typeof window !== 'undefined' ? window.location.origin : null),
        }),
      },
      {
        allowAuthRetry: false,
      },
    )
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  async requestPasswordReset(
    email: string,
  ): Promise<ServerForgotPasswordResponse> {
    return await this.request<ServerForgotPasswordResponse>(
      '/api/auth/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    )
  }

  async completePasswordReset(
    token: string,
    purpose: 'password_reset' | 'account_setup',
    password: string,
  ): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      '/api/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({ token, purpose, password }),
      },
    )
    if (
      (out.status === 'authenticated' ||
        out.status === 'mfa_enrollment_required') &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  async refreshAuthSession(): Promise<ServerLoginResponse> {
    const refreshToken = String(this.refreshToken || '').trim()
    if (!refreshToken && !this.hasRefreshCookie())
      throw new Error('Missing refresh token')
    const out = await this.request<ServerLoginResponse>('/api/auth/refresh', {
      method: 'POST',
      body: refreshToken
        ? JSON.stringify({ refresh_token: refreshToken })
        : JSON.stringify({}),
    })
    this.setSessionTokens(
      out.access_token,
      out.refresh_token,
      out.refresh_generation,
      out.refresh_cookie,
    )
    return out
  }

  async logout(): Promise<{ status: string }> {
    try {
      return await this.request<{ status: string }>('/api/auth/logout', {
        method: 'POST',
      })
    } finally {
      this.setSessionTokens(null, null)
    }
  }

  async logoutAll(): Promise<{ status: string }> {
    try {
      return await this.request<{ status: string }>('/api/auth/logout-all', {
        method: 'POST',
      })
    } finally {
      this.setSessionTokens(null, null)
    }
  }

  async listMfaFactors(): Promise<ServerMfaFactorListResponse> {
    return await this.request('/api/auth/mfa/factors', { method: 'GET' })
  }

  async startTotpEnrollment(
    display_name?: string | null,
  ): Promise<ServerTotpEnrollmentResponse> {
    return await this.request('/api/auth/mfa/totp/start', {
      method: 'POST',
      body: JSON.stringify({ display_name: display_name ?? null }),
    })
  }

  async confirmTotpEnrollment(
    factor_id: string,
    code: string,
  ): Promise<ServerRecoveryCodesResponse> {
    return await this.request('/api/auth/mfa/totp/confirm', {
      method: 'POST',
      body: JSON.stringify({ factor_id, code }),
    })
  }

  async regenerateRecoveryCodes(): Promise<ServerRecoveryCodesResponse> {
    return await this.request('/api/auth/mfa/recovery-codes/regenerate', {
      method: 'POST',
    })
  }

  async disableMfaFactor(factorId: string): Promise<ServerMfaFactor> {
    return await this.request(
      `/api/auth/mfa/factors/${encodeURIComponent(factorId)}`,
      {
        method: 'DELETE',
      },
    )
  }

  async startPasskeyRegistration(
    display_name?: string | null,
  ): Promise<ServerPasskeyRegistrationResponse> {
    return await this.request('/api/auth/mfa/passkeys/start-registration', {
      method: 'POST',
      body: JSON.stringify({ display_name: display_name ?? null }),
    })
  }

  async finishPasskeyRegistration(
    challenge_id: string,
    credential: any,
  ): Promise<ServerMfaFactor> {
    return await this.request('/api/auth/mfa/passkeys/finish-registration', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, credential }),
    })
  }

  async startMfaPasskeyChallenge(
    challengeId: string,
  ): Promise<ServerPasskeyAuthenticationResponse> {
    return await this.request(
      `/api/auth/mfa/challenges/${encodeURIComponent(challengeId)}/passkey/start`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )
  }

  async finishMfaPasskeyChallenge(
    challengeId: string,
    credential: any,
  ): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      `/api/auth/mfa/challenges/${encodeURIComponent(challengeId)}/passkey/finish`,
      {
        method: 'POST',
        body: JSON.stringify({ credential }),
      },
    )
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  async verifyMfaTotp(
    challengeId: string,
    code: string,
  ): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      `/api/auth/mfa/challenges/${encodeURIComponent(challengeId)}/totp/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
    )
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  async verifyMfaRecoveryCode(
    challengeId: string,
    code: string,
  ): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>(
      `/api/auth/mfa/challenges/${encodeURIComponent(challengeId)}/recovery-code/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
    )
    if (
      out.status === 'authenticated' &&
      out.access_token &&
      out.refresh_token
    ) {
      this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
      await this.migrateLoginResponseToRefreshCookie(out)
    }
    return out
  }

  async desktopCreateOpenIntent(
    req: DesktopCreateOpenIntentRequest,
  ): Promise<DesktopCreateOpenIntentResponse> {
    const wid = String(req?.workbook_id || '').trim()
    if (!wid) throw new Error('Missing workbook_id')
    return await this.request('/api/desktop/open-intents', {
      method: 'POST',
      body: JSON.stringify({
        workbook_id: wid,
        rev: req?.rev ?? null,
        state: req?.state ?? null,
      }),
    })
  }

  async desktopRedeemOpenIntent(
    req: DesktopRedeemOpenIntentRequest,
  ): Promise<DesktopRedeemOpenIntentResponse> {
    const code = String(req?.code || '').trim()
    if (!code) throw new Error('Missing code')
    const out = await this.request<DesktopRedeemOpenIntentResponse>(
      '/api/desktop/open-intents/redeem',
      {
        method: 'POST',
        body: JSON.stringify({
          code,
          device: req?.device ?? null,
        }),
      },
    )
    // If redemption succeeded, the desktop app should now be authenticated.
    if (out?.access_token)
      this.setSessionTokens(out.access_token, out.refresh_token)
    return out
  }

  async aiChat(req: AiChatRequest): Promise<AiChatResponse> {
    return await this.request('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async startAiAgentRun(
    req: AiAgentStartRequest,
  ): Promise<AiAgentStartResponse> {
    return await this.request('/api/ai/agent-runs', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async continueAiAgentRun(
    req: AiAgentContinueRequest,
  ): Promise<AiAgentContinueResponse> {
    return await this.request('/api/ai/agent-runs/continue', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async listAiAgentRunLogs(): Promise<AiAgentRunLogListResponse> {
    return await this.request('/api/ai/agent-runs/logs', { method: 'GET' })
  }

  async getAiAgentRunLog(runId: string): Promise<AiAgentRunLogDetailResponse> {
    return await this.request(
      `/api/ai/agent-runs/logs/${encodeURIComponent(runId)}`,
      { method: 'GET' },
    )
  }

  async recordAiAgentClientEvent(
    req: AiAgentClientEventRequest,
  ): Promise<AiAgentClientEventResponse> {
    return await this.request('/api/ai/agent-runs/client-events', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async getAiAgentTelemetryHealth(): Promise<AiAgentTelemetryHealthResponse> {
    return await this.request('/api/ai/agent-runs/telemetry/health', {
      method: 'GET',
    })
  }

  /**
   * Ingest a client-batched payload of product events, AI feedback, and bug
   * reports into the first-party telemetry spine (`POST /api/events`). Used by
   * the `src/analytics/` module's batched flush.
   */
  /**
   * Analytics holds its buffer while this is false: an unauthenticated
   * `POST /api/events` is a guaranteed 400 (bearer required), and the client
   * re-queues failed batches, so one pre-auth flush would poison the session.
   */
  canSendEvents(): boolean {
    return Boolean(this.token)
  }

  async ingestEvents(req: EventsIngestRequest): Promise<EventsIngestResponse> {
    return await this.request('/api/events', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async getFounderActivityDashboard(params?: {
    window_days?: number | null
    site_id?: string | null
    user_id?: string | null
    limit?: number | null
  }): Promise<FounderActivityDashboardResponse> {
    const qp = new URLSearchParams()
    const windowDays = Number(params?.window_days)
    if (Number.isFinite(windowDays) && windowDays > 0)
      qp.set('window_days', String(Math.floor(windowDays)))
    const limit = Number(params?.limit)
    if (Number.isFinite(limit) && limit > 0)
      qp.set('limit', String(Math.floor(limit)))
    const siteId = String(params?.site_id || '').trim()
    if (siteId) qp.set('site_id', siteId)
    const userId = String(params?.user_id || '').trim()
    if (userId) qp.set('user_id', userId)
    const qs = qp.toString()
    return await this.request(`/api/founder/activity${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    })
  }

  async listAccessGrants(): Promise<AccessGrantRow[]> {
    return await this.request('/api/founder/access/grants', { method: 'GET' })
  }

  /** Grant capabilities to an email; invites (account-setup email) when they have no login. */
  async grantAccess(req: {
    email: string
    capabilities: FgCapability[]
    note?: string
    invite?: boolean
  }): Promise<{ status: string; email: string; invited: boolean; loginState: string }> {
    return await this.request('/api/founder/access/grants', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async revokeAccessGrant(id: string): Promise<void> {
    await this.request(`/api/founder/access/grants/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  async getFounderActivityAccess(): Promise<FounderActivityAccessResponse> {
    return await this.request('/api/founder/activity/access', {
      method: 'GET',
    })
  }

  // WS2: per-call AI spend ledger (founder-only).
  async getFounderAiSpend(params?: {
    window_days?: number | null
    site_id?: string | null
    limit?: number | null
  }): Promise<FounderAiSpendResponse> {
    const qp = new URLSearchParams()
    const windowDays = Number(params?.window_days)
    if (Number.isFinite(windowDays) && windowDays > 0)
      qp.set('window_days', String(Math.floor(windowDays)))
    const limit = Number(params?.limit)
    if (Number.isFinite(limit) && limit > 0)
      qp.set('limit', String(Math.floor(limit)))
    const siteId = String(params?.site_id || '').trim()
    if (siteId) qp.set('site_id', siteId)
    const qs = qp.toString()
    return await this.request(`/api/founder/ai-spend${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    })
  }

  // WS2: full system+user+output prompt for one call (founder drill-down; fetched from GCS).
  async getFounderAiSpendPrompt(
    callId: string,
  ): Promise<{ fullPrompt: FounderAiSpendFullPrompt }> {
    const qp = new URLSearchParams({ call_id: String(callId || '').trim() })
    return await this.request(`/api/founder/ai-spend?${qp.toString()}`, {
      method: 'GET',
    })
  }

  // Founder system-health snapshot: alarms + scaling metrics + in-process cache stats.
  async getFounderSystemHealth(): Promise<FounderSystemHealthResponse> {
    return await this.request('/api/founder/system-health', { method: 'GET' })
  }

  /** Public: the published document for a tour, or a 404 the caller falls back from. */
  async getPublishedTour(slug: string): Promise<PublishedTourResponse> {
    return await this.request(`/api/tours/published/${encodeURIComponent(slug)}`, { method: 'GET' })
  }

  /**
   * Public: resolve a share link. Returns which gate is unmet rather than the
   * tour, so the entry page can prompt for exactly the missing thing.
   */
  async openTourShareLink(
    token: string,
    body?: { passcode?: string; email?: string },
  ): Promise<TourShareOpenResponse> {
    return await this.request(`/api/tours/share/${encodeURIComponent(token)}/open`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    })
  }

  async listTours(): Promise<TourListRow[]> {
    return await this.request('/api/tours', { method: 'GET' })
  }

  async listTourVersions(slug: string): Promise<TourVersionRow[]> {
    return await this.request(`/api/tours/${encodeURIComponent(slug)}/versions`, { method: 'GET' })
  }

  async saveTourDraft(req: {
    slug: string
    name?: string
    context?: string
    doc: unknown
    notes?: string | null
  }): Promise<{ tourId: string; version: number; docSha256: string }> {
    return await this.request('/api/tours/draft', { method: 'POST', body: JSON.stringify(req) })
  }

  async draftTourNarration(req: { doc: unknown; step_ids?: string[] }): Promise<{
    drafts: Array<{ id: string; title: string; body: string; voice_script: string }>
    provenance: { model_id: string; prompt_sha256: string; generated_at: string }
  }> {
    return await this.request('/api/tours/draft-narration', { method: 'POST', body: JSON.stringify(req) })
  }

  async publishTour(req: {
    slug: string
    version: number
    anchor_check?: unknown
    force?: boolean
  }): Promise<PublishedTourResponse> {
    return await this.request('/api/tours/publish', { method: 'POST', body: JSON.stringify(req) })
  }

  async listTourShareLinks(slug?: string | null): Promise<TourShareLinkRow[]> {
    const qs = slug ? `?slug=${encodeURIComponent(slug)}` : ''
    return await this.request(`/api/tours/share-links${qs}`, { method: 'GET' })
  }

  async createTourShareLink(req: {
    slug: string
    label?: string | null
    audience?: Record<string, string>
    passcode?: string | null
    require_email?: boolean
    domain_allowlist?: string[]
    expires_in_days?: number | null
    pin_version?: boolean
  }): Promise<TourShareLinkRow> {
    return await this.request('/api/tours/share-links', { method: 'POST', body: JSON.stringify(req) })
  }

  async revokeTourShareLink(token: string): Promise<void> {
    await this.request(`/api/tours/share-links/${encodeURIComponent(token)}`, { method: 'DELETE' })
  }

  async listCrmContacts(stage?: string | null): Promise<CrmContact[]> {
    const qs = stage ? `?stage=${encodeURIComponent(stage)}` : ''
    return await this.request(`/api/crm/contacts${qs}`, { method: 'GET' })
  }

  async getCrmContact(id: string): Promise<CrmContactDetail> {
    return await this.request(`/api/crm/contacts/${encodeURIComponent(id)}`, { method: 'GET' })
  }

  async updateCrmContact(id: string, patch: {
    stage?: CrmStage
    full_name?: string
    firm_name?: string
    phone?: string
    next_action_at?: string
    won_amount_cents?: number
  }): Promise<void> {
    await this.request(`/api/crm/contacts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  }

  async getCrmSessionTimeline(sessionId: string): Promise<Array<{
    at: string | null
    event: string
    stepId: string | null
    stepIndex: number | null
    dwellMs: number | null
    detail: Record<string, unknown> | null
  }>> {
    return await this.request(`/api/crm/sessions/${encodeURIComponent(sessionId)}/timeline`, { method: 'GET' })
  }

  async addCrmNote(id: string, body: string): Promise<void> {
    await this.request(`/api/crm/contacts/${encodeURIComponent(id)}/activities`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    })
  }

  async getFounderTourAnalytics(opts?: {
    windowDays?: number
    limit?: number
    slug?: string | null
  }): Promise<FounderTourAnalyticsResponse> {
    const params = new URLSearchParams()
    if (opts?.windowDays) params.set('window_days', String(opts.windowDays))
    if (opts?.limit) params.set('limit', String(opts.limit))
    if (opts?.slug) params.set('slug', opts.slug)
    const qs = params.toString()
    return await this.request(`/api/founder/tour-analytics${qs ? `?${qs}` : ''}`, { method: 'GET' })
  }

  async getFounderBilling(): Promise<FounderBillingResponse> {
    return await this.request('/api/founder/billing', { method: 'GET' })
  }

  async cancelAiAgentRun(
    req: AiAgentCancelRequest,
  ): Promise<AiAgentCancelResponse> {
    return await this.request('/api/ai/agent-runs/cancel', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async listConnections(): Promise<ServerConnection[]> {
    return await this.request('/api/connections', { method: 'GET' })
  }

  async createConnection(
    req: ServerCreateConnectionRequest,
  ): Promise<ServerConnection> {
    return await this.request('/api/connections', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async getConnection(
    connectionId: string,
    opts?: { includeConfig?: boolean | null },
  ): Promise<ServerConnection> {
    const qp = new URLSearchParams()
    if (opts?.includeConfig) qp.set('include_config', '1')
    const suffix = qp.toString()
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}${suffix ? `?${suffix}` : ''}`,
      {
        method: 'GET',
      },
    )
  }

  async updateConnection(
    connectionId: string,
    req: ServerUpdateConnectionRequest,
  ): Promise<ServerConnection> {
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(req),
      },
    )
  }

  async deleteConnection(connectionId: string): Promise<{ status: string }> {
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}`,
      { method: 'DELETE' },
    )
  }

  async testConnection(
    connectionId: string,
  ): Promise<ServerTestConnectionResponse> {
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}/test`,
      { method: 'POST' },
    )
  }

  async previewConnection(
    connectionId: string,
  ): Promise<ServerPreviewResponse> {
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}/preview`,
      { method: 'GET' },
    )
  }

  async getConnectionSchema(
    connectionId: string,
  ): Promise<ServerConnectionSchema> {
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}/schema`,
      { method: 'GET' },
    )
  }

  async previewConnectionTable(
    connectionId: string,
    req: { table: string; schema?: string | null; limit?: number | null },
  ): Promise<ServerExecuteQueryResponse> {
    const qp = new URLSearchParams()
    qp.set('table', String(req?.table || '').trim())
    if (String(req?.schema || '').trim())
      qp.set('schema', String(req.schema || '').trim())
    if (Number.isFinite(Number(req?.limit)))
      qp.set('limit', String(Math.max(1, Math.floor(Number(req.limit)))))
    return await this.request(
      `/api/connections/${encodeURIComponent(connectionId)}/table-preview?${qp.toString()}`,
      { method: 'GET' },
    )
  }

  async createQuery(req: {
    sql: string
    connection_id: string
  }): Promise<ServerQueryResponse> {
    return await this.request('/api/queries', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async executeQuery(queryId: string): Promise<ServerExecuteQueryResponse> {
    return await this.request(
      `/api/queries/${encodeURIComponent(queryId)}/execute`,
      { method: 'POST' },
    )
  }

  async getResult(resultId: string): Promise<{ id: string; data: any[][] }> {
    return await this.request(`/api/results/${encodeURIComponent(resultId)}`, {
      method: 'GET',
    })
  }

  async listDatasources(): Promise<ServerDatasource[]> {
    return await this.request('/api/datasources', { method: 'GET' })
  }

  async createDatasource(req: {
    name: string
    kind: 'md_table' | 'md_sql' | 'ext_table' | 'ext_sql'
    site_id?: string | null
    project_id?: string | null
    md_database?: string | null
    md_reference?: string | null
    md_sql?: string | null
    connection_id?: string | null
    external_table?: string | null
    external_sql?: string | null
    overwrite?: boolean
    schedule_config?: any | null
    retention_config?: any | null
    encryption_policy?: any | null
    definition_json?: any | null
  }): Promise<ServerDatasource> {
    return await this.request('/api/datasources', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async updateDatasource(
    datasourceId: string,
    req: Partial<{
      name: string
      md_database: string | null
      md_reference: string | null
      md_sql: string | null
      connection_id: string | null
      external_table: string | null
      external_sql: string | null
      schedule_config: any | null
      retention_config: any | null
      encryption_policy: any | null
      definition_json: any | null
    }>,
  ): Promise<ServerDatasource> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async publishExtract(
    datasourceId: string,
    req?: Partial<{
      artifact_uri: string
      etag: string
      bytes: number
      schema_json: any
      row_count: number
      source_fingerprint: any
    }>,
  ): Promise<ServerExtractVersion> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/publish`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async listExtracts(datasourceId: string): Promise<ServerExtractVersion[]> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/extracts`,
      { method: 'GET' },
    )
  }

  async getLatestExtract(datasourceId: string): Promise<ServerExtractVersion> {
    try {
      return await this.request(
        `/api/datasources/${encodeURIComponent(datasourceId)}/extracts/latest`,
        { method: 'GET' },
      )
    } catch (e: any) {
      // Production API currently returns 404 when there is no READY extract yet (e.g. publishing in progress).
      // Fall back to listing versions so the UI can surface "publishing"/"failed" instead of a hard 404.
      const msg = String(e?.message || e || '')
      const looks404 = /\bAPI\s+404\b/i.test(msg) || /\b404\b/i.test(msg)
      if (looks404) {
        try {
          const list = await this.listExtracts(datasourceId)
          if (Array.isArray(list) && list.length) return list[0]
        } catch {
          // ignore fallthrough
        }
      }
      throw e
    }
  }

  async getQuickBooksStatus(): Promise<QuickBooksStatusResponse> {
    return await this.request('/api/integrations/quickbooks/status', {
      method: 'GET',
    })
  }

  async listSiteQuickBooksSyncSchedules(
    siteSlug: string,
  ): Promise<QuickBooksSyncScheduleListResponse> {
    return await this.request(
      `/api/integrations/quickbooks/sites/${encodeURIComponent(siteSlug)}/schedules`,
      { method: 'GET' },
    )
  }

  async upsertSiteQuickBooksSyncSchedule(
    siteSlug: string,
    req: QuickBooksSyncScheduleUpsertRequest,
  ): Promise<QuickBooksSyncScheduleListResponse> {
    return await this.request(
      `/api/integrations/quickbooks/sites/${encodeURIComponent(siteSlug)}/schedules`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async triggerSiteQuickBooksIncrementalRefresh(
    siteSlug: string,
    connectionId: string,
  ): Promise<QuickBooksSyncScheduleListResponse> {
    return await this.request(
      `/api/integrations/quickbooks/sites/${encodeURIComponent(siteSlug)}/schedules/${encodeURIComponent(connectionId)}/refresh`,
      { method: 'POST' },
    )
  }

  /** One server-owned source update and report continuation, including retries. */
  async refreshCfoQuickBooks(
    workbookId: string,
    requestedPeriod?: string,
  ): Promise<CfoQuickBooksRefreshResponse> {
    // The shell may select a month-end date; server admission is month-scoped.
    const period = requestedPeriod?.replace(/^(\d{4}-\d{2})-\d{2}$/, '$1-01')
    const key = JSON.stringify([this.getToken(), workbookId, period || 'latest'])
    const active = this.cfoRefreshInFlight.get(key)
    if (active) return await active
    const query = period ? `?requested_period=${encodeURIComponent(period)}` : ''
    const pending = this.request<CfoQuickBooksRefreshResponse>(
      `/api/integrations/quickbooks/workbooks/${encodeURIComponent(workbookId)}/refresh${query}`,
      { method: 'POST' },
    ).finally(() => this.cfoRefreshInFlight.delete(key))
    this.cfoRefreshInFlight.set(key, pending)
    return await pending
  }

  async refreshCfoWorkbook(workbookId: string, requestedPeriod?: string): Promise<CfoQuickBooksRefreshResponse | null> {
    const sessionIdentity = accessSessionIdentity(this.getToken())
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (accessSessionIdentity(this.getToken()) !== sessionIdentity) throw new Error('Your session changed. Start the refresh again from your current account.')
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.refreshCfoQuickBooks(workbookId, requestedPeriod)
    }
    await this.refreshAccountingWorkbook(workbookId)
    return null
  }

  async getQuickBooksUsage(req?: {
    days?: number | null
    grain?: 'hour' | 'day' | 'week' | 'month' | string | null
    realmId?: string | null
    connectionId?: string | null
    from?: string | null
    to?: string | null
  }): Promise<QuickBooksUsageResponse> {
    const params = new URLSearchParams()
    if (req?.days != null) params.set('days', String(req.days))
    if (req?.grain) params.set('grain', req.grain)
    if (req?.realmId) params.set('realm_id', req.realmId)
    if (req?.connectionId) params.set('connection_id', req.connectionId)
    if (req?.from) params.set('from', req.from)
    if (req?.to) params.set('to', req.to)
    const qs = params.toString()
    return await this.request(
      `/api/integrations/quickbooks/usage${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  async createQuickBooksAuthorizeUrl(req?: {
    realm_id?: string | null
    redirect?: string | null
    intent?: string | null
    workbook_id?: string | null
  }): Promise<QuickBooksAuthorizeResponse> {
    return await this.request('/api/integrations/quickbooks/authorize', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async createQuickBooksConnectionRequest(req: {
    workbook_id: string
    admin_email?: string | null
    note?: string | null
  }): Promise<QuickBooksConnectionRequestResponse> {
    return await this.request(
      '/api/integrations/quickbooks/connection-requests',
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  /** The CFO Core's working connect link, or null when it has none. */
  async getQuickBooksConnectLink(workbookId: string): Promise<QuickBooksConnectLink | null> {
    const q = new URLSearchParams({ workbook_id: workbookId })
    const out = await this.request<{ link?: QuickBooksConnectLink | null }>(
      `/api/integrations/quickbooks/connect-links?${q.toString()}`,
      { method: 'GET' },
    )
    return out?.link ?? null
  }

  /** The CFO Core's connect link, created on first request. Asking again
   * returns the same link; naming a requester changes who it comes from and
   * keeps the URL. */
  async createQuickBooksConnectLink(
    workbookId: string,
    requestedByUserId?: string | null,
  ): Promise<QuickBooksConnectLink> {
    return await this.request('/api/integrations/quickbooks/connect-links', {
      method: 'POST',
      body: JSON.stringify({
        workbook_id: workbookId,
        ...(requestedByUserId ? { requested_by_user_id: requestedByUserId } : {}),
      }),
    })
  }

  /** Who the CFO Core's link can say the request comes from: site members
   * who can edit the CFO Core. */
  async listQuickBooksConnectLinkRequesters(workbookId: string): Promise<QuickBooksConnectLinkPerson[]> {
    const q = new URLSearchParams({ workbook_id: workbookId })
    const out = await this.request<{ requesters?: QuickBooksConnectLinkPerson[] | null }>(
      `/api/integrations/quickbooks/connect-links/requesters?${q.toString()}`,
      { method: 'GET' },
    )
    return Array.isArray(out?.requesters) ? out.requesters : []
  }

  async revokeQuickBooksConnectLink(linkId: string): Promise<void> {
    await this.request(`/api/integrations/quickbooks/connect-links/${encodeURIComponent(linkId)}/revoke`, {
      method: 'POST',
    })
  }

  async listQuickBooksConnectionRequests(): Promise<
    QuickBooksConnectionRequestEntry[]
  > {
    const out = await this.request<{
      requests?: QuickBooksConnectionRequestEntry[] | null
    }>('/api/integrations/quickbooks/connection-requests', { method: 'GET' })
    return Array.isArray(out?.requests) ? out.requests : []
  }

  async listQuickBooksAttachableConnections(
    workbookId: string,
  ): Promise<QuickBooksAttachableConnection[]> {
    const out = await this.request<{
      connections?: QuickBooksAttachableConnection[] | null
    }>(
      `/api/integrations/quickbooks/workbooks/${encodeURIComponent(workbookId)}/attachable-connections`,
      { method: 'GET' },
    )
    return Array.isArray(out?.connections) ? out.connections : []
  }

  async attachQuickBooksConnectionToWorkbook(
    workbookId: string,
    connectionId: string,
  ): Promise<{
    connection_id: string
    datasource_id: string
    realm_id: string
    company_name?: string | null
    status: string
  }> {
    return await this.request(
      `/api/integrations/quickbooks/workbooks/${encodeURIComponent(workbookId)}/attach`,
      {
        method: 'POST',
        body: JSON.stringify({ connection_id: connectionId }),
      },
    )
  }

  async disconnectQuickBooks(req: {
    realm_id: string
  }): Promise<{ status: string }> {
    return await this.request('/api/integrations/quickbooks/disconnect', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async syncQuickBooks(req: {
    realm_id: string
    entity?: string | null
  }): Promise<any> {
    return await this.request('/api/integrations/quickbooks/sync', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async listQuickBooksViews(): Promise<QuickBooksViewEntry[]> {
    const out = await this.request<{ views?: QuickBooksViewEntry[] | null }>(
      '/api/integrations/quickbooks/views',
      { method: 'GET' },
    )
    return Array.isArray(out?.views) ? out.views : []
  }

  async createQuickBooksView(req: {
    realm_id: string
    view_name: string
    s3_prefix?: string | null
    enabled?: boolean | null
  }): Promise<QuickBooksViewEntry> {
    return await this.request('/api/integrations/quickbooks/views', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async exportQuickBooksView(viewId: string): Promise<any> {
    return await this.request(
      `/api/integrations/quickbooks/views/${encodeURIComponent(viewId)}/export`,
      { method: 'POST' },
    )
  }

  async listQuickBooksExtracts(): Promise<QuickBooksExtractEntry[]> {
    const out = await this.request<{
      extracts?: QuickBooksExtractEntry[] | null
    }>('/api/integrations/quickbooks/extracts', { method: 'GET' })
    return Array.isArray(out?.extracts) ? out.extracts : []
  }

  async runQuickBooksFirstRun(req: {
    realm_id: string
  }): Promise<QuickBooksFirstRunResponse> {
    return await this.request('/api/integrations/quickbooks/first-run', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async getQuickBooksRun(runId: string): Promise<QuickBooksRunEntry> {
    return await this.request(
      `/api/integrations/quickbooks/runs/${encodeURIComponent(runId)}`,
      { method: 'GET' },
    )
  }

  async listQuickBooksRuns(
    realmId?: string | null,
  ): Promise<QuickBooksRunEntry[]> {
    const qs = realmId ? `?realm_id=${encodeURIComponent(realmId)}` : ''
    const out = await this.request<{ runs?: QuickBooksRunEntry[] | null }>(
      `/api/integrations/quickbooks/runs${qs}`,
      { method: 'GET' },
    )
    return Array.isArray(out?.runs) ? out.runs : []
  }

  async getQuickBooksDurableJob(
    jobId: string,
  ): Promise<QuickBooksDurableJobEntry> {
    return await this.request(
      `/api/integrations/quickbooks/jobs/${encodeURIComponent(jobId)}`,
      { method: 'GET' },
    )
  }

  async pauseQuickBooksDurableJob(
    jobId: string,
  ): Promise<{ paused: boolean; job_id: string }> {
    return await this.request(
      `/api/integrations/quickbooks/jobs/${encodeURIComponent(jobId)}/pause`,
      { method: 'POST' },
    )
  }

  async resumeQuickBooksDurableJob(
    jobId: string,
  ): Promise<{ resumed: boolean; job_id: string }> {
    return await this.request(
      `/api/integrations/quickbooks/jobs/${encodeURIComponent(jobId)}/resume`,
      { method: 'POST' },
    )
  }

  async cancelQuickBooksDurableJob(
    jobId: string,
  ): Promise<{ cancelled: boolean; job_id: string }> {
    return await this.request(
      `/api/integrations/quickbooks/jobs/${encodeURIComponent(jobId)}/cancel`,
      { method: 'POST' },
    )
  }

  async retryQuickBooksDurableTask(
    jobId: string,
    taskId: string,
  ): Promise<{
    retried: boolean
    job_id: string
    task_id: string
    tasks_reset: number
  }> {
    return await this.request(
      `/api/integrations/quickbooks/jobs/${encodeURIComponent(jobId)}/tasks/${encodeURIComponent(taskId)}/retry`,
      { method: 'POST' },
    )
  }

  async listQuickBooksFpaLayouts(
    connectionId: string,
  ): Promise<QuickBooksFpaLayoutVersion[]> {
    const out = await this.request<{
      layouts?: QuickBooksFpaLayoutVersion[] | null
    }>(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/layouts`,
      { method: 'GET' },
    )
    return Array.isArray(out?.layouts) ? out.layouts : []
  }

  async createQuickBooksFpaLayout(
    connectionId: string,
    req?: { name?: string | null },
  ): Promise<QuickBooksFpaLayoutMutation> {
    return await this.request(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/layouts`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async cloneQuickBooksFpaLayout(
    versionId: string,
    req?: { name?: string | null },
  ): Promise<QuickBooksFpaLayoutMutation> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/clone`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateQuickBooksFpaLayout(
    versionId: string,
    req: { name?: string | null },
  ): Promise<QuickBooksFpaLayoutMutation> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async patchQuickBooksFpaLayoutNodes(
    versionId: string,
    req: {
      nodes?: Array<
        Partial<QuickBooksFpaLayoutNode> & {
          clear_parent?: boolean
          clear_cash_flow_section?: boolean
          clear_qbo_account_sub_type?: boolean
        }
      >
      delete_node_ids?: string[]
    },
  ): Promise<QuickBooksFpaLayoutMutation> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/nodes`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async validateQuickBooksFpaLayout(
    versionId: string,
  ): Promise<QuickBooksFpaValidation> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/validate`,
      { method: 'POST' },
    )
  }

  async publishQuickBooksFpaLayout(
    versionId: string,
  ): Promise<QuickBooksFpaLayoutMutation> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/publish`,
      { method: 'POST' },
    )
  }

  async listServiceLineRollups(
    versionId: string,
  ): Promise<ServiceLineRollupList> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/service-lines`,
      { method: 'GET' },
    )
  }

  async suggestedServiceLineRollups(
    versionId: string,
  ): Promise<{ suggestions: SuggestedServiceLineRollup[] }> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/service-lines/suggested`,
      { method: 'GET' },
    )
  }

  async seedServiceLineRollups(
    versionId: string,
    force = false,
  ): Promise<ServiceLineSeedResult> {
    const qs = force ? '?force=1' : ''
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/service-lines/seed${qs}`,
      { method: 'POST' },
    )
  }

  async createServiceLineRollup(
    versionId: string,
    req: {
      name: string
      income_node_ids: string[]
      cogs_node_ids: string[]
      auto_detected?: boolean
      confidence?: number | null
      signals?: string[]
    },
  ): Promise<ServiceLineRollup> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/service-lines`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async updateServiceLineRollup(
    versionId: string,
    rollupId: string,
    req: {
      name?: string
      income_node_ids?: string[]
      cogs_node_ids?: string[]
      status?: string
      sort_order?: number
    },
  ): Promise<ServiceLineRollup> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/service-lines/${encodeURIComponent(rollupId)}`,
      { method: 'PATCH', body: JSON.stringify(req) },
    )
  }

  async deleteServiceLineRollup(
    versionId: string,
    rollupId: string,
  ): Promise<{ deleted: number }> {
    return await this.request(
      `/api/fpa/quickbooks/layouts/${encodeURIComponent(versionId)}/service-lines/${encodeURIComponent(rollupId)}`,
      { method: 'DELETE' },
    )
  }

  async listQuickBooksFpaAdjustments(
    connectionId: string,
  ): Promise<QuickBooksFpaJournalEntry[]> {
    const out = await this.request<{
      adjustments?: QuickBooksFpaJournalEntry[] | null
    }>(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/adjustments`,
      { method: 'GET' },
    )
    return Array.isArray(out?.adjustments) ? out.adjustments : []
  }

  async createQuickBooksFpaAdjustment(
    connectionId: string,
    req: Partial<QuickBooksFpaJournalEntry> & {
      lines: QuickBooksFpaJournalLine[]
    },
  ): Promise<QuickBooksFpaJournalEntry> {
    return await this.request(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/adjustments`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksFpaAdjustment(
    connectionId: string,
    req: Partial<QuickBooksFpaJournalEntry> & {
      id: string
      lines?: QuickBooksFpaJournalLine[]
    },
  ): Promise<QuickBooksFpaJournalEntry> {
    return await this.request(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/adjustments`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async prepareQuickBooksFpaCache(
    connectionId: string,
    opts?: {
      includeGl?: boolean
      detail?: boolean
      asyncDetail?: boolean
      includeAdjustments?: boolean
      includeArSupport?: boolean
      includeArCollectability?: boolean
      includeArAnalysis?: boolean
      artifactOnly?: boolean
      workbookId?: string
    },
  ): Promise<QuickBooksFpaCachePrepareResponse> {
    const includeGl = Boolean(opts?.includeGl || opts?.detail)
    const includeArCollectability = Boolean(opts?.includeArCollectability)
    const includeArSupport = Boolean(
      opts?.includeArSupport || includeArCollectability,
    )
    const params = new URLSearchParams()
    if (includeGl) params.set('include_gl', 'true')
    if (opts?.asyncDetail) params.set('async_detail', 'true')
    if (opts?.includeAdjustments === false)
      params.set('include_adjustments', 'false')
    if (includeArSupport) params.set('include_ar_support', 'true')
    if (includeArCollectability) params.set('include_ar_collectability', 'true')
    if (opts?.includeArAnalysis) params.set('include_ar_analysis', 'true')
    if (opts?.artifactOnly) params.set('artifact_only', 'true')
    if (opts?.workbookId) params.set('workbook_id', opts.workbookId)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/cache/prepare${suffix}`,
      {
        method: 'POST',
        body: JSON.stringify({
          include_gl: includeGl,
          async_detail: Boolean(opts?.asyncDetail),
          include_adjustments: opts?.includeAdjustments !== false,
          include_ar_support: includeArSupport,
          include_ar_collectability: includeArCollectability,
          include_ar_analysis: Boolean(opts?.includeArAnalysis),
          artifact_only: Boolean(opts?.artifactOnly),
          workbook_id: opts?.workbookId || null,
        }),
      },
    )
  }

  async getQuickBooksFpaCacheStatus(
    connectionId: string,
  ): Promise<QuickBooksFpaCacheStatusResponse> {
    return await this.request(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/cache/status`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaCacheManifest(
    connectionId: string,
  ): Promise<QuickBooksFpaCacheManifest> {
    const out = await this.request<{
      manifest?: QuickBooksFpaCacheManifest | null
    }>(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/cache/manifest`,
      { method: 'GET' },
    )
    if (!out?.manifest)
      throw new Error('QuickBooks FP&A cache manifest was not returned')
    return out.manifest
  }

  async getQuickBooksFpaCacheFile(
    connectionId: string,
    fileId: string,
  ): Promise<QuickBooksFpaCacheTablePayload> {
    return await this.request(
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/cache/files/${encodeURIComponent(fileId)}`,
      { method: 'GET' },
    )
  }

  getQuickBooksFpaCacheParquetUrl(
    connectionId: string,
    fileId: string,
    part?: number,
  ): string {
    const params = new URLSearchParams()
    const token = this.getToken()
    if (token) params.set('token', token)
    if (typeof part === 'number' && Number.isFinite(part) && part >= 0)
      params.set('part', String(Math.floor(part)))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return joinUrl(
      this.baseUrl,
      `/api/fpa/quickbooks/connections/${encodeURIComponent(connectionId)}/cache/files/${encodeURIComponent(fileId)}/parquet${suffix}`,
    )
  }

  async renderQuickBooksFpaStatement(req: {
    connection_id: string
    layout_version_id?: string | null
    statement_type?: 'profit_and_loss' | 'balance_sheet' | 'cash_flow' | string
    start_date?: string | null
    end_date?: string | null
    months?: number | null
    balance_sheet_mode?: 'ending_balance' | 'monthly_change' | string | null
    formatting?: QuickBooksFpaStatementFormatting | null
    filters?: any
    /** Optional workbook context so workbook-scoped segment entitlements apply. */
    workbook_id?: string | null
  }): Promise<QuickBooksFpaStatementRender> {
    return await this.request('/api/fpa/quickbooks/statements/render', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  /**
   * Fetch canonical metric values + period comparisons (MoM / QoQ / YoY / L3M /
   * L12M, in $ and % of sales) for a workbook's bound statement layout. Backed
   * by the shared metric registry + comparison engine in fpa.rs; consumed by the
   * CFO brief, ratio library, and the workbook-wide agent.
   */
  async getQuickBooksMetricComparisons(
    workbookId: string,
    opts?: GetMetricComparisonsOptions,
  ): Promise<MetricComparisonResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    if (opts?.comparisons && opts.comparisons.length > 0) {
      search.set('comparisons', opts.comparisons.join(','))
    }
    if (opts?.metrics && opts.metrics.length > 0) {
      search.set('metrics', opts.metrics.join(','))
    }
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/metrics/comparisons${suffix}`,
      { method: 'GET' },
    )
  }

  /**
   * Fetch the CFO ambient insight spine: ranked "what changed / what needs
   * attention" cards produced server-side by the deterministic detectors in
   * insights.rs over the canonical comparison engine. Consumed by the cfo-home
   * Brief and exposed to the workbook-wide agent as a tool.
   */
  async getQuickBooksInsights(
    workbookId: string,
    opts?: GetCfoInsightsOptions,
  ): Promise<CfoInsightsResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/insights${suffix}`,
      { method: 'GET' },
    )
  }

  /** Provider-neutral insight spine for cockpit and new CFO surfaces. */
  async getCfoInsights(
    workbookId: string,
    opts?: GetCfoInsightsOptions,
  ): Promise<CfoInsightsResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/insights${suffix}`,
      { method: 'GET' },
    )
  }

  /** Provider-neutral accounting contract. Xero surfaces use only these routes. */
  private accountingBootstrapInFlight = new Map<string, Promise<AccountingWorkbookBootstrap>>()

  async getAccountingWorkbookBootstrap(workbookId: string): Promise<AccountingWorkbookBootstrap> {
    const pending = this.accountingBootstrapInFlight.get(workbookId)
    if (pending) return pending
    const load = async (): Promise<AccountingWorkbookBootstrap> => {
      const bootstrap = await this.request<AccountingWorkbookBootstrap>(
        `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/bootstrap`, { method: 'GET' },
      )
      // The company identity is created when its datasource is attached. Its
      // first statement layout is a separate, asynchronous provisioning step.
      // All CFO consumers must wait for that step before asking for reports;
      // parallel home/ratio/materialization calls otherwise race the setup.
      if (bootstrap.accounting_provider === 'quickbooks'
        && ['pending', 'syncing'].includes(bootstrap.provisioning_state || '')) {
        const setup = await this.getQuickBooksCfoCoreBootstrap(workbookId)
        if (!setup.bindings?.length) {
          throw new Error('API 409: Financial setup has not completed for this workbook.')
        }
      }
      return bootstrap
    }
    const promise = load().finally(() => this.accountingBootstrapInFlight.delete(workbookId))
    this.accountingBootstrapInFlight.set(workbookId, promise)
    return promise
  }

  async getAccountingWorkbookConnection(workbookId: string): Promise<any> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/connection`, { method: 'GET' })
  }

  async refreshAccountingWorkbook(workbookId: string): Promise<any> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/refresh`, { method: 'POST' })
  }

  /**
   * Starts the Sage authorization hand-off.
   *
   * Accepts a bare redirect for the reconnect path, or the full form when the
   * caller already knows which site and project the business should land in —
   * the server round-trips those through signed OAuth state so the callback
   * can provision without asking again.
   */
  async createSageAccountingAuthorizeUrl(
    options?: string | null | { redirect?: string | null; site_id?: string | null; project_id?: string | null },
  ): Promise<{ authorize_url: string }> {
    const req = typeof options === 'string' || options == null ? { redirect: options } : options
    const search = new URLSearchParams()
    if (req.redirect) search.set('redirect', req.redirect)
    if ('site_id' in req && req.site_id) search.set('site_id', req.site_id)
    if ('project_id' in req && req.project_id) search.set('project_id', req.project_id)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(`/api/integrations/sage-accounting/authorize${suffix}`, { method: 'GET' })
  }

  async disconnectAccountingWorkbook(workbookId: string, revokeCredentialIfUnused = true): Promise<any> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/disconnect`, {
      method: 'POST',
      body: JSON.stringify({ revoke_credential_if_unused: revokeCredentialIfUnused }),
    })
  }

  async getAccountingStatements(workbookId: string, statement = 'profit_and_loss'): Promise<AccountingStatementResponse> {
    const search = new URLSearchParams({ statement })
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/statements?${search}`, { method: 'GET' })
  }

  async getAccountingReceivables(workbookId: string): Promise<AccountingReceivablesResponse> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/ar`, { method: 'GET' })
  }

  async getAccountingCashForecast(workbookId: string): Promise<AccountingCashResponse> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/cash`, { method: 'GET' })
  }

  async getCfoMetricComparisons(
    workbookId: string,
    opts?: GetMetricComparisonsOptions,
  ): Promise<MetricComparisonResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksMetricComparisons(workbookId, opts)
    }
    const [profitAndLoss, balanceSheet] = await Promise.all([
      this.getAccountingStatements(workbookId, 'profit_and_loss'),
      this.getAccountingStatements(workbookId, 'balance_sheet'),
    ])
    const statementComparisons = accountingStatementsToMetricComparisons(
      bootstrap,
      { profit_and_loss: profitAndLoss, balance_sheet: balanceSheet },
      opts,
    )
    if ((opts?.metrics || []).every((metric) => metric === 'cash' || metric === 'ending_cash')
      && statementComparisons.metrics.every((metric) => metric.latest_value == null)) {
      const cash = await this.getAccountingCashForecast(workbookId)
      return accountingCashToMetricComparisons(bootstrap, cash, opts)
    }
    return statementComparisons
  }

  async getCfoCashPlanning(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      as_of_date?: string | null
      history_weeks?: number | null
      forecast_weeks?: number | null
      due_date_assumption?: boolean
    },
    signal?: AbortSignal | null,
  ): Promise<QuickBooksCashPlanningResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksCashPlanning(workbookId, req, signal)
    }
    const cash = await this.getAccountingCashForecast(workbookId)
    return accountingCashToPlanning(bootstrap, cash)
  }

  async getAccountingForecast(workbookId: string): Promise<AccountingForecastResponse> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/forecast`, { method: 'GET' })
  }

  async getAccountingGeneralLedger(workbookId: string, signal?: AbortSignal | null): Promise<AccountingLedgerResponse> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/gl`, { method: 'GET', signal: signal || undefined })
  }

  async getAccountingClose(workbookId: string): Promise<CfoClosePeriodsResponse> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close`, { method: 'GET' })
  }

  async getCfoCloseReview(workbookId: string, period: string): Promise<CfoCloseReview> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}`,
      { method: 'GET' },
    )
  }

  async runCfoCloseReview(
    workbookId: string,
    period: string,
    body: { expected_source_set_token?: string | null; idempotency_key?: string | null } = {},
  ): Promise<{ status: string; run_id?: string; review: CfoCloseReview }> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}/runs`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async updateCfoCloseTask(
    workbookId: string,
    period: string,
    taskId: string,
    body: { status: string; expected_source_set_token: string; idempotency_key: string; comment?: string | null; waiver_reason?: string | null },
  ): Promise<{ status: string; review: CfoCloseReview }> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}/tasks/${encodeURIComponent(taskId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    )
  }

  async createCfoCloseCustomTask(
    workbookId: string,
    period: string,
    body: {
      title: string
      description?: string | null
      facet: string
      due_business_day: number
      evidence_required: boolean
      recurrence: 'one_time' | 'monthly'
      expected_source_set_token: string
      idempotency_key: string
    },
  ): Promise<{ status: string; task_key: string; review: CfoCloseReview }> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}/tasks/custom`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async dispositionCfoCloseFinding(
    workbookId: string,
    period: string,
    findingId: string,
    body: { lifecycle: string; reason: string; expected_source_set_token: string; idempotency_key: string },
  ): Promise<{ status: string; review: CfoCloseReview }> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}/findings/${encodeURIComponent(findingId)}/disposition`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async attachCfoCloseEvidence(
    workbookId: string,
    period: string,
    body: {
      document_type: string
      label: string
      content_hash: string
      content_base64?: string | null
      mime_type?: string | null
      byte_size?: number | null
      evidence_id?: string | null
      storage_uri?: string | null
      metadata?: Record<string, unknown>
      expected_source_set_token: string
      idempotency_key: string
    },
  ): Promise<{ status: string; review: CfoCloseReview }> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}/evidence`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async signOffCfoClose(
    workbookId: string,
    period: string,
    body: { role: 'preparer' | 'reviewer' | 'attester'; acknowledgement: string; expected_source_set_token: string; idempotency_key: string },
  ): Promise<{ status: string; review: CfoCloseReview }> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/${encodeURIComponent(period)}/signoffs`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async getCfoCloseHistory(workbookId: string): Promise<CfoCloseHistoryResponse> {
    return await this.request(
      `/api/cfo/workbooks/${encodeURIComponent(workbookId)}/close/history`,
      { method: 'GET' },
    )
  }

  async getAccountingArtifacts(workbookId: string): Promise<any> {
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/artifacts`, { method: 'GET' })
  }

  async getQuickBooksCfoHome(
    workbookId: string,
    opts?: GetCfoInsightsOptions,
  ): Promise<CfoHomeResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-home${suffix}`,
      { method: 'GET' },
    )
  }

  /**
   * Apply a human action to an incident.
   *
   * One method rather than five, mirroring the single server route, so the
   * audit trail cannot be forgotten on one of them. A 404 means there is no
   * open incident with that key — the caller must surface that rather than
   * reporting success, because a CEO who taps Acknowledge and sees nothing
   * change has been told a lie about their own action.
   */
  async postQuickBooksCeoIncidentAction(
    workbookId: string,
    incidentKey: string,
    action: 'acknowledge' | 'snooze' | 'assign' | 'resolve' | 'feedback',
    body?: {
      until?: string | null
      reason?: string | null
      owner_user_id?: string | null
      due_on?: string | null
      verdict?: 'useful' | 'not_useful' | 'wrong' | null
      note?: string | null
    },
  ): Promise<{ ok: boolean; incident_key: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/incidents/${encodeURIComponent(incidentKey)}/${action}`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    )
  }

  /** Set what this company is trying to move, and with what. */
  async putQuickBooksCeoCriticalInputs(
    workbookId: string,
    body: {
      outcome_metric?: string | null
      outcome_target?: number | null
      input_metric_definition_id?: string | null
      input_target?: number | null
      constraint_note?: string | null
      constraint_metric?: string | null
      materiality_usd?: number | null
      materiality_pct?: number | null
    },
  ): Promise<CeoCriticalInputs> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/critical-inputs`,
      { method: 'PUT', body: JSON.stringify(body) },
    )
  }

  /** Record one day's value for a critical input. */
  async postQuickBooksCeoPulse(
    workbookId: string,
    body: {
      metric_definition_id: string
      value: number
      period_start?: string | null
      note?: string | null
      channel?: 'web' | 'email' | 'sms' | 'slack' | 'api'
    },
  ): Promise<{ ok: boolean; period_start: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/pulse`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async getQuickBooksCeoCockpit(
    workbookId: string,
    opts?: { date?: string | null; retained?: boolean },
  ): Promise<CeoCockpitResponse> {
    const search = new URLSearchParams()
    if (opts?.date) search.set('date', opts.date)
    if (opts?.retained) search.set('retained', 'true')
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit${suffix}`,
      { method: 'GET' },
    )
  }

  async getQuickBooksCeoCockpitSnapshots(workbookId: string): Promise<CeoCockpitSnapshotIndex> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/snapshots`,
      { method: 'GET' },
    )
  }

  async startQuickBooksCeoCockpitPeriodRun(
    workbookId: string,
    asOfDate: string,
  ): Promise<CeoCockpitPeriodRun> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/period-runs`,
      { method: 'POST', body: JSON.stringify({ as_of_date: asOfDate }) },
    )
  }

  async getQuickBooksCeoCockpitPeriodRun(
    workbookId: string,
    runId: string,
  ): Promise<CeoCockpitPeriodRun> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/period-runs/${encodeURIComponent(runId)}`,
      { method: 'GET' },
    )
  }

  async rebuildQuickBooksCeoCockpit(workbookId: string): Promise<CeoCockpitResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/rebuild`,
      { method: 'POST' },
    )
  }

  // ── CEO Analysis (governed fg-dashboard/v1 surface) ────────────────────────

  /** Published dashboard head, or a retained revision when `revisionId` is given. */
  async getCeoAnalysisDashboard(
    workbookId: string,
    revisionId?: string | null,
  ): Promise<FgDashboardResponse> {
    const search = new URLSearchParams()
    if (revisionId) search.set('revision_id', revisionId)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/dashboard${suffix}`,
      { method: 'GET' },
    )
  }

  async getCeoAnalysisRevisions(
    workbookId: string,
    limit?: number | null,
  ): Promise<FgDashboardRevisionsResponse> {
    const search = new URLSearchParams()
    if (limit != null && Number.isFinite(limit)) search.set('limit', String(Math.max(1, Math.floor(limit))))
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/dashboard/revisions${suffix}`,
      { method: 'GET' },
    )
  }

  /**
   * Propose named operations against the current head. Nothing is published.
   *
   * The response carries the base revision, the candidate hash and the source
   * fingerprint that `publish` requires back verbatim — which is how a
   * concurrent edit becomes a conflict instead of an overwrite.
   */
  async postCeoAnalysisPropose(
    workbookId: string,
    body: { operations: FgDashboardOperation[]; expected_base_revision?: string | null },
  ): Promise<FgDashboardProposal> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/dashboard/propose`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async postCeoAnalysisPublish(
    workbookId: string,
    body: {
      proposal_id: string
      expected_base_revision: string
      expected_candidate_sha256: string
      expected_source_fingerprint: string
      idempotency_key: string
    },
  ): Promise<{ schema_version: string; published: boolean; revision: unknown }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/dashboard/publish`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  /**
   * Describe a view in plain English, or say how to change one on the board.
   *
   * Returns an ordinary proposal, already compiled and dry-run, plus the
   * model's message. Nothing is published: the caller previews it and then
   * publishes through `publishCeoAnalysisProposal` if it looks right, which
   * keeps every guard the manual path has.
   *
   * Pass `conversation` to iterate. Each turn sees the ones before it, so
   * "make it monthly instead" is a complete instruction.
   */
  async postCeoAnalysisAuthor(
    workbookId: string,
    body: FgDashboardAuthorRequest,
  ): Promise<FgDashboardAuthorResponse> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/dashboard/author`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  /**
   * Publish a proposal the caller is already holding.
   *
   * Split out from `applyCeoAnalysisSpecChange` because an authored change is
   * proposed once and published later, after a person has looked at it. The
   * guards are the proposal's own, so a board that moved underneath the
   * preview conflicts here rather than overwriting.
   */
  async publishCeoAnalysisProposal(
    workbookId: string,
    proposal: FgDashboardProposal,
    idempotencyKey: string,
  ): Promise<{ schema_version: string; published: boolean; revision: unknown }> {
    if (!proposal.accepted || !proposal.validation?.valid) {
      throw new Error(
        `dashboard_invalid: ${(proposal.validation?.errors || ['the change was refused']).join('; ')}`,
      )
    }
    if (proposal.dry_run && proposal.dry_run.ok === false) {
      throw new Error(
        `dashboard_invalid: the changed panels did not run — ${proposal.dry_run.error || 'dry run failed'}`,
      )
    }
    return await this.postCeoAnalysisPublish(workbookId, {
      proposal_id: proposal.proposal_id,
      expected_base_revision: proposal.base_revision_id,
      expected_candidate_sha256: proposal.candidate_sha256,
      expected_source_fingerprint: proposal.source_fingerprint,
      idempotency_key: idempotencyKey,
    })
  }

  /**
   * Propose and publish one edit, echoing the guards the proposal returned.
   *
   * A convenience over the two calls, NOT a shortcut around them: the guards
   * still come from the server's own proposal rather than from anything the
   * client believes about the head. A refused dry run throws with its reason
   * instead of publishing a spec that does not run.
   */
  async applyCeoAnalysisSpecChange(
    workbookId: string,
    operations: FgDashboardOperation[],
    idempotencyKey: string,
  ): Promise<{ schema_version: string; published: boolean; revision: unknown }> {
    const proposal = await this.postCeoAnalysisPropose(workbookId, { operations })
    if (!proposal.accepted || !proposal.validation?.valid) {
      throw new Error(
        `dashboard_invalid: ${(proposal.validation?.errors || ['the change was refused']).join('; ')}`,
      )
    }
    if (proposal.dry_run && proposal.dry_run.ok === false) {
      throw new Error(
        `dashboard_invalid: the changed panels did not run — ${proposal.dry_run.error || 'dry run failed'}`,
      )
    }
    return await this.postCeoAnalysisPublish(workbookId, {
      proposal_id: proposal.proposal_id,
      expected_base_revision: proposal.base_revision_id,
      expected_candidate_sha256: proposal.candidate_sha256,
      expected_source_fingerprint: proposal.source_fingerprint,
      idempotency_key: idempotencyKey,
    })
  }

  /**
   * The panel library, plus which slots are actually free.
   *
   * Coverage decides what is offered, so a workbook with no location dimension
   * is never shown a panel that would compile and return one anonymous row.
   */
  async getCeoAnalysisPanelTemplates(workbookId: string): Promise<FgPanelTemplatesResponse> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/panel-templates`,
      { method: 'GET' },
    )
  }

  /**
   * Expand a template into a panel spec bound to one page and slot.
   *
   * Returns the spec rather than publishing it: the caller sends it through
   * `applyCeoAnalysisSpecChange` as an `add_panel` operation, so a templated
   * panel is validated, dry-run and published exactly like one the assistant
   * proposed. No second path to the board.
   */
  async postCeoAnalysisPanelFromTemplate(
    workbookId: string,
    body: { template_id: string; page_id: string; slot_id: string },
  ): Promise<{ schema_version: string; panel: unknown }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/panel-templates/build`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  // ── Metrics ────────────────────────────────────────────────────────────────
  //
  // Each of these hits a route that delegates to the same tool the assistant
  // calls, so the UI and the assistant cannot drift in what they may do.

  /** Every metric this workbook can measure: the built-ins plus its own. */
  async getCeoAnalysisMetrics(workbookId: string): Promise<FgMetricsResponse> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/metrics`,
      { method: 'GET' },
    )
  }

  /**
   * Define or redefine one metric.
   *
   * The server validates against the registry this metric would JOIN, so a
   * ratio naming a metric that does not exist, one that would close a cycle,
   * or a gl_expression outside the panel-SQL allowlist all come back as 422
   * with the reason, not as a silent partial write.
   */
  async postCeoAnalysisMetric(
    workbookId: string,
    definition: Partial<FgMetricDefinition> & { metric_key: string; label: string; kind: FgMetricKind },
  ): Promise<{ schema_version: string; defined: string; targetable: boolean }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/metrics`,
      { method: 'POST', body: JSON.stringify(definition) },
    )
  }

  /** Refused with 409 while another metric still reads it. */
  async deleteCeoAnalysisMetric(
    workbookId: string,
    metricKey: string,
  ): Promise<{ schema_version: string; deleted: string }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/metrics/${encodeURIComponent(metricKey)}`,
      { method: 'DELETE' },
    )
  }

  /** What is already tracked, newest period first. */
  async getCeoAnalysisOperationalValues(
    workbookId: string,
    opts?: { measureKey?: string | null; limit?: number | null },
  ): Promise<FgOperationalValuesResponse> {
    const search = new URLSearchParams()
    if (opts?.measureKey) search.set('measure_key', opts.measureKey)
    if (opts?.limit != null && Number.isFinite(opts.limit)) {
      search.set('limit', String(Math.max(1, Math.floor(opts.limit))))
    }
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/metrics/values${suffix}`,
      { method: 'GET' },
    )
  }

  /**
   * Record tracked numbers. Upserts on the natural key, so re-sending a period
   * corrects it rather than duplicating it.
   *
   * The whole batch is validated before any of it is written, and refusals name
   * the row index — a two-hundred-row paste with one bad cell is otherwise
   * unfixable.
   */
  async postCeoAnalysisOperationalValues(
    workbookId: string,
    values: FgOperationalValue[],
  ): Promise<{ schema_version: string; rows_written: number }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/metrics/values`,
      { method: 'POST', body: JSON.stringify(values) },
    )
  }

  async postCeoAnalysisRollback(
    workbookId: string,
    body: { target_revision_id: string; idempotency_key: string },
  ): Promise<FgRollbackResponse> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/dashboard/rollback`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  /**
   * Save operating targets for one panel's scope.
   *
   * The body is the wire snapshot from `toWireSnapshot` — it carries an
   * explicit member set and period window, which is what scopes the server's
   * reconciling delete. Sending the cells alone would let a save made under a
   * cross-filter delete targets for every member the user could not see.
   *
   * The response's `refresh` block reports whether the governed artifact has
   * caught up yet; a save is not lost just because it has not.
   */
  async postCeoAnalysisTargets(
    workbookId: string,
    body: unknown,
  ): Promise<{
    saved: number
    deleted: number
    cells_sha256: string
    refresh?: { status: string; message?: string | null } | null
  }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/targets`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async getCeoAnalysisTargetHistory(
    workbookId: string,
    limit = 50,
  ): Promise<{ revisions: unknown[] }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/targets/history?limit=${encodeURIComponent(String(limit))}`,
      { method: 'GET' },
    )
  }

  async postCeoAnalysisTargetRollback(
    workbookId: string,
    body: { revision_id?: string; before?: string },
  ): Promise<{ saved: number; deleted: number }> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/targets/rollback`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async getCeoAnalysisManifest(workbookId: string): Promise<FgArtifactManifest> {
    return await this.request(
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/extract/manifest`,
      { method: 'GET' },
    )
  }

  /**
   * Authorized absolute URL for one artifact parquet part. The access token
   * rides as `?token=` because the fetch happens inside the analysis worker,
   * where attaching the Authorization header is awkward (the server accepts
   * both). Mirrors `workbookWebSocketUrl`'s token handling.
   */
  ceoAnalysisPartUrl(workbookId: string, relation: string, index: number): string {
    this.syncSessionTokensFromStorage()
    const tok = this.token ? String(this.token).trim() : ''
    const qp = tok ? `?token=${encodeURIComponent(tok)}` : ''
    return joinUrl(
      this.baseUrl,
      `/api/analysis/workbooks/${encodeURIComponent(workbookId)}/extract/parts/${encodeURIComponent(relation)}/${encodeURIComponent(String(index))}${qp}`,
    )
  }

  async getQuickBooksCeoCockpitSettings(workbookId: string): Promise<CeoCockpitSettings> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/settings`,
      { method: 'GET' },
    )
  }

  async putQuickBooksCeoCockpitSettings(
    workbookId: string,
    settings: Partial<CeoCockpitSettings>,
  ): Promise<CeoCockpitSettings> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ceo-cockpit/settings`,
      { method: 'PUT', body: JSON.stringify(settings) },
    )
  }

  async getDemoCompanies(): Promise<DemoCompaniesResponse> {
    return await this.request('/api/marketing/demo-companies', { method: 'GET' })
  }

  async getCfoHome(
    workbookId: string,
    opts?: GetCfoInsightsOptions,
  ): Promise<CfoHomeResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksCfoHome(workbookId, opts)
    }
    const [comparisons, insights, ar, ratios, forecast, artifacts] = await Promise.all([
      this.getCfoMetricComparisons(workbookId, {
        ...opts,
        metrics: ['revenue', 'gross_profit', 'net_income', 'cash'],
        comparisons: ['mom', 'l3m_vs_prior3m', 'ytd', 'yoy'],
      }),
      this.getCfoInsights(workbookId, opts),
      this.getCfoArAnalysis(workbookId, opts),
      this.getCfoRatios(workbookId, { asOf: opts?.asOf, grain: 'month' }),
      this.getAccountingForecast(workbookId).catch(() => ({ rows: [] })),
      this.getAccountingArtifacts(workbookId).catch(() => ({ artifacts: [] })),
    ])
    const severity = (delta: number | null | undefined) =>
      delta == null || Math.abs(delta) < 0.005 ? 'neutral' : delta > 0 ? 'good' : 'watch'
    const kpiTiles = comparisons.metrics.map((metric) => {
      const comparison = metric.comparisons.find((item) => item.kind === 'mom') || metric.comparisons[0]
      return {
        id: metric.id,
        label: metric.label,
        value_kind: metric.value_kind,
        latest_value: metric.latest_value,
        latest_pct_of_sales: metric.latest_pct_of_sales,
        comparison_kind: comparison?.kind || null,
        delta_abs: comparison?.delta_abs ?? null,
        delta_pct: comparison?.delta_pct ?? null,
        severity: severity(comparison?.delta_abs),
      }
    })
    const forecastRows = Array.isArray(forecast?.rows) ? forecast.rows : []
    const artifactRows = Array.isArray(artifacts?.artifacts) ? artifacts.artifacts : []
    return {
      workbook_id: workbookId,
      connection_id: bootstrap.accounting_company_id,
      realm_id: bootstrap.accounting_company_id,
      company_name: bootstrap.accounting_company_name || null,
      as_of: comparisons.as_of,
      contract: ar.contract,
      latest_sync_at: bootstrap.last_sync_at || null,
      kpi_tiles: kpiTiles,
      alerts: insights.cards,
      attention_count: insights.attention_count,
      comparisons,
      ar_summary: ar,
      forecast_status: {
        package_id: forecastRows.length ? `canonical-forecast:${bootstrap.accounting_company_id}` : null,
        status: forecastRows.length ? 'ready' : 'not_ready',
        active: forecastRows.length > 0,
        updated_at: bootstrap.last_sync_at || null,
      },
      artifact_status: {
        ready: artifactRows.some((artifact: any) => artifact?.status === 'ready'),
        status: artifactRows.some((artifact: any) => artifact?.status === 'ready') ? 'ready' : 'not_ready',
        artifact_count: artifactRows.length,
        query_engines: [],
      },
      health: ratios.health || null,
    }
  }

  async getPlatformClientContext(
    workbookId: string,
    opts?: { asOf?: string; sourceVersion?: string },
  ): Promise<any> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.sourceVersion) search.set('source_version', opts.sourceVersion)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/context${suffix}`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  /** Immutable company intelligence shared by website, Excel, and MCP AI. */
  async getCompanyIntelligence(
    workbookId: string,
    opts?: { asOf?: string; sourceVersion?: string },
  ): Promise<any> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.sourceVersion) search.set('source_version', opts.sourceVersion)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence${suffix}`,
      { method: 'GET' },
      { timeoutMs: 10_000 },
    )
  }

  async getCompanyIntelligenceEvidence(workbookId: string, evidenceId: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/evidence/${encodeURIComponent(evidenceId)}`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  async getCompanyIntelligenceStatus(workbookId: string): Promise<CompanyIntelligenceStatusResponse> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/status`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  async refreshCompanyIntelligence(workbookId: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/refresh`,
      { method: 'POST', body: '{}' },
      { timeoutMs: 5_000 },
    )
  }

  /** Complete governed statement hierarchy shared by website, Excel, and MCP. */
  async getGovernedStatementHierarchy(workbookId: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/statement-hierarchy`,
      { method: 'GET' },
      { timeoutMs: 10_000 },
    )
  }

  async getGovernedFinancialStatements(
    workbookId: string,
    opts?: { start?: string; end?: string },
  ): Promise<any> {
    const search = new URLSearchParams()
    if (opts?.start) search.set('start', opts.start)
    if (opts?.end) search.set('end', opts.end)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/financial-statements${suffix}`,
      { method: 'GET' },
      { timeoutMs: 15_000 },
    )
  }

  async createManagedPlanImport(workbookId: string, request: unknown): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plan-imports`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 20_000 },
    )
  }

  async getManagedPlanImport(workbookId: string, runId: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plan-imports/${encodeURIComponent(runId)}`,
      { method: 'GET' },
      { timeoutMs: 10_000 },
    )
  }

  async applyManagedPlanImportRepairs(workbookId: string, runId: string, request: unknown): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plan-imports/${encodeURIComponent(runId)}/repairs`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 15_000 },
    )
  }

  async recordManagedPlanImportDecisions(workbookId: string, runId: string, request: unknown): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plan-imports/${encodeURIComponent(runId)}/decisions`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 15_000 },
    )
  }

  async publishManagedPlanImport(workbookId: string, runId: string, request: unknown): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plan-imports/${encodeURIComponent(runId)}/publish`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 20_000 },
    )
  }

  async getManagedPlan(workbookId: string, kind: 'budget' | 'forecast', scenario: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(kind)}/${encodeURIComponent(scenario)}`,
      { method: 'GET' },
      { timeoutMs: 10_000 },
    )
  }

  async undoManagedPlan(
    workbookId: string,
    kind: 'budget' | 'forecast',
    scenario: string,
    expectedPublicationToken: string,
  ): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(kind)}/${encodeURIComponent(scenario)}/undo`,
      { method: 'POST', body: JSON.stringify({ expected_publication_token: expectedPublicationToken }) },
      { timeoutMs: 15_000 },
    )
  }

  async getGovernedMlForecast(workbookId: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/ml-forecast`,
      { method: 'GET' },
      { timeoutMs: 10_000 },
    )
  }

  async createManagedPlanFromMl(workbookId: string, request: unknown): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plans/from-ml`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 20_000 },
    )
  }

  async fillManagedPlanFromMl(workbookId: string, planId: string, request: unknown): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/fill-from-ml`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 20_000 },
    )
  }

  async getGovernedPlanVsActual(
    workbookId: string,
    opts: { kind: 'budget' | 'forecast'; scenario: string; start?: string; end?: string },
  ): Promise<any> {
    const search = new URLSearchParams({ kind: opts.kind, scenario: opts.scenario })
    if (opts.start) search.set('start', opts.start)
    if (opts.end) search.set('end', opts.end)
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/plan-vs-actual?${search.toString()}`,
      { method: 'GET' },
      { timeoutMs: 20_000 },
    )
  }

  async listCompanyIntelligenceContextItems(
    workbookId: string,
    opts?: { conversationRunId?: string | null },
  ): Promise<CompanyIntelligenceContextItemsResponse> {
    const search = new URLSearchParams()
    if (opts?.conversationRunId) search.set('conversation_run_id', opts.conversationRunId)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/context-items${suffix}`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  async createCompanyIntelligenceContextItem(
    workbookId: string,
    request: CompanyIntelligenceContextItemRequest,
  ): Promise<CompanyIntelligenceContextItemMutationResponse> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/context-items`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 10_000 },
    )
  }

  async reviewCompanyIntelligenceContextItem(
    workbookId: string,
    contextItemId: string,
    action: 'confirm' | 'reject' | 'expire',
    opts?: { conversationRunId?: string | null },
  ): Promise<CompanyIntelligenceContextItemMutationResponse> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/context-items/${encodeURIComponent(contextItemId)}`,
      { method: 'PATCH', body: JSON.stringify({ action, conversation_run_id: opts?.conversationRunId || undefined }) },
      { timeoutMs: 10_000 },
    )
  }

  async supersedeCompanyIntelligenceContextItem(
    workbookId: string,
    contextItemId: string,
    request: { value: any; item_type?: string | null; expires_at?: string | null; conversation_run_id?: string | null },
  ): Promise<CompanyIntelligenceContextItemMutationResponse> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/context-items/${encodeURIComponent(contextItemId)}`,
      { method: 'PATCH', body: JSON.stringify({ action: 'supersede', ...request }) },
      { timeoutMs: 10_000 },
    )
  }

  async listReleasedAnalyticalRecipes(workbookId: string): Promise<any> {
    return await this.request(
      `/api/platform/v1/workbooks/${encodeURIComponent(workbookId)}/intelligence/recipes`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  async createFinancialWorkRun(request: CreateFinancialWorkRunRequest): Promise<any> {
    return await this.request(
      '/api/ai/financial-work-runs',
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 10_000 },
    )
  }

  async getFinancialWorkRun(runId: string): Promise<any> {
    return await this.request(
      `/api/ai/financial-work-runs/${encodeURIComponent(runId)}`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  async inspectFinancialWorkRun(
    runId: string,
    request: FinancialWorkInspectionRequest,
  ): Promise<any> {
    return await this.request(
      `/api/ai/financial-work-runs/${encodeURIComponent(runId)}/workbook-inspection`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 20_000 },
    )
  }

  async submitFinancialWorkTurn(runId: string, request: FinancialWorkTurnRequest): Promise<any> {
    return await this.request(
      `/api/ai/financial-work-runs/${encodeURIComponent(runId)}/turns`,
      { method: 'POST', body: JSON.stringify(request) },
      { timeoutMs: 15_000 },
    )
  }

  async getFinancialWorkEvidence(runId: string, evidenceId: string): Promise<any> {
    return await this.request(
      `/api/ai/financial-work-runs/${encodeURIComponent(runId)}/evidence/${encodeURIComponent(evidenceId)}`,
      { method: 'GET' },
      { timeoutMs: 5_000 },
    )
  }

  async getFinancialWorkTranscript(runId: string): Promise<any> {
    return await this.request(
      `/api/ai/financial-work-runs/${encodeURIComponent(runId)}/transcript`,
      { method: 'GET' },
      { timeoutMs: 10_000 },
    )
  }

  async invokePlatformCapability(
    capabilityId: string,
    workbookId: string,
    input: Record<string, any>,
  ): Promise<any> {
    return await this.request(
      `/api/platform/v1/capabilities/${encodeURIComponent(capabilityId)}/invoke`,
      {
        method: 'POST',
        body: JSON.stringify({ workbook_id: workbookId, input }),
      },
      { timeoutMs: 5_000 },
    )
  }

  async getClientOperatingWorkspace(workbookId: string): Promise<any> {
    return await this.invokePlatformCapability('get_client_workspace', workbookId, {})
  }

  async manageClientOperatingWorkspace(
    capabilityId: 'record_goal_check_in' | 'manage_goal' | 'manage_action_item' | 'publish_client_workspace' | 'deliver_workspace_update',
    workbookId: string,
    input: Record<string, any>,
  ): Promise<any> {
    return await this.invokePlatformCapability(capabilityId, workbookId, input)
  }

  async getQuickBooksCfoStatementsRender(
    workbookId: string,
    opts?: GetCfoStatementsRenderOptions,
  ): Promise<QuickBooksCfoStatementsRenderContractResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    if (opts?.months != null) search.set('months', String(opts.months))
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/statements/render${suffix}`,
      { method: 'GET' },
    )
  }

  /** Shared CFO statement contract. QuickBooks keeps its mature renderer while
   * every other provider is projected from the governed accounting facts. */
  async getCfoStatementsRender(
    workbookId: string,
    opts?: GetCfoStatementsRenderOptions & { knownProvider?: string | null },
  ): Promise<QuickBooksCfoStatementsRenderContractResponse> {
    // Firm-cockpit rows already carry the provider. The bridge only needs the
    // governed statement contract, so do not put a bootstrap round-trip in
    // front of the mature QuickBooks renderer when that identity is known.
    if (opts?.knownProvider === 'quickbooks') {
      return await this.getQuickBooksCfoStatementsRender(workbookId, opts)
    }
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksCfoStatementsRender(workbookId, opts)
    }
    const [profitAndLoss, balanceSheet, cashFlow] = await Promise.all([
      this.getAccountingStatements(workbookId, 'profit_and_loss'),
      this.getAccountingStatements(workbookId, 'balance_sheet'),
      this.getAccountingStatements(workbookId, 'cash_flow'),
    ])
    return accountingStatementsToRenderContract(bootstrap, {
      profit_and_loss: profitAndLoss,
      balance_sheet: balanceSheet,
      cash_flow: cashFlow,
    }, opts)
  }

  /** Shared live board-report contract. Non-QuickBooks providers use the same
   * presentation renderer over reconciled canonical statements and A/R. */
  async getCfoPresentation(
    workbookId: string,
    opts?: { includeArAnalysis?: boolean; knownProvider?: string | null },
  ): Promise<QuickBooksCfoPresentationResponse> {
    // Firm-cockpit rows already carry the provider. Avoid a bootstrap request
    // before the mature QuickBooks presentation call when that identity is
    // known, while preserving the authoritative bootstrap for projections that
    // need company/layout metadata to assemble a provider-neutral response.
    if (opts?.knownProvider === 'quickbooks') {
      return await this.getQuickBooksCfoPresentation(workbookId, opts)
    }
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksCfoPresentation(workbookId, opts)
    }
    const arPromise: Promise<ArAnalysisResponse | null> = opts?.includeArAnalysis === false
      ? Promise.resolve(null)
      : this.getCfoArAnalysis(workbookId)
    const [statements, ar, insights, comparisons] = await Promise.all([
      this.getCfoStatementsRender(workbookId),
      arPromise,
      this.getCfoInsights(workbookId),
      this.getCfoMetricComparisons(workbookId, {
        metrics: ['revenue', 'gross_profit', 'net_income', 'cash'],
        comparisons: ['mom', 'l3m_vs_prior3m', 'ytd', 'yoy'],
      }),
    ])
    return accountingStatementsToPresentation(bootstrap, statements, ar, insights, comparisons)
  }

  private reportRevisionQuery(workbookId: string, hostWorkbookInstanceId: string): string {
    return new URLSearchParams({
      workbook_id: workbookId,
      host_workbook_instance_id: hostWorkbookInstanceId,
    }).toString()
  }

  async getReportRevisionHead(workbookId: string, hostWorkbookInstanceId: string): Promise<ReportRevisionRecord | null> {
    const response = await this.request<any>(
      `/api/platform/v1/data-cloud/report-revisions/head?${this.reportRevisionQuery(workbookId, hostWorkbookInstanceId)}`,
      { method: 'GET' },
    )
    return (response?.data ?? response)?.head ?? null
  }

  async getReportRevision(workbookId: string, hostWorkbookInstanceId: string, revisionId: string): Promise<ReportRevisionRecord> {
    const response = await this.request<any>(
      `/api/platform/v1/data-cloud/report-revisions/${encodeURIComponent(revisionId)}?${this.reportRevisionQuery(workbookId, hostWorkbookInstanceId)}`,
      { method: 'GET' },
    )
    return response?.data ?? response
  }

  /** 409 `report_revision_conflict` when `parent_revision` is not the head. */
  async createReportRevision(req: {
    workbook_id: string
    host_workbook_instance_id: string
    parent_revision: number | null
    authored_via: ReportRevisionAuthor
    change_summary: string
    composition: ReportCompositionRevision
  }): Promise<ReportRevisionRecord> {
    const response = await this.request<any>('/api/platform/v1/data-cloud/report-revisions', {
      method: 'POST',
      body: JSON.stringify(req),
    })
    return response?.data ?? response
  }

  async undoReportRevision(workbookId: string, hostWorkbookInstanceId: string, expectedRevision: number): Promise<ReportRevisionRecord> {
    const response = await this.request<any>('/api/platform/v1/data-cloud/report-revisions/undo', {
      method: 'POST',
      body: JSON.stringify({
        workbook_id: workbookId,
        host_workbook_instance_id: hostWorkbookInstanceId,
        expected_revision: expectedRevision,
      }),
    })
    return response?.data ?? response
  }

  async proposeReportStudioAiEdit(req: {
    workbook_id: string
    host_workbook_instance_id: string
    expected_revision: number
    message: string
    target_section_id?: string | null
  }): Promise<ReportStudioAiEditProposal> {
    const body: Record<string, unknown> = {
      workbook_id: req.workbook_id,
      host_workbook_instance_id: req.host_workbook_instance_id,
      expected_revision: req.expected_revision,
      message: req.message,
    }
    if (req.target_section_id) body.target_section_id = req.target_section_id
    const response = await this.request<any>('/api/platform/v1/data-cloud/report-revisions/ai-edit', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return response?.data ?? response
  }

  /** Enqueues `apply_report_draft` for the revision; the owner approves it in Excel. */
  async applyReportRevision(workbookId: string, hostWorkbookInstanceId: string, revisionId: string): Promise<{ operation: any }> {
    const response = await this.request<any>(
      `/api/platform/v1/data-cloud/report-revisions/${encodeURIComponent(revisionId)}/apply`,
      {
        method: 'POST',
        body: JSON.stringify({ workbook_id: workbookId, host_workbook_instance_id: hostWorkbookInstanceId }),
      },
    )
    return response?.data ?? response
  }

  async getCfoPresentationArAnalysis(
    workbookId: string,
  ): Promise<QuickBooksCfoPresentationArAnalysisResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksCfoPresentationArAnalysis(workbookId)
    }
    const ar = await this.getCfoArAnalysis(workbookId)
    return { workbook_id: workbookId, ar_analysis: ar.ar_analysis || null, warnings: ar.warnings || [] }
  }

  /**
   * Whether the workbook's AI analysis (forecast + understanding + close) is out of date relative to
   * the latest closed data month, and the live progress of any in-flight on-demand refresh. Drives the
   * "your analysis is out of date" banner + progress bar on the CFO home.
   */
  async getAiRefreshStatus(
    workbookId: string,
  ): Promise<AiRefreshStatusResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ai-refresh/status`,
      { method: 'GET' },
    )
  }

  /**
   * Trigger an on-demand refresh of the full AI suite (forecast + understanding + month-end close) for
   * a workbook. Idempotent: returns the existing job if one is already running. Poll
   * {@link getAiRefreshStatus} to drive the progress bar.
   */
  async triggerAiRefresh(
    workbookId: string,
  ): Promise<AiRefreshTriggerResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ai-refresh`,
      { method: 'POST' },
    )
  }

  async getCfoMaterializationStatus(
    workbookId: string,
    requestedPeriod?: string,
  ): Promise<CfoMaterializationStatusResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider !== 'quickbooks') {
      const period = bootstrap.default_period || bootstrap.available_periods?.at(-1) || null
      const fingerprint = `${bootstrap.accounting_provider}:${bootstrap.accounting_company_id}:${period || 'empty'}`
      const surfaces = Object.fromEntries(Object.entries(bootstrap.capabilities || {}).map(([key, enabled]) => [
        key,
        { status: enabled ? 'ready' : 'unavailable', stale_reasons: enabled ? [] : ['capability_unavailable'] },
      ]))
      return {
        status: bootstrap.accounting_connection_status === 'active' ? 'ready' : bootstrap.accounting_connection_status || 'blocked',
        serving: { mode: 'current', as_of_period: period, materialized_at: bootstrap.last_sync_at || null, source_fingerprint: fingerprint },
        latest: { closed_period: period, source_fingerprint: fingerprint, last_qbo_sync_at: bootstrap.last_sync_at || null },
        stale_reasons: [],
        refresh: { run_id: null, status: null, stage_key: null, stage_label: null, pct: null, job_ids: {} },
        surfaces,
        connection: {
          status: bootstrap.accounting_connection_status || 'active',
          needs_reconnect: bootstrap.accounting_connection_status === 'needs_reconnect',
          company_name: bootstrap.accounting_company_name || null,
          last_sync_at: bootstrap.last_sync_at || null,
          realm_id: bootstrap.accounting_company_id,
          reconnect_url: null,
        },
      }
    }
    const query = requestedPeriod ? `?requested_period=${encodeURIComponent(requestedPeriod)}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/materialization/status${query}`,
      { method: 'GET' },
    )
  }

  async getQuickBooksCfoArtifactCatalog(
    workbookId: string,
  ): Promise<QuickBooksCfoArtifactCatalogResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/artifact-catalog`,
      { method: 'GET' },
    )
  }

  /**
   * Set up CFO reports for an unbound QuickBooks workbook whose accounts are
   * synced (editors only). Binds the statement layout from the committed
   * account extract; it starts no report update by itself.
   */
  async setUpCfoReports(workbookId: string): Promise<CfoBindingSetupResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-core/binding-setup`,
      { method: 'POST' },
    )
  }

  async ensureCfoMaterialization(
    workbookId: string,
    requestedPeriod?: string,
  ): Promise<CfoMaterializationStatusResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider !== 'quickbooks') {
      return await this.getCfoMaterializationStatus(workbookId)
    }
    const query = requestedPeriod ? `?requested_period=${encodeURIComponent(requestedPeriod)}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/materialization/ensure${query}`,
      { method: 'POST' },
    )
  }

  /**
   * Lightweight A/R summary (total open receivable + DSO) for the calm A/R &
   * collectability page hero, computed server-side from the canonical comparison
   * engine. The full customer aging lives in the embedded legacy A/R panels.
   */
  async getQuickBooksArAnalysis(
    workbookId: string,
    opts?: GetArAnalysisOptions,
  ): Promise<ArAnalysisResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    if (opts?.responseMode) search.set('response_mode', opts.responseMode)
    if (opts?.artifactId) search.set('artifact_id', opts.artifactId)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ar-analysis${suffix}`,
      { method: 'GET' },
    )
  }

  /** Provider-neutral A/R contract backed by the canonical open-receivable fact. */
  async getCfoArAnalysis(
    workbookId: string,
    opts?: GetArAnalysisOptions,
  ): Promise<ArAnalysisResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksArAnalysis(workbookId, opts)
    }
    const receivables = await this.getAccountingReceivables(workbookId)
    return accountingReceivablesToArAnalysis(bootstrap, receivables, opts)
  }

  async getCurrentQuickBooksGl(
    workbookId: string,
    opts: GetCurrentQuickBooksGlOptions = {},
  ): Promise<CurrentQuickBooksGlPage> {
    const query = new URLSearchParams()
    if (opts.start) query.set('start', opts.start)
    if (opts.end) query.set('end', opts.end)
    if (opts.accountId) query.set('account_id', opts.accountId)
    if (opts.cursor) query.set('cursor', opts.cursor)
    if (opts.expectedOrigin) query.set('expected_origin', JSON.stringify(opts.expectedOrigin))
    if (opts.limit != null) query.set('limit', String(opts.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/gl-current${suffix}`,
      { method: 'GET', signal: opts.signal },
    )
  }

  async getQuickBooksGlSummary(
    workbookId: string,
    opts?: GetQuickBooksGlSummaryOptions,
  ): Promise<QuickBooksGlSummaryResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.statementType) search.set('statement_type', opts.statementType)
    if (opts?.layoutNodeId) search.set('layout_node_id', opts.layoutNodeId)
    if (opts?.accountId) search.set('account_id', opts.accountId)
    if (opts?.search) search.set('search', opts.search)
    if (Number.isFinite(opts?.limit))
      search.set('limit', String(Math.max(1, Math.floor(Number(opts?.limit)))))
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/gl-summary${suffix}`,
      {
        method: 'GET',
        signal: opts?.signal ?? undefined,
      },
    )
  }

  /** Provider-neutral bounded GL browser contract. */
  async getCfoGlSummary(
    workbookId: string,
    opts?: GetQuickBooksGlSummaryOptions,
  ): Promise<QuickBooksGlSummaryResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksGlSummary(workbookId, opts)
    }
    const ledger = await this.getAccountingGeneralLedger(workbookId, opts?.signal)
    return accountingLedgerToGlSummary(bootstrap, ledger, opts)
  }

  /**
   * Slide-deck export for the Presentations page: a structured deck outline
   * (cover + one slide per enabled report block) built from the same curated
   * presentation config the HTML/PDF export uses. Additive — the HTML/PDF paths
   * are unchanged.
   */
  async getQuickBooksCfoPresentationSlideDeck(
    workbookId: string,
  ): Promise<SlideDeckResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/slide-deck`,
      { method: 'GET' },
    )
  }

  /**
   * Fetch the categorized CFO ratio library (liquidity / leverage / efficiency
   * / profitability / coverage) for a workbook's bound statement layout. Each
   * ratio carries a monthly trend, period comparisons, DuPont/CCC
   * decompositions where relevant, and a plain-language interpretation. Backed
   * by the shared statement + metric layer in fpa.rs.
   */
  async getQuickBooksRatios(
    workbookId: string,
    opts?: GetRatiosOptions,
  ): Promise<RatioAnalysisResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/ratios${suffix}`,
      { method: 'GET' },
    )
  }

  async getCfoRatios(
    workbookId: string,
    opts?: GetRatiosOptions,
  ): Promise<RatioAnalysisResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksRatios(workbookId, opts)
    }
    const [profitAndLoss, balanceSheet, receivables] = await Promise.all([
      this.getAccountingStatements(workbookId, 'profit_and_loss'),
      this.getAccountingStatements(workbookId, 'balance_sheet'),
      this.getAccountingReceivables(workbookId).catch(() => null),
    ])
    return accountingStatementsToRatios(
      bootstrap,
      { profit_and_loss: profitAndLoss, balance_sheet: balanceSheet },
      receivables,
      opts?.asOf,
    )
  }

  /**
   * Fetch the active Client Understanding belief ledger for a workbook (plan Part 4): the
   * AI-diagnosed `forecast.*` + `metric.*` directives that personalize both the forecast and the
   * ratios. Read-only; an empty `claims` list is the normal "still learning your business" state.
   * Map the raw wire claims to the UI shape with `mapServerClaim` from `cfoUnderstanding.ts`.
   */
  async getQuickBooksUnderstanding(
    workbookId: string,
  ): Promise<UnderstandingLedgerResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/understanding`,
      { method: 'GET' },
    )
  }

  /** User AGREES with a belief (B3): stamps user_confirmed + promotes a gated forecast claim. */
  async confirmUnderstandingClaim(
    workbookId: string,
    claimId: string,
  ): Promise<{ updated: number }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/understanding/claims/${encodeURIComponent(claimId)}/confirm`,
      { method: 'POST' },
    )
  }

  /** User REJECTS a belief (B3). */
  async rejectUnderstandingClaim(
    workbookId: string,
    claimId: string,
  ): Promise<{ updated: number }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/understanding/claims/${encodeURIComponent(claimId)}/reject`,
      { method: 'POST' },
    )
  }

  /** ACCEPT (or re-open) one or more assumptions in bulk. Presentational: claims stay active and keep
   * driving the forecast/ratios; the panel collapses accepted ones behind a "Show accepted" toggle. */
  async ackUnderstandingClaims(
    workbookId: string,
    claimIds: string[],
    acknowledged: boolean,
  ): Promise<{ updated: number; acknowledged: boolean }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/understanding/claims/ack`,
      {
        method: 'POST',
        body: JSON.stringify({ claim_ids: claimIds, acknowledged }),
      },
    )
  }

  /** One-click REVERT of a superseded fact group (B3). */
  async revertUnderstandingOrigin(
    workbookId: string,
    originId: string,
  ): Promise<{ reactivated: number }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/understanding/origins/${encodeURIComponent(originId)}/revert`,
      { method: 'POST' },
    )
  }

  /** REDIRECT a belief (B3 / Part 4): re-authors the fact across both lenses via the refine pass. */
  async redirectUnderstanding(
    workbookId: string,
    originId: string,
    assertionText: string,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/understanding/redirect`,
      {
        method: 'POST',
        body: JSON.stringify({
          origin_id: originId,
          assertion_text: assertionText,
        }),
      },
    )
  }

  /** Save a user override of the composite-health category weights (keys: category names, sum ~1). */
  async saveHealthCategoryWeights(
    workbookId: string,
    weights: Record<string, number>,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/health/category-weights`,
      { method: 'POST', body: JSON.stringify({ weights }) },
    )
  }

  /** Save a user override of ONE category's per-metric weights (metric_id → relative weight; 0 excludes
   *  the metric from the score). Merged server-side into the other categories' weights. */
  async saveHealthRatioWeights(
    workbookId: string,
    ratioWeights: Record<string, number>,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/health/category-weights`,
      { method: 'POST', body: JSON.stringify({ ratio_weights: ratioWeights }) },
    )
  }

  /** Set per-company CFO display preferences (health letter grades, 13-week
   * min-cash floor). Partial update: omitted fields keep their stored values;
   * `clear_min_cash_floor: true` removes the floor. The boolean shorthand keeps
   * existing callers (health-grade toggle) unchanged. */
  async setCfoDisplayPrefs(
    workbookId: string,
    prefs:
      | boolean
      | {
          show_health_grades?: boolean
          native_write_fence?: NativeCashWriteFence
          min_cash_floor?: number | string | null
          min_cash_rule_kind?: string
          min_cash_rule_param?: number | string
          credit_line_limit?: number | string
          cash_reserve_amount?: number | string
          credit_line_drawn?: number | string
          credit_line_drawn_at?: string
          clear_min_cash_floor?: boolean
        },
  ): Promise<{
    status: string
    show_health_grades?: boolean | null
    min_cash_floor?: number | string | null
  }> {
    const body =
      typeof prefs === 'boolean' ? { show_health_grades: prefs } : prefs
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo/display-prefs`,
      { method: 'PUT', body: JSON.stringify(cashAuthoringBody(body, ['min_cash_floor', 'min_cash_rule_param', 'credit_line_limit', 'cash_reserve_amount', 'credit_line_drawn'])) },
    )
  }

  /** Key this week's bank balance, immediately before producing the forecast. */
  async putCashBankObservation(
    workbookId: string,
    observation: {
      balance_date: string
      bank_balance: number | string
      native_write_fence?: NativeCashWriteFence
      account_scope?: string | null
      note?: string | null
    },
  ): Promise<{ status: string; id?: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/bank-observations`,
      { method: 'PUT', body: JSON.stringify(cashAuthoringBody(observation, ['bank_balance'])) },
    )
  }

  // --- Month-end close (AI close module) ---

  /** List closed periods that have a close report, with status. */
  async getQuickBooksCloseablePeriods(workbookId: string): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/close/periods`,
      { method: 'GET' },
    )
  }

  /** Full structured close report (report_json + digest_json) for a period (YYYY-MM-DD). */
  async getQuickBooksCloseReport(
    workbookId: string,
    period: string,
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/close/report/${encodeURIComponent(period)}`,
      { method: 'GET' },
    )
  }

  /** Enqueue an on-demand month-end close run for the workbook. */
  async runQuickBooksClose(
    workbookId: string,
  ): Promise<{ status: string; job_id?: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/close/run`,
      { method: 'POST' },
    )
  }

  /** Answer an open close question (records a confirmed belief + supersedes the question). */
  async answerQuickBooksCloseQuestion(
    workbookId: string,
    claimId: string,
    answer: string,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/close/answer`,
      { method: 'POST', body: JSON.stringify({ claim_id: claimId, answer }) },
    )
  }

  /** Attest (mark reviewed) a month-end close period. */
  async attestQuickBooksClose(
    workbookId: string,
    period: string,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/close/report/${encodeURIComponent(period)}/attest`,
      { method: 'POST' },
    )
  }

  /** On-view history self-correction: re-review a previously-closed month if its books changed since
   * the stored AI review. Returns `unchanged` when the books still match, `regenerating` when a
   * rewrite was kicked off (poll the annotations `generated_at`), or `no_report` / `attested` /
   * `already_running` / `over_budget`. */
  async refreshQuickBooksCloseReview(
    workbookId: string,
    period: string,
  ): Promise<{ status?: string }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/close/report/${encodeURIComponent(period)}/refresh`,
      { method: 'POST' },
    )
  }

  /** AI headline + per-line flags for one statement of a closed period (drives the Financials
   * headline band + QuickBooks-style per-line AI flags). */
  async getQuickBooksFinancialsAiAnnotations(
    workbookId: string,
    opts: {
      statement: string
      period?: string | null
      layoutVersionId?: string | null
    },
  ): Promise<any> {
    const params = new URLSearchParams({ statement: opts.statement })
    if (opts.period) params.set('period', opts.period)
    if (opts.layoutVersionId)
      params.set('layout_version_id', opts.layoutVersionId)
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/financials/ai-annotations?${params.toString()}`,
      { method: 'GET' },
    )
  }

  /** Confirm or correct an AI flag on a statement line — saves the per-line note (round-trips) and
   * authors closed-loop forecast/metric directives. */
  async answerQuickBooksFinancialsFlag(
    workbookId: string,
    body: {
      node_id: string
      statement: string
      period: string
      label?: string
      finding?: string
      mode: 'confirm' | 'correct'
      text?: string
      claim_id?: string | null
    },
  ): Promise<{ status: string; note?: string; directives?: any[] }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/financials/flag-answer`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  /** Acknowledge (or clear) a books-trust readiness check — excludes it from the trust score on the
   * next annotations fetch (e.g. the owner confirms the books really are closed). */
  async acknowledgeQuickBooksReadinessCheck(
    workbookId: string,
    body: {
      check_id: string
      period: string
      acknowledged: boolean
      text?: string
    },
  ): Promise<{ status: string; check_id: string; acknowledged: boolean }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/financials/trust-ack`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async getQuickBooksIndustryProfile(
    workbookId: string,
  ): Promise<IndustryBenchmarkProfileResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/industry-profile`,
      { method: 'GET' },
    )
  }

  async updateQuickBooksIndustryProfile(
    workbookId: string,
    payload: {
      naics_code: string
      selection_source?: string | null
      confidence?: number | null
      evidence_json?: any
    },
  ): Promise<IndustryBenchmarkProfileResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/industry-profile`,
      { method: 'PUT', body: JSON.stringify(payload) },
    )
  }

  async detectQuickBooksIndustryProfile(
    workbookId: string,
    payload?: { web_search?: boolean | null; persist?: boolean | null },
  ): Promise<IndustryBenchmarkProfileResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/industry-profile/detect`,
      { method: 'POST', body: JSON.stringify(payload || {}) },
    )
  }

  async getQuickBooksCompanyContext(
    workbookId: string,
  ): Promise<CompanyContextResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/company-context`,
      { method: 'GET' },
    )
  }

  async updateQuickBooksCompanyContext(
    workbookId: string,
    payload: CompanyContextUpdatePayload,
  ): Promise<CompanyContextResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/company-context`,
      { method: 'PUT', body: JSON.stringify(payload) },
    )
  }

  async detectQuickBooksCompanyContext(
    workbookId: string,
    payload?: {
      persist?: boolean | null
      // The user's current form values. Any field they filled in is kept verbatim AND used to focus
      // the search; only blank fields are re-filled from the search. website_url is the strongest
      // anchor — it pins the company identity to that exact domain.
      website_url?: string | null
      website_title?: string | null
      business_summary?: string | null
      inferred_industry?: string | null
      geography?: string | null
      ai_context_notes?: string | null
      customer_segments?: string[] | null
      services?: string[] | null
      // Free-text steering note to disambiguate the search (transient — not saved).
      search_hint?: string | null
    },
  ): Promise<CompanyContextResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/company-context/detect`,
      { method: 'POST', body: JSON.stringify(payload || {}) },
    )
  }

  /**
   * CFO Core segment/account entitlements (workbook-scoped). Manager-only.
   * Lists the rules that restrict which accounts/segments a user/group may see
   * in this workbook's statements. Enforcement is server-side at the render
   * boundary; these are just the rules.
   */
  async listCfoEntitlements(workbookId: string): Promise<CfoEntitlementRule[]> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-entitlements`,
      { method: 'GET' },
    )
  }

  /** Replace this workbook's segment/account entitlement rules. Manager-only. */
  async putCfoEntitlements(
    workbookId: string,
    rules: CfoEntitlementRuleInput[],
  ): Promise<CfoEntitlementRule[]> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-entitlements`,
      { method: 'PUT', body: JSON.stringify({ rules }) },
    )
  }

  /** List the per-audience view-only shares for this workbook's CFO report. */
  async listCfoReportShares(workbookId: string): Promise<CfoReportShare[]> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-report-shares`,
      { method: 'GET' },
    )
  }

  /** Replace the per-audience shares for this workbook's CFO report. */
  async putCfoReportShares(
    workbookId: string,
    shares: CfoReportShareInput[],
  ): Promise<CfoReportShare[]> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-report-shares`,
      { method: 'PUT', body: JSON.stringify({ shares }) },
    )
  }

  /** Users + groups in this workbook's site, for entitlement/share subject pickers. */
  async listCfoAudienceOptions(
    workbookId: string,
  ): Promise<CfoAudienceOptions> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-audience-options`,
      { method: 'GET' },
    )
  }

  /** CFO reports shared with the current user (view-only). */
  async listCfoSharedReports(): Promise<CfoSharedReport[]> {
    return await this.request('/api/fpa/quickbooks/cfo-shared-reports', {
      method: 'GET',
    })
  }

  /**
   * Fetch the transparent CFO valuation overview: annualized base inputs, the
   * value-driver explainer (growth / margin / capital efficiency / risk),
   * industry-aware framework primers (earnings / EBITDA / revenue multiples + a
   * basic DCF), and a defensible equity-value range. Every assumption is
   * surfaced in `assumptions.notes`. Backed by valuation.rs over the shared
   * metric + ratio layer.
   */
  async getQuickBooksValuation(
    workbookId: string,
    opts?: GetValuationOptions,
  ): Promise<ValuationOverviewResponse> {
    const search = new URLSearchParams()
    if (opts?.asOf) search.set('as_of', opts.asOf)
    if (opts?.grain) search.set('grain', opts.grain)
    if (opts?.industry) search.set('industry', opts.industry)
    if (opts?.monthlySelection) search.set('monthly_selection', opts.monthlySelection)
    if (opts?.nativeDcfPricing) search.set('native_dcf_pricing', JSON.stringify(opts.nativeDcfPricing))
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/valuation${suffix}`,
      { method: 'GET' },
    )
  }

  async getCfoValuation(
    workbookId: string,
    opts?: GetValuationOptions,
  ): Promise<ValuationOverviewResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksValuation(workbookId, opts)
    }
    const [profitAndLoss, balanceSheet] = await Promise.all([
      this.getAccountingStatements(workbookId, 'profit_and_loss'),
      this.getAccountingStatements(workbookId, 'balance_sheet'),
    ])
    return accountingStatementsToValuation(
      bootstrap,
      { profit_and_loss: profitAndLoss, balance_sheet: balanceSheet },
      opts?.asOf,
    )
  }

  /**
   * Run a transparent "what if" valuation simulation. Pass additive driver
   * deltas (margin, growth, WACC, multiples, terminal growth); the server
   * re-runs the same frameworks and returns base-vs-simulated equity values per
   * framework plus a simulated range. Backed by valuation.rs.
   */
  async simulateQuickBooksValuation(
    workbookId: string,
    req: ValuationSimulationRequest,
  ): Promise<ValuationSimulationResponse> {
    const { asOf, grain, monthlySelection, ...rest } = req
    const body: Record<string, unknown> = { ...rest }
    if (asOf) body.as_of = asOf
    if (grain) body.grain = grain
    if (monthlySelection) body.monthly_selection = monthlySelection
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/valuation/simulate`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async getQuickBooksCfoPresentation(
    workbookId: string,
    opts?: { includeArAnalysis?: boolean; requestedPeriod?: string | null },
  ): Promise<QuickBooksCfoPresentationResponse> {
    const search = new URLSearchParams()
    if (opts?.includeArAnalysis === false)
      search.set('include_ar_analysis', 'false')
    if (opts?.requestedPeriod) search.set('requested_period', opts.requestedPeriod)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksCfoPresentationArAnalysis(
    workbookId: string,
  ): Promise<QuickBooksCfoPresentationArAnalysisResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/ar-analysis`,
      { method: 'GET' },
    )
  }

  async updateQuickBooksCfoPresentation(
    workbookId: string,
    req: {
      presentation: any
    },
  ): Promise<QuickBooksCfoPresentationResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation`,
      {
        method: 'PUT',
        body: JSON.stringify(req),
      },
    )
  }

  /**
   * Append a report block to the CFO presentation config — the persistence
   * primitive behind the "Add to report" action on cfo-home Brief insight cards
   * and the copilot answer canvas (plan Section 6 area 5). It reuses the
   * existing `reportBlocks` config model + the presentation PUT route (the same
   * path the manual block editor and the AI `add_block` op use), so callers do
   * not have to understand the config shape. Returns the refreshed presentation.
   *
   * Reads the current config, appends a normalized block (text/commentary by
   * default), and persists. `text` becomes the block's `content.text`.
   */
  async appendQuickBooksCfoPresentationBlock(
    workbookId: string,
    block: {
      title: string
      text?: string | null
      kind?:
        | 'text'
        | 'kpi_grid'
        | 'statement_rollup'
        | 'statement_detail'
        | 'trend_chart'
        | 'waterfall'
        | 'ar_summary'
        | 'action_list'
        | 'ratio_card'
      section?:
        | 'home'
        | 'profitAndLoss'
        | 'balanceSheet'
        | 'cashFlow'
        | 'accountsReceivable'
        | 'actions'
      source?: Record<string, any>
      content?: Record<string, any>
    },
  ): Promise<QuickBooksCfoPresentationResponse> {
    const current = await this.getQuickBooksCfoPresentation(workbookId)
    const config =
      current.config && typeof current.config === 'object'
        ? { ...current.config }
        : {}
    const existing: any[] = Array.isArray((config as any).reportBlocks)
      ? (config as any).reportBlocks.slice()
      : []
    const kind = block.kind || 'text'
    const section = block.section || 'home'
    const title = String(block.title ?? '').trim() || 'Added insight'
    const id = `added-${section}-${kind}-${Date.now().toString(36)}`
    const text = String(block.text ?? '').trim()
    const next: Record<string, any> = {
      id,
      kind,
      title,
      section,
      enabled: true,
    }
    if (block.source && typeof block.source === 'object')
      next.source = block.source
    if (block.content && typeof block.content === 'object')
      next.content = block.content
    else if (kind === 'text') next.content = { text }
    existing.push(next)
    ;(config as any).reportBlocks = existing
    return await this.updateQuickBooksCfoPresentation(workbookId, {
      presentation: config,
    })
  }

  /** List published board-pack months (freeze-on-publish snapshots), newest first. */
  async listQuickBooksCfoPresentationSnapshots(
    workbookId: string,
  ): Promise<CfoPresentationSnapshotListResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/snapshots`,
      { method: 'GET' },
    )
  }

  /** Fetch a frozen published month exactly as it was shared (read-only, can_edit=false). */
  async getQuickBooksCfoPresentationSnapshot(
    workbookId: string,
    period: string,
  ): Promise<CfoPresentationSnapshotResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/snapshots/${encodeURIComponent(period)}`,
      { method: 'GET' },
    )
  }

  /** Freeze the current draft into a published month snapshot (defaults to the as-of month). */
  async publishQuickBooksCfoPresentation(
    workbookId: string,
    req?: { period?: string | null },
  ): Promise<CfoPresentationPublishResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/publish`,
      { method: 'POST', body: JSON.stringify(req || {}) },
    )
  }

  async listQuickBooksCfoReportRevisions(workbookId: string): Promise<{ workbook_id: string; revisions: CfoReportRevisionSummary[] }> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions`, { method: 'GET' })
  }

  async listCfoReportRevisions(workbookId: string): Promise<{ workbook_id: string; revisions: CfoReportRevisionSummary[] }> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.listQuickBooksCfoReportRevisions(workbookId)
    }
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions`, { method: 'GET' })
  }

  async getCfoReportRevision(workbookId: string, revisionId: string): Promise<CfoReportRevisionResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.getQuickBooksCfoReportRevision(workbookId, revisionId)
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}`, { method: 'GET' })
  }

  async captureCfoReportRevision(
    workbookId: string,
    req: { source_signature: string; reporting_period?: string | null; snapshot: QuickBooksCfoPresentationResponse; status?: 'captured' | 'issued' },
  ): Promise<CfoReportRevisionResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.captureQuickBooksCfoReportRevision(workbookId, req)
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions`, { method: 'POST', body: JSON.stringify(req) })
  }

  async getQuickBooksCfoReportRevision(workbookId: string, revisionId: string): Promise<CfoReportRevisionResponse> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}`, { method: 'GET' })
  }

  async captureQuickBooksCfoReportRevision(
    workbookId: string,
    req: { source_signature: string; reporting_period?: string | null; snapshot: QuickBooksCfoPresentationResponse; status?: 'captured' | 'issued' },
  ): Promise<CfoReportRevisionResponse> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions`, { method: 'POST', body: JSON.stringify(req) })
  }

  async createQuickBooksCfoReviewCheckpoint(
    workbookId: string,
    req: { source_signature: string; reporting_period?: string | null; configuration_hash?: string | null; insight_digest?: string | null; highlight_digest?: string | null; data_state?: any },
  ): Promise<CfoReviewCheckpoint> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/review-checkpoints`, { method: 'POST', body: JSON.stringify(req) })
  }

  /**
   * @param knownProvider skips the bootstrap round-trip when the caller already knows the client's
   *   accounting provider. The firm cockpit does — it carries `accounting_provider` per roster row —
   *   and without this every "Mark reviewed" click would pay an extra request just to route itself.
   */
  async createCfoReviewCheckpoint(
    workbookId: string,
    req: { source_signature: string; reporting_period?: string | null; configuration_hash?: string | null; insight_digest?: string | null; highlight_digest?: string | null; data_state?: any },
    knownProvider?: string | null,
  ): Promise<CfoReviewCheckpoint> {
    const provider =
      knownProvider ?? (await this.getAccountingWorkbookBootstrap(workbookId)).accounting_provider
    if (provider === 'quickbooks') return await this.createQuickBooksCfoReviewCheckpoint(workbookId, req)
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/review-checkpoints`, { method: 'POST', body: JSON.stringify(req) })
  }

  async recordQuickBooksCfoHighlightSelection(
    workbookId: string,
    req: {
      source_signature: string
      visual_selection_version: string
      visual_evidence_schema_version: string
      selected: import('../components/parity-dev/cfo/visuals/types').KeyHighlight[]
      evidence: import('../components/parity-dev/cfo/visuals/types').HighlightVisualEvidence[]
      rejections: import('../components/parity-dev/cfo/visuals/types').HighlightSelectionRejection[]
    },
  ): Promise<void> {
    await this.requestNoContent(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/highlight-selection`,
      { method: 'POST', body: JSON.stringify(req) },
    )
  }

  async recordCfoHighlightSelection(
    workbookId: string,
    req: {
      source_signature: string
      visual_selection_version: string
      visual_evidence_schema_version: string
      selected: import('../components/parity-dev/cfo/visuals/types').KeyHighlight[]
      evidence: import('../components/parity-dev/cfo/visuals/types').HighlightVisualEvidence[]
      rejections: import('../components/parity-dev/cfo/visuals/types').HighlightSelectionRejection[]
    },
  ): Promise<void> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.recordQuickBooksCfoHighlightSelection(workbookId, req)
    await this.requestNoContent(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/highlight-selection`, { method: 'POST', body: JSON.stringify(req) })
  }

  async registerQuickBooksCfoReportMaterialization(
    workbookId: string,
    revisionId: string,
    req: { format: 'pdf' | 'html' | 'presentation'; status?: 'building' | 'ready' | 'failed'; content_hash?: string; mime_type?: string; byte_size?: number | null; renderer_version?: string | null; theme_version?: string | null; content_base64?: string | null; error_message?: string | null },
  ): Promise<CfoReportMaterialization> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}/materializations`, { method: 'POST', body: JSON.stringify(req) })
  }

  async listQuickBooksCfoReportMaterializations(workbookId: string, revisionId: string): Promise<CfoReportMaterialization[]> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}/materializations`, { method: 'GET' })
  }

  async registerCfoReportMaterialization(
    workbookId: string,
    revisionId: string,
    req: { format: 'pdf' | 'html' | 'presentation'; status?: 'building' | 'ready' | 'failed'; content_hash?: string; mime_type?: string; byte_size?: number | null; renderer_version?: string | null; theme_version?: string | null; content_base64?: string | null; error_message?: string | null },
  ): Promise<CfoReportMaterialization> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.registerQuickBooksCfoReportMaterialization(workbookId, revisionId, req)
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}/materializations`, { method: 'POST', body: JSON.stringify(req) })
  }

  async listCfoReportMaterializations(workbookId: string, revisionId: string): Promise<CfoReportMaterialization[]> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.listQuickBooksCfoReportMaterializations(workbookId, revisionId)
    return await this.request(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}/materializations`, { method: 'GET' })
  }

  async downloadCfoWorkbook(workbookId: string, signal?: AbortSignal): Promise<{ blob: Blob; fileName: string }> {
    let fileName = 'FuseGrid CFO Workbook.xlsx'
    const blob = await this.requestBlob(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-workbook`, {
      method: 'GET', signal, cache: 'no-store', headers: { 'X-FuseGrid-Writer': CFO_WORKBOOK_WRITER },
    }, { onResponse: (response) => {
      if (response.headers.get('X-FuseGrid-Writer') !== CFO_WORKBOOK_WRITER) throw Object.assign(new Error('Something went wrong on our side.'), { status: 503, code: 'workbook_build_mismatch' })
      const encoded = /filename\*=UTF-8''([^;]+)/i.exec(response.headers.get('Content-Disposition') || '')?.[1]
      if (encoded) fileName = decodeURIComponent(encoded)
    } })
    return { blob, fileName }
  }

  async getCfoWorkbookReadiness(workbookId: string): Promise<{ latest_closed: string | null }> {
    const presentation = await this.getQuickBooksCfoPresentation(workbookId, { includeArAnalysis: false })
    const statement = presentation.statements?.profit_and_loss
    if (!statement?.periods?.length || !statement.rows?.length) return { latest_closed: null }
    const latestClosed = presentation.plan_vs_actual?.latest_closed
      || (await this.getQuickBooksRatios(workbookId)).as_of
      || statement.periods.at(-1)
    return { latest_closed: latestClosed ? String(latestClosed).slice(0, 7) : null }
  }

  async downloadCfoReportMaterialization(workbookId: string, revisionId: string, artifactId: string): Promise<Blob> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.downloadQuickBooksCfoReportMaterialization(workbookId, revisionId, artifactId)
    return await this.requestBlob(`/api/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}/materializations/${encodeURIComponent(artifactId)}/content`, { method: 'GET' })
  }

  async downloadQuickBooksCfoReportMaterialization(workbookId: string, revisionId: string, artifactId: string): Promise<Blob> {
    return await this.requestBlob(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/revisions/${encodeURIComponent(revisionId)}/materializations/${encodeURIComponent(artifactId)}/content`, { method: 'GET' })
  }

  async listQuickBooksCfoReviewCheckpoints(workbookId: string): Promise<CfoReviewCheckpoint[]> {
    return await this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/review-checkpoints`, { method: 'GET' })
  }

  /** Roll the editable draft forward a month (clones the last published month as a starting point). */
  async rollForwardQuickBooksCfoPresentation(
    workbookId: string,
    req?: { source_period?: string | null; target_period?: string | null },
  ): Promise<QuickBooksCfoPresentationResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/roll-forward`,
      { method: 'POST', body: JSON.stringify(req || {}) },
    )
  }

  /** Delete a published month's frozen snapshot (does not touch the draft). */
  async deleteQuickBooksCfoPresentationMonth(
    workbookId: string,
    period: string,
  ): Promise<void> {
    await this.requestNoContent(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/snapshots/${encodeURIComponent(period)}`,
      { method: 'DELETE' },
    )
  }

  /** List external view-only share links for a workbook's board pack (metadata only). */
  async listCfoReportShareLinks(workbookId: string): Promise<CfoShareLink[]> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    const prefix = bootstrap.accounting_provider === 'quickbooks' ? '/api/fpa/quickbooks/workbooks' : '/api/cfo/workbooks'
    return await this.request(
      `${prefix}/${encodeURIComponent(workbookId)}/cfo-share-links`,
      { method: 'GET' },
    )
  }

  /** Mint an external view-only link. The plaintext token is returned ONCE. */
  async createCfoReportShareLink(
    workbookId: string,
    req?: {
      revision_id?: string | null
      label?: string | null
      expires_at?: string | null
    },
  ): Promise<CreateCfoShareLinkResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    const prefix = bootstrap.accounting_provider === 'quickbooks' ? '/api/fpa/quickbooks/workbooks' : '/api/cfo/workbooks'
    return await this.request(
      `${prefix}/${encodeURIComponent(workbookId)}/cfo-share-links`,
      { method: 'POST', body: JSON.stringify(req || {}) },
    )
  }

  /** Revoke (soft-delete) an external share link. */
  async revokeCfoReportShareLink(
    workbookId: string,
    linkId: string,
  ): Promise<void> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    const prefix = bootstrap.accounting_provider === 'quickbooks' ? '/api/fpa/quickbooks/workbooks' : '/api/cfo/workbooks'
    await this.requestNoContent(
      `${prefix}/${encodeURIComponent(workbookId)}/cfo-share-links/${encodeURIComponent(linkId)}`,
      { method: 'DELETE' },
    )
  }

  /**
   * Fetch a publicly-shared board-pack snapshot by token. UNAUTHENTICATED — this
   * deliberately bypasses the Bearer-injecting `request()` so a logged-out
   * client (or external recipient) can render a read-only report.
   */
  async getPublicCfoSnapshot(token: string): Promise<PublicCfoSnapshot> {
    const res = await fetch(
      `${this.baseUrl}/api/public/cfo/share/${encodeURIComponent(token)}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
    )
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(text || `Share link unavailable (HTTP ${res.status}).`)
    }
    return (await res.json()) as PublicCfoSnapshot
  }

  /**
   * Month-specific CFO Analysis Report — the editable source of truth that the
   * guided-analysis modal tags evidence into. Keyed by `period` (`YYYY-MM`).
   * Returns the report (created empty on first GET). Shape matches the frontend
   * `CfoMonthlyAnalysisReport` type (cast at the call site).
   */
  async getQuickBooksCfoMonthlyAnalysisReport(
    workbookId: string,
    period: string,
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/monthly-analysis-report?period=${encodeURIComponent(period)}`,
      { method: 'GET' },
    )
  }

  /** Replace the full month report (title/status/summary/sections/items). */
  async updateQuickBooksCfoMonthlyAnalysisReport(
    workbookId: string,
    req: { report: any },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/monthly-analysis-report`,
      { method: 'PUT', body: JSON.stringify(req) },
    )
  }

  /** Append one tagged analysis item to a month's report (returns refreshed report). */
  async appendQuickBooksCfoMonthlyAnalysisReportItem(
    workbookId: string,
    req: { period: string; item: any },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/monthly-analysis-report/items`,
      { method: 'POST', body: JSON.stringify(req) },
    )
  }

  /**
   * Persisted guided-analysis sessions — power the analysis modal's History so a
   * prior session's full transcript can be restored. List newest-first
   * (optionally filtered by `period` = YYYY-MM). Returns `{ sessions: [...] }`.
   */
  async listQuickBooksCfoAnalysisSessions(
    workbookId: string,
    opts?: {
      period?: string | null
      limit?: number | null
      q?: string | null
      archived?: 'false' | 'true' | 'all' | null
    },
  ): Promise<any> {
    const params = new URLSearchParams()
    if (opts?.period) params.set('period', opts.period)
    if (opts?.limit) params.set('limit', String(opts.limit))
    if (opts?.q) params.set('q', opts.q)
    if (opts?.archived) params.set('archived', opts.archived)
    const qs = params.toString()
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/analysis-sessions${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  /** Upsert one analysis session by its client UUID (call as the turn completes). */
  async saveQuickBooksCfoAnalysisSession(
    workbookId: string,
    sessionId: string,
    req: { session: any },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/analysis-sessions/${encodeURIComponent(sessionId)}`,
      { method: 'PUT', body: JSON.stringify(req) },
    )
  }

  /** Archive/unarchive or rename a saved analysis session. */
  async patchQuickBooksCfoAnalysisSession(
    workbookId: string,
    sessionId: string,
    req: { archived?: boolean; label?: string },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/analysis-sessions/${encodeURIComponent(sessionId)}`,
      { method: 'PATCH', body: JSON.stringify(req) },
    )
  }

  /** Permanently delete a saved analysis session. */
  async deleteQuickBooksCfoAnalysisSession(
    workbookId: string,
    sessionId: string,
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/analysis-sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE' },
    )
  }

  /** Deterministic CFO instant cards, persisted by workbook/surface/card key. */
  async listQuickBooksCfoInstantCards(
    workbookId: string,
    opts?: {
      surface?: string | null
      cardKey?: string | null
      metricId?: string | null
      period?: string | null
      comparison?: string | null
      limit?: number | null
    },
  ): Promise<any> {
    const params = new URLSearchParams()
    if (opts?.surface) params.set('surface', opts.surface)
    if (opts?.cardKey) params.set('card_key', opts.cardKey)
    if (opts?.metricId) params.set('metric_id', opts.metricId)
    if (opts?.period) params.set('period', opts.period)
    if (opts?.comparison) params.set('comparison', opts.comparison)
    if (opts?.limit) params.set('limit', String(opts.limit))
    const qs = params.toString()
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-cards${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  /** Upsert one deterministic CFO instant card. */
  async saveQuickBooksCfoInstantCard(
    workbookId: string,
    req: { card: any; connection_id?: string | null },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-cards`,
      { method: 'POST', body: JSON.stringify(req) },
    )
  }

  /** Bulk upsert deterministic CFO instant cards for a page/materialized artifact. */
  async saveQuickBooksCfoInstantCards(
    workbookId: string,
    req: { cards: any[]; connection_id?: string | null },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-cards`,
      { method: 'POST', body: JSON.stringify(req) },
    )
  }

  /** Persisted next-level option packets for CFO instant cards. */
  async listQuickBooksCfoInstantCardEnrichments(
    workbookId: string,
    opts?: {
      surface?: string | null
      cardKey?: string | null
      metricId?: string | null
      period?: string | null
      comparison?: string | null
      freshnessToken?: string | null
      limit?: number | null
    },
  ): Promise<any> {
    const params = new URLSearchParams()
    if (opts?.surface) params.set('surface', opts.surface)
    if (opts?.cardKey) params.set('card_key', opts.cardKey)
    if (opts?.metricId) params.set('metric_id', opts.metricId)
    if (opts?.period) params.set('period', opts.period)
    if (opts?.comparison) params.set('comparison', opts.comparison)
    if (opts?.freshnessToken) params.set('freshness_token', opts.freshnessToken)
    if (opts?.limit) params.set('limit', String(opts.limit))
    const qs = params.toString()
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-card-enrichments${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  /** Return a fresh enrichment packet or build a bounded deterministic packet if missing. */
  async ensureQuickBooksCfoInstantCardEnrichment(
    workbookId: string,
    req: { card: any; connection_id?: string | null },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-card-enrichments/ensure`,
      { method: 'POST', body: JSON.stringify(req) },
    )
  }

  /** Persisted deterministic change explanations for CFO instant cards. */
  async listQuickBooksCfoInstantCardExplanations(
    workbookId: string,
    opts?: {
      surface?: string | null
      cardKey?: string | null
      metricId?: string | null
      period?: string | null
      comparison?: string | null
      freshnessToken?: string | null
      periodKind?: string | null
      limit?: number | null
    },
  ): Promise<any> {
    const params = new URLSearchParams()
    if (opts?.surface) params.set('surface', opts.surface)
    if (opts?.cardKey) params.set('card_key', opts.cardKey)
    if (opts?.metricId) params.set('metric_id', opts.metricId)
    if (opts?.period) params.set('period', opts.period)
    if (opts?.comparison) params.set('comparison', opts.comparison)
    if (opts?.freshnessToken) params.set('freshness_token', opts.freshnessToken)
    if (opts?.periodKind) params.set('period_kind', opts.periodKind)
    if (opts?.limit) params.set('limit', String(opts.limit))
    const qs = params.toString()
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-card-explanations${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  /** AI-facing deterministic context packets for CFO instant cards. */
  async listQuickBooksCfoInstantCardContext(
    workbookId: string,
    opts?: {
      surface?: string | null
      cardKey?: string | null
      metricId?: string | null
      period?: string | null
      comparison?: string | null
      freshnessToken?: string | null
      limit?: number | null
    },
  ): Promise<any> {
    const params = new URLSearchParams()
    if (opts?.surface) params.set('surface', opts.surface)
    if (opts?.cardKey) params.set('card_key', opts.cardKey)
    if (opts?.metricId) params.set('metric_id', opts.metricId)
    if (opts?.period) params.set('period', opts.period)
    if (opts?.comparison) params.set('comparison', opts.comparison)
    if (opts?.freshnessToken) params.set('freshness_token', opts.freshnessToken)
    if (opts?.limit) params.set('limit', String(opts.limit))
    const qs = params.toString()
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-card-context${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  }

  /** Return a fresh AI context packet or build deterministic context from the card. */
  async ensureQuickBooksCfoInstantCardContext(
    workbookId: string,
    req: { card: any; connection_id?: string | null },
  ): Promise<any> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/instant-card-context/ensure`,
      { method: 'POST', body: JSON.stringify(req) },
    )
  }

  /**
   * Render the CFO presentation HTML to a real PDF server-side. This is the
   * preferred export path (returns a genuine `application/pdf`), replacing the
   * browser-print path. Posts the same themed HTML produced by
   * `buildQuickBooksCfoPresentationPdfHtml` so the PDF is pixel-identical to the
   * live tab / HTML export; the server only rasterizes it with a headless
   * renderer (Chrome/Chromium or wkhtmltopdf).
   *
   * Throws when the server has no renderer available (HTTP 503 availability
   * gate) or on any other failure, so callers can transparently fall back to the
   * browser-print path. Route:
   * `POST /api/quickbooks/cfo/workbooks/:id/presentation/pdf`.
   */
  async renderQuickBooksCfoPresentationPdf(
    workbookId: string,
    req: { html: string; title?: string | null; revision_id?: string | null },
  ): Promise<Blob> {
    return await this.requestBlob(
      `/api/quickbooks/cfo/workbooks/${encodeURIComponent(workbookId)}/presentation/pdf`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: req.html, title: req.title ?? null, revision_id: req.revision_id ?? null }),
      },
    )
  }

  async renderCfoPresentationPdf(
    workbookId: string,
    req: { html: string; title?: string | null; revision_id?: string | null },
  ): Promise<Blob> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    const prefix = bootstrap.accounting_provider === 'quickbooks' ? '/api/quickbooks/cfo' : '/api/cfo'
    return await this.requestBlob(
      `${prefix}/workbooks/${encodeURIComponent(workbookId)}/presentation/pdf`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: req.html, title: req.title ?? null, revision_id: req.revision_id ?? null }),
      },
    )
  }

  async getQuickBooksCfoGrowthAnalysis(
    workbookId: string,
    opts?: { asOf?: string | null },
  ): Promise<QuickBooksGrowthAnalysisResponse> {
    const qp = new URLSearchParams()
    if (opts?.asOf) qp.set('as_of', opts.asOf)
    const suffix = qp.toString()
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/growth-analysis${suffix ? `?${suffix}` : ''}`,
      {
        method: 'GET',
      },
    )
  }

  async updateQuickBooksCfoGrowthAnalysis(
    workbookId: string,
    req: {
      growth_analysis?: QuickBooksGrowthAnalysisConfig
      growthAnalysis?: QuickBooksGrowthAnalysisConfig
    },
  ): Promise<QuickBooksGrowthAnalysisResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/growth-analysis`,
      {
        method: 'PUT',
        body: JSON.stringify(req),
      },
    )
  }

  async listQuickBooksFpaPlans(
    workbookId: string,
  ): Promise<QuickBooksFpaPlanVersion[]> {
    const out = await this.request<{
      plans?: QuickBooksFpaPlanVersion[] | null
    }>(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans`,
      { method: 'GET' },
    )
    return Array.isArray(out?.plans) ? out.plans : []
  }

  async getQuickBooksActivePlan(workbookId: string): Promise<QuickBooksActivePlan> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast/current`,
      { method: 'GET' },
    )
  }

  async getQuickBooksActivePlanVariance(
    workbookId: string,
  ): Promise<QuickBooksActivePlanVarianceResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast/current/plan-vs-actual`,
      { method: 'GET' },
    )
  }

  async getCfoActivePlan(workbookId: string): Promise<QuickBooksActivePlan> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.getQuickBooksActivePlan(workbookId)
    const forecast = await this.getAccountingForecast(workbookId)
    const periods = [...new Set((forecast.rows || []).filter((row) => row.scenario === 'base').map((row) => row.period))].sort()
    return {
      schema_version: 'fg.active_plan.v1',
      availability: periods.length ? 'available' : 'unavailable',
      reason_code: periods.length ? null : 'forecast_not_materialized',
      message: periods.length ? null : 'The provider-neutral forecast is not prepared yet.',
      plan_id: periods.length ? `accounting:${bootstrap.accounting_company_id}:base` : null,
      name: periods.length ? 'FuseGrid base forecast' : null,
      kind: 'forecast',
      primary_source: 'fusegrid_default',
      publication_token: null,
      publication_revision: 1,
      published_at: bootstrap.last_sync_at || null,
      layout_version_id: null,
      current_layout_version_id: null,
      layout_drift: false,
      accounting_basis: 'accrual',
      horizon: { months: periods.length, start_period: periods[0] || null, end_period: periods.at(-1) || null },
      validation: { source: 'accounting_forecast_monthly', provider: bootstrap.accounting_provider },
      last_receipt: null,
      newer_web_model_available: false,
      web_candidates: [],
    }
  }

  async getCfoActivePlanVariance(workbookId: string): Promise<QuickBooksActivePlanVarianceResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') return await this.getQuickBooksActivePlanVariance(workbookId)
    const plan = await this.getCfoActivePlan(workbookId)
    return {
      schema_version: 'fg.plan_vs_actual.v1',
      comparison_basis: 'provider_neutral_forecast_vs_governed_actuals',
      currency_code: bootstrap.currency_code || '',
      plan,
      coverage: {
        latest_closed: bootstrap.default_period || null,
        latest_available: bootstrap.available_periods?.[0] || bootstrap.default_period || null,
        includes_partial_current_month: false,
        actuals_integrity: 'passed',
      },
      rows: [],
      warnings: ['historical_plan_versions_not_yet_available'],
    }
  }

  async activateQuickBooksWebPlan(
    workbookId: string,
    request: {
      expected_publication_token: string
      plan_id: string
      confirmed_excel_supersession: boolean
      idempotency_key: string
    },
  ): Promise<{ status: string; plan: QuickBooksActivePlan }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast/current/activate-web`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    )
  }

  async getQuickBooksFpaPlan(
    workbookId: string,
    planId: string,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}`,
      {
        method: 'GET',
      },
    )
  }

  async generateQuickBooksFpaPlan(
    workbookId: string,
    req?: QuickBooksFpaGeneratePlanRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/generate`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async createQuickBooksFpaPlanAgentDraft(
    workbookId: string,
    req?: QuickBooksFpaPlanAgentDraftRequest,
  ): Promise<QuickBooksFpaPlanAgentDraftResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/agent-draft`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async generateQuickBooksFpaPlanFromDraft(
    workbookId: string,
    req?: QuickBooksFpaGeneratePlanFromDraftRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/generate-from-draft`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateQuickBooksFpaPlan(
    workbookId: string,
    planId: string,
    req: QuickBooksFpaUpdatePlanRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async deleteQuickBooksFpaPlan(
    workbookId: string,
    planId: string,
  ): Promise<void> {
    await this.requestNoContent(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}`,
      {
        method: 'DELETE',
      },
      { timeoutMs: 15_000 },
    )
  }

  async createQuickBooksFpaEditableDraft(
    workbookId: string,
    planId: string,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/editable-draft`,
      {
        method: 'POST',
      },
    )
  }

  async publishQuickBooksFpaPlan(
    workbookId: string,
    planId: string,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/publish`,
      {
        method: 'POST',
      },
    )
  }

  async rollForwardQuickBooksFpaPlan(
    workbookId: string,
    planId: string,
    req?: QuickBooksFpaGeneratePlanRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/roll-forward`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async getQuickBooksFpaBudgetVsActual(
    workbookId: string,
    planId: string,
  ): Promise<QuickBooksFpaBudgetVsActualReport> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/budget-vs-actual`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlForecastPackage(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlPackageResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/package${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async listQuickBooksFpaMlForecastPackages(
    workbookId: string,
    limit?: number,
  ): Promise<QuickBooksFpaMlPackageListResponse> {
    const params = new URLSearchParams()
    if (Number.isFinite(limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/packages${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlStatus(
    workbookId: string,
  ): Promise<QuickBooksFpaMlStatusResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/status`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlDebug(
    workbookId: string,
  ): Promise<QuickBooksFpaMlDebugResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/debug`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlOrchestrationSettings(
    workbookId: string,
  ): Promise<QuickBooksFpaMlOrchestrationSettingsResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/orchestration-settings`,
      {
        method: 'GET',
      },
    )
  }

  async updateQuickBooksFpaMlOrchestrationSettings(
    workbookId: string,
    req: {
      enabled?: boolean | null
      run_after_sync?: boolean | null
      scheduled_enabled?: boolean | null
      schedule_cadence?: string | null
    },
  ): Promise<QuickBooksFpaMlOrchestrationSettingsResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/orchestration-settings`,
      {
        method: 'PATCH',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async runQuickBooksFpaMlForecast(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      force_refresh?: boolean | null
      forecast_start_period?: string | null
      modeled_cash_flow_policy?: QuickBooksCurrentForecastRead['modeled_cash_flow_policy']
      economic_cash_flow_policy?: QuickBooksNativeEconomicCashFlowPolicy | null
    },
  ): Promise<QuickBooksFpaMlRunResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/run`,
      {
        method: 'POST',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async getQuickBooksFpaMlJobStatus(
    workbookId: string,
    jobId: string,
  ): Promise<QuickBooksFpaMlJobStatusResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/jobs/${encodeURIComponent(jobId)}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlSelection(
    workbookId: string,
  ): Promise<QuickBooksFpaMlSelectionResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/selection`,
      {
        method: 'GET',
      },
    )
  }

  async setQuickBooksFpaMlSelection(
    workbookId: string,
    req: { package_id: string; scenario_key?: string | null },
  ): Promise<QuickBooksFpaMlSelectionResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/selection`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async getQuickBooksFpaMlMonthlyForecast(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      as_of_date?: string | null
      future_months?: number | null
      plan_id?: string | null
      statement_type?: string | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlMonthlyForecastResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req?.as_of_date) params.set('as_of_date', req.as_of_date)
    if (Number.isFinite(req?.future_months)) params.set('future_months', String(Math.floor(Number(req?.future_months))))
    if (req?.plan_id) params.set('plan_id', req.plan_id)
    if (req?.statement_type) params.set('statement_type', req.statement_type)
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/monthly-forecast${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlCash13Week(
    workbookId: string,
    packageId?: string | null,
    scenarioKey?: string | null,
  ): Promise<QuickBooksFpaMlCash13WeekResponse> {
    const params = new URLSearchParams()
    if (packageId) params.set('package_id', packageId)
    if (scenarioKey) params.set('scenario_key', scenarioKey)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/cash-13-week${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksCashPlanning(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      as_of_date?: string | null
      history_weeks?: number | null
      forecast_weeks?: number | null
      due_date_assumption?: boolean
    },
    signal?: AbortSignal | null,
  ): Promise<QuickBooksCashPlanningResponse> {
    const params = new URLSearchParams()
    params.set('payload', 'summary_v2')
    if (req?.due_date_assumption != null) params.set('due_date_assumption', String(req.due_date_assumption))
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req?.as_of_date) params.set('as_of_date', req.as_of_date)
    if (Number.isFinite(req?.history_weeks))
      params.set(
        'history_weeks',
        String(Math.max(0, Math.floor(Number(req?.history_weeks)))),
      )
    if (Number.isFinite(req?.forecast_weeks))
      params.set(
        'forecast_weeks',
        String(Math.max(1, Math.floor(Number(req?.forecast_weeks)))),
      )
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning${suffix}`,
      {
        method: 'GET',
        signal: signal || undefined,
      },
    )
  }

  async refreshQuickBooksCashPlanning(
    workbookId: string,
    req?: {
      source_contract?: 'qbo-native-cash.v1'
      source_connection_id?: string
      due_date_assumption?: boolean
      package_id?: string | null
      scenario_key?: string | null
      as_of_date?: string | null
      history_weeks?: number | null
      forecast_weeks?: number | null
    },
  ): Promise<QuickBooksCashPlanningResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning`,
      {
        method: 'POST',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async getQuickBooksCashPlanningDetails(
    workbookId: string,
    req: {
      summary_id: string
      week: string
      category_id: string
      cursor?: string | null
      limit?: number | null
      as_of_date?: string | null
      scenario_key?: string | null
      history_weeks?: number | null
      forecast_weeks?: number | null
      due_date_assumption?: boolean
    },
    signal?: AbortSignal | null,
  ): Promise<QuickBooksCashPlanningDetailsResponse> {
    const params = new URLSearchParams({
      summary_id: req.summary_id,
      week: req.week,
      category_id: req.category_id,
    })
    if (req.cursor) params.set('cursor', req.cursor)
    if (req.as_of_date) params.set('as_of_date', req.as_of_date)
    if (req.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req.history_weeks != null) params.set('history_weeks', String(req.history_weeks))
    if (req.forecast_weeks != null) params.set('forecast_weeks', String(req.forecast_weeks))
    if (req.due_date_assumption != null) params.set('due_date_assumption', String(req.due_date_assumption))
    if (Number.isFinite(req.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req.limit)))))
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/details?${params.toString()}`,
      { method: 'GET', signal: signal || undefined },
    )
  }

  async listQuickBooksCashPlanningSources(
    workbookId: string,
    req?: {
      source_kind?: string | null
      scenario_key?: string | null
      limit?: number | null
      as_of_date?: string | null
      due_date_assumption?: boolean
    },
  ): Promise<QuickBooksCashPlanningSourceResponse> {
    const params = new URLSearchParams()
    if (req?.source_kind) params.set('source_kind', req.source_kind)
    if (req?.as_of_date) params.set('as_of_date', req.as_of_date)
    if (req?.due_date_assumption != null) params.set('due_date_assumption', String(req.due_date_assumption))
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/sources${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async exportQuickBooksCashPlanning(
    workbookId: string,
    req?: { package_id?: string | null; scenario_key?: string | null },
  ): Promise<QuickBooksCashPlanningExportResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/export${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async createQuickBooksCashPlanningExpectation(
    workbookId: string,
    req: QuickBooksCashPlanningExpectationRequest,
  ): Promise<QuickBooksCashPlanningRow> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/expectations`,
      {
        method: 'POST',
        body: JSON.stringify(cashAuthoringBody(req, ['expected_amount', 'low_amount', 'high_amount'])),
      },
    )
  }

  async bulkCreateQuickBooksCashPlanningExpectations(
    workbookId: string,
    expectations: QuickBooksCashPlanningExpectationRequest[],
    nativeWriteFence?: NativeCashWriteFence,
  ): Promise<{
    rows: QuickBooksCashPlanningRow[]
    overlay_revision: number
    aggregate_deltas: QuickBooksCashAggregateDelta[]
  }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/expectations/bulk`,
      {
        method: 'POST',
        body: JSON.stringify({ expectations: expectations.map(row => cashAuthoringBody(row, ['expected_amount', 'low_amount', 'high_amount'])), ...(nativeWriteFence ? { native_write_fence: nativeWriteFence } : {}) }),
      },
    )
  }

  async updateQuickBooksCashPlanningExpectation(
    workbookId: string,
    expectationId: string,
    req: Partial<QuickBooksCashPlanningExpectationRequest> & {
      status?: string | null
    },
  ): Promise<QuickBooksCashPlanningRow> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/expectations/${encodeURIComponent(expectationId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(cashAuthoringBody(req, ['expected_amount', 'low_amount', 'high_amount'])),
      },
    )
  }

  async deleteQuickBooksCashPlanningExpectation(
    workbookId: string,
    expectationId: string,
    nativeWriteFence?: NativeCashWriteFence,
  ): Promise<{
    status: string
    id: string
    overlay_revision: number
    aggregate_deltas: QuickBooksCashAggregateDelta[]
  }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/expectations/${encodeURIComponent(expectationId)}`,
      {
        method: 'DELETE',
        ...(nativeWriteFence ? { body: JSON.stringify({ native_write_fence: nativeWriteFence }) } : {}),
      },
    )
  }

  async startQuickBooksCashPlanningWeeklyReview(
    workbookId: string,
    req?: { review_week_start?: string | null },
  ): Promise<QuickBooksCashPlanningReviewResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/weekly-review/start`,
      {
        method: 'POST',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async updateQuickBooksCashPlanningWeeklyReviewItem(
    workbookId: string,
    reviewId: string,
    itemId: string,
    req: {
      actual_amount?: number | null
      variance_amount?: number | null
      disposition?: string | null
      resolution_action?: string | null
      note?: string | null
    },
  ): Promise<QuickBooksCashPlanningReviewResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/weekly-review/${encodeURIComponent(reviewId)}/items/${encodeURIComponent(itemId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async rollForwardQuickBooksCashPlanningWeeklyReview(
    workbookId: string,
    reviewId: string,
    req?: { item_ids?: string[] | null; target_week_start?: string | null },
  ): Promise<QuickBooksCashPlanningReviewResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/weekly-review/${encodeURIComponent(reviewId)}/roll-forward`,
      {
        method: 'POST',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async completeQuickBooksCashPlanningWeeklyReview(
    workbookId: string,
    reviewId: string,
  ): Promise<QuickBooksCashPlanningReviewResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/weekly-review/${encodeURIComponent(reviewId)}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )
  }

  async createQuickBooksCashPlanningAiDraft(
    workbookId: string,
    req: {
      prompt: string
      scenario_key?: string | null
      provider?: string | null
      model_id?: string | null
      use_ai?: boolean | null
    },
  ): Promise<QuickBooksCashPlanningAiDraftResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/ai-draft`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksCashPlanningAiDraft(
    workbookId: string,
    draftId: string,
    req: {
      answers_json?: any
      selected_source_json?: any
      status?: string | null
    },
  ): Promise<QuickBooksCashPlanningAiDraftResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/ai-draft/${encodeURIComponent(draftId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async applyQuickBooksCashPlanningAiDraft(
    workbookId: string,
    draftId: string,
    req?: { operation_ids?: string[] | null },
  ): Promise<QuickBooksCashPlanningResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/ai-draft/${encodeURIComponent(draftId)}/apply`,
      {
        method: 'POST',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async listQuickBooksCashPlanningScenarios(
    workbookId: string,
  ): Promise<{ scenarios: QuickBooksCashPlanningScenario[] }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/scenarios`,
      {
        method: 'GET',
      },
    )
  }

  async createQuickBooksCashPlanningScenario(
    workbookId: string,
    req: {
      scenario_key?: string | null
      name?: string | null
      description?: string | null
      clone_from_scenario_key?: string | null
    },
  ): Promise<QuickBooksCashPlanningScenario> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/scenarios`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksCashPlanningScenario(
    workbookId: string,
    scenarioId: string,
    req: {
      name?: string | null
      description?: string | null
      status?: string | null
    },
  ): Promise<QuickBooksCashPlanningScenario> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/scenarios/${encodeURIComponent(scenarioId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async cloneQuickBooksCashPlanningScenario(
    workbookId: string,
    scenarioId: string,
    req: {
      scenario_key?: string | null
      name?: string | null
      description?: string | null
    },
  ): Promise<QuickBooksCashPlanningScenario> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/scenarios/${encodeURIComponent(scenarioId)}/clone`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async promoteQuickBooksCashPlanningScenario(
    workbookId: string,
    scenarioId: string,
  ): Promise<QuickBooksCashPlanningScenario> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/scenarios/${encodeURIComponent(scenarioId)}/promote`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )
  }

  async listQuickBooksCashPlanningSnapshots(
    workbookId: string,
  ): Promise<{ snapshots: QuickBooksCashPlanningSnapshot[] }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/snapshots`,
      {
        method: 'GET',
      },
    )
  }

  async createQuickBooksCashPlanningSnapshot(
    workbookId: string,
    req?: { scenario_key?: string | null; version_label?: string | null },
  ): Promise<QuickBooksCashPlanningSnapshot> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/snapshots`,
      {
        method: 'POST',
        body: JSON.stringify(req || {}),
      },
    )
  }

  async publishQuickBooksCashPlanningSnapshot(
    workbookId: string,
    snapshotId: string,
  ): Promise<QuickBooksCashPlanningSnapshot> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/snapshots/${encodeURIComponent(snapshotId)}/publish`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )
  }

  async rollbackQuickBooksCashPlanningSnapshot(
    workbookId: string,
    snapshotId: string,
  ): Promise<QuickBooksCashPlanningSnapshot> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/snapshots/${encodeURIComponent(snapshotId)}/rollback`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )
  }

  async listQuickBooksCashPlanningAudit(
    workbookId: string,
    req?: { limit?: number | null },
  ): Promise<{ events: QuickBooksCashPlanningAuditEvent[] }> {
    const params = new URLSearchParams()
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cash-planning/audit${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlDrivers(
    workbookId: string,
    packageId?: string | null,
    scenarioKey?: string | null,
  ): Promise<QuickBooksFpaMlDriverImpactResponse> {
    const params = new URLSearchParams()
    if (packageId) params.set('package_id', packageId)
    if (scenarioKey) params.set('scenario_key', scenarioKey)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/drivers${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlStatementForecast(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      grain?: QuickBooksFpaForecastGrain | string | null
      statement_type?: string | null
      value_case?: QuickBooksFpaForecastValueCase | string | null
      as_of_date?: string | null
      future_months?: number | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlStatementForecastResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req?.grain) params.set('grain', req.grain)
    if (req?.statement_type) params.set('statement_type', req.statement_type)
    if (req?.value_case) params.set('value_case', req.value_case)
    if (req?.as_of_date) params.set('as_of_date', req.as_of_date)
    if (Number.isFinite(req?.future_months))
      params.set(
        'future_months',
        String(Math.max(1, Math.floor(Number(req?.future_months)))),
      )
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/statement-forecast${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getCfoStatementForecast(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      grain?: QuickBooksFpaForecastGrain | string | null
      statement_type?: string | null
      value_case?: QuickBooksFpaForecastValueCase | string | null
      as_of_date?: string | null
      future_months?: number | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlStatementForecastResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.getQuickBooksFpaMlStatementForecast(workbookId, req)
    }
    const forecast = await this.getAccountingForecast(workbookId)
    return accountingForecastToStatementForecast(bootstrap, forecast, req)
  }

  async getQuickBooksFpaMlForecastPackageSnapshot(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      grain?: QuickBooksFpaForecastGrain | string | null
      as_of_date?: string | null
      future_months?: number | null
      daily_limit?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<QuickBooksFpaMlForecastPackageSnapshotResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req?.grain) params.set('grain', req.grain)
    if (req?.as_of_date) params.set('as_of_date', req.as_of_date)
    if (Number.isFinite(req?.future_months))
      params.set(
        'future_months',
        String(Math.max(1, Math.floor(Number(req?.future_months)))),
      )
    if (Number.isFinite(req?.daily_limit))
      params.set(
        'daily_limit',
        String(Math.max(1, Math.floor(Number(req?.daily_limit)))),
      )
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/package-snapshot${suffix}`,
      {
        method: 'GET',
      },
      opts,
    )
  }

  async ensureQuickBooksFpaMlForecastArtifacts(
    workbookId: string,
    req: {
      as_of_date: string
      scenario_key?: string | null
      future_months?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<QuickBooksFpaMlForecastArtifactsEnsureResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/artifacts/ensure`,
      {
        method: 'POST',
        body: JSON.stringify({
          as_of_date: req.as_of_date,
          scenario_key: req.scenario_key || null,
          future_months: Number.isFinite(req.future_months)
            ? Math.max(1, Math.floor(Number(req.future_months)))
            : null,
        }),
      },
      opts,
    )
  }

  async getCfoForecastArtifactStatus(
    workbookId: string,
    req: { as_of_date: string; scenario_key?: string | null; future_months?: number | null },
    opts?: { timeoutMs?: number },
  ): Promise<QuickBooksFpaMlForecastArtifactsEnsureResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider !== 'quickbooks') {
      return this.ensureCfoForecastArtifacts(workbookId, req, opts)
    }
    const params = new URLSearchParams({ as_of_date: req.as_of_date })
    if (req.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req.future_months != null) params.set('future_months', String(req.future_months))
    return this.request(`/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/artifacts/status?${params}`, { method: 'GET' }, opts)
  }

  async ensureCfoForecastArtifacts(
    workbookId: string,
    req: {
      as_of_date: string
      scenario_key?: string | null
      future_months?: number | null
    },
    opts?: { timeoutMs?: number },
  ): Promise<QuickBooksFpaMlForecastArtifactsEnsureResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      return await this.ensureQuickBooksFpaMlForecastArtifacts(workbookId, req, opts)
    }
    const forecast = await this.getAccountingForecast(workbookId)
    const rows = Array.isArray(forecast.rows) ? forecast.rows : []
    return {
      status: rows.length ? 'ready' : 'missing_source_data',
      as_of_date: req.as_of_date || bootstrap.default_period || '',
      forecast_start_period: rows[0]?.period || null,
      scenario_key: req.scenario_key || 'base',
      package_id: rows.length ? `canonical-forecast:${bootstrap.accounting_company_id}:${req.scenario_key || 'base'}` : null,
      job_id: null,
      message: rows.length
        ? 'Forecast is ready from the reconciled provider-neutral planning dataset.'
        : 'A reconciled planning dataset is required.',
      validation: {
        provider: bootstrap.accounting_provider,
        cross_company_learning: false,
      },
    }
  }

  async getQuickBooksFpaMlDeltas(
    workbookId: string,
    req?: {
      scenario_key?: string | null
      target_kind?: string | null
      since?: string | null
    },
  ): Promise<QuickBooksFpaMlUserDeltaListResponse> {
    const params = new URLSearchParams()
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (req?.target_kind) params.set('target_kind', req.target_kind)
    if (req?.since) params.set('since', req.since)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async patchQuickBooksFpaMlLineDelta(
    workbookId: string,
    req: {
      scenario_key?: string | null
      period_grain?: string | null
      statement_type: string
      node_id: string
      period_start: string
      final_value?: number | null
      low_value?: number | null
      high_value?: number | null
      note?: string | null
      reason_code?: string | null
      baseline?: {
        final_value?: number | null
        low_value?: number | null
        high_value?: number | null
      } | null
      package_id?: string | null
      expected_revision?: number | null
    },
  ): Promise<QuickBooksFpaMlUserDelta> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas/line`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async patchQuickBooksFpaMlLineDeltaReview(
    workbookId: string,
    req: {
      scenario_key?: string | null
      period_grain?: string | null
      statement_type: string
      node_id: string
      period_start: string
      status: string
      note?: string | null
      reason_code?: string | null
      package_id?: string | null
    },
  ): Promise<QuickBooksFpaMlUserDelta> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas/line/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async patchQuickBooksFpaMlCashWeekDelta(
    workbookId: string,
    req: {
      scenario_key?: string | null
      week_start: string
      cash_event_type: string
      source_key?: string | null
      counterparty_id?: string | null
      expected_amount?: number | null
      low_amount?: number | null
      high_amount?: number | null
      new_week_start?: string | null
      note?: string | null
      reason_code?: string | null
      baseline?: {
        expected_amount?: number | null
        week_start?: string | null
      } | null
      package_id?: string | null
      expected_revision?: number | null
    },
  ): Promise<QuickBooksFpaMlUserDelta> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas/cash-week`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async patchQuickBooksFpaMlCashWeekDeltaReview(
    workbookId: string,
    req: {
      scenario_key?: string | null
      week_start: string
      cash_event_type: string
      source_key?: string | null
      counterparty_id?: string | null
      status: string
      note?: string | null
      reason_code?: string | null
      package_id?: string | null
    },
  ): Promise<QuickBooksFpaMlUserDelta> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas/cash-week/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async patchQuickBooksFpaMlDriverDeltaReview(
    workbookId: string,
    req: {
      scenario_key?: string | null
      statement_type: string
      node_id: string
      period_start: string
      driver_key: string
      status: string
      note?: string | null
      reason_code?: string | null
      package_id?: string | null
    },
  ): Promise<QuickBooksFpaMlUserDelta> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas/driver/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async deleteQuickBooksFpaMlDelta(
    workbookId: string,
    deltaId: string,
  ): Promise<QuickBooksFpaMlUserDelta> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/deltas/${encodeURIComponent(deltaId)}`,
      {
        method: 'DELETE',
      },
    )
  }

  getQuickBooksFpaMlForecastPackageArtifactParquetUrl(
    workbookId: string,
    artifactKey: string,
    packageId?: string | null,
  ): string {
    const params = new URLSearchParams()
    const token = this.getToken()
    if (token) params.set('token', token)
    if (packageId) params.set('package_id', packageId)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return joinUrl(
      this.baseUrl,
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/package-artifacts/${encodeURIComponent(artifactKey)}/parquet${suffix}`,
    )
  }

  async getQuickBooksFpaMlDriverAssumptions(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaMlDriverAssumptionResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/driver-assumptions${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async updateQuickBooksFpaMlDriverAssumptions(
    workbookId: string,
    assumptions: any,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaMlDriverAssumptionResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/driver-assumptions${suffix}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ assumptions }),
      },
    )
  }

  /**
   * Stateless driver-edit preview: replays the exact server forecast
   * generation with the caller's assumption overrides and returns only the
   * lines that moved (all scenarios). 409s on packages that predate the
   * feature-snapshot artifact.
   */
  async previewQuickBooksFpaMlForecastDrivers(
    workbookId: string,
    req: {
      packageId?: string | null
      scenarioKey?: string | null
      asOfDate?: string | null
      futureMonths?: number | null
      assumptions: any
    },
  ): Promise<QuickBooksFpaMlDriverPreviewResponse> {
    const params = new URLSearchParams()
    if (req?.packageId) params.set('package_id', req.packageId)
    if (req?.scenarioKey) params.set('scenario_key', req.scenarioKey)
    if (req?.asOfDate) params.set('as_of_date', req.asOfDate)
    if (Number.isFinite(req?.futureMonths)) params.set('future_months', String(Math.floor(Number(req?.futureMonths))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/preview/drivers${suffix}`,
      {
        method: 'POST',
        body: JSON.stringify({ assumptions: req?.assumptions ?? {} }),
      },
    )
  }

  async generateQuickBooksFpaMlScenarioAutopilot(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      prompt?: string | null
      scenario_count?: number | null
    },
  ): Promise<QuickBooksFpaMlScenarioAutopilotResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/scenario-autopilot${suffix}`,
      {
        method: 'POST',
        body: JSON.stringify({
          prompt: req?.prompt || null,
          scenario_count: Number.isFinite(req?.scenario_count)
            ? Math.max(1, Math.floor(Number(req?.scenario_count)))
            : null,
        }),
      },
    )
  }

  async getQuickBooksFpaMlCashActions(
    workbookId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlCashActionResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.scenario_key) params.set('scenario_key', req.scenario_key)
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/cash-actions${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async listQuickBooksFpaMlSignals(
    workbookId: string,
    req?: {
      package_id?: string | null
      status?: string | null
      limit?: number | null
    },
  ): Promise<QuickBooksFpaMlSignalListResponse> {
    const params = new URLSearchParams()
    if (req?.package_id) params.set('package_id', req.package_id)
    if (req?.status) params.set('status', req.status)
    if (Number.isFinite(req?.limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(req?.limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/signals${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async updateQuickBooksFpaMlForecastLineReview(
    workbookId: string,
    lineId: string,
    req: {
      status: string
      note?: string | null
      reason_code?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaForecastLineMetadata> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/lines/${encodeURIComponent(lineId)}/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async editQuickBooksFpaMlForecastLine(
    workbookId: string,
    lineId: string,
    req: {
      final_value?: number | null
      low_value?: number | null
      high_value?: number | null
      note?: string | null
      reason_code?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaForecastLineMetadata> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/lines/${encodeURIComponent(lineId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksFpaMlCashWeekReview(
    workbookId: string,
    cashWeekId: string,
    req: {
      status: string
      note?: string | null
      reason_code?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaMlCashWeek> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/cash-weeks/${encodeURIComponent(cashWeekId)}/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async editQuickBooksFpaMlCashWeek(
    workbookId: string,
    cashWeekId: string,
    req: {
      week_start?: string | null
      expected_amount?: number | null
      low_amount?: number | null
      high_amount?: number | null
      note?: string | null
      reason_code?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaMlCashWeek> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/cash-weeks/${encodeURIComponent(cashWeekId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksFpaMlDriverReview(
    workbookId: string,
    driverId: string,
    req: {
      status: string
      note?: string | null
      reason_code?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaMlDriverImpact> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/drivers/${encodeURIComponent(driverId)}/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksFpaMlSignalReview(
    workbookId: string,
    signalId: string,
    req: {
      status: string
      note?: string | null
      reason_code?: string | null
      scenario_key?: string | null
    },
  ): Promise<QuickBooksFpaMlSignal> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/signals/${encodeURIComponent(signalId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async getQuickBooksFpaMlModelReview(
    workbookId: string,
    packageId?: string | null,
  ): Promise<QuickBooksFpaMlModelReviewResponse> {
    const params = new URLSearchParams()
    if (packageId) params.set('package_id', packageId)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/model-review${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async updateQuickBooksFpaMlModelRunReview(
    workbookId: string,
    runId: string,
    req: { status: string; note?: string | null },
  ): Promise<QuickBooksFpaMlModelRun> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/model-runs/${encodeURIComponent(runId)}/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async getQuickBooksFpaMlForecastVsActual(
    workbookId: string,
    packageId?: string | null,
    scenarioKey?: string | null,
  ): Promise<QuickBooksFpaMlForecastVsActualResponse> {
    const params = new URLSearchParams()
    if (packageId) params.set('package_id', packageId)
    if (scenarioKey) params.set('scenario_key', scenarioKey)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/forecast-vs-actual${suffix}`,
      {
        method: 'GET',
      },
    )
  }

  async getQuickBooksFpaMlEvidence(
    workbookId: string,
    targetType: string,
    targetId: string,
  ): Promise<QuickBooksFpaMlEvidenceResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/evidence/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`,
      {
        method: 'GET',
      },
    )
  }

  async listQuickBooksFpaMlModelRuns(
    workbookId: string,
    limit?: number,
  ): Promise<QuickBooksFpaMlModelRun[]> {
    const params = new URLSearchParams()
    if (Number.isFinite(limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const out = await this.request<{
      model_runs?: QuickBooksFpaMlModelRun[] | null
    }>(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/model-runs${suffix}`,
      { method: 'GET' },
    )
    return Array.isArray(out?.model_runs) ? out.model_runs : []
  }

  async listQuickBooksFpaMlBacktests(
    workbookId: string,
    limit?: number,
  ): Promise<QuickBooksFpaMlModelRun[]> {
    const params = new URLSearchParams()
    if (Number.isFinite(limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const out = await this.request<{
      model_runs?: QuickBooksFpaMlModelRun[] | null
    }>(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/backtests${suffix}`,
      { method: 'GET' },
    )
    return Array.isArray(out?.model_runs) ? out.model_runs : []
  }

  async listQuickBooksFpaMlFeedback(
    workbookId: string,
    limit?: number,
  ): Promise<QuickBooksFpaMlFeedbackEvent[]> {
    const params = new URLSearchParams()
    if (Number.isFinite(limit))
      params.set('limit', String(Math.max(1, Math.floor(Number(limit)))))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const out = await this.request<{
      feedback?: QuickBooksFpaMlFeedbackEvent[] | null
    }>(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/feedback${suffix}`,
      { method: 'GET' },
    )
    return Array.isArray(out?.feedback) ? out.feedback : []
  }

  async createQuickBooksFpaMlFeedback(
    workbookId: string,
    req: QuickBooksFpaMlFeedbackRequest,
  ): Promise<{
    feedback: QuickBooksFpaMlFeedbackEvent
    training_example_created: boolean
  }> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/forecast-ml/feedback`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async createQuickBooksFpaBudgetVsActualCommentaryDraft(
    workbookId: string,
    planId: string,
    req?: {
      mode?: 'month' | 'ytd' | string
      max_items?: number | null
      variance_threshold?: number | null
    },
  ): Promise<QuickBooksFpaBudgetVsActualCommentaryDraft> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/budget-vs-actual/commentary-draft`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateQuickBooksFpaPlanAssumption(
    workbookId: string,
    planId: string,
    assumptionId: string,
    req: QuickBooksFpaUpdateAssumptionRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/assumptions/${encodeURIComponent(assumptionId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateQuickBooksFpaPlanAssumptionsBulk(
    workbookId: string,
    planId: string,
    req: QuickBooksFpaUpdateAssumptionsBulkRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/assumptions/bulk`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? { updates: [] }),
      },
    )
  }

  async updateQuickBooksFpaPlanLinesBulk(
    workbookId: string,
    planId: string,
    req: QuickBooksFpaUpdateLinesBulkRequest,
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/lines/bulk`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? { updates: [] }),
      },
    )
  }

  async recalculateQuickBooksFpaPlan(
    workbookId: string,
    planId: string,
    mode: 'validate' | 'rebalance' = 'validate',
  ): Promise<QuickBooksFpaPlanDetail> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/recalculate`,
      {
        method: 'POST',
        body: JSON.stringify({ mode }),
      },
    )
  }

  async applyQuickBooksFpaMlForecastToPlan(
    workbookId: string,
    planId: string,
    req?: {
      package_id?: string | null
      scenario_key?: string | null
      include_unreviewed?: boolean
      note?: string | null
    },
  ): Promise<QuickBooksFpaApplyMlForecastResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/plans/${encodeURIComponent(planId)}/apply-ml-forecast`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async generateQuickBooksCfoPresentationAiDraft(
    workbookId: string,
    req: {
      provider?: string | null
      model_id?: string | null
      confirmed_share_evidence: true
      sections?: Array<{
        id?: string
        title?: string
        prompt?: string
        promptHints?: string
        promptAvoid?: string
      }>
    },
  ): Promise<QuickBooksCfoPresentationAiDraftResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/ai-draft`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async generateQuickBooksCfoPresentationAgentEdit(
    workbookId: string,
    req: {
      provider?: string | null
      model_id?: string | null
      confirmed_share_evidence: true
      message: string
      target_block_id?: string | null
      target_section?: string | null
      conversation_id?: string | null
    },
  ): Promise<QuickBooksCfoPresentationAgentEditResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/agent-edit`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async undoQuickBooksCfoPresentationAgentRun(
    workbookId: string,
    runId: string,
  ): Promise<QuickBooksCfoPresentationAgentUndoResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/presentation/agent-runs/${encodeURIComponent(runId)}/undo`,
      {
        method: 'POST',
      },
    )
  }

  async getQuickBooksFpaStatementDrilldown(req: {
    connection_id: string
    layout_version_id: string
    node_id: string
    period_start: string
    period_end?: string | null
    limit?: number | null
  }): Promise<{ rows: any[]; row_count: number; limit: number }> {
    const qs = new URLSearchParams()
    qs.set('connection_id', req.connection_id)
    qs.set('layout_version_id', req.layout_version_id)
    qs.set('node_id', req.node_id)
    qs.set('period_start', req.period_start)
    if (req.period_end) qs.set('period_end', req.period_end)
    if (req.limit) qs.set('limit', String(req.limit))
    return await this.request(
      `/api/fpa/quickbooks/statements/drilldown?${qs.toString()}`,
      { method: 'GET' },
    )
  }

  async listQuickBooksFpaWorkbookStatementBindings(
    workbookId: string,
  ): Promise<QuickBooksFpaWorkbookStatementBinding[]> {
    const out = await this.request<{
      bindings?: QuickBooksFpaWorkbookStatementBinding[] | null
    }>(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/statements`,
      { method: 'GET' },
    )
    return Array.isArray(out?.bindings) ? out.bindings : []
  }

  async getQuickBooksCfoCoreBootstrap(
    workbookId: string,
  ): Promise<QuickBooksCfoCoreBootstrapResponse> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/cfo-core/bootstrap`,
      {
        method: 'GET',
      },
    )
  }

  /** Provider-neutral CFO shell bootstrap. QuickBooks keeps its layout data
   * while receiving authoritative shared identity and capability metadata;
   * other providers receive a canonical virtual layout binding. */
  async getCfoCoreBootstrap(
    workbookId: string,
  ): Promise<QuickBooksCfoCoreBootstrapResponse> {
    const bootstrap = await this.getAccountingWorkbookBootstrap(workbookId)
    if (bootstrap.accounting_provider === 'quickbooks') {
      const quickBooks = await this.getQuickBooksCfoCoreBootstrap(workbookId)
      return {
        ...quickBooks,
        accounting_provider: bootstrap.accounting_provider,
        accounting_provider_display_name: bootstrap.accounting_provider_display_name || quickBooks.accounting_provider_display_name,
        accounting_company_id: bootstrap.accounting_company_id,
        accounting_connection_status: bootstrap.accounting_connection_status || quickBooks.accounting_connection_status,
        currency_code: bootstrap.currency_code || quickBooks.currency_code,
        country_code: bootstrap.country_code || quickBooks.country_code,
        capabilities: bootstrap.capabilities || {},
        capability_details: bootstrap.capability_details || {},
      }
    }
    return accountingBootstrapToCfoBootstrap(bootstrap)
  }

  async createQuickBooksFpaWorkbookStatementBinding(
    workbookId: string,
    req: {
      connection_id: string
      layout_version_id: string
      config_json?: any
    },
  ): Promise<QuickBooksFpaWorkbookStatementBinding> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/statements`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async updateQuickBooksFpaWorkbookStatementBinding(
    workbookId: string,
    bindingId: string,
    req: {
      connection_id: string
      layout_version_id: string
      config_json?: any
      native_policy_write_fence?: NativePolicyWriteFence
    },
  ): Promise<QuickBooksFpaWorkbookStatementBinding> {
    return await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/statements/${encodeURIComponent(bindingId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    )
  }

  async deleteQuickBooksFpaWorkbookStatementBinding(
    workbookId: string,
    bindingId: string,
  ): Promise<void> {
    await this.request(
      `/api/fpa/quickbooks/workbooks/${encodeURIComponent(workbookId)}/statements/${encodeURIComponent(bindingId)}`,
      {
        method: 'DELETE',
      },
    )
  }

  async getSchedule(datasourceId: string): Promise<ServerRefreshSchedule> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/schedule`,
      { method: 'GET' },
    )
  }

  async createOrUpdateSchedule(
    datasourceId: string,
    req: {
      schedule_type: 'cron' | 'interval'
      schedule_value: string
      timezone?: string | null
      requires_approval?: boolean | null
    },
  ): Promise<ServerRefreshSchedule> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/schedule`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  async deleteSchedule(datasourceId: string): Promise<{ status: string }> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/schedule`,
      { method: 'DELETE' },
    )
  }

  async triggerSchedule(
    datasourceId: string,
  ): Promise<{ status: string; message?: string }> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/schedule/trigger`,
      { method: 'POST' },
    )
  }

  async deleteDatasource(datasourceId: string): Promise<{ status: string }> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}`,
      { method: 'DELETE' },
    )
  }

  async listExtractRuns(datasourceId: string): Promise<ServerExtractRun[]> {
    return await this.request(
      `/api/datasources/${encodeURIComponent(datasourceId)}/runs`,
      { method: 'GET' },
    )
  }

  async getExtractRun(runId: string): Promise<ServerExtractRun> {
    return await this.request(`/api/runs/${encodeURIComponent(runId)}`, {
      method: 'GET',
    })
  }

  async listUserFieldOverrides(opts?: {
    dataset_id?: string | null
  }): Promise<ServerUserFieldOverride[]> {
    const qp = opts?.dataset_id
      ? `?dataset_id=${encodeURIComponent(String(opts.dataset_id))}`
      : ''
    return await this.request(`/api/user/field_overrides${qp}`, {
      method: 'GET',
    })
  }

  async upsertUserFieldOverrides(req: {
    overrides: Array<{
      dataset_id: string
      field_id: string
      display_name?: string | null
      hidden?: boolean | null
      type_override?: string | null
    }>
  }): Promise<ServerUserFieldOverride[]> {
    return await this.request('/api/user/field_overrides', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  // ---------------- Workbooks (documents + overrides) ----------------

  async listWorkbooks(opts?: {
    signal?: AbortSignal | null
  }): Promise<ServerWorkbook[]> {
    return await this.request('/api/workbooks', {
      method: 'GET',
      signal: opts?.signal ?? undefined,
    })
  }

  async createWorkbook(req?: {
    name?: string | null
    site_id?: string | null
    project_id?: string | null
    workbook_kind?: 'spreadsheet' | 'tableau' | string | null
  }): Promise<ServerWorkbook> {
    return await this.request('/api/workbooks', {
      method: 'POST',
      body: JSON.stringify({
        name: req?.name ?? null,
        site_id: req?.site_id ?? null,
        project_id: req?.project_id ?? null,
        workbook_kind: req?.workbook_kind ?? null,
      }),
    })
  }

  async getWorkbook(
    workbookId: string,
    opts?: { timeoutMs?: number },
  ): Promise<ServerWorkbook> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}`,
      { method: 'GET' },
      {
        timeoutMs: opts?.timeoutMs,
      },
    )
  }

  async renameWorkbook(
    workbookId: string,
    name: string,
  ): Promise<ServerWorkbook> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      },
    )
  }

  async moveWorkbook(
    workbookId: string,
    req: { site_id?: string | null; project_id?: string | null },
  ): Promise<ServerWorkbook> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/move`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async deleteWorkbook(workbookId: string): Promise<{ status: string }> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}`,
      {
        method: 'DELETE',
      },
    )
  }

  async getWorkbookDeleteImpact(
    workbookId: string,
  ): Promise<ServerWorkbookDeleteImpact> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/delete-impact`,
      {
        method: 'GET',
      },
    )
  }

  async deleteWorkbookWithOptions(
    workbookId: string,
    options?: { delete_unshared_datasources?: boolean | null },
  ): Promise<ServerWorkbookDeleteResult> {
    const qp = new URLSearchParams()
    if (options?.delete_unshared_datasources != null) {
      qp.set(
        'delete_unshared_datasources',
        options.delete_unshared_datasources ? 'true' : 'false',
      )
    }
    const suffix = qp.toString()
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/delete_with_options${suffix ? `?${suffix}` : ''}`,
      { method: 'DELETE' },
    )
  }

  async listAdminSites(): Promise<ServerSite[]> {
    return await this.request('/api/admin/sites', { method: 'GET' })
  }

  async getPlatformAccess(): Promise<{ role: 'founder' | 'admin' | 'sales' | null }> {
    return await this.request('/api/platform/access', { method: 'GET' })
  }

  async listPlatformTenants(): Promise<PlatformTenantDirectory> {
    return await this.request('/api/platform/tenants', { method: 'GET' })
  }

  async extendPlatformTenantTrial(
    tenantId: string,
    request: PlatformTrialExtensionRequest,
  ): Promise<PlatformTrialExtensionResponse> {
    return await this.request(`/api/platform/tenants/${encodeURIComponent(tenantId)}/trial-extension`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async grantPlatformSiteAccess(siteId: string, request: {
    approved_by_name: string
    approval_received_on: string
    approval_note?: string
    confirm_verbal_approval: boolean
  }): Promise<{ status: string; grant_id: string }> {
    return await this.request(`/api/platform/sites/${encodeURIComponent(siteId)}/access`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async revokePlatformSiteAccess(siteId: string): Promise<{ status: string }> {
    return await this.request(`/api/platform/sites/${encodeURIComponent(siteId)}/access`, { method: 'DELETE' })
  }

  async listPlatformStaff(): Promise<PlatformStaff[]> {
    return await this.request('/api/platform/staff', { method: 'GET' })
  }

  async grantPlatformStaff(email: string, role: 'founder' | 'sales' | 'admin'): Promise<{ status: string }> {
    return await this.request('/api/platform/staff', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
  }

  async revokePlatformStaff(userId: string): Promise<{ status: string }> {
    return await this.request(`/api/platform/staff/${encodeURIComponent(userId)}`, { method: 'DELETE' })
  }

  async listMySites(): Promise<ServerSite[]> {
    return await this.request('/api/sites', { method: 'GET' })
  }

  async createFirstSite(req: {
    name: string
    slug?: string | null
  }): Promise<ServerSite> {
    return await this.request('/api/sites/bootstrap', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async createCfoExperience(req: {
    company_name: string
    site_id?: string | null
    project_id?: string | null
  }): Promise<ServerCfoExperience> {
    return await this.request('/api/cfo/experiences', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async getXeroAvailability(): Promise<XeroAvailability> {
    return await this.request('/api/integrations/xero/availability', { method: 'GET' })
  }

  async getAccountingProviders(siteId?: string | null): Promise<AccountingProviderCatalog> {
    const query = siteId ? `?${new URLSearchParams({ site_id: siteId }).toString()}` : ''
    return await this.request(`/api/integrations/accounting/providers${query}`, { method: 'GET' })
  }

  async listSageAccountingBusinesses(credentialId: string): Promise<SageBusinessCandidate[]> {
    const q = new URLSearchParams({ credential_id: credentialId })
    const out = await this.request<{ businesses: SageBusinessCandidate[] }>(
      `/api/integrations/sage-accounting/businesses?${q.toString()}`, { method: 'GET' },
    )
    return out.businesses
  }

  async listSageAccountingEntities(credentialId: string, businessId: string): Promise<SageEntityCandidate[]> {
    const q = new URLSearchParams({ credential_id: credentialId, business_id: businessId })
    const out = await this.request<{ entities: SageEntityCandidate[] }>(
      `/api/integrations/sage-accounting/entities?${q.toString()}`, { method: 'GET' },
    )
    return out.entities
  }

  async listSageAccountingBooks(
    credentialId: string, businessId: string, entityId: string,
  ): Promise<SageBookCandidate[]> {
    const q = new URLSearchParams({ credential_id: credentialId, business_id: businessId, entity_id: entityId })
    const out = await this.request<{ books: SageBookCandidate[] }>(
      `/api/integrations/sage-accounting/books?${q.toString()}`, { method: 'GET' },
    )
    return out.books
  }

  async probeSageAccountingCapabilities(req: {
    credential_id: string
    business_id: string
    entity_id: string
    book_id: string
  }): Promise<SageCapabilityProbe> {
    const q = new URLSearchParams({
      credential_id: req.credential_id,
      business_id: req.business_id,
      entity_id: req.entity_id,
      book_id: req.book_id,
    })
    const out = await this.request<{ capabilities: SageCapabilityProbe }>(
      `/api/integrations/sage-accounting/capabilities?${q.toString()}`, { method: 'GET' },
    )
    return out.capabilities
  }

  async provisionSageAccountingCompany(req: {
    credential_id: string
    site_id: string
    business_id: string
    entity_id: string
    book_id: string
    idempotency_key: string
  }): Promise<SageProvisionedCompany> {
    const out = await this.request<{ company: SageProvisionedCompany }>(
      '/api/integrations/sage-accounting/provision',
      { method: 'POST', body: JSON.stringify(req) },
    )
    return out.company
  }

  async createXeroAuthorizeUrl(redirect: string): Promise<{ authorize_url: string }> {
    const q = new URLSearchParams({ redirect })
    return await this.request(`/api/integrations/xero/authorize?${q.toString()}`, { method: 'GET' })
  }

  async createXeroIdentityAuthorizeUrl(redirect: string): Promise<{ authorize_url: string }> {
    const q = new URLSearchParams({ redirect })
    return await this.request(`/api/integrations/xero/identity/authorize?${q.toString()}`, { method: 'GET' })
  }

  /** Completes an emailed "Sign up with Xero" confirmation link; the account exists afterwards, unsigned-in. */
  async verifyXeroSignupEmail(token: string): Promise<{ status: 'email_verified' }> {
    return await this.request('/api/integrations/xero/identity/verify-email', {
      method: 'POST', body: JSON.stringify({ token }),
    })
  }

  async exchangeXeroIdentitySession(): Promise<ServerLoginResponse> {
    const out = await this.request<ServerLoginResponse>('/api/integrations/xero/identity/session', {
      method: 'POST', body: JSON.stringify({}), credentials: 'include',
    })
    this.setSessionTokens(out.access_token, out.refresh_token, out.refresh_generation)
    return out
  }

  async exchangeXeroAccountingHandoff(): Promise<{ grant_id: string }> {
    return await this.request<{ grant_id: string }>(
      '/api/integrations/xero/accounting/session',
      { method: 'POST', body: JSON.stringify({}), credentials: 'include' },
    )
  }

  async listXeroOrganizations(grantId: string): Promise<XeroOrganizationCandidate[]> {
    const q = new URLSearchParams({ grant_id: grantId })
    return await this.request(`/api/integrations/xero/organizations?${q.toString()}`, { method: 'GET' })
  }

  async provisionXeroOrganizations(req: {
    grant_id: string
    organization_ids: string[]
    site_id?: string | null
    idempotency_key: string
  }): Promise<{ companies: XeroProvisionedCompany[]; site_id: string }> {
    return await this.request('/api/integrations/xero/organizations/provision', {
      method: 'POST', body: JSON.stringify(req),
    })
  }

  async createAdminSite(req: {
    name: string
    slug?: string | null
  }): Promise<ServerSite> {
    return await this.request('/api/admin/sites', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async updateAdminSite(
    siteId: string,
    req: { name?: string | null; slug?: string | null; cfo_reporting_enabled?: boolean | null },
  ): Promise<ServerSite> {
    return await this.request(
      `/api/admin/sites/${encodeURIComponent(siteId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async deleteAdminSite(siteId: string): Promise<{ status: string }> {
    return await this.request(
      `/api/admin/sites/${encodeURIComponent(siteId)}`,
      { method: 'DELETE' },
    )
  }

  async listAdminUsers(): Promise<ServerTenantUser[]> {
    return await this.request('/api/admin/users', { method: 'GET' })
  }

  async inviteAdminUser(req: {
    email: string
    tenant_admin?: boolean | null
    /** At least one site, each administered by the inviter; the server refuses any other site. */
    memberships: Array<{
      site_id: string
      site_role: string
      status?: string | null
    }>
  }): Promise<ServerTenantUser> {
    return await this.request('/api/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify(req ?? {}),
    })
  }

  async updateAdminUserMemberships(
    userId: string,
    req: {
      tenant_admin?: boolean | null
      memberships: Array<{
        site_id: string
        site_role: string
        status?: string | null
      }>
    },
  ): Promise<ServerTenantUser> {
    return await this.request(
      `/api/admin/users/${encodeURIComponent(userId)}/memberships`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async deleteAdminUser(userId: string): Promise<{ status: string }> {
    return await this.request(
      `/api/admin/users/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    )
  }

  async getSiteSummary(siteSlug: string): Promise<ServerSiteSummary> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/summary`,
      { method: 'GET' },
    )
  }

  /** Firm Cockpit: the firm's whole book of clients (attention feed + roster + close pipeline). */
  async getFirmPortfolio(siteSlug: string): Promise<ServerFirmPortfolio> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/firm/portfolio`,
      { method: 'GET' },
    )
  }

  /** Assign (or clear, with null) the responsible firm member for a client (CFO workbook). */
  async assignFirmClient(
    siteSlug: string,
    workbookId: string,
    assignedToUserId: string | null,
  ): Promise<{
    ok: boolean
    workbook_id: string
    assigned_to_user_id: string | null
  }> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/clients/${encodeURIComponent(workbookId)}/assignee`,
      {
        method: 'PATCH',
        body: JSON.stringify({ assigned_to_user_id: assignedToUserId }),
      },
    )
  }

  async listSiteUsers(siteSlug: string): Promise<ServerSiteMembership[]> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/users`,
      { method: 'GET' },
    )
  }

  async generateSiteUserSetupLink(siteSlug: string, userId: string): Promise<{ setup_url: string; expires_at: string }> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/users/${encodeURIComponent(userId)}/setup-link`,
      { method: 'POST' },
    )
  }

  async createSiteUser(
    siteSlug: string,
    req: { email: string; site_role: string; status?: string | null },
  ): Promise<ServerSiteMembership> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/users`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateSiteUser(
    siteSlug: string,
    userId: string,
    req: { site_role?: string | null; status?: string | null },
  ): Promise<ServerSiteMembership> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/users/${encodeURIComponent(userId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async deleteSiteUser(
    siteSlug: string,
    userId: string,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/users/${encodeURIComponent(userId)}`,
      {
        method: 'DELETE',
      },
    )
  }

  async listSiteGroups(siteSlug: string): Promise<ServerGroup[]> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/groups`,
      { method: 'GET' },
    )
  }

  async createSiteGroup(
    siteSlug: string,
    req: {
      name: string
      slug?: string | null
      grant_site_role?: string | null
    },
  ): Promise<ServerGroup> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/groups`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateSiteGroup(
    siteSlug: string,
    groupId: string,
    req: {
      name?: string | null
      slug?: string | null
      grant_site_role?: string | null
    },
  ): Promise<ServerGroup> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/groups/${encodeURIComponent(groupId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async deleteSiteGroup(
    siteSlug: string,
    groupId: string,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/groups/${encodeURIComponent(groupId)}`,
      {
        method: 'DELETE',
      },
    )
  }

  async replaceSiteGroupMembers(
    siteSlug: string,
    groupId: string,
    user_ids: string[],
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/groups/${encodeURIComponent(groupId)}/members`,
      {
        method: 'PUT',
        body: JSON.stringify({ user_ids }),
      },
    )
  }

  async listSiteProjects(siteSlug: string): Promise<ServerProject[]> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/projects`,
      { method: 'GET' },
    )
  }

  async createSiteProject(
    siteSlug: string,
    req: {
      name: string
      slug?: string | null
      parent_project_id?: string | null
      description?: string | null
      asset_permission_mode?: string | null
      is_personal_space?: boolean | null
    },
  ): Promise<ServerProject> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/projects`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async updateSiteProject(
    siteSlug: string,
    projectId: string,
    req: {
      name?: string | null
      slug?: string | null
      description?: string | null
      asset_permission_mode?: string | null
    },
  ): Promise<ServerProject> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/projects/${encodeURIComponent(projectId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async moveSiteProject(
    siteSlug: string,
    projectId: string,
    parent_project_id?: string | null,
  ): Promise<ServerProject> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/projects/${encodeURIComponent(projectId)}/move`,
      {
        method: 'POST',
        body: JSON.stringify({ parent_project_id: parent_project_id ?? null }),
      },
    )
  }

  async deleteSiteProject(
    siteSlug: string,
    projectId: string,
  ): Promise<{ status: string }> {
    return await this.request(
      `/api/sites/${encodeURIComponent(siteSlug)}/projects/${encodeURIComponent(projectId)}`,
      {
        method: 'DELETE',
      },
    )
  }

  async listProjectPermissions(
    projectId: string,
  ): Promise<ServerProjectPermissionRule[]> {
    return await this.request(
      `/api/projects/${encodeURIComponent(projectId)}/permissions`,
      { method: 'GET' },
    )
  }

  async putProjectPermissions(
    projectId: string,
    rules: Array<{
      subject_kind: 'user' | 'group' | string
      subject_id: string
      capabilities: Record<string, string>
      is_project_leader?: boolean | null
    }>,
  ): Promise<ServerProjectPermissionRule[]> {
    return await this.request(
      `/api/projects/${encodeURIComponent(projectId)}/permissions`,
      {
        method: 'PUT',
        body: JSON.stringify({ rules }),
      },
    )
  }

  async upsertWorkbookUserState(
    workbookId: string,
    req?: ServerWorkbookUserStateRequest,
  ): Promise<ServerWorkbookUserState> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/user_state`,
      {
        method: 'POST',
        body: JSON.stringify({
          last_opened_tab: req?.last_opened_tab ?? null,
          last_authoring_mode: req?.last_authoring_mode ?? null,
          thumbnail_url: req?.thumbnail_url ?? null,
          is_favorite:
            typeof req?.is_favorite === 'boolean' ? req.is_favorite : null,
        }),
      },
    )
  }

  async exportWorkbook(workbookId: string): Promise<ServerWorkbookExport> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/export`,
      { method: 'GET' },
    )
  }

  async importWorkbook(
    req: ServerWorkbookImportRequest,
  ): Promise<ServerWorkbookImportResponse> {
    return await this.request('/api/workbooks/import', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  // ---------------- Tableau Import ----------------

  async tableauCreateSession(
    req: TableauCreateSessionRequest,
  ): Promise<TableauCreateSessionResponse> {
    if (this.shouldUseElectronTableauBridge()) {
      return await this.electronInvoke('tableau_create_session_local', { req })
    }
    return await this.request('/api/tableau/session', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async tableauDeleteSession(sessionKey: string): Promise<{ ok: boolean }> {
    if (this.shouldUseElectronTableauBridge()) {
      return await this.electronInvoke('tableau_delete_session_local', {
        session_key: String(sessionKey || '').trim(),
      })
    }
    return await this.request(
      `/api/tableau/session/${encodeURIComponent(String(sessionKey))}`,
      { method: 'DELETE' },
    )
  }

  async tableauKeychainGet(): Promise<TableauKeychainGetResponse> {
    if (!this.shouldUseElectronTableauBridge()) return { value: null }
    const keychainTimeoutMs = 90_000
    const res: any = await this.electronInvokeWithTimeoutRetry(
      'tableau_keychain_get_local',
      {},
      {
        timeoutMs: keychainTimeoutMs,
        maxAttempts: 1,
        retryDelayMs: 0,
        opName: 'tableau keychain get',
      },
    )
    const value = res?.value
    if (!value || typeof value !== 'object')
      return { value: null, debug: res?.debug }
    const creds = {
      server_url: String(value.server_url ?? '').trim(),
      site_url: String(value.site_url ?? '').trim(),
      pat_name: String(value.pat_name ?? '').trim(),
      pat_secret: String(value.pat_secret ?? ''),
    }
    const empty =
      !creds.server_url &&
      !creds.site_url &&
      !creds.pat_name &&
      !creds.pat_secret
    return { value: empty ? null : creds, debug: res?.debug }
  }

  async tableauKeychainSet(
    payload: TableauKeychainCredentials,
  ): Promise<TableauKeychainSetResponse> {
    if (!this.shouldUseElectronTableauBridge()) return { ok: false }
    return await this.electronInvoke('tableau_keychain_set_local', {
      req: payload,
    })
  }

  async tableauKeychainClear(): Promise<TableauKeychainSetResponse> {
    if (!this.shouldUseElectronTableauBridge()) return { ok: false }
    return await this.electronInvoke('tableau_keychain_clear_local', {})
  }

  async tableauImportWorkbookFile(
    file: File,
    workbookId: string,
  ): Promise<TableauWorkbookSummaryResponse> {
    if (this.shouldUseElectronTableauBridge()) {
      const local = await this.tableauImportWorkbookFileElectronLocal(file)
      return local
    }

    const scopedWorkbookId = String(workbookId || '').trim()
    if (!scopedWorkbookId) throw new Error('Missing workbookId for web Tableau import')
    const formData = new FormData()
    formData.append('file', file)
    formData.append('workbook_id', scopedWorkbookId)
    const path = '/api/workbooks/import_file'
    const runUpload = async (
      allowAuthRetry: boolean,
    ): Promise<TableauWorkbookSummaryResponse> => {
      const headers: Record<string, string> = {}
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`
      }

      const url = joinUrl(this.baseUrl, path)
      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        url.startsWith('http://')
      ) {
        const httpsUrl = url.replace('http://', 'https://')
        try {
          const res = await fetch(httpsUrl, {
            method: 'POST',
            headers,
            body: formData,
          })
          if (res.ok)
            return (await res.json()) as TableauWorkbookSummaryResponse
          const text = await res.text().catch(() => '')
          const message = text || res.statusText
          if (this.isAuthFailure(res.status, message)) {
            if (
              allowAuthRetry &&
              (await this.maybeRecoverAuthFailure(path, res.status, message))
            ) {
              return await runUpload(false)
            }
            throw this.buildApiError(res.status, message, httpsUrl)
          }
        } catch (e) {
          if (typeof (e as any)?.status === 'number') throw e
          console.warn(
            '[ServerApiClient] HTTPS request failed, trying HTTP fallback:',
            e,
          )
        }
      }

      const res = await fetch(url, { method: 'POST', headers, body: formData })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        const message = text || res.statusText
        if (
          allowAuthRetry &&
          (await this.maybeRecoverAuthFailure(path, res.status, message))
        ) {
          return await runUpload(false)
        }
        throw this.buildApiError(res.status, message, url)
      }
      return (await res.json()) as TableauWorkbookSummaryResponse
    }
    return await runUpload(true)
  }

  private async tableauImportWorkbookFileElectronLocal(
    file: File,
  ): Promise<TableauWorkbookSummaryResponse> {
    const filename = String((file as any)?.name || '').trim()
    if (!/\.twb(x)?$/i.test(filename)) {
      throw new Error('Please select a .twb or .twbx file')
    }

    const mod = await import('../tableau/inBrowserWorkbookImport')
    const parsed = await mod.importTableauWorkbookWithWasm(file)
    const workbookIr = parsed.workbookIr ?? {}

    try {
      if (!workbookIr.meta) workbookIr.meta = {}
      // Preserve source XML for baseline pinning and parity/debug flows.
      ;(workbookIr.meta as any).source_twb_xml = parsed.twbXml
    } catch {
      // ignore non-critical metadata write failures
    }

    await this.tableauImportLocalFromWasm({
      import_id: parsed.importId,
      container_kind: parsed.fileKind,
      twb_entry_name: parsed.twbEntryName ?? null,
      twb_xml: parsed.twbXml,
      workbook_ir: workbookIr,
      original_filename: filename || null,
      bytes: parsed.fileKind === 'twbx' ? parsed.fileBytes : null,
    })

    let summaryIr: any = workbookIr
    try {
      const restored = await this.tableauImportGetIR(parsed.importId)
      if (restored && typeof restored === 'object') {
        summaryIr = restored
      }
    } catch {
      // Keep import usable if IR restore fails; local extraction can still continue.
    }
    try {
      if (!summaryIr.meta) summaryIr.meta = {}
      ;(summaryIr.meta as any).source_twb_xml =
        (summaryIr.meta as any).source_twb_xml ?? parsed.twbXml
      ;(summaryIr.meta as any).backend_import_id =
        String(
          (summaryIr.meta as any).backend_import_id ?? parsed.importId,
        ).trim() || parsed.importId
    } catch {
      // ignore non-critical metadata write failures
    }

    const worksheetViews = Array.isArray((summaryIr as any)?.worksheets)
      ? (summaryIr as any).worksheets
          .map((ws: any) => {
            const name = String(ws?.name || '').trim()
            if (!name) return null
            return { id: name, name }
          })
          .filter(Boolean)
      : []
    const dashboardViews = Array.isArray((summaryIr as any)?.dashboards)
      ? (summaryIr as any).dashboards
          .map((db: any) => {
            const name = String(db?.name || '').trim()
            if (!name) return null
            return { id: name, name }
          })
          .filter(Boolean)
      : []
    const views = [...worksheetViews, ...dashboardViews] as Array<{
      id: string
      name: string
    }>

    return {
      summary: summaryIr,
      views,
      parse_diagnostics: (summaryIr as any)?.parse_diagnostics ?? null,
      unsupported_constructs:
        (summaryIr as any)?.unsupported_constructs ?? null,
      import: {
        import_id: parsed.importId,
        file_kind: parsed.fileKind,
        source: 'electron_local',
      },
      debug: {
        mode: 'electron_local',
      },
    }
  }

  async tableauImportApplyPatch(importId: string, ops: any[]): Promise<any> {
    void importId
    void ops
    throw new Error('Patch endpoint retired: use canonical import/export APIs')
  }

  async tableauImportGetManifest(importId: string): Promise<any> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      return await this.electronInvokeWithTimeoutRetry(
        'tableau_get_manifest_local',
        { req: { import_id: id } },
        {
          opName: 'manifest restore',
          importId: id,
        },
      )
    }
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/manifest`,
      {
        method: 'GET',
      },
    )
  }

  async tableauImportGetIR(importId: string): Promise<any> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      const irTimeoutMs = 90_000
      const out = await this.electronInvokeWithTimeoutRetry<any>(
        'tableau_get_ir_local',
        { req: { import_id: id } },
        {
          timeoutMs: irTimeoutMs,
          opName: 'IR restore',
          importId: id,
        },
      )
      if (
        out &&
        typeof out === 'object' &&
        (out as any).__fg_missing_local_import === true
      ) {
        const err: any = new Error(`Unknown import_id: ${id}`)
        err.status = 404
        err.importId = id
        err.command = 'tableau_get_ir_local'
        throw err
      }
      return out
    }
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/ir`,
      {
        method: 'GET',
      },
    )
  }

  async tableauImportGetSemanticRegistry(
    importId: string,
  ): Promise<ServerSemanticRegistry> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      try {
        return await this.electronInvokeWithTimeoutRetry<ServerSemanticRegistry>(
          'tableau_get_semantic_registry_local',
          { req: { import_id: id } },
          {
            opName: 'semantic registry lookup',
            importId: id,
          },
        )
      } catch (err) {
        if (this.isMissingLocalArtifactError(err)) {
          return {}
        }
        throw err
      }
    }
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/semantic-registry`,
      { method: 'GET' },
    )
  }

  async tableauImportPutSemanticRegistry(
    importId: string,
    registry: ServerSemanticRegistry,
  ): Promise<ServerSemanticRegistry> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      return await this.electronInvoke('tableau_put_semantic_registry_local', {
        req: { import_id: id, value: registry ?? {} },
      })
    }
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/semantic-registry`,
      {
        method: 'PUT',
        body: JSON.stringify(registry ?? {}),
      },
    )
  }

  async workbookTableauGetSemanticRegistry(
    workbookId: string,
  ): Promise<ServerSemanticRegistry> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    return await this.request(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/semantic-registry`,
      {
        method: 'GET',
      },
    )
  }

  async workbookTableauPutSemanticRegistry(
    workbookId: string,
    registry: ServerSemanticRegistry,
  ): Promise<ServerSemanticRegistry> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    return await this.request(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/semantic-registry`,
      {
        method: 'PUT',
        body: JSON.stringify(registry ?? {}),
      },
    )
  }

  async tableauImportGetBindingMap(
    importId: string,
  ): Promise<ServerBindingMap> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      const cached = _localBindingMapCache.get(id)
      const ageMs = cached
        ? Math.max(0, Date.now() - (cached.ts || 0))
        : Number.POSITIVE_INFINITY
      const hasFreshUsableCache =
        Boolean(cached) &&
        ageMs < LOCAL_BINDING_MAP_CACHE_TTL_MS &&
        hasUsableBindingMap(cached?.bindingMap)
      if (hasFreshUsableCache && cached) {
        return cached.bindingMap
      }
      if (cached && !hasUsableBindingMap(cached.bindingMap)) {
        _localBindingMapCache.delete(id)
        persistLocalBindingMapCache()
      }

      let inflight = _localBindingMapInflight.get(id)
      if (!inflight) {
        inflight = (async () => {
          let out: ServerBindingMap | null = null
          try {
            out = await this.electronInvokeWithTimeoutRetry<ServerBindingMap>(
              'tableau_get_binding_map_local',
              { req: { import_id: id } },
              {
                opName: 'binding map lookup',
                importId: id,
              },
            )
          } catch (err) {
            if (this.isMissingLocalArtifactError(err)) {
              const missingErr: any = new Error(
                `Binding map not ready for import_id=${id}`,
              )
              missingErr.status = 404
              missingErr.importId = id
              missingErr.command = 'tableau_get_binding_map_local'
              throw missingErr
            }
            throw err
          }
          const bindingMap = out && typeof out === 'object' ? out : null
          if (!hasUsableBindingMap(bindingMap)) {
            const missingErr: any = new Error(
              `Binding map not ready for import_id=${id}`,
            )
            missingErr.status = 404
            missingErr.importId = id
            missingErr.command = 'tableau_get_binding_map_local'
            throw missingErr
          }
          _localBindingMapCache.set(id, {
            bindingMap,
            ts: Date.now(),
          })
          persistLocalBindingMapCache()
          return bindingMap
        })()
        _localBindingMapInflight.set(id, inflight)
        attachInflightCleanup(inflight, () => {
          const current = _localBindingMapInflight.get(id)
          if (current === inflight) {
            _localBindingMapInflight.delete(id)
          }
        })
      }

      try {
        return await inflight
      } catch (err) {
        if (cached && hasUsableBindingMap(cached.bindingMap)) {
          console.warn(
            '[ServerApiClient] local binding-map failed; using cached value',
            {
              importId: id,
              cacheAgeMs: Math.round(ageMs),
              error: err,
            },
          )
          return cached.bindingMap
        }
        throw err
      }
    }
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/binding-map`,
      { method: 'GET' },
    )
  }

  async tableauImportPutBindingMap(
    importId: string,
    bindingMap: ServerBindingMap,
  ): Promise<ServerBindingMap> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      const out = await this.electronInvokeWithTimeoutRetry(
        'tableau_put_binding_map_local',
        {
          req: { import_id: id, value: bindingMap ?? {} },
        },
        {
          opName: 'binding map write',
          importId: id,
        },
      )
      const next =
        out && typeof out === 'object'
          ? (out as ServerBindingMap)
          : (bindingMap ?? {})
      _localBindingMapCache.set(id, {
        bindingMap: next,
        ts: Date.now(),
      })
      persistLocalBindingMapCache()
      return next
    }
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/binding-map`,
      {
        method: 'PUT',
        body: JSON.stringify(bindingMap ?? {}),
      },
    )
  }

  async workbookTableauGetBindingMap(
    workbookId: string,
  ): Promise<ServerBindingMap> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    return await this.request(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/binding-map`,
      {
        method: 'GET',
      },
    )
  }

  async workbookTableauPutBindingMap(
    workbookId: string,
    bindingMap: ServerBindingMap,
  ): Promise<ServerBindingMap> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    return await this.request(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/binding-map`,
      {
        method: 'PUT',
        body: JSON.stringify(bindingMap ?? {}),
      },
    )
  }

  async tableauPublishDashboardPackage(
    importId: string,
    payload?: {
      dashboard_id?: string | null
      dashboard_name?: string | null
      include_all_datasources?: boolean | null
    },
  ): Promise<any> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/publish-dashboard`,
      {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      },
    )
  }

  async datasetGetManifest(datasetId: string, version: string): Promise<any> {
    const ds = String(datasetId || '').trim()
    const v = String(version || '').trim()
    if (!ds) throw new Error('Missing datasetId')
    if (!v) throw new Error('Missing version')
    return await this.request(
      `/api/datasets/${encodeURIComponent(ds)}/${encodeURIComponent(v)}/manifest`,
      {
        method: 'GET',
      },
    )
  }

  async dashboardPackageGetManifest(
    packageId: string,
    version: string,
  ): Promise<any> {
    const pid = String(packageId || '').trim()
    const v = String(version || '').trim()
    if (!pid) throw new Error('Missing packageId')
    if (!v) throw new Error('Missing version')
    return await this.request(
      `/api/dashboards/${encodeURIComponent(pid)}/${encodeURIComponent(v)}/package/manifest`,
      {
        method: 'GET',
      },
    )
  }

  async tableauImportGetExtractionStatus(
    importId: string,
    debug?: {
      worksheetName?: string | null
      preferredTokens?: string[]
      preferredNames?: string[]
      preferredIds?: string[]
      preferredMatchCount?: number
      preferredReadyCount?: number
      preferredFailedCount?: number
      attempt?: number
      logBody?: boolean
    },
  ): Promise<{
    results: any[]
    debug?: {
      backendInstance?: string | null
      artifactsDir?: string | null
      dumpPath?: string | null
    }
    worksheetArtifactSummary?: WorksheetArtifactProgressSummary | null
    run_id?: string | null
    run_status?: string | null
    stable_error_code?: string | null
  }> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      const cached = _localExtractionStatusCache.get(id)
      const ageMs = cached
        ? Math.max(0, Date.now() - (cached.ts || 0))
        : Number.POSITIVE_INFINITY
      const cacheEligible =
        Boolean(cached) &&
        ageMs < LOCAL_EXTRACTION_STATUS_CACHE_TTL_MS &&
        isFinalLocalExtractionResults(cached?.results)
      if (cacheEligible && cached) {
        return {
          results: cached.results || [],
          debug: cached.debug,
          worksheetArtifactSummary: null,
        }
      }

      let inflight = _localExtractionStatusInflight.get(id)
      if (!inflight) {
        inflight = (async () => {
          const res = await this.electronInvokeWithTimeoutRetry<any>(
            'tableau_get_extraction_status_local',
            { req: { import_id: id } },
            {
              opName: 'extraction status lookup',
              importId: id,
              timeoutMs: LOCAL_EXTRACTION_STATUS_LOOKUP_TIMEOUT_MS,
              maxAttempts: LOCAL_EXTRACTION_STATUS_LOOKUP_MAX_ATTEMPTS,
            },
          )
          const out = {
            results: Array.isArray(res?.results) ? res.results : [],
            debug: {
              backendInstance: 'electron-sidecar',
              artifactsDir: null,
              dumpPath: null,
            } as ExtractionStatusDebugInfo,
            worksheetArtifactSummary:
              res &&
              typeof res === 'object' &&
              (res as any).worksheet_artifact_summary
                ? ((res as any)
                    .worksheet_artifact_summary as WorksheetArtifactProgressSummary)
                : null,
          }
          if (out.results.length > 0) {
            _localExtractionStatusCache.set(id, {
              results: out.results,
              debug: out.debug,
              ts: Date.now(),
            })
            if (isFinalLocalExtractionResults(out.results)) {
              persistLocalExtractionStatusCache()
            }
          }
          return out
        })()
        _localExtractionStatusInflight.set(id, inflight)
        attachInflightCleanup(inflight, () => {
          const current = _localExtractionStatusInflight.get(id)
          if (current === inflight) {
            _localExtractionStatusInflight.delete(id)
          }
        })
      }

      try {
        return await inflight
      } catch (err) {
        if (cached) {
          console.warn(
            '[ServerApiClient] local extraction-status failed; using cached status',
            {
              importId: id,
              cacheAgeMs: Math.round(ageMs),
              error: err,
            },
          )
          return {
            results: cached.results || [],
            debug: cached.debug,
            worksheetArtifactSummary: null,
          }
        }
        throw err
      }
    }
    const params = new URLSearchParams()
    if (isFuseGridNativeDesktopShell()) {
      params.set('local_paths', '1')
    }
    if (debug) {
      params.set('debug', '1')
      if (debug.worksheetName)
        params.set('worksheet_name', String(debug.worksheetName))
      if (
        Array.isArray(debug.preferredTokens) &&
        debug.preferredTokens.length
      ) {
        params.set('preferred_tokens', JSON.stringify(debug.preferredTokens))
      }
      if (Array.isArray(debug.preferredNames) && debug.preferredNames.length) {
        params.set('preferred_names', JSON.stringify(debug.preferredNames))
      }
      if (Array.isArray(debug.preferredIds) && debug.preferredIds.length) {
        params.set('preferred_ids', JSON.stringify(debug.preferredIds))
      }
      if (typeof debug.preferredMatchCount === 'number')
        params.set('preferred_match_count', String(debug.preferredMatchCount))
      if (typeof debug.preferredReadyCount === 'number')
        params.set('preferred_ready_count', String(debug.preferredReadyCount))
      if (typeof debug.preferredFailedCount === 'number')
        params.set('preferred_failed_count', String(debug.preferredFailedCount))
      if (typeof debug.attempt === 'number')
        params.set('attempt', String(debug.attempt))
      if (debug.logBody) params.set('log_body', '1')
    }
    const cacheKey = `${id}?${params.toString()}`
    const requestEpoch = _remoteExtractionStatusEpoch.get(id) ?? 0
    const cachedRemote = _remoteExtractionStatusCache.get(cacheKey)
    const remoteCacheAgeMs = cachedRemote
      ? Math.max(0, Date.now() - (cachedRemote.ts || 0))
      : Number.POSITIVE_INFINITY
    if (
      cachedRemote &&
      remoteCacheAgeMs <
        getRemoteExtractionStatusCacheTtlMs(cachedRemote.payload)
    ) {
      return cachedRemote.payload
    }
    let inflight = _remoteExtractionStatusInflight.get(cacheKey)
    if (inflight) {
      try {
        return await inflight
      } catch (err) {
        if (cachedRemote) return cachedRemote.payload
        throw err
      }
    }
    const method = 'GET'

    const runFetch = async (
      fullUrl: string,
      allowAuthRetry: boolean,
    ): Promise<{ data: any; headers: Headers }> => {
      const headers: Record<string, string> = {}
      if (this.token) headers['Authorization'] = `Bearer ${this.token}`
      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null
      const timeoutMs = 20000
      const timer = controller
        ? setTimeout(() => controller.abort(), timeoutMs)
        : null
      let res: Response
      try {
        res = await fetch(fullUrl, {
          method,
          headers,
          cache: 'no-store',
          signal: controller ? controller.signal : undefined,
        })
      } catch (err: any) {
        if (
          err &&
          (err.name === 'AbortError' ||
            /aborted/i.test(String(err?.message || '')))
        ) {
          throw new Error(`Extraction status timed out after ${timeoutMs}ms`)
        }
        throw err
      } finally {
        if (timer != null) clearTimeout(timer)
      }
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        const message = text || res.statusText
        if (
          allowAuthRetry &&
          (await this.maybeRecoverAuthFailure(fullUrl, res.status, message))
        ) {
          return await runFetch(fullUrl, false)
        }
        throw this.buildApiError(res.status, message, fullUrl)
      }
      const data = await res.json()
      return { data, headers: res.headers }
    }

    inflight = (async (): Promise<ExtractionStatusPayload> => {
      const requestParams = new URLSearchParams(params)
      requestParams.set('_ts', String(Date.now()))
      const qs = requestParams.toString()
      const url = `/api/tableau/imports/${encodeURIComponent(id)}/extraction-status${qs ? `?${qs}` : ''}`
      const fullUrl = joinUrl(this.baseUrl, url)
      let out: { data: any; headers: Headers }
      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        fullUrl.startsWith('http://')
      ) {
        const httpsUrl = fullUrl.replace('http://', 'https://')
        try {
          out = await runFetch(httpsUrl, true)
        } catch (e) {
          if (typeof (e as any)?.status === 'number') throw e
          console.warn(
            '[ServerApiClient] HTTPS request failed, trying HTTP fallback:',
            e,
          )
          out = await runFetch(fullUrl, true)
        }
      } else {
        out = await runFetch(fullUrl, true)
      }

      const debugInfo = {
        backendInstance: out.headers.get('x-fg-backend-instance'),
        artifactsDir: out.headers.get('x-fg-artifacts-dir'),
        dumpPath: out.headers.get('x-fg-extraction-status-dump'),
      }
      const payload: ExtractionStatusPayload = {
        results: Array.isArray((out.data as any)?.results)
          ? (out.data as any).results
          : [],
        debug: debugInfo,
        worksheetArtifactSummary:
          out.data &&
          typeof out.data === 'object' &&
          (out.data as any).worksheet_artifact_summary
            ? ((out.data as any)
                .worksheet_artifact_summary as WorksheetArtifactProgressSummary)
            : null,
        run_id:
          typeof (out.data as any)?.run_id === 'string'
            ? String((out.data as any).run_id)
            : null,
        run_status:
          typeof (out.data as any)?.run_status === 'string'
            ? String((out.data as any).run_status)
            : null,
        stable_error_code:
          typeof (out.data as any)?.stable_error_code === 'string'
            ? String((out.data as any).stable_error_code)
            : null,
      }
      if ((_remoteExtractionStatusEpoch.get(id) ?? 0) === requestEpoch) {
        _remoteExtractionStatusCache.set(cacheKey, {
          payload,
          ts: Date.now(),
        })
      }
      return payload
    })()
    _remoteExtractionStatusInflight.set(cacheKey, inflight)
    attachInflightCleanup(inflight, () => {
      const current = _remoteExtractionStatusInflight.get(cacheKey)
      if (current === inflight) {
        _remoteExtractionStatusInflight.delete(cacheKey)
      }
    })
    try {
      return await inflight
    } catch (err) {
      if (cachedRemote) return cachedRemote.payload
      throw err
    }
  }

  async workbookTableauGetExtractionStatus(
    workbookId: string,
    debug?: {
      worksheetName?: string | null
      preferredTokens?: string[]
      preferredNames?: string[]
      preferredIds?: string[]
      preferredMatchCount?: number
      preferredReadyCount?: number
      preferredFailedCount?: number
      attempt?: number
      logBody?: boolean
    },
  ): Promise<{
    results: any[]
    debug?: {
      backendInstance?: string | null
      artifactsDir?: string | null
      dumpPath?: string | null
    }
    worksheetArtifactSummary?: WorksheetArtifactProgressSummary | null
  }> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    const params = new URLSearchParams()
    if (debug) {
      params.set('debug', '1')
      if (debug.worksheetName)
        params.set('worksheet_name', String(debug.worksheetName))
      if (
        Array.isArray(debug.preferredTokens) &&
        debug.preferredTokens.length
      ) {
        params.set('preferred_tokens', JSON.stringify(debug.preferredTokens))
      }
      if (Array.isArray(debug.preferredNames) && debug.preferredNames.length) {
        params.set('preferred_names', JSON.stringify(debug.preferredNames))
      }
      if (Array.isArray(debug.preferredIds) && debug.preferredIds.length) {
        params.set('preferred_ids', JSON.stringify(debug.preferredIds))
      }
      if (typeof debug.preferredMatchCount === 'number')
        params.set('preferred_match_count', String(debug.preferredMatchCount))
      if (typeof debug.preferredReadyCount === 'number')
        params.set('preferred_ready_count', String(debug.preferredReadyCount))
      if (typeof debug.preferredFailedCount === 'number')
        params.set('preferred_failed_count', String(debug.preferredFailedCount))
      if (typeof debug.attempt === 'number')
        params.set('attempt', String(debug.attempt))
      if (debug.logBody) params.set('log_body', '1')
    }
    const url = `/api/workbooks/${encodeURIComponent(id)}/tableau/extraction-status${params.toString() ? `?${params.toString()}` : ''}`
    return await this.request(url, { method: 'GET' })
  }

  async tableauImportWaitForExtractionStatus(
    importId: string,
    opts?: {
      waitMs?: number
      pollMs?: number
    },
  ): Promise<{
    results: any[]
    timedOut?: boolean
    debug?: {
      backendInstance?: string | null
      artifactsDir?: string | null
      dumpPath?: string | null
    }
    worksheetArtifactSummary?: WorksheetArtifactProgressSummary | null
    run_id?: string | null
    run_status?: string | null
    stable_error_code?: string | null
  }> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      // Do not bump epoch preemptively here. If the wait RPC times out/fails,
      // pre-marking invalidates otherwise-usable cached final status and forces
      // subsequent 60s status lookups on every sheet fetch.
      _localExtractionStatusInflight.delete(id)
      const waitMs =
        typeof opts?.waitMs === 'number' && Number.isFinite(opts.waitMs)
          ? Math.max(0, Math.floor(opts.waitMs))
          : undefined
      const pollMs =
        typeof opts?.pollMs === 'number' && Number.isFinite(opts.pollMs)
          ? Math.max(50, Math.floor(opts.pollMs))
          : undefined
      const invokeTimeoutMs = Math.max(
        LOCAL_RESTORE_INVOKE_TIMEOUT_MS,
        Math.min(300_000, Number(waitMs ?? 120_000) + 10_000),
      )
      const res: any = await this.electronInvokeWithTimeoutRetry(
        'tableau_wait_extraction_status_local',
        {
          req: {
            import_id: id,
            wait_ms: waitMs,
            poll_ms: pollMs,
          },
        },
        {
          opName: 'extraction status wait',
          importId: id,
          timeoutMs: invokeTimeoutMs,
          // This RPC already long-polls; avoid doubling worst-case wait with retries.
          maxAttempts: 1,
        },
      )
      const results = Array.isArray(res?.results) ? res.results : []
      const epochTs = markExtractionStatusEpoch(id)
      if (results.length > 0) {
        _localExtractionStatusCache.set(id, {
          results,
          debug: {
            backendInstance: 'electron-sidecar',
            artifactsDir: null,
            dumpPath: null,
          },
          ts: epochTs || Date.now(),
        })
        if (isFinalLocalExtractionResults(results)) {
          persistLocalExtractionStatusCache()
        }
      }
      return {
        results,
        timedOut: Boolean(res?.timed_out),
        debug: {
          backendInstance: 'electron-sidecar',
          artifactsDir: null,
          dumpPath: null,
        },
        worksheetArtifactSummary: null,
      }
    }
    const waitMs =
      typeof opts?.waitMs === 'number' && Number.isFinite(opts.waitMs)
        ? Math.max(0, Math.floor(opts.waitMs))
        : 0
    const pollMs =
      typeof opts?.pollMs === 'number' && Number.isFinite(opts.pollMs)
        ? Math.max(100, Math.floor(opts.pollMs))
        : 1000
    const startedAt = Date.now()
    let last = await this.tableauImportGetExtractionStatus(id)
    if (waitMs <= 0 || isExtractionStatusTerminalForWait(last)) {
      return last
    }
    while (true) {
      const elapsedMs = Date.now() - startedAt
      if (elapsedMs >= waitMs) {
        return { ...last, timedOut: true }
      }
      const delayMs = Math.min(pollMs, Math.max(0, waitMs - elapsedMs))
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
      }
      last = await this.tableauImportGetExtractionStatus(id)
      if (isExtractionStatusTerminalForWait(last)) {
        return last
      }
    }
  }

  async tableauExtractDatasources(
    importId: string,
    workbookId: string,
    opts?: {
      target?: 'parquet' | 'motherduck' | string | null
      lock?: boolean | null
      force?: boolean | null
      fresh?: boolean | null
      source?: 'auto' | 'primary' | 'tableau' | string | null
      storage?: 'local' | 's3' | string | null
      worksheetName?: string | null
    },
  ): Promise<{ results: any[] }> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    if (this.isElectronLocalTableauImportId(id)) {
      // Clear any stale status snapshot before starting a new extraction job.
      markExtractionStatusEpoch(id)
      _localExtractionStatusCache.delete(id)
      _localExtractionStatusInflight.delete(id)
      persistLocalExtractionStatusCache()
      const target = String(opts?.target ?? '').trim() || 'parquet'
      const storage =
        String(opts?.storage ?? '').trim() ||
        (isFuseGridWebFirstTableauCreatorEnabled() ? 's3' : 'local')
      const worksheet_name =
        String(opts?.worksheetName ?? '').trim() || undefined
      const source = String(opts?.source ?? '').trim() || undefined
      await this.electronInvoke('tableau_extract_local', {
        req: {
          import_id: id,
          target,
          storage,
          lock: !!opts?.lock,
          force: !!opts?.force || !!opts?.fresh,
          source,
          worksheet_name,
        },
      })
      return { results: [] }
    }
    const scopedWorkbookId = String(workbookId || '').trim()
    if (!scopedWorkbookId)
      throw new Error('Missing workbookId for web Tableau extraction')
    const qp = new URLSearchParams()
    qp.set('workbook_id', scopedWorkbookId)
    const target = String(opts?.target ?? '').trim()
    if (target) qp.set('target', target)
    const storage = String(opts?.storage ?? '').trim()
    if (storage) {
      qp.set('storage', storage)
    } else if (isFuseGridWebFirstTableauCreatorEnabled()) {
      qp.set('storage', 's3')
    } else if (
      !isFuseGridElectronDesktop() &&
      !isFuseGridNativeDesktopShell()
    ) {
      // Web mode should always extract to S3 so worksheet artifacts/signing are available.
      qp.set('storage', 's3')
    } else if (isFuseGridNativeDesktopShell()) {
      qp.set('storage', 'local')
    }
    if (opts?.lock) qp.set('lock', '1')
    if (opts?.force) qp.set('force', '1')
    if (opts?.fresh) qp.set('fresh', '1')
    const source = String(opts?.source ?? '').trim()
    if (source) qp.set('source', source)
    const worksheetName = String(opts?.worksheetName ?? '').trim()
    if (worksheetName) qp.set('worksheet_name', worksheetName)
    const suffix = qp.toString()
    const path = `/api/tableau/imports/${encodeURIComponent(id)}/extract-datasources${suffix ? `?${suffix}` : ''}`
    const response = await this.request<{ results: any[] }>(path, { method: 'POST' })
    invalidateRemoteExtractionStatus(id)
    return response
  }

  async tableauGetPublishedExtractParquet(
    importId: string,
    datasourceKey: string,
  ): Promise<Blob> {
    const id = String(importId || '').trim()
    const dsKey = String(datasourceKey || '').trim()
    if (!id) throw new Error('Missing importId')
    if (!dsKey) throw new Error('Missing datasourceKey')
    return await this.requestBlob(
      `/api/tableau/published-extracts/${encodeURIComponent(id)}/${encodeURIComponent(dsKey)}/parquet`,
      { method: 'GET' },
    )
  }

  async workbookTableauGetPublishedExtractParquet(
    workbookId: string,
    datasourceKey: string,
  ): Promise<Blob> {
    const id = String(workbookId || '').trim()
    const dsKey = String(datasourceKey || '').trim()
    if (!id) throw new Error('Missing workbookId')
    if (!dsKey) throw new Error('Missing datasourceKey')
    return await this.requestBlob(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/published-extracts/${encodeURIComponent(dsKey)}/parquet`,
      { method: 'GET' },
    )
  }

  async tableauListPublishedExtracts(): Promise<
    ServerTableauPublishedExtract[]
  > {
    const out = await this.request<{
      extracts?: ServerTableauPublishedExtract[] | null
    }>('/api/tableau/published-extracts', { method: 'GET' })
    return Array.isArray(out?.extracts) ? out.extracts : []
  }

  async tableauListWorksheetArtifacts(
    importId: string,
    opts?: {
      worksheetName?: string | null
      workbookId?: string | null
      ttlSeconds?: number | null
    },
  ): Promise<TableauWorksheetArtifactsResponse> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    const qp = new URLSearchParams()
    const worksheetName = String(opts?.worksheetName || '').trim()
    if (worksheetName) qp.set('worksheet_name', worksheetName)
    const workbookId = String(opts?.workbookId || '').trim()
    if (workbookId) qp.set('workbook_id', workbookId)
    const ttlSeconds = Number(opts?.ttlSeconds ?? 0)
    if (Number.isFinite(ttlSeconds) && ttlSeconds > 0)
      qp.set('ttl_seconds', String(Math.floor(ttlSeconds)))
    const suffix = qp.toString()
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/worksheet-artifacts${suffix ? `?${suffix}` : ''}`,
      { method: 'GET' },
    )
  }

  async workbookTableauListWorksheetArtifacts(
    workbookId: string,
    opts?: {
      worksheetName?: string | null
      ttlSeconds?: number | null
    },
  ): Promise<TableauWorksheetArtifactsResponse> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    const qp = new URLSearchParams()
    const worksheetName = String(opts?.worksheetName || '').trim()
    if (worksheetName) qp.set('worksheet_name', worksheetName)
    const ttlSeconds = Number(opts?.ttlSeconds ?? 0)
    if (Number.isFinite(ttlSeconds) && ttlSeconds > 0)
      qp.set('ttl_seconds', String(Math.floor(ttlSeconds)))
    const suffix = qp.toString()
    return await this.request(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/worksheet-artifacts${suffix ? `?${suffix}` : ''}`,
      { method: 'GET' },
    )
  }

  async tableauGetImportArtifactSignedUrl(
    importId: string,
    req: {
      artifactType: 'full_extract' | 'reduced_extract' | 'viz_result'
      datasourceKey: string
      worksheetKey?: string | null
      workbookId?: string | null
      ttlSeconds?: number | null
      accessMode?: 'default' | 'auto_cache' | string
    },
  ): Promise<{ uri: string; signed_url: string }> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    const artifactType = String(req?.artifactType || '').trim()
    const datasourceKey = String(req?.datasourceKey || '').trim()
    if (!artifactType) throw new Error('artifactType is required')
    if (!datasourceKey) throw new Error('datasourceKey is required')
    const qp = new URLSearchParams()
    qp.set('artifact_type', artifactType)
    qp.set('datasource_key', datasourceKey)
    const worksheetKey = String(req?.worksheetKey || '').trim()
    if (worksheetKey) qp.set('worksheet_key', worksheetKey)
    const workbookId = String(req?.workbookId || '').trim()
    if (workbookId) qp.set('workbook_id', workbookId)
    const ttlSeconds = Number(req?.ttlSeconds ?? 0)
    if (Number.isFinite(ttlSeconds) && ttlSeconds > 0)
      qp.set('ttl_seconds', String(Math.floor(ttlSeconds)))
    const accessMode = String(req?.accessMode || '').trim()
    if (accessMode) qp.set('access_mode', accessMode)
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/artifact-signed-url?${qp.toString()}`,
      { method: 'GET' },
    )
  }

  async workbookTableauGetArtifactSignedUrl(
    workbookId: string,
    req: {
      artifactType: 'full_extract' | 'reduced_extract' | 'viz_result'
      datasourceKey: string
      worksheetKey?: string | null
      ttlSeconds?: number | null
      accessMode?: 'default' | 'auto_cache' | string
    },
  ): Promise<{ uri: string; signed_url: string }> {
    const id = String(workbookId || '').trim()
    if (!id) throw new Error('Missing workbookId')
    const artifactType = String(req?.artifactType || '').trim()
    const datasourceKey = String(req?.datasourceKey || '').trim()
    if (!artifactType) throw new Error('artifactType is required')
    if (!datasourceKey) throw new Error('datasourceKey is required')
    const qp = new URLSearchParams()
    qp.set('artifact_type', artifactType)
    qp.set('datasource_key', datasourceKey)
    const worksheetKey = String(req?.worksheetKey || '').trim()
    if (worksheetKey) qp.set('worksheet_key', worksheetKey)
    const ttlSeconds = Number(req?.ttlSeconds ?? 0)
    if (Number.isFinite(ttlSeconds) && ttlSeconds > 0)
      qp.set('ttl_seconds', String(Math.floor(ttlSeconds)))
    const accessMode = String(req?.accessMode || '').trim()
    if (accessMode) qp.set('access_mode', accessMode)
    return await this.request(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/artifact-signed-url?${qp.toString()}`,
      { method: 'GET' },
    )
  }

  async tableauPublishExtractToS3(
    importId: string,
    payload: {
      parquet: Blob
      metadata: {
        datasource_key: string
        datasource_name?: string | null
        workbook_revision_sha256: string
        engine?: string | null
        datasource_id?: string | null
        row_count?: number | null
        schema_json?: any
        schema_sha256?: string | null
        field_manifest?: any
        manifest_sha256?: string | null
      }
      filename?: string | null
    },
  ): Promise<any> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    const parquet = payload?.parquet
    if (!(parquet instanceof Blob)) throw new Error('Missing parquet blob')
    const metadata = payload?.metadata ?? ({} as any)
    const dsKey = String(metadata.datasource_key || '').trim()
    if (!dsKey) throw new Error('metadata.datasource_key is required')
    const revision = String(metadata.workbook_revision_sha256 || '').trim()
    if (!revision)
      throw new Error('metadata.workbook_revision_sha256 is required')

    const form = new FormData()
    const filename = String(payload?.filename || `${dsKey}.parquet`)
    form.append('parquet', parquet, filename)
    form.append(
      'meta',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
      'meta.json',
    )
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/publish-extract`,
      {
        method: 'POST',
        body: form,
      },
    )
  }

  async tableauPromoteDatasourceToManaged(
    importId: string,
    req: {
      datasource_key: string
      source_mode?: 'primary' | 'tableau' | string
      overwrite?: boolean
    },
  ): Promise<TableauPromotedDatasource> {
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    const datasourceKey = String(req?.datasource_key || '').trim()
    if (!datasourceKey) throw new Error('datasource_key is required')
    return await this.request(
      `/api/tableau/imports/${encodeURIComponent(id)}/promote-datasource`,
      {
        method: 'POST',
        body: JSON.stringify({
          datasource_key: datasourceKey,
          source_mode: String(req?.source_mode || '').trim() || undefined,
          overwrite: req?.overwrite ?? true,
        }),
      },
    )
  }

  async tableauImportGetArtifactBlob(
    importId: string,
    name: string,
  ): Promise<Blob> {
    const id = String(importId || '').trim()
    const nm = String(name || '').trim()
    if (!id) throw new Error('Missing importId')
    if (!nm) throw new Error('Missing artifact name')
    const lowerName = String(nm || '')
      .trim()
      .toLowerCase()
    const isOptionalArtifact = LOCAL_OPTIONAL_ARTIFACTS.has(lowerName)
    const optionalMissKey = `${id}::${lowerName}`
    const optionalContentType = lowerName.endsWith('.json')
      ? 'application/json'
      : lowerName.endsWith('.xml')
        ? 'application/xml'
        : 'application/octet-stream'
    if (this.isElectronLocalTableauImportId(id)) {
      if (isOptionalArtifact) {
        const missUntil = Number(
          _localOptionalArtifactMissingUntil.get(optionalMissKey) || 0,
        )
        if (missUntil > Date.now()) {
          return new Blob([], { type: optionalContentType })
        }
      }
      const timeoutMs =
        lowerName === 'data_registry.json' ||
        lowerName === 'required_fields.json' ||
        lowerName === 'canonical_manifest.json'
          ? LOCAL_ARTIFACT_READ_TIMEOUT_MS_FAST
          : LOCAL_ARTIFACT_READ_TIMEOUT_MS_DEFAULT
      let out: any
      try {
        out = await this.electronInvokeWithTimeoutRetry(
          'tableau_read_artifact_b64_local',
          { req: { import_id: id, name: nm } },
          {
            timeoutMs,
            maxAttempts: 1,
            retryDelayMs: 0,
            opName: `read local artifact ${nm}`,
            importId: id,
          },
        )
      } catch (e: any) {
        if (isOptionalArtifact && this.isMissingLocalArtifactError(e)) {
          _localOptionalArtifactMissingUntil.set(
            optionalMissKey,
            Date.now() + LOCAL_OPTIONAL_ARTIFACT_MISS_TTL_MS,
          )
          return new Blob([], { type: optionalContentType })
        }
        throw e
      }
      _localOptionalArtifactMissingUntil.delete(optionalMissKey)
      const b64 = String(out?.b64 || '')
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      const lower = lowerName
      const contentType = lower.endsWith('.json')
        ? 'application/json'
        : lower.endsWith('.xml')
          ? 'application/xml'
          : 'application/octet-stream'
      return new Blob([bytes], { type: contentType })
    }
    if (isOptionalArtifact) {
      const missUntil = Number(
        _remoteOptionalArtifactMissingUntil.get(optionalMissKey) || 0,
      )
      if (missUntil > Date.now()) {
        return new Blob([], { type: optionalContentType })
      }
      const inflight = _remoteOptionalArtifactInflight.get(optionalMissKey)
      if (inflight) return await inflight
      const promise = (async (): Promise<Blob> => {
        const headers: Record<string, string> = {}
        if (this.token) headers['Authorization'] = `Bearer ${this.token}`
        const path = `/api/tableau/imports/${encodeURIComponent(id)}/artifacts/${encodeURIComponent(nm)}`
        const url = joinUrl(this.baseUrl, path)
        const isRetryableOptionalArtifactError = (error: any): boolean => {
          const message = String(error?.message || error || '')
            .trim()
            .toLowerCase()
          if (!message) return false
          return (
            message.includes('content length') ||
            message.includes('failed to fetch') ||
            message.includes('networkerror') ||
            message.includes('load failed') ||
            message.includes('network request failed') ||
            message.includes('body stream')
          )
        }
        const fetchBlobWithRetry = async (
          attempt: number,
          allowAuthRetry: boolean,
        ): Promise<Blob> => {
          const headers: Record<string, string> = {}
          if (this.token) headers['Authorization'] = `Bearer ${this.token}`
          let res: Response
          try {
            res = await fetch(url, {
              method: 'GET',
              headers,
              ...(attempt > 0 ? { cache: 'no-store' as RequestCache } : {}),
            })
          } catch (e: any) {
            if (attempt === 0 && isRetryableOptionalArtifactError(e)) {
              await new Promise((resolve) =>
                globalThis.setTimeout(resolve, 120),
              )
              return await fetchBlobWithRetry(1, allowAuthRetry)
            }
            throw e
          }
          if (res.status === 404) {
            _remoteOptionalArtifactMissingUntil.set(
              optionalMissKey,
              Date.now() + LOCAL_OPTIONAL_ARTIFACT_MISS_TTL_MS,
            )
            return new Blob([], { type: optionalContentType })
          }
          if (!res.ok) {
            const text = await res.text().catch(() => '')
            const message = text || res.statusText
            if (
              allowAuthRetry &&
              (await this.maybeRecoverAuthFailure(path, res.status, message))
            ) {
              return await fetchBlobWithRetry(attempt, false)
            }
            throw this.buildApiError(res.status, message, url)
          }
          try {
            _remoteOptionalArtifactMissingUntil.delete(optionalMissKey)
            return await res.blob()
          } catch (e: any) {
            if (attempt === 0 && isRetryableOptionalArtifactError(e)) {
              await new Promise((resolve) =>
                globalThis.setTimeout(resolve, 120),
              )
              return await fetchBlobWithRetry(1, allowAuthRetry)
            }
            throw e
          }
        }
        return await fetchBlobWithRetry(0, true)
      })()
      _remoteOptionalArtifactInflight.set(optionalMissKey, promise)
      try {
        return await promise
      } finally {
        _remoteOptionalArtifactInflight.delete(optionalMissKey)
      }
    }
    return await this.requestBlob(
      `/api/tableau/imports/${encodeURIComponent(id)}/artifacts/${encodeURIComponent(nm)}`,
      { method: 'GET' },
    )
  }

  async workbookTableauGetArtifactBlob(
    workbookId: string,
    name: string,
  ): Promise<Blob> {
    const id = String(workbookId || '').trim()
    const nm = String(name || '').trim()
    if (!id) throw new Error('Missing workbookId')
    if (!nm) throw new Error('Missing artifact name')
    return await this.requestBlob(
      `/api/workbooks/${encodeURIComponent(id)}/tableau/artifacts/${encodeURIComponent(nm)}`,
      { method: 'GET' },
    )
  }

  async tableauImportWriteArtifact(
    importId: string,
    name: string,
    payload: { text?: string | null; bytes?: Uint8Array | null },
  ): Promise<{ ok: boolean; bytes?: number; reason?: string }> {
    const id = String(importId || '').trim()
    const nm = String(name || '').trim()
    if (!id) return { ok: false, reason: 'Missing importId' }
    if (!nm) return { ok: false, reason: 'Missing artifact name' }

    const bytes = (() => {
      if (payload?.bytes instanceof Uint8Array) return payload.bytes
      if (payload?.text != null) {
        try {
          const enc = new TextEncoder()
          return enc.encode(String(payload.text ?? ''))
        } catch {
          return new Uint8Array([])
        }
      }
      return new Uint8Array([])
    })()

    const toBase64 = (buf: Uint8Array): string => {
      if (!buf || buf.length === 0) return ''
      // Prefer Node Buffer if available (Electron renderer w/ Node integration).
      try {
        const b: any = (globalThis as any).Buffer
        if (b && typeof b.from === 'function') {
          return b.from(buf).toString('base64')
        }
      } catch {}
      // Browser fallback: chunked btoa
      let bin = ''
      const chunk = 0x8000
      for (let i = 0; i < buf.length; i += chunk) {
        const slice = buf.subarray(i, i + chunk)
        bin += String.fromCharCode(...slice)
      }
      try {
        return btoa(bin)
      } catch {
        return ''
      }
    }

    if (this.isElectronLocalTableauImportId(id)) {
      try {
        const b64 = toBase64(bytes)
        const res: any = await this.electronInvoke(
          'tableau_write_artifact_b64_local',
          {
            req: { import_id: id, name: nm, b64 },
          },
        )
        _localOptionalArtifactMissingUntil.delete(
          `${id}::${String(nm || '')
            .trim()
            .toLowerCase()}`,
        )
        const written = Number(res?.bytes ?? bytes.length ?? 0)
        return { ok: true, bytes: written }
      } catch (e: any) {
        return { ok: false, reason: String(e?.message || e || 'write failed') }
      }
    }

    try {
      const b64 = toBase64(bytes)
      const res = await this.request<{ ok?: boolean; bytes?: number }>(
        `/api/tableau/imports/${encodeURIComponent(id)}/artifacts/${encodeURIComponent(nm)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ b64 }),
        },
      )
      return {
        ok: Boolean(res?.ok),
        bytes: Number(res?.bytes ?? bytes.length ?? 0),
      }
    } catch (e: any) {
      return { ok: false, reason: String(e?.message || e || 'write failed') }
    }
  }

  async workbookTableauWriteArtifact(
    workbookId: string,
    name: string,
    payload: { text?: string | null; bytes?: Uint8Array | null },
  ): Promise<{ ok: boolean; bytes?: number; reason?: string }> {
    const id = String(workbookId || '').trim()
    const nm = String(name || '').trim()
    if (!id) return { ok: false, reason: 'Missing workbookId' }
    if (!nm) return { ok: false, reason: 'Missing artifact name' }

    const bytes = (() => {
      if (payload?.bytes instanceof Uint8Array) return payload.bytes
      if (payload?.text != null) {
        try {
          const enc = new TextEncoder()
          return enc.encode(String(payload.text ?? ''))
        } catch {
          return new Uint8Array([])
        }
      }
      return new Uint8Array([])
    })()

    const toBase64 = (buf: Uint8Array): string => {
      if (!buf || buf.length === 0) return ''
      try {
        const b: any = (globalThis as any).Buffer
        if (b && typeof b.from === 'function') {
          return b.from(buf).toString('base64')
        }
      } catch {}
      let bin = ''
      const chunk = 0x8000
      for (let i = 0; i < buf.length; i += chunk) {
        const slice = buf.subarray(i, i + chunk)
        bin += String.fromCharCode(...slice)
      }
      try {
        return btoa(bin)
      } catch {
        return ''
      }
    }

    try {
      const b64 = toBase64(bytes)
      const res = await this.request<{ ok?: boolean; bytes?: number }>(
        `/api/workbooks/${encodeURIComponent(id)}/tableau/artifacts/${encodeURIComponent(nm)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ b64 }),
        },
      )
      return {
        ok: Boolean(res?.ok),
        bytes: Number(res?.bytes ?? bytes.length ?? 0),
      }
    } catch (e: any) {
      return { ok: false, reason: String(e?.message || e || 'write failed') }
    }
  }

  async tableauLookupWorksheetCacheEntryLocal(req: {
    importId: string
    key: string
    sqlHash: number
    sqlLen: number
    extractFingerprint: string
    includePayload?: boolean
  }): Promise<any | null> {
    const id = String(req?.importId || '').trim()
    if (!id || !this.isElectronLocalTableauImportId(id)) return null
    const key = String(req?.key || '').trim()
    const extractFingerprint = String(req?.extractFingerprint || '').trim()
    if (!key || !extractFingerprint) return null
    try {
      const out: any = await this.electronInvoke(
        'tableau_lookup_worksheet_cache_local',
        {
          req: {
            import_id: id,
            key,
            sql_hash: Number(req?.sqlHash ?? 0),
            sql_len: Number(req?.sqlLen ?? 0),
            extract_fingerprint: extractFingerprint,
            include_payload: req?.includePayload !== false,
          },
        },
      )
      return out && typeof out === 'object' ? out : null
    } catch {
      return null
    }
  }

  async tableauUpsertWorksheetCacheEntryLocal(req: {
    importId: string
    entry: any
    maxEntries: number
    validExtractFingerprints?: Set<string>
  }): Promise<{
    ok: boolean
    prunedStale: number
    droppedLru: number
    kept: number
  }> {
    const id = String(req?.importId || '').trim()
    if (!id || !this.isElectronLocalTableauImportId(id)) {
      return { ok: false, prunedStale: 0, droppedLru: 0, kept: 0 }
    }
    try {
      const out: any = await this.electronInvoke(
        'tableau_upsert_worksheet_cache_local',
        {
          req: {
            import_id: id,
            entry: req?.entry ?? null,
            max_entries: Math.max(1, Number(req?.maxEntries ?? 24)),
            valid_extract_fingerprints: Array.from(
              req?.validExtractFingerprints ?? [],
            ),
          },
        },
      )
      return {
        ok: !!out?.ok,
        prunedStale: Number(out?.pruned_stale ?? 0),
        droppedLru: Number(out?.dropped_lru ?? 0),
        kept: Number(out?.kept ?? 0),
      }
    } catch {
      return { ok: false, prunedStale: 0, droppedLru: 0, kept: 0 }
    }
  }

  async tableauPruneWorksheetCacheLocal(req: {
    importId: string
    maxEntries: number
    validExtractFingerprints?: Set<string>
  }): Promise<{
    ok: boolean
    prunedStale: number
    droppedLru: number
    kept: number
  }> {
    const id = String(req?.importId || '').trim()
    if (!id || !this.isElectronLocalTableauImportId(id)) {
      return { ok: false, prunedStale: 0, droppedLru: 0, kept: 0 }
    }
    try {
      const out: any = await this.electronInvoke(
        'tableau_prune_worksheet_cache_local',
        {
          req: {
            import_id: id,
            max_entries: Math.max(1, Number(req?.maxEntries ?? 24)),
            valid_extract_fingerprints: Array.from(
              req?.validExtractFingerprints ?? [],
            ),
          },
        },
      )
      return {
        ok: !!out?.ok,
        prunedStale: Number(out?.pruned_stale ?? 0),
        droppedLru: Number(out?.dropped_lru ?? 0),
        kept: Number(out?.kept ?? 0),
      }
    } catch {
      return { ok: false, prunedStale: 0, droppedLru: 0, kept: 0 }
    }
  }

  /**
   * Electron-only: persist a local Tableau import with strict artifacts.
   *
   * The sidecar now reparses `twb_xml` with the canonical server parser so local and cloud
   * imports use the same parser implementation. `workbook_ir` remains accepted for compatibility
   * with existing callers, but is no longer authoritative for artifact generation.
   */
  async tableauImportLocalFromWasm(req: {
    import_id: string
    container_kind?: string | null
    twb_entry_name?: string | null
    twb_xml: string
    workbook_ir: any
    original_filename?: string | null
    // For TWBX: raw zip bytes so the sidecar can materialize embedded Hyper extracts.
    bytes?: Uint8Array | null
  }): Promise<{
    import_id: string
    import_dir: string
    workbook_revision_sha256: string
  }> {
    const id = String(req?.import_id || '').trim()
    if (!id) throw new Error('Missing import_id')
    if (!this.isElectronLocalTableauImportId(id)) {
      throw new Error(
        'tableauImportLocalFromWasm is only supported in Electron for local_* import ids',
      )
    }
    const res: any = await this.electronInvoke('tableau_import_local', { req })
    return {
      import_id: String(res?.import_id || id),
      import_dir: String(res?.import_dir || ''),
      workbook_revision_sha256: String(res?.workbook_revision_sha256 || ''),
    }
  }

  // ── .fgpkg package save/load ────────────────────────────────────────────

  /**
   * Save a .fgpkg package. In Electron mode, writes to ~/FuseGrid/packages/.
   * In web mode, returns a Blob for browser download.
   */
  async tableauSavePackage(
    importId: string,
    opts?: {
      filePath?: string | null
      dataMode?: 'embedded' | 'remote' | null
      remoteBaseUrl?: string | null
      includeDuckdbCache?: boolean | null
      saveIntent?: 'manual' | 'autosave' | null
      saveFingerprint?: string | null
    },
  ): Promise<{ path?: string; bytes: number; filename?: string; blob?: Blob }> {
    if (!areFuseGridLocalPackageWorkflowsEnabled()) {
      throw new Error(
        'Local package workflows are retired in web-first creator mode',
      )
    }
    const id = String(importId || '').trim()
    if (!id) throw new Error('Missing importId')
    const filePath = String(opts?.filePath ?? '').trim() || null
    const dataMode = (opts?.dataMode ?? null) as string | null
    const remoteBaseUrl = String(opts?.remoteBaseUrl ?? '').trim() || null
    const includeDuckdbCache =
      typeof opts?.includeDuckdbCache === 'boolean'
        ? opts.includeDuckdbCache
        : null
    const saveIntentRaw = String(opts?.saveIntent ?? 'manual')
      .trim()
      .toLowerCase()
    const saveIntent = saveIntentRaw === 'autosave' ? 'autosave' : 'manual'
    const saveFingerprint = String(opts?.saveFingerprint ?? '').trim() || null
    const isUnknownImportIdError = (err: any): boolean => {
      const msg =
        `${String(err?.message || '')} ${String(err?.cause?.message || '')}`.toLowerCase()
      return msg.includes('unknown import_id')
    }

    // Electron: prefer local disk save when available (even for non-local import ids).
    if (isFuseGridElectronDesktop()) {
      try {
        const res: any = await this.electronInvoke('fgpkg_save_local', {
          req: {
            import_id: id,
            file_path: filePath,
            data_mode: dataMode,
            remote_base_url: remoteBaseUrl,
            include_duckdb_cache: includeDuckdbCache,
            save_intent: saveIntent,
            save_fingerprint: saveFingerprint,
          },
        })
        return {
          path: String(res?.path || ''),
          bytes: Number(res?.bytes || 0),
          filename: String(res?.filename || ''),
        }
      } catch (e) {
        // Self-heal: if workspace mapping was not ready yet for this import_id,
        // register using the chosen path and retry exactly once.
        if (
          this.isElectronLocalTableauImportId(id) &&
          filePath &&
          isUnknownImportIdError(e)
        ) {
          try {
            await this.tableauRegisterPackageWorkspace(id, filePath)
            const retried: any = await this.electronInvoke('fgpkg_save_local', {
              req: {
                import_id: id,
                file_path: filePath,
                data_mode: dataMode,
                remote_base_url: remoteBaseUrl,
                include_duckdb_cache: includeDuckdbCache,
                save_intent: saveIntent,
                save_fingerprint: saveFingerprint,
              },
            })
            return {
              path: String(retried?.path || ''),
              bytes: Number(retried?.bytes || 0),
              filename: String(retried?.filename || ''),
            }
          } catch (retryErr) {
            if (this.isElectronLocalTableauImportId(id)) {
              throw retryErr
            }
          }
        }
        if (this.isElectronLocalTableauImportId(id)) {
          throw e
        }
        console.warn(
          '[ServerApiClient] fgpkg_save_local failed, falling back to server:',
          e,
        )
      }
    }

    // Web mode: download as blob from server.
    const blob = await this.requestBlob(
      `/api/tableau/imports/${encodeURIComponent(id)}/package`,
      { method: 'POST' },
    )
    return { blob, bytes: blob.size }
  }

  /**
   * Electron-only: prompt for a .fgpkg save path.
   */
  async tableauRegisterPackageWorkspace(
    importId: string,
    filePath: string,
  ): Promise<{ workspaceRoot?: string; importDir?: string }> {
    if (!areFuseGridLocalPackageWorkflowsEnabled()) return {}
    const id = String(importId || '').trim()
    const path = String(filePath || '').trim()
    if (!id || !path) return {}
    if (!this.shouldUseElectronTableauBridge()) return {}
    try {
      const res: any = await this.electronInvoke('fgpkg_register_workspace', {
        req: { import_id: id, file_path: path },
      })
      return {
        workspaceRoot: String(res?.workspace_root || ''),
        importDir: String(res?.import_dir || ''),
      }
    } catch {
      return {}
    }
  }

  /**
   * Electron-only: prompt for a .fgpkg save path.
   */
  async tableauPromptSavePackagePath(opts?: {
    defaultName?: string | null
    suggestedPath?: string | null
  }): Promise<string | null> {
    if (!areFuseGridLocalPackageWorkflowsEnabled()) return null
    if (!this.shouldUseElectronTableauBridge()) return null
    const res: any = await this.electronInvoke('fgpkg_prompt_save_path', {
      req: {
        default_name: String(opts?.defaultName ?? '').trim(),
        suggested_path: String(opts?.suggestedPath ?? '').trim(),
      },
    })
    const path = String(res?.path || '').trim()
    return path || null
  }

  /**
   * Electron-only: prompt to open a .fgpkg package.
   */
  async tableauPromptOpenPackagePath(): Promise<string | null> {
    if (!areFuseGridLocalPackageWorkflowsEnabled()) return null
    if (!this.shouldUseElectronTableauBridge()) return null
    try {
      const res: any = await this.electronInvoke('fgpkg_prompt_open_path', {
        req: {},
      })
      const path = String(res?.path || '').trim()
      return path || null
    } catch {
      return null
    }
  }

  /**
   * Load a .fgpkg package. In Electron mode, reads from a local file path.
   * In web mode, uploads the File to the server.
   */
  async tableauLoadPackage(source: File | string, workbookId: string): Promise<{
    import_id: string
    workbook_ir?: any
    original_filename?: string | null
    workbook_revision_sha256?: string
  }> {
    if (!areFuseGridLocalPackageWorkflowsEnabled()) {
      throw new Error(
        'Local package workflows are retired in web-first creator mode',
      )
    }
    if (typeof source === 'string') {
      // Electron mode: source is a file path.
      const res: any = await this.electronInvoke('fgpkg_load_local', {
        req: { file_path: source },
      })
      return {
        import_id: String(res?.import_id || ''),
        workbook_ir: res?.workbook_ir ?? null,
        original_filename: res?.original_filename ?? null,
        workbook_revision_sha256: String(res?.workbook_revision_sha256 || ''),
      }
    }
    if (isFuseGridElectronDesktop()) {
      const filePath = String((source as any)?.path || '').trim()
      if (filePath) {
        const res: any = await this.electronInvoke('fgpkg_load_local', {
          req: { file_path: filePath },
        })
        return {
          import_id: String(res?.import_id || ''),
          workbook_ir: res?.workbook_ir ?? null,
          original_filename: res?.original_filename ?? null,
          workbook_revision_sha256: String(res?.workbook_revision_sha256 || ''),
        }
      }
    }

    // Web mode: upload file to server.
    const scopedWorkbookId = String(workbookId || '').trim()
    if (!scopedWorkbookId) {
      throw new Error('workbook_id is required to load a Tableau package in web mode')
    }
    const formData = new FormData()
    formData.append('file', source)
    formData.append('workbook_id', scopedWorkbookId)
    return await this.request('/api/tableau/packages/load', {
      method: 'POST',
      body: formData,
    })
  }

  async tableauExportWorksheetSpecTwbx(
    payload: TableauSpecExportBody,
    opts?: { filename?: string | null; include_extract?: boolean | null },
  ): Promise<Blob> {
    const filename = String(opts?.filename ?? '').trim()
    const includeExtract = opts?.include_extract !== false
    const qp = new URLSearchParams()
    qp.set('format', 'twbx')
    if (filename) qp.set('filename', filename)
    if (includeExtract) qp.set('include_extract', 'true')
    const path = `/api/tableau/spec/export?${qp.toString()}`
    return await this.requestBlob(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    })
  }

  async tableauExportWorksheetSpecTwbxServer(
    payload: TableauSpecExportBody,
    opts?: {
      filename?: string | null
      include_extract?: boolean | null
      publish?: boolean | null
    },
  ): Promise<TableauSpecExportStoreResponse> {
    const filename = String(opts?.filename ?? '').trim()
    const includeExtract = opts?.include_extract !== false
    const publish = opts?.publish === true
    const qp = new URLSearchParams()
    qp.set('format', 'twbx')
    qp.set('store', 'true')
    if (publish) qp.set('publish', 'true')
    if (filename) qp.set('filename', filename)
    if (includeExtract) qp.set('include_extract', 'true')
    const path = `/api/tableau/spec/export?${qp.toString()}`
    return await this.request(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    })
  }

  async workbookTableauSetBaseline(
    workbookId: string,
    req: { import_id: string; clear_ops?: boolean | null },
  ): Promise<ServerWorkbookDocument> {
    const wid = String(workbookId || '').trim()
    if (!wid) throw new Error('Missing workbookId')
    const import_id = String(req?.import_id || '').trim()
    if (!import_id) throw new Error('Missing import_id')
    return await this.request(
      `/api/workbooks/${encodeURIComponent(wid)}/tableau/baseline`,
      {
        method: 'POST',
        body: JSON.stringify({
          import_id,
          clear_ops: req?.clear_ops === true,
        }),
      },
    )
  }

  async workbookTableauAppendOps(
    workbookId: string,
    req: {
      ops: any[]
      note?: string | null
      baseline_import_id?: string | null
    },
  ): Promise<ServerWorkbookDocument> {
    void workbookId
    void req
    throw new Error(
      'Workbook tableau ops endpoint retired: use canonical import/export APIs',
    )
  }

  async workbookTableauThreeWayMerge(
    workbookId: string,
    req: { new_import_id: string; auto_resolve_conflicts?: boolean | null },
  ): Promise<any> {
    void workbookId
    void req
    throw new Error(
      'Workbook three-way merge endpoint retired: use canonical import/export APIs',
    )
  }

  async tableauListWorkbooks(
    sessionKey: string,
    opts?: { page_size?: number; page_number?: number },
  ): Promise<TableauListWorkbooksResponse> {
    const ps =
      typeof opts?.page_size === 'number'
        ? Math.max(1, Math.floor(opts.page_size))
        : 100
    const pn =
      typeof opts?.page_number === 'number'
        ? Math.max(1, Math.floor(opts.page_number))
        : 1
    if (this.shouldUseElectronTableauBridge()) {
      const resolvedSessionKey =
        await this.ensureElectronLocalTableauSessionKey(sessionKey)
      return await this.electronInvokeTableauWithSessionRecovery(
        'tableau_list_workbooks_local',
        {
          session_key: String(resolvedSessionKey || '').trim(),
          page_size: ps,
          page_number: pn,
        },
      )
    }
    const qp = `?session_key=${encodeURIComponent(String(sessionKey))}&page_size=${encodeURIComponent(String(ps))}&page_number=${encodeURIComponent(String(pn))}`
    return await this.request(`/api/tableau/workbooks${qp}`, { method: 'GET' })
  }

  async tableauGetWorkbookSummary(
    sessionKey: string,
    workbookId: string,
    opts?: { persist?: boolean; fusegridWorkbookId?: string | null },
  ): Promise<TableauWorkbookSummaryResponse> {
    // Axum Query<bool> expects "true"/"false" (not "1"/"0").
    const sk = encodeURIComponent(String(sessionKey))
    const wid = encodeURIComponent(String(workbookId))
    const base = `/api/tableau/workbooks/${wid}/summary`

    if (this.shouldUseElectronTableauBridge()) {
      const persist = opts?.persist ?? false
      const key = `${String(sessionKey || '').trim()}::${String(workbookId || '').trim()}::${persist ? '1' : '0'}`
      let inflight: Promise<TableauWorkbookSummaryResponse> | null =
        this._tableauWorkbookSummaryInflight.get(key) ?? null
      if (!inflight) {
        const created: Promise<TableauWorkbookSummaryResponse> = (async () => {
          const resolvedSessionKey =
            await this.ensureElectronLocalTableauSessionKey(sessionKey)
          return await this.electronInvokeTableauWithSessionRecovery<TableauWorkbookSummaryResponse>(
            'tableau_get_workbook_summary_local',
            {
              session_key: String(resolvedSessionKey || '').trim(),
              workbook_id: String(workbookId || '').trim(),
              persist,
            },
          )
        })()
        inflight = created
        this._tableauWorkbookSummaryInflight.set(key, created)
        attachInflightCleanup(created, () => {
          const current = this._tableauWorkbookSummaryInflight.get(key)
          if (current === created)
            this._tableauWorkbookSummaryInflight.delete(key)
        })
      }
      return await inflight
    }

    // Primary: modern backend expects "true"/"false"
    const fusegridWorkbookId = String(opts?.fusegridWorkbookId || '').trim()
    if (opts?.persist === true && !fusegridWorkbookId)
      throw new Error('Missing fusegridWorkbookId for persisted Tableau import')
    const scoped = fusegridWorkbookId
      ? `&fusegrid_workbook_id=${encodeURIComponent(fusegridWorkbookId)}`
      : ''
    const qpTrue = `?session_key=${sk}${opts?.persist === true ? '&persist=true' : ''}${scoped}`
    const r1 = await this.request<TableauWorkbookSummaryResponse>(
      `${base}${qpTrue}`,
      { method: 'GET' },
    )

    // Compat: older deployments sometimes parse bools as 0/1.
    // If caller requested persist and the response didn't include an import id, retry with persist=1.
    if (opts?.persist === true) {
      const id1 = String(
        (r1 as any)?.import?.import_id || (r1 as any)?.import_id || '',
      ).trim()
      if (!id1) {
        const qpOne = `?session_key=${sk}&persist=1${scoped}`
        try {
          const r2 = await this.request<TableauWorkbookSummaryResponse>(
            `${base}${qpOne}`,
            { method: 'GET' },
          )
          const id2 = String(
            (r2 as any)?.import?.import_id || (r2 as any)?.import_id || '',
          ).trim()
          if (id2) return r2
        } catch {
          // ignore; fall back to r1
        }
      }
    }

    return r1
  }

  async tableauGetViewDataTable(
    sessionKey: string,
    viewId: string,
    opts?: { limit?: number; params?: Record<string, string> | null },
  ): Promise<TableauViewDataTableResponse> {
    const limit =
      typeof opts?.limit === 'number'
        ? Math.max(1, Math.floor(opts.limit))
        : 5000
    const extra =
      opts?.params && typeof opts.params === 'object' ? opts.params : null
    const extraParts: string[] = []
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        const kk = String(k || '').trim()
        if (!kk) continue
        const vv = String(v ?? '').trim()
        extraParts.push(`${encodeURIComponent(kk)}=${encodeURIComponent(vv)}`)
      }
    }
    if (this.shouldUseElectronTableauBridge()) {
      const resolvedSessionKey =
        await this.ensureElectronLocalTableauSessionKey(sessionKey)
      const res: any = await this.electronInvokeTableauWithSessionRecovery(
        'tableau_get_view_data_csv_local',
        {
          session_key: String(resolvedSessionKey || '').trim(),
          view_id: String(viewId || '').trim(),
          params: extra || null,
        },
      )
      const b64 = String(res?.b64 || '')
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      const csvText = new TextDecoder('utf-8').decode(bytes)
      const { parseCsv } = await import('../utils/csvParser')
      const parsed = parseCsv(csvText, { hasHeaders: true, limit })
      return {
        columns: parsed.columns,
        rows: parsed.rows,
        row_count: parsed.rowCount,
      }
    }
    const qp = `?session_key=${encodeURIComponent(String(sessionKey))}&limit=${encodeURIComponent(String(limit))}${extraParts.length ? `&${extraParts.join('&')}` : ''}`
    return await this.request(
      `/api/tableau/views/${encodeURIComponent(String(viewId))}/data_table${qp}`,
      { method: 'GET' },
    )
  }

  /**
   * Browser-side data extraction: fetch raw CSV and parse in browser.
   * This enables rapid iteration on parsing logic without backend rebuilds.
   */
  async tableauGetViewDataTableBrowser(
    sessionKey: string,
    viewId: string,
    opts?: { limit?: number; params?: Record<string, string> | null },
  ): Promise<TableauViewDataTableResponse> {
    const limit =
      typeof opts?.limit === 'number'
        ? Math.max(1, Math.floor(opts.limit))
        : 5000
    const extra =
      opts?.params && typeof opts.params === 'object' ? opts.params : null
    const extraParts: string[] = []
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        const kk = String(k || '').trim()
        if (!kk) continue
        const vv = String(v ?? '').trim()
        extraParts.push(`${encodeURIComponent(kk)}=${encodeURIComponent(vv)}`)
      }
    }
    const qp = `?session_key=${encodeURIComponent(String(sessionKey))}${extraParts.length ? `&${extraParts.join('&')}` : ''}`

    if (this.shouldUseElectronTableauBridge()) {
      const resolvedSessionKey =
        await this.ensureElectronLocalTableauSessionKey(sessionKey)
      const res: any = await this.electronInvokeTableauWithSessionRecovery(
        'tableau_get_view_data_csv_local',
        {
          session_key: String(resolvedSessionKey || '').trim(),
          view_id: String(viewId || '').trim(),
          params: extra || null,
        },
      )
      const b64 = String(res?.b64 || '')
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      const csvText = new TextDecoder('utf-8').decode(bytes)
      const { parseCsv } = await import('../utils/csvParser')
      const parsed = parseCsv(csvText, { hasHeaders: true, limit })
      return {
        columns: parsed.columns,
        rows: parsed.rows,
        row_count: parsed.rowCount,
      }
    }

    // Fetch raw CSV from proxy endpoint
    const path = `/api/tableau/views/${encodeURIComponent(String(viewId))}/data_csv${qp}`
    const fetchParsedCsv = async (
      allowAuthRetry: boolean,
    ): Promise<TableauViewDataTableResponse> => {
      const headers: Record<string, string> = {
        'Content-Type': 'text/csv',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      }

      const parseCsvResponse = async (res: Response) => {
        const csvText = await res.text()
        const { parseCsv } = await import('../utils/csvParser')
        const parsed = parseCsv(csvText, { hasHeaders: true, limit })
        return {
          columns: parsed.columns,
          rows: parsed.rows,
          row_count: parsed.rowCount,
        }
      }

      const url = joinUrl(this.baseUrl, path)

      // Handle HTTPS/HTTP mixed content (same as request method)
      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        url.startsWith('http://')
      ) {
        const httpsUrl = url.replace('http://', 'https://')
        try {
          const res = await fetch(httpsUrl, { method: 'GET', headers })
          if (res.ok) return await parseCsvResponse(res)
          const text = await res.text().catch(() => '')
          const message = text || res.statusText
          if (this.isAuthFailure(res.status, message)) {
            if (
              allowAuthRetry &&
              (await this.maybeRecoverAuthFailure(path, res.status, message))
            ) {
              return await fetchParsedCsv(false)
            }
            throw this.buildApiError(res.status, message, httpsUrl)
          }
        } catch (e) {
          if (typeof (e as any)?.status === 'number') throw e
          console.warn(
            '[ServerApiClient] HTTPS CSV request failed, trying HTTP fallback:',
            e,
          )
        }
      }

      const res = await fetch(url, { method: 'GET', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        const message = text || res.statusText
        if (
          allowAuthRetry &&
          (await this.maybeRecoverAuthFailure(path, res.status, message))
        ) {
          return await fetchParsedCsv(false)
        }
        throw this.buildApiError(res.status, message, url)
      }

      return await parseCsvResponse(res)
    }

    return await fetchParsedCsv(true)
  }

  async tableauGetViewImagePng(
    sessionKey: string,
    viewId: string,
    opts?: { params?: Record<string, string> | null },
  ): Promise<Blob> {
    const extra =
      opts?.params && typeof opts.params === 'object' ? opts.params : null
    const extraParts: string[] = []
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        const kk = String(k || '').trim()
        if (!kk) continue
        const vv = String(v ?? '').trim()
        extraParts.push(`${encodeURIComponent(kk)}=${encodeURIComponent(vv)}`)
      }
    }
    if (this.shouldUseElectronTableauBridge()) {
      const resolvedSessionKey =
        await this.ensureElectronLocalTableauSessionKey(sessionKey)
      const res: any = await this.electronInvokeTableauWithSessionRecovery(
        'tableau_get_view_image_png_local',
        {
          session_key: String(resolvedSessionKey || '').trim(),
          view_id: String(viewId || '').trim(),
          params: extra || null,
        },
      )
      const b64 = String(res?.b64 || '')
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      return new Blob([bytes], { type: 'image/png' })
    }
    const qp = `?session_key=${encodeURIComponent(String(sessionKey))}${extraParts.length ? `&${extraParts.join('&')}` : ''}`
    return await this.requestBlob(
      `/api/tableau/views/${encodeURIComponent(String(viewId))}/image_png${qp}`,
      { method: 'GET' },
    )
  }

  async parityCreateRun(
    req: ParityCreateRunRequest,
  ): Promise<ParityCreateRunResponse> {
    return await this.request('/api/parity/runs', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async parityUploadFusegrid(
    runId: string,
    req: ParityUploadFusegridRequest,
  ): Promise<{ ok: boolean }> {
    return await this.request(
      `/api/parity/runs/${encodeURIComponent(String(runId))}/fusegrid`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async parityUploadFusegridBlob(
    runId: string,
    name: 'fusegrid.png' | 'fusegrid.csv' | 'fusegrid_debug.json',
    bytes: Uint8Array,
    opts?: { chunkSize?: number; contentType?: string },
  ): Promise<void> {
    const rid = String(runId || '').trim()
    if (!rid) throw new Error('Missing runId')
    const chunkSize = Math.max(
      64 * 1024,
      Math.min(
        2 * 1024 * 1024,
        Math.floor(Number(opts?.chunkSize ?? 512 * 1024)),
      ),
    )
    const total = bytes.byteLength >>> 0
    const ctype = String(opts?.contentType || '').trim()

    for (let off = 0; off < total; off += chunkSize) {
      const end = Math.min(total, off + chunkSize)
      const chunk = bytes.subarray(off, end)
      const qp =
        `?name=${encodeURIComponent(String(name))}` +
        `&offset=${encodeURIComponent(String(off))}` +
        `&total_size=${encodeURIComponent(String(total))}`
      const path = `/api/parity/runs/${encodeURIComponent(rid)}/fusegrid_blob${qp}`

      await this.requestNoContent(path, {
        method: 'POST',
        headers: ctype ? { 'Content-Type': ctype } : undefined,
        body: chunk as any,
      })
    }
  }

  async parityComputeDiff(
    runId: string,
    req: ParityComputeDiffRequest,
  ): Promise<ParityDiffReport> {
    return await this.request(
      `/api/parity/runs/${encodeURIComponent(String(runId))}/diff`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async parityRunCloudSanity(
    req: ParityRunCloudSanityRequest,
  ): Promise<ParityRunCloudSanityResponse> {
    return await this.request('/api/parity/runs/cloud_sanity', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async parityGetArtifactBlob(runId: string, name: string): Promise<Blob> {
    return await this.requestBlob(
      `/api/parity/runs/${encodeURIComponent(String(runId))}/artifacts/${encodeURIComponent(String(name))}`,
      { method: 'GET' },
    )
  }

  async parityCreateSuite(
    req: ParityCreateSuiteRequest,
  ): Promise<ParityCreateSuiteResponse> {
    return await this.request('/api/parity/suites', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  async parityGetSuiteStatus(suiteId: string): Promise<any> {
    return await this.request(
      `/api/parity/suites/${encodeURIComponent(String(suiteId))}`,
      { method: 'GET' },
    )
  }

  async parityComputeSuiteDiff(
    suiteId: string,
    req: ParityComputeSuiteDiffRequest,
  ): Promise<ParitySuiteDiffReport> {
    return await this.request(
      `/api/parity/suites/${encodeURIComponent(String(suiteId))}/diff`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      },
    )
  }

  async parityGetSuiteArtifactBlob(
    suiteId: string,
    name: string,
  ): Promise<Blob> {
    return await this.requestBlob(
      `/api/parity/suites/${encodeURIComponent(String(suiteId))}/artifacts/${encodeURIComponent(String(name))}`,
      { method: 'GET' },
    )
  }

  async getWorkbookDocument(
    workbookId: string,
  ): Promise<ServerWorkbookDocument> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/document`,
      { method: 'GET' },
    )
  }

  async putWorkbookDocument(
    workbookId: string,
    req: { version: number; document_json: any },
  ): Promise<ServerWorkbookDocument> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/document`,
      {
        method: 'PUT',
        body: JSON.stringify(req),
      },
    )
  }

  async mergeWorkbookDocument(
    workbookId: string,
    req: { patch: any; max_retries?: number | null },
    opts?: { keepalive?: boolean },
  ): Promise<ServerWorkbookDocument> {
    const path = `/api/workbooks/${encodeURIComponent(String(workbookId))}/document/merge`
    return await this.request(path, {
      method: 'POST',
      body: JSON.stringify(req),
      keepalive: !!opts?.keepalive,
    })
  }

  async listWorkbookFieldOverrides(
    workbookId: string,
    opts?: { dataset_id?: string | null },
  ): Promise<ServerWorkbookFieldOverride[]> {
    const qp = opts?.dataset_id
      ? `?dataset_id=${encodeURIComponent(String(opts.dataset_id))}`
      : ''
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/field_overrides${qp}`,
      { method: 'GET' },
    )
  }

  async upsertWorkbookFieldOverrides(
    workbookId: string,
    req: {
      overrides: Array<{
        dataset_id: string
        field_id: string
        override_json: any
      }>
    },
  ): Promise<ServerWorkbookFieldOverride[]> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/field_overrides`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    )
  }

  // ---------------- Tableau workbook events (delta-only cloud save) ----------------

  async getWorkbookTableauBootstrap(
    workbookId: string,
    opts?: { limit_events?: number | null; omit_workbook_ir?: boolean | null },
  ): Promise<ServerTableauWorkbookBootstrap> {
    const params: string[] = [
      `protocol_version=${encodeURIComponent(String(CREATOR_COLLAB_PROTOCOL_VERSION))}`,
    ]
    if (
      typeof opts?.limit_events === 'number' &&
      Number.isFinite(opts.limit_events)
    ) {
      params.push(
        `limit_events=${encodeURIComponent(String(Math.max(0, Math.floor(opts.limit_events))))}`,
      )
    }
    if (opts?.omit_workbook_ir === true) {
      params.push('omit_workbook_ir=true')
    }
    const qp = params.length ? `?${params.join('&')}` : ''
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/tableau/bootstrap${qp}`,
      { method: 'GET' },
    )
  }

  async getWorkbookCurrentTableauIr(
    workbookId: string,
  ): Promise<ServerTableauCurrentWorkbookIr> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/tableau/current-ir`,
      {
        method: 'GET',
      },
      {
        timeoutMs: DEFAULT_TABLEAU_CURRENT_IR_REQUEST_TIMEOUT_MS,
      },
    )
  }

  async listWorkbookTableauEvents(
    workbookId: string,
    opts?: { after_seq?: number | null; limit?: number | null },
  ): Promise<ServerTableauWorkbookEvent[]> {
    const params: string[] = [
      `protocol_version=${encodeURIComponent(String(CREATOR_COLLAB_PROTOCOL_VERSION))}`,
    ]
    if (
      typeof opts?.after_seq === 'number' &&
      Number.isFinite(opts.after_seq)
    ) {
      params.push(
        `after_seq=${encodeURIComponent(String(Math.max(0, Math.floor(opts.after_seq))))}`,
      )
    }
    if (typeof opts?.limit === 'number' && Number.isFinite(opts.limit)) {
      params.push(
        `limit=${encodeURIComponent(String(Math.max(0, Math.floor(opts.limit))))}`,
      )
    }
    const qp = params.length ? `?${params.join('&')}` : ''
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/tableau/events${qp}`,
      { method: 'GET' },
    )
  }

  async appendWorkbookTableauEvents(
    workbookId: string,
    req: {
      base_seq?: number | null
      client_id?: string | null
      events: Array<{
        client_event_id?: string | null
        event_type: string
        payload: any
      }>
    },
  ): Promise<ServerAppendTableauWorkbookEventsAck> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/tableau/events`,
      {
        method: 'POST',
        body: JSON.stringify({
          protocol_version: CREATOR_COLLAB_PROTOCOL_VERSION,
          ...req,
        }),
      },
    )
  }

  async clearWorkbookTableauImports(workbookId: string): Promise<any> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/tableau/imports`,
      {
        method: 'DELETE',
      },
    )
  }

  async detachWorkbookTableauImport(
    workbookId: string,
    importId: string,
  ): Promise<any> {
    return await this.request(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/tableau/imports/${encodeURIComponent(String(importId))}`,
      {
        method: 'DELETE',
      },
    )
  }

  async getWorkbookBootstrap(
    workbookId: string,
    opts?: { timeoutMs?: number },
  ): Promise<ServerWorkbookBootstrap> {
    const response = await this.request<unknown>(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/bootstrap`,
      {
        method: 'GET',
      },
      {
        timeoutMs: opts?.timeoutMs,
      },
    )
    const bootstrap = normalizeWorkbookBootstrap(response, String(workbookId))
    if (!bootstrap) {
      throw new Error('Invalid workbook bootstrap response')
    }
    return bootstrap
  }

  async getWorkbookDocumentSessionBootstrap(
    workbookId: string,
    opts?: { timeoutMs?: number },
  ): Promise<ServerDocumentSessionBootstrap> {
    const response = await this.request<unknown>(
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/session/bootstrap`,
      {
        method: 'GET',
      },
      {
        timeoutMs: opts?.timeoutMs,
      },
    )
    const bootstrap = normalizeDocumentSessionServerMessage({
      ...(response && typeof response === 'object' && !Array.isArray(response)
        ? response
        : {}),
      type: 'bootstrap_ready',
    })
    if (!bootstrap || bootstrap.type !== 'bootstrap_ready') {
      throw new Error('Invalid document session bootstrap response')
    }
    const requestedWorkbookId = String(workbookId || '').trim()
    if (
      !bootstrap.workbook_id ||
      bootstrap.workbook_id !== requestedWorkbookId ||
      !bootstrap.session_id ||
      !bootstrap.lease.session_id ||
      !bootstrap.lease.resume_token
    ) {
      throw new Error('Invalid document session bootstrap response')
    }
    return {
      protocol_version: bootstrap.protocol_version,
      workbook_id: bootstrap.workbook_id,
      session_id: bootstrap.session_id,
      snapshot_seq: bootstrap.snapshot_seq,
      snapshot_payload_b64: bootstrap.snapshot_payload_b64,
      chunk_snapshot_payloads_b64: bootstrap.chunk_snapshot_payloads_b64,
      tail: bootstrap.tail,
      chunk_descriptors: bootstrap.chunk_descriptors,
      projection_descriptors: bootstrap.projection_descriptors,
      lease: bootstrap.lease,
    }
  }

  workbookWebSocketUrl(workbookId: string): string {
    const tok = this.token ? String(this.token).trim() : ''
    const qp = tok ? `?token=${encodeURIComponent(tok)}` : ''
    const url = joinUrl(
      this.baseUrl,
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/ws${qp}`,
    )
    // Mirror `request()` mixed-content handling: if the app is HTTPS, prefer wss/https.
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:'
    ) {
      return url
        .replace(/^http:\/\//i, 'wss://')
        .replace(/^https:\/\//i, 'wss://')
    }
    return url.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'ws://')
  }

  async workbookWebSocketUrlAsync(workbookId: string): Promise<string> {
    await this.ensureFreshRealtimeAuthToken()
    return this.workbookWebSocketUrl(workbookId)
  }

  workbookDocumentSessionWebSocketUrl(workbookId: string): string {
    const tok = this.token ? String(this.token).trim() : ''
    const qp = tok ? `?token=${encodeURIComponent(tok)}` : ''
    const url = joinUrl(
      this.baseUrl,
      `/api/workbooks/${encodeURIComponent(String(workbookId))}/session/ws${qp}`,
    )
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:'
    ) {
      return url
        .replace(/^http:\/\//i, 'wss://')
        .replace(/^https:\/\//i, 'wss://')
    }
    return url.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'ws://')
  }

  async workbookDocumentSessionWebSocketUrlAsync(
    workbookId: string,
  ): Promise<string> {
    await this.ensureFreshRealtimeAuthToken()
    return this.workbookDocumentSessionWebSocketUrl(workbookId)
  }
}
